/**
 * Deutsch — die Rückfallsprache und zugleich der verbindliche Schlüsselvorrat.
 *
 * **Der Schlüssel ist die Bedeutung, der Text ist die Beschriftung.** Das ist
 * genau die Trennung aus R148 und R157: Der Kunde darf „Buchungskreis" schreiben,
 * wo hier „Gesellschaft" steht — es ändert die Beschriftung, nicht die Sache. Der
 * Bereichsschnitt selbst ist deshalb **nicht** kundenveränderlich: `bereich.
 * stammdaten.name` lässt sich umbenennen, aber der Bereich lässt sich nicht
 * verschieben, teilen oder abschaffen.
 *
 * **Fachbegriffe kommen aus dem Glossar** (`docs/00-projekt/glossar.md`), nicht
 * aus freier Übersetzung — einschließlich der Wörter, die dort ausdrücklich
 * nicht verwendet werden: keine „Mandanten", keine „Ledger", keine „User".
 */
export const de = {
  'sprache.name': 'Deutsch',

  /*
    Nicht nur Sätze sind sprachabhängig, sondern auch Zahlen und Monate.
    „13.514.000,00" liest ein englischsprachiger Anwender als dreizehn Komma
    fünf — eine übersetzte Oberfläche mit deutscher Zifferngruppierung ist an
    der einen Stelle falsch, an der es um Beträge geht. Deshalb steht die
    Ländereinstellung in der Sprachdatei und nicht im Code.
  */
  'sprache.gebietsschema': 'de-DE',

  /* Das Fenster für eine Maske — an jeder Stelle dasselbe (R710) */
  'fenster.schliessen': 'Schließen',
  'fenster.griff': 'Ziehen verschiebt das Fenster, Doppelklick macht es groß und wieder klein',
  'fenster.bildschirmfuellend': 'Bildschirmfüllend',
  'fenster.alsfenster': 'Als Fenster zeigen',

  /*
    Was gelang — das Kästchen oben rechts (7.2 Abschnitt 34, R1130–R1132).
    Die Tat und ihr Gegenstand, sonst nichts: keine Menge, keine Kennung,
    keine Nummer (R1131).
  */
  'erfolg.aufforderung.zeigen': 'Datenimporte dieser Periode zeigen',
  'erfolg.datenimport.gespeichert': 'Datenimport erfolgreich gespeichert',
  'erfolg.konto.eingefuegt': 'Konto erfolgreich eingefügt',
  'erfolg.position.eingefuegt': 'Position erfolgreich eingefügt',
  'erfolg.kontogruppe.gesetzt': 'Kontengruppe erfolgreich geändert',
  'erfolg.datenimporte.gespeichert': 'Datenimporte erfolgreich gespeichert',
  'erfolg.datenimporte.aktualisiert': 'Datenimporte erfolgreich aktualisiert',
  'erfolg.datenimport.geloescht': 'Datenimport erfolgreich gelöscht',
  'erfolg.datenimport.verschoben': 'Datenimport erfolgreich verschoben',
  'erfolg.datenimporte.verschoben': 'Datenimporte erfolgreich verschoben',
  'erfolg.package.gespeichert': 'Package erfolgreich gespeichert',
  'erfolg.konzernabschluss.angelegt': 'Konzernabschluss erfolgreich angelegt',
  'erfolg.konzernabschluss.gerechnet': 'Konzernabschluss erfolgreich gerechnet und gespeichert',
  'erfolg.handbuch.gespeichert': 'Ausgabe erfolgreich gespeichert',
  'erfolg.handbuch.entfernt': 'Ausgabe erfolgreich entfernt',
  'erfolg.schritt.angelegt': 'Schritt erfolgreich angelegt',
  'erfolg.schritt.entfernt': 'Schritt erfolgreich entfernt',
  'erfolg.prozess.gespeichert': 'Prozess erfolgreich gespeichert',
  'erfolg.spaltenzuordnung.gespeichert': 'Spaltenzuordnung erfolgreich gespeichert',
  'erfolg.stammdaten.angelegt': 'Eintrag erfolgreich angelegt',
  'erfolg.stammdaten.entfernt': 'Eintrag erfolgreich entfernt',
  'erfolg.stammdaten.stillgelegt': 'Eintrag erfolgreich stillgelegt',
  'erfolg.stammdaten.uebernommen': 'Stammdaten erfolgreich übernommen',
  'erfolg.tafel.kopiert': 'Erfolgreich kopiert',
  'erfolg.tafel.eingefuegt': 'Erfolgreich eingefügt und gespeichert',
  'erfolg.bewegungsart.angelegt': 'Bewegungsart erfolgreich angelegt',
  'erfolg.wert.angelegt': 'Wert erfolgreich angelegt',
  'erfolg.beleg.gebucht': 'Beleg erfolgreich gebucht',
  'erfolg.beleg.berichtigt': 'Beleg erfolgreich berichtigt',
  'erfolg.beleg.aufgeloest': 'Beleg erfolgreich aufgelöst',
  'erfolg.beleg.imjournal': 'Der Bericht zeigt ihn mit dem nächsten Lauf; im Buchungsjournal steht er schon.',
  'erfolg.packagevorlage.gespeichert': 'Packagevorlage erfolgreich gespeichert',
  'erfolg.packagevorlage.heruntergeladen': 'Packagevorlage erfolgreich heruntergeladen',
  'erfolg.zeitraum.gespeichert': 'Zeitraum erfolgreich gespeichert',
  'erfolg.kontenzuordnung.stillgelegt': 'Zuordnung erfolgreich stillgelegt',
  'erfolg.kontenzuordnung.inkraft': 'Zuordnung wieder in Kraft gesetzt',
  'erfolg.kontenzuordnung.berichtigt': 'Zuordnung berichtigt',
  'erfolg.konto.angelegt': 'Konto erfolgreich angelegt',
  'erfolg.position.angelegt': 'Position erfolgreich angelegt',
  'erfolg.kfrzeile.angelegt': 'Zeile erfolgreich angelegt',

  /* --- Die Leiste --- */
  'leiste.bezeichnung': 'Bereiche',
  /* Die vier Blöcke der Leiste (7.2 Abschnitt 3a, R324). Sie benennen einen
     Arbeitsgang und keine Stammdatenart: „Der Abschluss" ist das, was im Monat
     passiert, nicht der Ort, an dem etwas liegt. */
  'leiste.gruppe.abschluss': 'Der Abschluss',
  'leiste.gruppe.auswerten': 'Auswerten und prüfen',
  'leiste.gruppe.einrichten': 'Einrichten und pflegen',
  'leiste.gruppe.verwaltung': 'Verwaltung',
  'leiste.einklappen': 'Einklappen',
  'leiste.ausklappen': 'Ausklappen',
  'leiste.einklappen.hilfe': 'Bereichsleiste einklappen',
  'leiste.ausklappen.hilfe': 'Bereichsleiste ausklappen',
  'leiste.geplant': 'geplant',
  /* Das Kontextmenü an einem Eintrag der Leiste (7.2 Abschnitt 3c, R529).
     Der Punkt trägt den **Namen des Eintrags**, nicht das Wort „Bereich" —
     auf einem Unterpunkt wäre es sonst schlicht falsch. */
  'leiste.menue.fenster': '{name} in neuem Fenster öffnen',
  'leiste.menue.tab': '{name} in neuem Tab öffnen',
  'leiste.menue.adresse': 'Adresse kopieren',
  'leiste.menue.bezeichnung': 'Was mit diesem Eintrag geschehen soll',
  'marke.zusatz': 'Konsolidierung',

  /* --- 1 Arbeitsliste --- */
  'bereich.arbeitsliste.name': 'Arbeitsliste',
  'bereich.arbeitsliste.zweck': 'Der Arbeitsvorrat des laufenden Abschlusses.',

  /* --- 2 Abschluss --- */
  'bereich.abschluss.name': 'Abschluss',
  'bereich.abschluss.zweck': 'Den laufenden Abschluss rechnen, prüfen und freigeben.',
  // R1607: der Bildschirm, der über die Freigabe eines Monats entscheidet.
  'bereich.abschluss.monitor': 'Konzernabschluss-Monitor',
  'bereich.abschluss.konzernhandbuch': 'Konzernhandbuch',
  'bereich.abschluss.schritte': 'Konsolidierungsschritte',
  'bereich.abschluss.ic-abstimmung': 'IC-Abstimmung',

  /* --- 3 Berichte --- */
  'bereich.berichte.name': 'Berichte',
  'bereich.berichte.zweck':
    'Abschlussbericht mit fester Gliederung und freie Analyse — getrennt.',
  'bereich.berichte.bilanz': 'Konzernbilanz',
  'bereich.berichte.guv': 'Gewinn- und Verlustrechnung',
  'bereich.berichte.kapitalflussrechnung': 'Kapitalflussrechnung (DRS 21)',
  'bereich.berichte.spiegel': 'Anlagenspiegel',
  'bereich.berichte.abschlussbericht': 'Abschlussbericht',
  'bereich.berichte.wertsuche': 'Wertsuche',

  /* --- 4 Datenimport --- */
  'bereich.datenimport.name': 'Datenimport',
  'bereich.datenimport.zweck': 'Lieferungen annehmen, zuordnen und die Klärung führen.',
  'bereich.datenimport.quellen': 'Datenquellen',
  'bereich.datenimport.packagevorlagen': 'Package-Vorlagen',
  'bereich.datenimport.lieferungen': 'Datenimporte',
  'bereich.datenimport.importmapping': 'Zuordnungen',
  'bereich.datenimport.klaerungsliste': 'Klärungsliste',
  'bereich.datenimport.ladeprozesse': 'Ladeprozesse und Zeitpläne',
  'bereich.datenimport.landing': 'Archiv der Lieferdateien',

  /* --- 5 Konzernstruktur --- */
  'bereich.konzernstruktur.name': 'Konzernstruktur',
  'bereich.konzernstruktur.zweck': 'Wer gehört wem, seit wann und in welchem Kreis.',
  'bereich.konzernstruktur.struktur': 'Grafische Struktur',
  'bereich.konzernstruktur.beteiligungsregister': 'Beteiligungsregister',
  'bereich.konzernstruktur.kreise': 'Konsolidierungskreise',
  'bereich.konzernstruktur.einbeziehung': 'Einbeziehungszeitpunkte',
  'bereich.konzernstruktur.ueberleitung': 'Überleitung Teilkonzern → Gesamt',

  /* --- 6 Stammdaten --- */
  'bereich.stammdaten.name': 'Stammdaten',
  'bereich.stammdaten.zweck': 'Der gepflegte Vorrat, aus dem jeder Abschluss gerechnet wird.',
  'bereich.stammdaten.einheiten': 'Gesellschaften',
  'bereich.stammdaten.kontenrahmen': 'Kontenrahmen',
  'bereich.stammdaten.positionsplaene': 'Positionspläne',
  'bereich.stammdaten.ledger': 'Bewertungsebenen',
  'bereich.stammdaten.bewegungsarten': 'Bewegungsarten',
  'bereich.stammdaten.analysedimensionen': 'Optionale Dimensionen',
  'bereich.stammdaten.partnercodes': 'Partnercodes',
  'partnercode.titel': 'Aufbau der Partnercodes',
  'partnercode.neu': 'Partnercode anlegen',
  'partnercode.neuegruppe': 'Gruppe anlegen',
  'partnercode.leer': 'Es gibt noch keine Partnercodes.',
  'partnercode.wurzel': 'oberste Ebene',
  'partnercode.neu.titel': 'Neuer Partnercode unter {eltern}',
  'partnercode.neu.titel.gruppe': 'Neue Gruppe unter {eltern}',
  'partnercode.marke.gruppe': 'Gruppe',
  'partnercode.marke.einheit': 'Gesellschaft {name}',
  'partnercode.marke.ohneeinheit': 'ohne Konzerngesellschaft',
  'partnercode.aussondern': 'Löschen',
  'partnercode.aussondern.frage': '{name} löschen? Was verwendet wurde, wird stillgelegt; was nie verwendet wurde, verschwindet.',
  'partnercode.menue.neu': 'Neuen Partnercode anlegen',
  'partnercode.menue.neuegruppe': 'Neue Gruppe anlegen',
  'partnercode.menue.hoch': 'Nach oben',
  'partnercode.menue.runter': 'Nach unten',
  'partnercode.menue.einruecken': 'In die Gruppe darüber',
  'partnercode.menue.ausruecken': 'Eine Ebene höher',
  'partnercode.menue.aussondern': 'Löschen',
  'bereich.stammdaten.laender': 'Länder',
  'bereich.stammdaten.waehrungen': 'Währungen',
  'bereich.stammdaten.kurse': 'Währungskurse',
  'bereich.stammdaten.historische-kurse': 'Historische Kurse',
  'bereich.stammdaten.abstimmkreise': 'Abstimmkreise',

  /* --- 7 Regelwerk --- */

  /* --- 12 Perioden (2.19) --- */
  'bereich.perioden.name': 'Perioden',
  'bereich.perioden.zweck': 'Der Zeitraum, in dem dieses Haus Daten führt.',
  'bereich.perioden.zeitachse': 'Zeitachse',

  /* --- 8 Nachweis --- */
  'bereich.nachweis.name': 'Nachweis',
  'bereich.nachweis.zweck':
    'Was gerechnet wurde, womit — und dass daran nichts verändert wurde.',
  'bereich.nachweis.buchungsjournal': 'Buchungsjournal',
  'bereich.nachweis.aenderungsnachweis': 'Änderungsnachweis',
  'bereich.nachweis.laeufe': 'Läufe mit Prüfcode',
  'bereich.nachweis.prueferansicht': 'Prüferansicht',
  'bereich.nachweis.uebergangene': 'Übergangene Prüfungen',

  /* --- 9 Anwender und Rechte --- */
  'bereich.anwender.name': 'Anwender und Rechte',
  'bereich.anwender.zweck': 'Wer darf was — auf Gesellschaft, Bewertungsebene und Periode.',
  'bereich.anwender.liste': 'Anwender',
  'bereich.anwender.rollen': 'Rollen',
  'bereich.anwender.rechte': 'Rechte',
  'bereich.anwender.vieraugen': 'Vier-Augen-Kriterien',

  /* --- 10 Anhang --- */
  'bereich.anhang.name': 'Anhang',
  'bereich.anhang.zweck':
    'Der sechste Abschlussbestandteil — Sätze mit Zahlen darin, und jede Zahl aus dem Lauf.',
  'bereich.anhang.anhangsbuch': 'Anhangsbuch',
  'bereich.anhang.zulieferungen': 'Zulieferungen',
  'bereich.anhang.anteilsbesitz': 'Anteilsbesitz',
  'bereich.anhang.angabenkatalog': 'Angabenkatalog',
  'bereich.anhang.vollstaendigkeit': 'Vollständigkeit',
  'bereich.anhang.ausgabe': 'Ausgabe',

  /* --- Das Anhangsbuch --- */
  'anhang.gliederung': 'Gliederung',
  'anhang.angabe': 'Die Angabe',
  'anhang.herkunft': 'Woher die Zahlen kommen',
  'anhang.offen': '{offen} von {alle} offen',
  'anhang.blockieren': '{zahl} blockieren die Freigabe',
  'anhang.blockiert.keine': 'Nichts blockiert die Freigabe.',
  'anhang.gruppe.allgemeines': 'Allgemeines',
  'anhang.gruppe.methoden': 'Methoden',
  'anhang.gruppe.bilanz': 'Bilanz',
  'anhang.gruppe.guv': 'Gewinn- und Verlustrechnung',
  'anhang.gruppe.anteilsbesitz': 'Anteilsbesitz',
  'anhang.gruppe.sonstiges': 'Sonstiges',
  'anhang.herkunft.gerechnet': 'gerechnet',
  'anhang.herkunft.erhoben': 'erhoben',
  'anhang.herkunft.erklaert': 'erklärt',
  'anhang.art.pflicht': 'Pflicht',
  'anhang.art.wahlrecht': 'Wahlrecht',
  'anhang.art.freiwillig': 'freiwillig',
  'anhang.zustand.fehlt': 'fehlt',
  'anhang.zustand.offen': 'offen',
  'anhang.zustand.blockiert': 'blockiert',
  'anhang.zustand.veraltet': 'veraltet',
  'anhang.zustand.fertig': 'fertig',
  'anhang.fehlanzeige': 'Fehlanzeige',
  'anhang.fehlanzeige.erklaerung':
    'Ausdrücklich festgestellt, dass die Angabe nicht zutrifft. Gilt als erledigt.',
  'anhang.leer.titel': 'Für diese Angabe steht noch kein Text.',
  'anhang.leer.satz':
    'Leer sieht aus wie vergessen. Eine Pflichtangabe, die nicht zutrifft, wird ausdrücklich auf Fehlanzeige gesetzt — mit einem Satz dazu.',
  'anhang.keineangabe': 'Wählen Sie links eine Angabe.',
  'anhang.keinlauf':
    'Für diese Periode ist noch kein Abschluss gerechnet. Die Anker können ihre Beträge deshalb nicht zeigen.',
  'anhang.anker.nichtaufloesbar': 'löst sich nicht auf',
  'anhang.anker.vorjahr': 'Vorjahr',
  'anhang.anker.nachweis': 'Nachweis',
  'anhang.anker.melder': 'Gemeldet von',
  'anhang.anker.auszulieferung': 'aus Zulieferung',
  'anhang.anker.kfr': 'aus der Kapitalflussrechnung — in der Investitionstätigkeit aufgehoben',
  'anhang.verantwortlich': 'Verantwortlich',
  'anhang.frist': 'Frist',
  'anhang.pruefcode': 'Prüfcode des Anhangs',
  'anhang.pruefcode.erklaerung':
    'Über Text, Anker und Katalogausgabe — nicht der Prüfcode des Ergebnisses. Der Abschluss nennt beide.',
  'anhang.katalogausgabe': 'Katalogausgabe',
  'anhang.katalog.fundstelle': 'Fundstelle',
  'anhang.katalog.bezeichnung': 'Bezeichnung',
  'anhang.katalog.art': 'Art',
  'anhang.katalog.herkunft': 'Herkunft',
  'anhang.katalog.gruppe': 'Gliederung',
  'anhang.katalog.schutzklausel': 'Schutzklausel nach § 286 HGB anwendbar',
  'anhang.vollstaendigkeit.hart': 'Blockiert die Freigabe',
  'anhang.vollstaendigkeit.weich': 'Übergehbar mit Begründung',
  'anhang.vollstaendigkeit.leer': 'Keine offenen Punkte.',

  /* --- 11 Betrieb --- */
  'bereich.betrieb.name': 'Betrieb',
  'bereich.betrieb.zweck': 'Wie die Anlage läuft — und was sie noch nicht kann.',
  'bereich.betrieb.systemzustand': 'Systemzustand',
  'bereich.betrieb.antwortzeiten': 'Antwortzeiten',
  'bereich.betrieb.sprachen': 'Sprachen und Begriffe des Hauses',
  'bereich.betrieb.sicherung': 'Sicherung',
  'bereich.betrieb.lizenz': 'Lizenz',
  'bereich.betrieb.kundenwuensche': 'Nicht erfüllte Kundenwünsche',

  /* --- Der ehrliche leere Bildschirm --- */
  'platzhalter.titel': 'Dieser Bildschirm ist noch nicht gebaut.',
  'platzhalter.herkunft': 'Fachlich entschieden ist er in {herkunft}.',
  'platzhalter.stand': 'Dieser Bildschirm ist noch nicht gebaut.',
  'platzhalter.stand.verb.eins': 'steht',
  'platzhalter.stand.verb.mehrere': 'stehen',
  'platzhalter.fertig': 'Fertig sind:',
  'platzhalter.vorschau': 'Was hier liegen wird',

  /* --- Sprachen und Begriffe des Hauses --- */
  'sprachen.gewaehlt': 'Ausgelieferte Sprachen',
  'sprachen.beschriftungen.titel': 'Begriffe des Hauses',
  'sprachen.beschriftungen.stand': 'Die Pflege der Begriffe des Hauses am Bildschirm ist noch nicht gebaut.',

  /* --- Die Anwendung --- */
  'anwendung.titel': 'CoKo — Konzernabschluss',

  /* --- Der Schnellzugriff in der Kopfzeile --- */
  'kopf.sprache': 'Sprache',
  'kopf.zahlen': 'Zahlen',
  'kopf.vorzeichen': 'Beträge',
  'vorzeichen.plusminus': 'Plus und Minus',
  'vorzeichen.sollhaben': 'Soll und Haben',
  'kopf.minusfarbe': 'Negative Beträge',
  'zellsumme.summe': 'Summe',
  'zwischensumme.kopf': 'Summe',
  'zellsumme.anzahl': '{anzahl} Werte',
  'minusfarbe.rot': 'rot',
  'minusfarbe.schwarz': 'schwarz',

  /* --- Zahlenformat --- */
  'zahlenformat.titel': 'Zahlenformat',
  'zahlenformat.sprache': 'der Sprache folgen',

  /* --- Die Kontextzeile --- */
  'kontext.kreis': 'Konsolidierungskreis',
  'kontext.element': 'Organisationselement',
  /*
    Der Satz unter der Auswahlliste (R764). Er steht nur da, wo wirklich
    etwas fehlt — ein Hinweis, der immer kommt, wird nicht mehr gelesen.
  */
  'kontext.welt.konzernabschluss': 'Konzernabschluss',
  'kontext.welt.lieferdaten': 'Lieferdaten',
  'welt.standjetzt': 'Stand jetzt',
  'welt.gerechnet': 'gerechnet am {zeitpunkt}',
  'welt.ohnelauf': 'Für {monate} ist kein Konzernabschluss gerechnet.',
  'welt.daten': 'Seit der Übernahme sind Daten eingegangen ({monate}).',
  'welt.stammdaten': 'Seit der Übernahme sind Stammdaten geändert ({monate}).',
  'welt.beides': 'Seit der Übernahme sind Daten eingegangen und Stammdaten geändert ({monate}).',
  'welt.weg.rechnen': 'Den {name} neu rechnen',
  'welt.weg.oeffnen': 'Den {name} öffnen und rechnen',
  'welt.weg.anlegen': 'Einen Konzernabschluss anlegen',
  'kontext.element.offen':
    'Noch nicht aufgenommen: {was}. Aufnehmen unter Stammdaten → Organisationselemente.',
  'kontext.element.offen.gesellschaften.eins': 'eine Gesellschaft',
  'kontext.element.offen.gesellschaften.mehrere': '{anzahl} Gesellschaften',
  'kontext.element.offen.kreise.eins': 'ein Konsolidierungskreis',
  'kontext.element.offen.kreise.mehrere': '{anzahl} Konsolidierungskreise',
  'kontext.element.offen.und': ' und ',
  'kontext.kreis.rueckfrage': 'im Kreis',
  'kontext.ledger': 'Bewertungsebene',
  'kontext.periode': 'Periode',
  'kontext.periode.keine': 'keine Periode gewählt',
  'periode.anfang': 'Anfang',
  'kontext.zeitraum': 'Zeitraum',
  'kontext.raster': 'Raster',
  'zeitraum.von': 'Anfang des Zeitraums',
  'zeitraum.bis': 'Ende des Zeitraums',
  'zeitraum.ohnelauf': 'kein gerechneter Lauf',
  // Am gemerkten Zeitraum, wenn es neuere Zahlen gibt (R1365).
  'zeitraum.neuere': 'Zahlen bis {bis}',
  /*
    **Ein Abschnitt der Zeitachse heißt nach dem Raster** — Monat, Quartal,
    Jahr. Bis zum 2026-09-17 stand hier „Einheit", und das war am Bildschirm
    das Wort für die Gesellschaft: Wer „Klicken wählt eine Einheit" über einem
    Zeitstreifen liest, sucht eine Tochter und findet einen Monat. Es ist
    derselbe Gedanke wie R430 beim Einzelwert — die Beschriftung nennt den
    Zeitraum, den sie wirklich trägt, und nicht einen Oberbegriff darüber.
  */
  'zeitraum.streifen.hilfe':
    'Klicken wählt {abschnitt}, Ziehen spannt den Zeitraum auf, Umschalt-Klick erweitert ihn. Mit den Pfeiltasten verschieben Sie ihn, mit Umschalt und Pfeil ziehen Sie ihn auf.',
  'zeitraum.abschnitt.monat': 'einen Monat',
  'zeitraum.abschnitt.quartal': 'ein Quartal',
  'zeitraum.abschnitt.jahr': 'ein Jahr',
  'zeitraum.keinlauf.kurz': 'kein Lauf',
  'zeitraum.ueberblick.hilfe':
    'Die ganze Zeitachse. Klicken oder ziehen verschiebt den Ausschnitt darunter; gewählt wird im Streifen.',
  'zeitraum.quartal': 'Q{quartal} {jahr}',
  'kontext.rechnet': 'Rechnet …',
  'kontext.kreis.rueckfall': 'Konzern',

  /*
    Die fünf Zustände (R141). Eigene Schlüssel, obwohl „offen" auch weiter
    unten steht: Der Zustand eines Objekts und der Zustand eines Abschlusses
    sind zwei Bedeutungen, die im Deutschen zufällig dasselbe Wort treffen —
    im Englischen tun sie es nicht. Zwei Bedeutungen, zwei Schlüssel.
  */
  'zustand.fehlt': 'fehlt',
  'zustand.offen': 'offen',
  'zustand.blockiert': 'blockiert',
  'zustand.veraltet': 'veraltet',
  'zustand.fertig': 'fertig',

  /* --- Die Zustände eines Abschlusses (2.13) --- */
  'status.offen': 'offen',
  'status.in_arbeit': 'in Arbeit',
  'status.freigegeben': 'freigegeben',
  'status.testiert': 'testiert',

  /* --- Arbeitsliste --- */
  'arbeitsliste.kopf.leer': 'keine Aktion nötig. Alles, was geprüft werden kann, ist geprüft.',
  'arbeitsliste.kopf.eins': '{anzahl} Aktion nötig. Sortiert nach Schweregrad des Befundes.',
  'arbeitsliste.kopf.mehrere': '{anzahl} Aktionen nötig. Sortiert nach Schweregrad des Befundes.',
  'ueberwachung.aufforderung': 'Legen Sie fest, was Sie überwachen.',
  'ueberwachung.aufforderung.weg':
    'Wählen Sie unter [[Überwachung festlegen]] Konzerne, Ordner oder Gesellschaften und einen Zeitraum. Danach steht hier je Gesellschaft, was zu tun ist.',
  'ueberwachung.festlegen': 'Überwachung festlegen',
  'ueberwachung.festlegen.erlaeuterung':
    'Wählen Sie aus dem Baum der Berichtsauswahl, was die Arbeitsliste überwacht. Ein Konzern bringt alle seine Gesellschaften mit, ein Ordner die, die im Zeitraum unter ihm hängen — auch später dazukommende.',
  'ueberwachung.festlegen.von': 'Von',
  'ueberwachung.festlegen.bis': 'Bis',
  'ueberwachung.festlegen.bestandteil': 'Bestandteil',
  'ueberwachung.festlegen.speichern': 'Festlegen',
  'ueberwachung.festlegen.abbrechen': 'Abbrechen',
  'ueberwachung.art.kreis': 'Konzern',
  'ueberwachung.art.ordner': 'Ordner',
  'ueberwachung.art.gesellschaft': 'Gesellschaft',
  'ueberwachung.befund.zeitraum': 'Geben Sie einen Zeitraum von bis an.',
  'ueberwachung.befund.reihenfolge': 'Der Zeitraum endet vor seinem Anfang. Tauschen Sie von und bis.',
  'ueberwachung.befund.leer': 'Wählen Sie mindestens einen Konzern, einen Ordner oder eine Gesellschaft.',
  'ueberwachung.kopf': 'Überwacht: {bestandteile} · {von} bis {bis}.',
  'ueberwachung.nochnichts': 'Im gewählten Zeitraum ist noch kein Monat vorbei; geprüft wird ab dem ersten abgelaufenen.',
  'ueberwachung.blatt.kopf': '{von} bis {bis}, geprüft bis {geprueft}',
  'ueberwachung.blatt.ohnebefund': '{ohne} von {anzahl} Gesellschaften ohne Befund.',
  'ueberwachung.blatt.ohnebefund.eins': '{ohne} von {anzahl} Gesellschaft ohne Befund.',
  'ueberwachung.konzerne': 'Konzerne',
  'ueberwachung.gesellschaften': 'Gesellschaften',
  'ueberwachung.gruppe.Lieferung': 'Lieferung',
  'ueberwachung.gruppe.Spiegel': 'Spiegel',
  /* Die langen Köpfe tragen Trennstellen (­): Das Blatt zeigt alle acht Gruppen auf einen Blick (R1519). */
  'ueberwachung.gruppe.Konzernintern': 'Konzern­intern',
  'ueberwachung.gruppe.KapitalUndBeteiligungen': 'Kapital und Beteili­gungen',
  'ueberwachung.gruppe.Kapitalflussrechnung': 'Kapital­fluss­rechnung',
  'ueberwachung.gruppe.Kurse': 'Kurse',
  'ueberwachung.gruppe.Anhang': 'Anhang',
  'ueberwachung.gruppe.Konsolidierungsprozess': 'Konsolidierungs­prozess',
  'ueberwachung.liste.schliessen': 'Schließen',
  'ueberwachung.liste.leer': 'Hier ist nichts zu tun.',
  'ueberwachung.ausgefallen': 'Die Überwachung ließ sich gerade nicht lesen. Laden Sie die Seite gleich noch einmal.',
  // R1400: je Paar und Gegenstand eine Zeile.
  'arbeitsliste.ic-differenz':
    'Konzerninterne Differenz nicht geklärt: {gesellschaft} gegen {partner} — {gegenstand} weichen um {differenz} {waehrung} voneinander ab.',
  'ic-differenz.gegenstand.schuldenkonsolidierung': 'Forderungen und Verbindlichkeiten',
  'ic-differenz.gegenstand.aufwandertragkonsolidierung': 'Aufwendungen und Erträge kumuliert seit Jahresbeginn',
  // R1510: Bei der Ausschüttung stehen beide Seiten und die Quote da.
  'ic-differenz.gegenstand.dividendenkonsolidierung': 'Beteiligungsertrag und Ausschüttung',
  'arbeitsliste.ic-differenz.dividende':
    'Konzerninterne Differenz nicht geklärt: {gesellschaft} gegen {partner} — kumuliert seit Jahresbeginn Beteiligungsertrag {ertrag} {waehrung}, Ausschüttung {ausschuettung} {waehrung} mal Kapitalanteil {quote}, Unterschied {differenz} {waehrung}.',
  'arbeitsliste.beteiligung-ohne-partner':
    'Beteiligung ohne gehaltene Gesellschaft: {konto} bei {einheit} im {periode} mit {betrag} — die Kapitalkonsolidierung unterbleibt für diesen Betrag.',
  'arbeitsliste.keinedaten': 'Für {periode} liegen keine Daten vor.',
  'arbeitsliste.keinedaten.ohne': 'Für die gewählte Periode liegen keine Daten vor.',
  'arbeitsliste.veraltet.regelwerk':
    'Für „{abschluss}“ gilt inzwischen eine neue Ausgabe des Konzernhandbuchs.',
  'arbeitsliste.bilanz': 'Die Bilanz geht nicht auf: Aktiva und Passiva stimmen nicht überein.',
  'arbeitsliste.antwortzeit.verletzt':
    'Die Antwortzeitzusage von {budget} ms ist verletzt: {ueber} von {anzahl} Aufrufen liegen darüber.',
  'arbeitsliste.weg.freigeben': 'Geben Sie ihn im [[Konzernabschluss-Monitor]] frei.',
  'arbeitsliste.weg.bilanz':
    'Öffnen Sie die [[Konzernbilanz]] und suchen Sie die Herkunft der Differenz. Geht sie auf, verschwindet diese Meldung.',
  'arbeitsliste.weg.kette':
    'Verständigen Sie den Betrieb: Die Kette der Einträge ist unterbrochen. Wo, zeigt der [[Änderungsnachweis]].',
  'arbeitsliste.weg.antwortzeiten': 'Die einzelnen Aufrufe stehen unter [[Antwortzeiten]].',
  'arbeitsliste.weg.keinedaten':
    'Lesen Sie die Lieferungen der Gesellschaften unter [[Datenimporte]] ein. Sobald Daten vorliegen, rechnet CoKo den Konzernabschluss, und diese Meldung verschwindet.',
  'arbeitsliste.weg.neurechnen':
    'Wechseln Sie in den Bereich [[Konzernabschlüsse]] und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'arbeitsliste.weg.status': 'Jede spätere Änderung steht im [[Änderungsnachweis]].',
  'arbeitsliste.weg.schritte': 'Die Einstellungen des Prozesses stehen am Konzernabschluss unter [[Konzernabschlüsse]].',
  'arbeitsliste.freigabe': '„{abschluss}“ ist für {periode} freigabefähig.',
  'arbeitsliste.status': '„{abschluss}“ ist für {periode} {status}.',
  'arbeitsliste.schwere.blockiert': 'Blockiert die Freigabe',
  'arbeitsliste.schwere.veraltet': 'Veraltet',
  'arbeitsliste.schwere.offen': 'Hinweis',
  'arbeitsliste.schwere.fehlt': 'Fehlt',
  'arbeitsliste.schwere.fertig': 'Erledigt',
  // R1473: Die Aufgabe „Historische Kurse anlegen“, je Gesellschaft eine Zeile.
  'arbeitsliste.historisch.menge': '{anzahl} Bewegungen',
  'arbeitsliste.historisch.menge.eins': 'eine Bewegung',
  'arbeitsliste.historisch.was':
    'Für {bewegungen} von {gesellschaft} fehlen im {periode} historische Kurse. Bis die Tabelle zur Lieferung passt, rechnet der Konzernabschluss nicht.',
  'arbeitsliste.historisch.weg.klick':
    'Klicken Sie auf „Anlegen“: Die Zeilen entstehen mit dem vorgeschlagenen Kurs, und diese Meldung verschwindet. Einzeln stehen sie unter [[Historische Kurse]].',
  'arbeitsliste.historisch.weg.eintragen':
    'Tragen Sie unter [[Historische Kurse]] den Kurs ein, wo es keinen Vorschlag gibt, oder passen Sie die Zeilen an die Lieferung an. Dann verschwindet diese Meldung.',
  'arbeitsliste.historisch.anlegen': 'Anlegen',
  /* R1448 — eine inaktive Gesellschaft, die noch im Kreis steht. */
  'arbeitsliste.inaktiv.was': '{gesellschaft} ist seit {seit} inaktiv, steht im Kreis {kreis} aber noch ohne Ende.',
  'arbeitsliste.inaktiv.weg':
    'Beenden Sie unter [[Konsolidierungskreise]] die Mitgliedschaft zum {seit}. Bis dahin rechnet der Konzernabschluss mit dem Gelieferten.',

  /* --- Konzernabschluss-Monitor (R1607) --- */
  'monitor.hinweis':
    'Kreis, Bewertungsebene und Periode oben wählen. Der Lauf dazu entsteht von selbst, sobald Daten vorliegen.',
  'monitor.lauf': 'Gerechnet',
  'monitor.waehrung': 'Konzernwährung',
  'monitor.regelwerk': 'Regelwerk',
  'monitor.ausgabe': 'Ausgabe {nummer}',
  'monitor.pruefcode': 'Prüfcode des Ergebnisses',
  'monitor.hinweise': 'Hinweise aus dem Lauf',

  /* --- Freigabe --- */
  'freigabe.moeglich': 'Der Abschluss ist freigabefähig.',
  'freigabe.gesperrt': 'Die Freigabe ist gesperrt.',
  'freigabe.zustand': 'Der Abschluss ist {status}.',
  'freigabe.zeitraum': 'Geprüft {von} bis {bis}.',
  'freigabe.knopf': 'Abschluss freigeben',
  'freigabe.laeuft': 'Gibt frei …',

  /* --- Bericht --- */
  'gliederung.wahl': 'Gliederung',
  'spalte.position': 'Position',
  /* Der Konsolidierungssachverhalt tritt abgekuerzt auf wie seine fuenf Nachbarn
     (R464): In der Kopfzeile steht die Kurzform, ausgeschrieben wird er dort,
     wo erklaert wird. */
  'spalte.hb2': 'HB II',
  'spalte.kapko': 'KapKo',
  'spalte.schuko': 'SchuKo',
  'spalte.aeko': 'AEKo',
  'spalte.zwerge_uv': 'ZwErgE UV',
  'spalte.zwerge_av': 'ZwErgE AV',
  'spalte.geliefert': 'Geliefert',
  'spalte.konzern': 'Konzern',
  'bericht.aufklappen': 'Diese Zeile auf- und zuklappen',
  'bericht.menue.einheiten': 'Nach Gesellschaften aufreißen',
  'bericht.menue.konten': 'Nach Sachkonten aufreißen',
  'bericht.menue.alle.einheiten': 'Alles nach Gesellschaften aufklappen',
  'bericht.menue.alle.konten': 'Alles nach Sachkonten aufklappen',
  'bericht.menue.zuklappen': 'Alles zuklappen',
  /* Die drei Ansichtsgruppen stehen hinter je einem Punkt (7.3 Abschnitt 3, R576). */
  'bericht.menue.alles': 'Aufriss im ganzen Bericht',
  'bericht.menue.gliederung': 'Gliederungsebenen',
  'bericht.menue.buchen': 'Buchen …',

  /* --- Die Gliederung schiebt sich zusammen (7.3 Abschnitt 13, R444-R445) --- */
  'bericht.gliederung.klappen': 'Diese Gliederungsposition auf- und zuklappen',
  'bericht.menue.gliederung.ebene': 'Gliederung bis Ebene {ebene} zeigen',
  'bericht.menue.gliederung.ganz': 'Ganze Gliederung zeigen',

  /* --- Der Aufriss als Pfad --- */
  'aufriss.titel': 'Aufriss',
  'aufriss.konten': 'Sachkonten',
  'aufriss.einheiten': 'Gesellschaften',
  'suche.oeffnen': 'Im Bericht suchen (Strg+F)',
  'suche.titel': 'Im Bericht suchen',
  'suche.platzhalter': 'Bezeichnung oder Betrag',
  'suche.leer': 'kein Treffer',
  'suche.zaehler': '{nummer} von {anzahl}',
  'suche.gruppen': '{texte} in Bezeichnungen · {werte} in Beträgen',
  'suche.umgekehrt': 'vorzeichenumgekehrt',
  'suche.weiter': 'Nächster Treffer',
  'suche.zurueck': 'Vorheriger Treffer',
  'suche.schliessen': 'Suche schließen',

  /* Die Auswahlliste, in die man hineintippen kann (7.2 Abschnitt 10, R585).
     Der Satz nennt das gesuchte Wort, sonst wüsste niemand, wonach gerade
     gesucht wurde — und ein Griff „… anlegen" steht bewusst nicht daneben. */
  'auswahl.leer': 'Kein Eintrag passt zu „{wort}"',
  /* R1447 — ein schon eingetragener Wert, der inzwischen stillgelegt ist. */

  // Eine Wahl, die noch aussteht (2.8 Abschnitt 13.46, R1355).
  'auswahl.bitte': 'Bitte auswählen',
  /* R1043–R1047 — Ein Konto wird aus dem Baum von Bilanz und GuV gewählt. */
  'kontofeld.ohneposition': 'Ohne Position — erscheint in keinem Bericht',
  // Der oberste Ast der GuV in jeder Auswahl (7.2 Abschnitt 52, R1612).
  'kontofeld.guv': 'Gewinn- und Verlustrechnung',
  // Die Statistikkonten unter ihrer Position (2.1 Abschnitt 3.6, R1733).
  'kontofeld.kfr': 'Kapitalflussrechnung',
  'kontofeld.laedt': 'Die Gliederung wird geladen …',
  'kontofeld.gliederung': 'Gliederung',
  'kontofeld.keinegliederung': 'Es ist keine Gliederung angelegt — die Konten stehen ohne Baum da.',
  // Anlegen aus der Liste (7.2 Abschnitt 47, R1493, R1497).
  'kontofeld.anlegen': 'Anlegen …',
  'kontofeld.ohnekonto': 'noch ohne Konto',

  'bereich.berichte.eigenkapitalspiegel': 'Eigenkapitalspiegel',
  'bereich.berichte.kreditspiegel': 'Kreditspiegel',
  'spiegel.spalte.eroeffnung': 'Anfangsbestand',
  // R1062: Die Vortragsspalten heißen fest — der Vortrag ist keine Bewegungsart (2.5 Abschnitt 15).
  'spiegel.vortrag.anfangsbestand': 'Anfangsbestand',
  'spiegel.vortrag.anfangsbestand_abschreibung': 'Kumulierte Abschreibung Anfang',
  'spiegel.vortrag.ergebnisvortrag': 'Ergebnis der Vormonate',
  // R1061: Die Probe einer Spiegellieferung, ohne den Namen einer Bewegungsart.
  'quelle.spiegel.anfangsbestand': 'Anfangsbestand (nur Probe)',
  'spiegel.spalte.zugang': 'Zugänge',
  'spiegel.spalte.abgang': 'Abgänge',
  'spiegel.spalte.umbuchung': 'Umbuchungen',
  'spiegel.spalte.kreis': 'Kreisänderung',
  'spiegel.spalte.waehrung': 'Währungsdifferenz',
  'spiegel.spalte.abschreibung': 'Abschreibungen des Geschäftsjahres',
  'spiegel.spalte.zuschreibung': 'Zuschreibungen',
  'spiegel.spalte.kapitalerhoehung': 'Kapitalerhöhung',
  'spiegel.spalte.ausschuettung': 'Ausschüttung',
  'spiegel.spalte.jahresergebnis': 'Jahresergebnis',
  'spiegel.spalte.vortrag': 'Ergebnisvortrag',
  'spiegel.spalte.aufnahme': 'Aufnahme',
  'spiegel.spalte.tilgung': 'Tilgung',
  'spiegel.spalte.endbestand': 'Endbestand',
  'spiegel.spalte.default': 'Default — ohne Bewegungsart',
  'spiegel.block.bestand': 'Anschaffungs- und Herstellungskosten',
  'spiegel.block.ahk': 'Anschaffungs- und Herstellungskosten',
  'spiegel.block.abschreibung': 'Kumulierte Abschreibungen',
  'spiegel.block.buchwert': 'Buchwerte',
  'spiegel.ahk.eroeffnung': 'Anfangsbestand',
  'spiegel.ahk.zugang': 'Zugänge',
  'spiegel.ahk.abgang': 'Abgänge',
  'spiegel.ahk.umbuchung': 'Umbuchungen',
  'spiegel.ahk.kreis': 'Kreisänderung',
  'spiegel.ahk.waehrung': 'Währungsdifferenz',
  'spiegel.ahk.endbestand': 'Endbestand',
  'spiegel.abschreibung.eroeffnung': 'Anfangsbestand',
  'spiegel.abschreibung.abschreibung': 'Abschreibungen des Geschäftsjahres',
  'spiegel.abschreibung.zuschreibung': 'Zuschreibungen',
  'spiegel.abschreibung.abgang': 'Abgänge',
  'spiegel.abschreibung.umbuchung': 'Umbuchungen',
  'spiegel.abschreibung.kreis': 'Kreisänderung',
  'spiegel.abschreibung.waehrung': 'Währungsdifferenz',
  'spiegel.abschreibung.endbestand': 'Endbestand',
  'spiegel.buchwert.endbestand': 'Buchwert am Ende',
  'spiegel.buchwert.eroeffnung': 'Buchwert am Anfang',
  'spiegel.leer': 'Für dieses Geschäftsjahr liegt noch kein gerechneter Monat mit Anlagevermögen vor.',
  'aufriss.marker.konten': 'nach Sachkonten',
  'aufriss.marker.einheiten': 'nach Gesellschaften',
  'aufriss.menue.konten': 'Nach Sachkonten aufreißen',
  'aufriss.menue.einheiten': 'Nach Gesellschaften aufreißen',
  'aufriss.menue.herkunft': 'Wertenachweis öffnen',
  'aufriss.menue.zuklappen': 'Diese Zeile zuklappen',
  'aufriss.menue.zeile': 'Aufriss dieser Zeile',
  'aufriss.partner': 'Partner',
  'aufriss.guv': 'GuV-Gliederung',
  'aufriss.segment': 'Segmente',
  'aufriss.menue.partner': 'Nach Partnern aufreißen',
  'aufriss.menue.guv': 'Nach der GuV-Gliederung aufreißen',
  'aufriss.menue.segment': 'Nach Segmenten aufreißen',
  'aufriss.menue.dimension': 'Nach {achse} aufreißen',
  'aufriss.marker.partner': 'nach Partnern',
  'aufriss.marker.segment': 'nach Segmenten',
  'aufriss.marker.dimension': 'nach {achse}',
  /*
    **Der Vorgabewert einer Dimension heißt „Default"** (R429) — einheitlich
    durch die ganze Anwendung, ein Schlüssel für alle Stellen. Vorher stand an
    der einen „nicht zugeordnet", an der nächsten „kein Partner", an der dritten
    ein Gedankenstrich. Drei Schreibweisen für eine Ausprägung sind schon am
    Bildschirm unruhig; sobald ein Drittwerkzeug die Software abfragt, sind sie
    unbrauchbar: Ein „—" lässt sich in keiner Abfrage benennen.

    Und es ist keine Auslassung, sondern eine Aussage: Eine Buchung ohne
    Partner ist eine Buchung gegen Extern, und das ist der Normalfall.
  */
  'dimension.default': 'Default',
  'aufriss.laedt': 'Wird geladen …',
  'aufriss.fehler': 'Dieser Aufriss konnte nicht geladen werden.',
  'aufriss.leer': 'Auf dieser Achse steht hier nichts.',
  'bericht.menueknopf': 'Menü zu dieser Zeile',

  /* --- Anzeigewährung (2.12 Abschnitt 3.21, R993–R996) --- */
  'anzeigewaehrung.titel': 'Anzeigewährung',
  'nachweis.art.waehrungsdifferenz': 'Währungsdifferenz',

  /* --- Anzeigeoptionen --- */
  'anzeige.titel': 'Anzeigeoptionen',
  'anzeige.keine': 'Vollständig',
  'anzeige.mehrere': '{anzahl} gewählt',
  'anzeige.null.titel': 'Nullzeilen unterdrücken',
  'anzeige.zwischen.titel': 'Zwischensummen im Periodenbild',
  'anzeige.zwischen.quartal': 'Quartal',
  'anzeige.zwischen.halbjahr': 'Halbjahr',
  'anzeige.zwischen.jahr': 'Jahr',
  'anzeige.zwischen.zeitraum': 'Gewählter Zeitraum',
  'anzeige.zwischen.erlaeuterung':
    'Je eine Spalte hinter der letzten Periode, die sie zusammenfasst. Summiert werden Monatswerte und Verkehrszahlen; der Finanzmittelfonds am Anfang und am Ende steht als Stand. Bei Salden und bei „Kumuliert seit Jahresbeginn" steht der Stand schon in der letzten Spalte — dort kommt keine hinzu.',
  'anzeige.null.positionen': 'Positionen',
  'anzeige.null.positionen.erlaeuterung':
    'Posten ohne Betrag ausblenden. Eine Gruppe bleibt stehen, solange ein Posten unter ihr einen Betrag trägt.',
  'anzeige.null.konten': 'Konten',
  'anzeige.null.konten.erlaeuterung': 'Im Aufriss die Konten ohne Betrag ausblenden.',
  'anzeige.null.einheiten': 'Gesellschaften',
  'anzeige.null.einheiten.erlaeuterung':
    'Im Aufriss die Gesellschaften ohne Betrag ausblenden — und im Konzernaufriss ihre Spalten.',
  'anzeige.null.bewegungsarten': 'Bewegungsarten',
  'anzeige.null.bewegungsarten.erlaeuterung':
    'Im Spiegel die Spalten ohne Betrag ausblenden. Ohne Haken steht jede Bewegungsart der Kategorie da — auch eine neu angelegte, auf die noch niemand gebucht hat.',
  'anzeige.null.achse.erlaeuterung': 'Im Aufriss die Werte dieser Achse ohne Betrag ausblenden.',

  /* --- Zeitwert: einzeln oder kumuliert (7.3 Abschnitt 2a) --- */
  'zeitwert.bestand.titel': 'Saldo/Verkehrszahl',
  'zeitwert.bestand.kumuliert': 'Saldo',
  'zeitwert.bestand.kumuliert.erlaeuterung':
    'Der Bestand am Ende jeder Spalte — die Bilanz, wie sie an diesem Stichtag steht.',
  'zeitwert.bestand.einzeln': 'Verkehrszahl',
  'zeitwert.bestand.einzeln.erlaeuterung':
    'Statt der Bestände die Veränderung zur Vorspalte. Zeigt einen Sprung sofort, statt ihn aus zwölf Beträgen ablesen zu lassen.',
  'zeitwert.strom.titel': 'Monatswert/Kumuliert',
  /*
    **Der Einzelwert heißt nach dem Raster** (R430). „Monatswert" über einer
    Quartalsspalte behauptete einen Zeitraum, den die Spalte nicht trägt —
    derselbe Fehler, den R185 an der Spaltenüberschrift verbietet. Der
    Feldname `zeitwert.strom.titel` folgt dem nicht: Er benennt die Wahl und
    nicht ihr Ergebnis.
  */
  'zeitwert.strom.einzeln.monat': 'Monatswert',
  'zeitwert.strom.einzeln.quartal': 'Quartalswert',
  'zeitwert.strom.einzeln.jahr': 'Jahreswert',
  'zeitwert.strom.einzeln.erlaeuterung':
    'Je Spalte, was in ihrem eigenen Zeitraum angefallen ist.',
  'zeitwert.strom.kumuliert': 'Kumuliert seit Jahresbeginn',
  'zeitwert.strom.kumuliert.erlaeuterung':
    'Je Spalte alles vom Geschäftsjahresanfang bis zu ihrem Ende — im Controlling „year to date". Der Zähler fängt mit dem Jahr neu an.',
  'bewegung.vorbehalt':
    'Zugänge zum Konsolidierungskreis sind herausgerechnet — eine Gesellschaft, die hinzukommt, bringt ihren Bestand mit, ohne dass etwas bewegt wurde. Der Währungseffekt steckt dagegen noch in der Bewegung: Dafür fehlt die Bewegungsart im festgeschriebenen Ergebnis.',

  /* --- Spaltenaufriss --- */
  'spalten.titel': 'Spaltenansicht',
  'spalten.zeitraum': 'Zeitraum',
  'spalten.konsolidierung': 'Konsolidierungssachverhalt',
  'spalten.schichten': 'Bewertungsschichten',
  'spalten.struktur': 'Konzernstruktur',
  'spalten.perioden': 'Perioden',
  'spalten.raster.monat': 'Monat',
  'spalten.raster.quartal': 'Quartal',
  'spalten.raster.jahr': 'Jahr',
  'spalten.kurz': 'Verkürzter Konzernaufriss',
  'spalten.partner': 'Partner',
  'spalten.gesellschaft': 'Gesellschaft',
  'spalten.gesellschaft.alle': 'alle Gesellschaften',
  'spalte.summe': 'Summe',
  'spalte.konsolidierung': 'Konsolidierung',
  /* Die Spalte, die aus den Gesellschaftsspalten die Konzernwahrheit macht
     (2.7 Abschnitt 3.7, R535). Sie steht nur da, wo sie etwas trägt. */
  'spalte.saldowechsel': 'Saldowechsel',
  'spalten.perioden.leer': 'Im gewählten Zeitraum ist für diesen Kreis und diese Bewertungsebene keine Periode gerechnet.',
  'bericht.leer.bilanz': 'Die Bilanz enthält keine bebuchten Positionen.',
  'bericht.ohnestelle':
    'Nicht enthalten, weil ohne Stelle in der Gliederung:',
  /* --- Ein Monat ohne Lieferung (2.12 Abschnitt 3.20, R973–R978) --- */
  'lieferstand.fortgeschrieben':
    '{gesellschaft} hat für {periode} nichts geliefert. Gezeigt wird der Stand aus {aus}.',
  'lieferstand.ohne': '{gesellschaft} hat für {periode} nichts geliefert. Letzte Lieferung: {aus}.',
  'lieferstand.nie': '{gesellschaft} hat bis {periode} noch nichts geliefert.',
  'lieferstand.nicht-faellig':
    '{gesellschaft}: Lieferung für {periode} nicht fällig ({rhythmus}). Bis zur ersten Lieferung des Geschäftsjahres steht sie mit null da.',
  'bericht.leer.guv': 'Die Gewinn- und Verlustrechnung enthält keine bebuchten Positionen.',
  'bilanz.differenz': 'Die Bilanz geht nicht auf. Differenz: {betrag} {waehrung}.',

  /* --- Konsolidierungsschritte --- */
  'vorgang.laedt': 'Buchungen werden geholt …',
  'vorgang.leer': 'Dieser Vorgang hat keine Buchungen erzeugt.',
  'vorgang.spalte.nummer': 'Nr.',
  'vorgang.spalte.einheit': 'Gesellschaft',
  'vorgang.spalte.konto': 'Konto',
  'vorgang.spalte.betrag': 'Betrag',
  'vorgang.spalte.regel': 'Regel und Herleitung',

  /* --- Wertenachweis (2.14, R204–R208) --- */
  'nachweis.titel': 'Wertenachweis',
  'nachweis.schliessen': 'Schließen',
  'nachweis.laedt': 'Der Nachweis wird zusammengestellt …',
  'nachweis.leer':
    'Für dieses Geschäftsjahr liegt keine Rechnung vor — es gibt deshalb nichts nachzuweisen.',
  'nachweis.aufbau.bestand':
    'Anfangsbestand des Geschäftsjahres, dann die Bewegungen je Periode — {von} bis {bis}.',
  'nachweis.aufbau.spiegel':
    'Die Bewegungen dieser Spiegelspalte je Periode — {von} bis {bis}. Der Eröffnungswert zählt einmal, aus dem ersten Monat.',
  'nachweis.aufbau.erfolg':
    'Erfolgskonten zeigen die Buchungen der gewählten Periode — {bis}. Der Bericht weist den ' +
    'Monat aus, nicht das Jahr; für den Verlauf ist das Periodenbild da.',
  'nachweis.spalte.periode': 'Periode',
  'nachweis.spalte.was': 'Bewegungsart',
  'nachweis.spalte.betrag': 'Betrag',
  'nachweis.spalte.beleg': 'Beleg',
  'nachweis.spalte.einheit': 'Gesellschaft',
  'nachweis.spalte.konto': 'Konto',
  'nachweis.spalte.stufe': 'Bewertungsebene',
  'nachweis.spalte.partner': 'Partner',
  'nachweis.spalte.segment': 'Segment',
  'nachweis.spalte.waehrung': 'Währung',
  'nachweis.spalte.kurs': 'Kurs',
  // R1083: Die genauen Kurse einer Umrechnungszeile, in der Spalte Kurs.
  'nachweis.kurs.historisch': 'historisch',
  'nachweis.kurs.stichtag': 'Stichtag',
  'nachweis.spalte.betraglokal': 'Betrag (Landeswährung)',
  'nachweis.spalte.betragin': 'Betrag ({waehrung})',
  // Die einzige Spalte des Nachweises, die ein Mensch geschrieben hat (R395).
  'nachweis.spalte.buchungstext': 'Buchungstext',
  // Was der Datenimport selbst als Buchungstext schreibt (7.2 Abschnitt 34, R1134).
  'nachweis.buchungstext.datenimport': 'Datenimport',

  /* --- Die Spaltenwahl im Kopf (R215) --- */
  'nachweis.wahl.titel': 'Dimensionen',
  'nachweis.wahl.leer': 'in diesem Nachweis nirgends belegt',
  'nachweis.wahl.alle': 'Alle',
  'nachweis.wahl.wenige': 'Wenige',
  'nachweis.wahl.zusammengefasst': 'zusammengefasst',
  'nachweis.spiegelspalte': 'Spiegelspalte',
  /* Der Saldowechsel im Nachweis (2.7 Abschnitt 3.7, R536). Fachsprache, kein
     Hinweis auf eine Mechanik: Der Anwender liest, WO gebucht wurde und WO es
     steht — nicht, dass ein Vorzeichen verglichen wurde. */
  'nachweis.saldowechsel.ziel':
    'Ausgewiesen hier, gebucht auf den Konten der Position {quelle} — der aufgelaufene Wert trägt das andere Vorzeichen.',
  'nachweis.saldowechsel.quelle':
    'Hier steht nichts: Der aufgelaufene Wert trägt das andere Vorzeichen und ist unter {ziel} ausgewiesen.',
  /* R1429 — Der Saldowechsel am Konto, je Gesellschaft (2.7 Abschnitt 3.7a). */
  'nachweis.saldowechsel.konto.ziel':
    'Ausgewiesen hier: {konten} trägt das andere Vorzeichen; zugeordnet ist das Konto der Position {quelle}.',
  'nachweis.saldowechsel.konto.quelle':
    '{konten} trägt das andere Vorzeichen und ist unter {ziel} ausgewiesen.',
  /* R1622 — Ein gewechseltes Konto sagt es im Namen (2.7 Abschnitt 3.7b). */
  'saldowechsel.zusatz.haben': '(Saldowechsel – im Haben)',
  'saldowechsel.zusatz.soll': '(Saldowechsel – im Soll)',
  /* R1624 — Wechselt eine Position als Ganzes, steht unter dem Ziel ein Stellvertreterkonto. */
  'saldowechsel.stellvertreter': 'Saldowechsel aus {position}',
  /* R1642, R1643 — Ein Konto wird im Bericht ausgeschnitten und eingefügt (7.3 Abschnitt 31). */
  'bericht.menue.ausschneiden': 'Ausschneiden',
  'bericht.menue.einfuegen': 'Einfügen: {konto}',
  'einfuegen.titel': 'Konto einfügen',
  'einfuegen.titel.position': 'Position einfügen',
  'bis.zusatz': '· bis {monat}',
  'einfuegen.satz': '{konto} unter {position} einhängen.',
  'einfuegen.wirkung': 'Das Einhängen gilt',
  'einfuegen.knopf': 'Einfügen',
  'einfuegen.schliessen': 'Schließen',
  'einfuegen.gruppe-ohne-geschwister':
    'Die Kontengruppe bleibt, wie sie war: Unter {position} hängt noch kein anderes Konto, dessen Gruppe es erben könnte.',
  /* R1648, R1649 — Die Kontengruppe gehört zur Zuordnung, geändert ab Monat oder immer (2.1 Abschnitt 3.3). */
  'kontogruppe.titel': 'Kontengruppe',
  'kontogruppe.wirkung': 'Die Änderung gilt',
  'kontogruppe.uebernehmen': 'Übernehmen',
  'stammdaten.konto.gruppe.meldung':
    'Die Kontengruppe von {konto} ändert sich nur im Abschnitt „Kontengruppe“ des Kontofensters — ab einem Monat oder immer.',
  'stammdaten.konto.gruppe.weg': 'Öffnen Sie das Konto mit Rechtsklick → Eigenschaften und ändern Sie die Gruppe dort.',
  'gliederung.kontengruppe.unbekannt.meldung': 'Diese Kontengruppe gibt es nicht.',
  'einfuegen.gruppe-uneins':
    'Die Kontengruppe bleibt, wie sie war: Unter {position} hängen Konten verschiedener Gruppen. Die Arbeitsliste meldet die Position.',
  'nachweis.festgelegt': 'Festgelegt durch die Zelle',
  'nachweis.art.anfangsbestand': 'Anfangsbestand',
  'nachweis.art.bewegung': 'Bewegung',
  'nachweis.art.veraenderung': 'Veränderung',
  'nachweis.art.endbestand': 'Endbestand',
  // Der letzte Notnagel: Seit 2026-09-27 traegt jede Herkunft einen Namen —
  // die Lieferung ihren, die Rechenstufe ihren (R824). Steht hier trotzdem
  // etwas, fehlt eine Marke im Kopf des Nachweises.
  'nachweis.zumbeleg': 'zum Beleg',
  // Der Name einer Zeile, die aus keiner Buchung stammt (R824): die
  // Waehrungsdifferenz der Anzeigewaehrung. Bis zum 2026-10-12 stand dort
  // „kein einzelner Beleg“.
  'nachweis.beleg.stichtagskurs': 'Anpassung an den Stichtagskurs',
  // Die Zeile heisst nach dem Kurs, an den sie anpasst (R1653) — bis zum
  // 2026-10-12 hiess jede „Anpassung an den Stichtagskurs“, auch die
  // Kapitalruecklage zum Eintrittskurs.
  'nachweis.beleg.historischerkurs': 'Anpassung an den historischen Kurs',
  'nachweis.beleg.durchschnittskurs': 'Anpassung an den Durchschnittskurs',
  'nachweis.beleg.gegenbuchung': 'Gegenbuchung der Umrechnung',
  // Der Rechenweg als Mouse-over (R1652): nur die Namen der Variablen.
  'nachweis.rechenweg.stichtag': 'Stichtagskurs',
  'nachweis.rechenweg.durchschnitt': 'Durchschnittskurs',
  'nachweis.rechenweg.historisch': 'historischer Kurs',
  'nachweis.rechenweg.eintritt': 'Eintrittskurs',
  'nachweis.rechenweg.zeilen': 'umgerechnete Zeilen',
  // Steht nur dabei, wenn eine Zeile MEHRERE Herkuenfte hat: Bei einer
  // einzigen ist der Anteil der Betrag der Zeile, und der steht daneben.
  'nachweis.anteil': 'Anteil {betrag}',
  'nachweis.probe.stimmt': 'Der Nachweis summiert auf die angeklickte Zelle: {betrag} {waehrung}.',
  'nachweis.probe.weichtab':
    'Der Nachweis weicht ab: Zelle {zelle} {waehrung}, Nachweis {summe} {waehrung}, ' +
    'Abweichung {abweichung} {waehrung}.',
  'nachweis.menue': 'Wertenachweis',

  /* --- Buchungsjournal (2.14) --- */
  'journal.lauf': 'Gerechnet',
  'journal.lauf.frueher': 'frühere Rechnung',
  'journal.periode': 'Periode',
  'journal.periode.alle': 'Alle Perioden',
  'journal.grenze': 'Gezeigt werden die jüngsten {anzahl} Belege. Wer einen Monat wählt, sieht alle Belege dieses Monats.',
  'journal.belegklammer': 'Belegklammer',
  'journal.belegklammer.wert': 'Vorgang',
  'journal.was.fehlt':
    'Der Beleg aus 2.14 — Kopf, Zeilen, Nullsumme, lückenlose Nummer — ist entschieden und noch ' +
    'nicht gebaut. Bis dahin klammert der Vorgang: Jede Konsolidierungsbuchung liegt unter dem ' +
    'Schritt, der sie erzeugt hat, mit Regel, Erläuterung und Herleitung. Gelieferte Salden, der ' +
    'Vortrag und die Währungsumrechnung tragen heute keinen Beleg und stehen deshalb nicht hier.',
  'journal.abschnitt.belege': 'Belege dieser Periode',
  'journal.fremdeperiode':
    'Der verlinkte Beleg {nummer} gehört zu {periode} und steht deshalb hier oben, obwohl er ' +
    'nicht in die eingestellte Periode fällt.',
  'journal.abschnitt.ohnebeleg': 'Noch ohne Beleg: die Konsolidierungsbuchungen',
  'journal.keine.belege': 'Für diese Periode liegt kein Beleg im Journal.',
  'journal.spalte.bewegungsart': 'Bewegungsart',
  'journal.spalte.partner': 'Partner',
  'journal.nullsumme': 'Der Beleg geht auf: Seine Zeilen summieren je Währung auf null.',
  'journal.hebt.auf': 'Dieser Beleg hebt Beleg {nummer} in voller Höhe auf.',
  'journal.storniert.durch': 'Dieser Beleg ist durch Beleg {nummer} storniert und gilt nicht mehr.',
  'journal.art.lieferung': 'Lieferung',
  'journal.art.erfassung': 'Erfassung',
  'journal.art.vortrag': 'Vortrag',
  'journal.art.umrechnung': 'Umrechnung',
  'journal.art.konsolidierung': 'Konsolidierung',
  'journal.art.storno': 'Storno',
  /*
    **Belegtypen** (R391) — woher der Beleg kommt, nicht was er tut. Die
    Belegart sagt das Zweite, der Typ das Erste; der Anwender fragt beim
    Durchsehen des Journals fast immer nach dem Ersten.
  */
  'belegtyp.manuell': 'Manuelle Buchung',
  'belegtyp.datenimport': 'Datenimport',
  'belegtyp.excel': 'Excel-Buchung',
  'belegtyp.konsolidierung': 'Konsolidierungsbuchung',
  'belegtyp.sonstiges': 'Sonstiges',

  /*
    **Der Stand: das jeweils letzte Datum** (R393) — und darunter, welches es
    ist. Ein Beleg, der am Dienstag erfasst und nie angefasst wurde, und einer,
    der am Dienstag berichtigt wurde, stünden mit dem Datum allein gleich da.
  */
  'journal.spalte.stand': 'Stand',
  'journal.stand.erfasst': 'erfasst',
  'journal.stand.geaendert': 'berichtigt',

  'journal.suche': 'Konto, Gesellschaft oder Buchungsnummer',
  'journal.suche.platzhalter': 'z. B. 1200 oder NL-US',
  'journal.filter.aktiv': 'Aus dem Wertenachweis: Beleg {beleg}, Konto {konto}.',
  'journal.filter.loesen': 'Filter lösen',
  'journal.keinlauf': 'Für diese Auswahl liegt noch keine Rechnung vor — es gibt deshalb keine Belege.',
  'journal.leer': 'Zu dieser Suche steht keine Buchung im Journal.',
  'journal.fremderlauf':
    'Gezeigt wird der Lauf, in dem dieser Beleg gebucht wurde — nicht der der Kontextzeile. ' +
    'Eine Konsolidierungsbuchung, die im Nachweis als Anfangsbestand steht, ist im Dezember ' +
    'des Vorjahres entstanden.',
  'journal.art.zuordnung': 'Zuordnung',
  'journal.art.ueberleitung': 'Überleitung',

  /* --- Handerfassung und Belegordner (2.16) --- */

  'journal.sicht.ordner': 'Belegordner',
  'journal.sicht.chronologisch': 'Chronologisch',
  'journal.sicht.suche': 'Suche',
  'suche.journal': 'Im Journal suchen',
  'suche.journal.platzhalter': 'Konto, Name, Partner — oder ein Betrag',
  'suche.alsText': 'Gesucht wird nach Text: in Belegnummer, Name, Erläuterung, Begründung, Erfasser — und in Konto, Bewegungsart und Partner der Zeilen.',
  'suche.alsWert': 'Gesucht wird nach dem Betrag {wert} (± {toleranz}), in beiden Vorzeichen.',
  'suche.journal.leer.ordner':
    'In diesem Ordner steht dazu keine Zeile — {anzahl} in anderen Ordnern dieser Gesellschaft. Der Baum links sagt, in welchen.',
  'suche.journal.leer': 'Zu dieser Suche steht in dieser Gesellschaft keine Zeile im Journal.',
  'suche.journal.treffer': '{anzahl} Zeilen gefunden.',
  'suche.journal.treffer.einer': 'Eine Zeile gefunden.',
  'suche.journal.umgekehrt': 'umgekehrtes Vorzeichen',
  'suche.journal.umgekehrt.zahl': 'Davon {anzahl} mit umgekehrtem Vorzeichen — das sind die Gegenbuchungen.',
  'suche.spalte.ordner': 'Ordner',
  'sortierung.hilfe': 'Sortieren: erster Klick aufsteigend, zweiter absteigend, dritter zurück zur Reihenfolge der Liste',
  'journal.gesellschaft': 'Gesellschaft',
  // Kurz, seit das Ordnerbrett dasselbe ZEIGT: Der Kopf über der Liste nennt
  // Gesellschaft und Ordner. Ein Satz, der nur wiederholt, was das Bild sagt,
  // ist kein Hinweis mehr, sondern Rauschen.
  'journal.gesellschaft.hinweis': 'Der Ordner setzt die Gesellschaft; jede Zeile des Belegs erbt sie.',
  'ordner.titel': 'Ordner',
  'ordner.eingang': 'Eingang',
  'ordner.allePerioden': 'alle Perioden',
  'ordner.inUnterordnern': '· {anzahl} in Unterordnern',
  'ablage.kopiert': 'In der Zwischenablage: {nummer} — {name}',
  'ablage.ausgeschnitten': 'Ausgeschnitten: {nummer} — {name}',
  'ablage.einfuegen.hier': 'Hier einfügen: {ordner} (Strg+V)',
  'ablage.verwerfen': 'Zwischenablage leeren',
  'ordner.neu': 'Neuer Ordner',
  'ordner.neu.name': 'Name des Ordners',
  'ordner.neu.platzhalter': 'z. B. Abgrenzungen 2025',
  'ordner.neu.anlegen': 'Anlegen',
  'ordner.umbenennen': 'Umbenennen (F2)',
  'ordner.umbenennen.tun': 'Umbenennen',
  /* --- Der Ordnerbaum wie im Explorer (2.16 Abschnitt 5.5, R980–R985) --- */
  'ordner.menue.neu': 'Neuer Ordner (Einfg)',
  'ordner.menue.umbenennen': 'Umbenennen (F2)',
  'ordner.menue.ausschneiden': 'Ausschneiden (Strg+X)',
  'ordner.menue.einfuegen': 'Einfügen (Strg+V)',
  'ordner.menue.loeschen': 'Löschen (Entf)',
  'ordner.fehler.titel': 'Das geht nicht',
  'ordner.fehler.verstanden': 'Verstanden',
  'ordner.fehler.gesellschaft': 'Ein Beleg oder Ordner bleibt bei seiner Gesellschaft.',
  'ordner.fehler.jahresordner':
    'Ein Jahresordner heißt nach seinem Geschäftsjahr. Er lässt sich nicht umbenennen, verschieben oder löschen.',
  'ordner.fehler.name': 'Hier gibt es schon einen Ordner „{name}".',
  'ordner.fehler.name.weg': 'Bitte einen anderen Namen wählen — Groß- und Kleinschreibung zählt dabei nicht.',
  'ordner.fehler.nichtleer':
    'Nur ein leerer Ordner lässt sich löschen. In „{name}" liegen noch {belege} Belege und {ordner} Unterordner.',
  'ordner.fehler.nichtleer.weg':
    'Erst die Belege in einen anderen Ordner oder zurück in ihr Jahr ziehen und die Unterordner löschen.',
  'ordner.fehler.jahr': 'Der Ordner „{name}" gehört zu {jahr} und bleibt in diesem Geschäftsjahr.',
  'ordner.fehler.belegjahr':
    'Beleg {nummer} gehört nicht ins Geschäftsjahr {jahr}. Der Ordner „{ordner}" nimmt nur Belege dieses Jahres auf.',
  'ordner.fehler.zyklus': 'Ein Ordner kann nicht in sich selbst verschoben werden.',
  'ordner.loeschen': 'Ordner löschen',
  // Die Frage fragt, der Hinweis erklärt die Folge. Beides in einen Satz zu
  // packen hieß, dass der Anwender zwei Dinge auf einmal liest — und den
  // beruhigenden Teil überliest.
  'ordner.loeschen.frage': 'Ordner „{name}" mit seinen {belege} Belegen löschen?',
  'ordner.loeschen.hinweis':
    'Die Belege wandern in den Eingang. Ein Ordner ist Ablage — sein Löschen reißt keinen ' +
    'Beleg mit.',
  'ordner.eingang.bleibt': 'Der Eingang lässt sich nicht löschen.',
  'ordner.leer': 'In diesem Ordner liegt kein Beleg.',
  'ordner.belege': '{anzahl} Belege',
  'ordner.beleg': '1 Beleg',

  /*
    **Eigene Reihenfolge** (R389): Die Pfeile stehen nur da, solange keine
    Spalte sortiert — zwei Ordnungen gleichzeitig wären keine Ordnung. Dass
    sie Tasten sind und keine Ziehgriffe, ist Absicht (R143).
  */
  'ordner.hoeher': 'Eine Zeile nach oben',
  'ordner.tiefer': 'Eine Zeile nach unten',
  'ordner.folge.hinweis':
    'Eigene Reihenfolge — mit den Pfeilen umstellen. ' +
    'Ein Klick auf einen Spaltenkopf sortiert stattdessen; die eigene Reihenfolge bleibt gespeichert.',

  'beleg.buchen': 'Buchen',
  'beleg.neu': 'Neue Buchung',
  'beleg.name': 'Name des Belegs',
  'beleg.ebene': 'Bewertungsebene',
  'beleg.ebene.waehlen': 'Bewertungsebene wählen …',
  'beleg.name.platzhalter': 'z. B. Umgliederung Bauzinsen Q2',
  'beleg.erlaeuterung': 'Erläuterung — was gebucht wird',
  'beleg.begruendung': 'Begründung — warum eingegriffen wird',
  'beleg.ordner': 'Ablegen in',
  'beleg.spalte.konto': 'Konto',
  'beleg.spalte.bewegungsart': 'Bewegungsart',
  'beleg.spalte.partner': 'Partner',
  'beleg.spalte.soll': 'Soll',
  'beleg.spalte.haben': 'Haben',
  'beleg.spalte.segment': 'Segment',
  'beleg.spalte.funktionsbereich': 'Funktionsbereich',
  'beleg.spalte.einbeziehungsart': 'Einbeziehungsart',
  'beleg.spalte.transaktionswaehrung': 'Transaktionswährung',
  'beleg.spalte.nachbuchung': 'Nachbuchung für',
  'beleg.spalte.zeilentext': 'Buchungstext',
  'beleg.spalten': 'Weitere Spalten',
  /* R369 — Die übrigen Dimensionen sind buchbar, aber ausgeblendet. */
  'beleg.spalten.hinweis':
    'Was der Konzern an Merkmalen führt, ist buchbar. Im Standard ausgeblendet — was Sie hier ' +
    'anhaken, bleibt beim nächsten Mal stehen.',
  'beleg.spalte.leer': 'in diesem Konzern nicht eingerichtet',
  'beleg.konto.waehlen': 'Konto wählen',
  /* R368 — Der Partner ist Pflicht als Entscheidung. */
  'beleg.partner.pflicht':
    'Der Partner ist eine Pflichtangabe — „kein Partner" ist eine gültige Antwort und ' +
    'muss ausdrücklich gewählt werden.',
  'beleg.guv.hinweis': 'Erfolgskonto — gebucht als Default',
  /* R372 — Kein Bewegungsartfeld auf einem Erfolgskonto; R199 — Jede Journalzeile trägt eine Bewegungsart. */
  'beleg.guv.warum':
    'Auf einem Erfolgskonto gibt es keine Bewegungsart. Gespeichert wird 080 Default, ' +
    'wie in der Lieferung, weil jede Journalzeile eine trägt.',
  'beleg.konto.ohnezuordnung':
    'Dieses Konto ist keiner Position zugeordnet — es erscheint in keinem Bericht.',
  'beleg.zeile.hinzu': 'Zeile hinzufügen',
  'beleg.zeile.weg': 'Zeile entfernen',
  'beleg.summe.soll': 'Summe Soll',
  'beleg.summe.haben': 'Summe Haben',
  'beleg.differenz': 'Differenz',
  'beleg.gehtauf': 'Der Beleg geht auf.',
  'beleg.gehtnichtauf': 'Soll an Haben geht nicht auf — es fehlen {betrag}.',
  /* R1053 — Der Knopf nennt, was er tut. */
  'beleg.ausgleichen': 'Zeile {zeile} ausgleichen: {betrag} {seite}',
  'beleg.speichern': 'Buchen (Strg+S)',
  'beleg.abbrechen': 'Abbrechen (Esc)',
  'beleg.vorbelegt': 'Aus dem Bericht übernommen: {was}. Alles änderbar.',
  'beleg.vorbelegt.spalte':
    'Diese Zelle liegt in einer Konsolidierungsspalte — dort stehen gerechnete Werte. ' +
    'Vorbelegt wird deshalb nichts außer Periode und Bewertungsebene; gebucht wird in die ' +
    'Handelsbilanz II.',
  'beleg.pflicht.fehlt': 'Zum Speichern fehlt: {felder}.',
  'beleg.pflicht.name': 'Name',
  'beleg.pflicht.erlaeuterung': 'Erläuterung',
  'beleg.pflicht.begruendung': 'Begründung',

  'beleg.berichtigen': 'Beleg {nummer} berichtigen',
  'beleg.berichtigen.speichern': 'Berichtigung speichern (Strg+S)',
  'beleg.berichtigen.hinweis':
    'Gesellschaft, Bewertungsebene, Periode und Belegnummer bleiben, wie sie sind. ' +
    'Geändert werden Name, Erläuterung, Begründung und die Zeilen — der alte ' +
    'Stand bleibt im Änderungsprotokoll einsehbar.',

  'beleg.menue.bearbeiten': 'Beleg bearbeiten',
  'beleg.menue.oeffnen': 'Beleg ansehen',
  'beleg.menue.umkehren': 'In Folgeperiode auflösen',
  // R1832–R1834: Eine Handbuchung steht, bis sie endet.
  'beleg.menue.umkehren.jahresende': 'Zum Geschäftsjahresende auflösen',
  'beleg.menue.umkehren.neues-jahr': 'Zu Beginn des neuen Geschäftsjahres auflösen',
  'beleg.menue.protokoll': 'Änderungsprotokoll',
  'beleg.menue.loeschen': 'Löschen (Entf)',
  'beleg.menue.kopieren': 'Kopieren (Strg+C)',
  'beleg.menue.ausschneiden': 'Ausschneiden (Strg+X)',
  'beleg.menue.einfuegen': 'Einfügen (Strg+V)',

  'beleg.umkehrt': 'Löst Beleg {nummer} in dieser Periode auf.',
  'beleg.umgekehrt': 'Wird durch Beleg {nummer} aufgelöst.',
  'beleg.umkehren.frage': 'Beleg {nummer} in der Folgeperiode auflösen?',
  'beleg.umkehren.hinweis':
    'Es entsteht sofort ein eigener Beleg mit umgekehrtem Vorzeichen. Der Ursprungsbeleg gilt ' +
    'weiter — in seiner Periode war er richtig.',
  'beleg.umkehren.jahresende.frage': 'Beleg {nummer} zum Geschäftsjahresende auflösen?',
  'beleg.umkehren.jahresende.hinweis':
    'Es entsteht sofort ein eigener Beleg im letzten Monat des Geschäftsjahres, mit umgekehrtem ' +
    'Vorzeichen. Der Jahresabschluss zeigt die Buchung nicht.',
  'beleg.umkehren.neues-jahr.frage': 'Beleg {nummer} zu Beginn des neuen Geschäftsjahres auflösen?',
  'beleg.umkehren.neues-jahr.hinweis':
    'Es entsteht sofort ein eigener Beleg im ersten Monat des neuen Geschäftsjahres. Der ' +
    'Jahresabschluss zeigt die Buchung; ihre GuV wird aus dem Gewinnvortrag aufgelöst.',

  'beleg.einfuegen.frage': 'Was soll aus „{name}" entstehen?',
  'beleg.einfuegen.duplikat': 'Duplikat — mit denselben Beträgen',
  'beleg.einfuegen.duplikat.was':
    'Ein neuer Beleg mit denselben Zeilen und Beträgen, neue Nummer. Für „dasselbe noch ' +
    'einmal, anderer Monat".',
  'beleg.einfuegen.vorlage': 'Vorlage — Merkmale ohne Werte',
  'beleg.einfuegen.vorlage.was':
    'Die Maske geht mit Konten, Bewegungsarten, Partnern und Dimensionen auf — die ' +
    'Wertspalten sind leer. Sie wird nicht gespeichert, bis Sie buchen.',

  'loeschen.titel': 'Beleg löschen',
  'loeschen.frage':
    'Beleg {nummer} „{name}" über {summe} in {zeilen} Zeilen wird gelöscht. ' +
    'Das lässt sich nicht rückgängig machen.',
  'loeschen.laeufe':
    'Dieser Beleg steht in {anzahl} gerechneten Läufen. Sie veralten dadurch und müssen neu ' +
    'gerechnet werden.',
  'loeschen.freigegeben':
    'Davon sind {anzahl} freigegeben. Ihre Zahlen und Prüfcodes bleiben unverändert — sie ' +
    'tragen künftig den Hinweis, dass ihre Grundlage sich geändert hat.',
  'loeschen.umkehrung':
    'Dazu gehört die Umkehrbuchung {nummer}. Sie wird mitgelöscht — eine Auflösung ohne ' +
    'ihren Sachverhalt wäre eine Buchung ohne Bezug.',
  /* R378 — Das Änderungsprotokoll überlebt den Beleg. */
  'loeschen.protokoll':
    'Das Änderungsprotokoll überlebt: Nummer, Zeitpunkt, Anwender und jede Zeile mit ihrem ' +
    'Betrag bleiben nachlesbar. Die Lücke in der Nummernfolge ist damit erklärt.',
  'loeschen.tun': 'Endgültig löschen',
  /* R634 — Die Lieferung ist seit dem 2026-09-20 die zweite löschbare Belegart. */
  'loeschen.hindernis.belegart':
    'Ein Beleg der Art „{belegart}" lässt sich nicht löschen — gelöscht werden nur eine ' +
    'Handerfassung und ein Datenimport. Er wird storniert.',
  'loeschen.hindernis.storniert':
    'Dieser Beleg ist durch {nummer} storniert. Wer storniert hat, hat sich für den anderen ' +
    'Weg entschieden.',
  'loeschen.hindernis.weg': 'Stornieren Sie ihn statt ihn zu löschen.',

  'protokoll.titel': 'Änderungsprotokoll — Beleg {nummer}',
  'protokoll.spalte.zeitpunkt': 'Zeitpunkt',
  'protokoll.spalte.ereignis': 'Ereignis',
  'protokoll.spalte.anwender': 'Anwender',
  'protokoll.spalte.angabe': 'Angabe',
  'protokoll.ereignis.angelegt': 'angelegt',
  'protokoll.ereignis.umbenannt': 'umbenannt',
  'protokoll.ereignis.verschoben': 'verschoben',
  'protokoll.ereignis.umgekehrt': 'in Folgeperiode aufgelöst',
  'protokoll.ereignis.storniert': 'storniert',
  'protokoll.ereignis.geloescht': 'gelöscht',
  'protokoll.ereignis.geaendert': 'berichtigt',
  'protokoll.wertnachweis': 'Wertnachweis',
  /*
    **Der Stand davor, vollständig** (R390): Der Wertnachweis in der Zeile
    nennt die Summe. Wer einen elfzeiligen Beleg prüft, braucht die elf Zeilen
    — und zwar die von vorher, nicht die von jetzt.
  */
  'protokoll.vorher': 'Stand davor ansehen',
  'protokoll.vorher.name': 'Name',
  'protokoll.vorher.erlaeuterung': 'Erläuterung',
  'protokoll.vorher.begruendung': 'Begründung',
  'protokoll.vorher.titel': 'Beleg {nummer} — Stand vor der Berichtigung vom {zeitpunkt}',
  'protokoll.vorher.leer': 'Zu diesem Eintrag ist kein vollständiger Stand hinterlegt.',
  'protokoll.vorher.schliessen': 'Zurück zum Protokoll',
  'protokoll.leer': 'Zu diesem Beleg ist noch nichts protokolliert.',
  'protokoll.geloescht.titel': 'Erklärte Lücken im Belegkreis',
  /* R379 — Die Belegnummer wird lückenlos vergeben. */
  'protokoll.geloescht.hinweis':
    'Eine erklärte Lücke ist kein Prüfungsbefund; eine unerklärte bleibt einer. ' +
    'Hier steht, was in ihr stand.',
  'protokoll.geloescht.leer': 'Im Belegkreis dieser Periode fehlt keine Nummer.',

  /* --- Änderungsnachweis --- */
  'nachweis.eintraege': 'Einträge',
  'nachweis.kette': 'Kette',
  'nachweis.unversehrt': 'unversehrt',
  'nachweis.unterbrochen': 'unterbrochen',
  'nachweis.bruch': 'Erster Bruch bei',

  /* --- Antwortzeiten --- */
  'messung.aufrufe': 'Aufrufe',
  'messung.schnellste': 'Schnellste',
  'messung.mittel': 'Mittel',
  'messung.p95': '95 % der Aufrufe unter',
  'messung.langsamste': 'Langsamste',
  'messung.zusage': 'Zusage',
  'messung.erlaeuterung':
    '{ueber} von {anzahl} Aufrufen liegen über der Zusage. Die Zusage bezieht sich auf die Referenzkonfiguration.',

  /* --- Betrieb · Systemzustand: die zwei Zusagen, die bis zum 2026-09-11 in der Fußzeile standen --- */
  'zustand.nachweis': 'Änderungsnachweis: {meldung}',
  'zustand.antwortzeit':
    'Antwortzeit über {anzahl} Aufrufe: Mittel {mittel} ms, 95 % davon unter {p95} ms, Zusage {budget} ms',
  'zustand.einzelheiten': 'Alle Messwerte',

  /* --- Prüferansicht: der Regelnachweis (2.13 Abschnitt 9) --- */
  'pruefer.einleitung': 'Dieser Bericht beantwortet zwei Fragen. Erstens: Hält das System seine eigenen Grundregeln? Das mißt es bei jedem Aufruf an allem, was in CoKo gebucht ist. Zweitens: Was tut es, wenn es konsolidiert? Das rechnet es an einem erfundenen Beispielkonzern vor — mit derselben Rechnung, die auch Ihren Abschluß rechnet.',

  'pruefer.grundsaetze': 'Die Grundsätze, gemessen am eigenen Bestand',
  'pruefer.grundsaetze.erlaeuterung':
    'Hier ist nichts abzuhaken. Jede Zeile trägt das Urteil, das eben gerechnet wurde, und sagt dazu, über wie viele Fälle es geht — ein „in Ordnung" ohne diese Zahl wäre keine Auskunft.',

  'pruefer.grundsatz.beleg-geht-auf': 'Jeder Beleg geht auf',
  'pruefer.grundsatz.beleg-geht-auf.was': 'Soll und Haben stimmen in jedem Beleg überein, und zwar je Währung getrennt. CoKo weist einen Beleg, der das nicht tut, schon beim Buchen zurück — diese Messung sieht nach, ob das lückenlos gegolten hat.',
  'pruefer.grundsatz.beleg-geht-auf.gut': 'Alle {anzahl} Belege gehen auf.',
  'pruefer.grundsatz.beleg-geht-auf.schlecht': '{fehler} von {anzahl} Belegen gehen nicht auf.',
  'pruefer.grundsatz.beleg-geht-auf.leer': 'Es ist noch kein Beleg gebucht.',

  'pruefer.grundsatz.zeile-hat-beleg': 'Keine Buchung ohne Beleg',
  'pruefer.grundsatz.zeile-hat-beleg.was':
    'Jede Zeile des Journals hängt an einem Beleg — auch jede Buchung der Konsolidierung. Eine Zahl, die von nirgendwoher kommt, kann es damit nicht geben.',
  'pruefer.grundsatz.zeile-hat-beleg.gut': 'Alle {anzahl} Journalzeilen hängen an einem Beleg.',
  'pruefer.grundsatz.zeile-hat-beleg.schlecht':
    '{fehler} von {anzahl} Journalzeilen hängen an keinem Beleg.',
  'pruefer.grundsatz.zeile-hat-beleg.leer': 'Es ist noch nichts gebucht.',

  'pruefer.grundsatz.bilanz-geht-auf': 'Aktiva gleich Passiva',
  'pruefer.grundsatz.bilanz-geht-auf.was':
    'In jedem geltenden Abschluß stimmen Aktiva und Passiva überein — je Monat im freigegebenen, sonst im jüngsten Lauf. Geprüft wird jeder Lauf mit der Gliederung seines eigenen Datenstandes: Hielte man die Zahlen von damals gegen die Gliederung von heute, fiele eine Bilanz schon deshalb auseinander, weil jemand seither eine Position umgehängt hat.',
  'pruefer.grundsatz.bilanz-geht-auf.gut': 'Alle {anzahl} geltenden Abschlüsse gehen auf.',
  'pruefer.grundsatz.bilanz-geht-auf.schlecht':
    '{fehler} von {anzahl} geltenden Abschlüssen gehen nicht auf. Die größte Abweichung beträgt {groesste}.',
  'pruefer.grundsatz.bilanz-geht-auf.leer': 'Es ist noch kein Abschluß gerechnet.',

  'pruefer.grundsatz.spiegel-geht-auf': 'Jeder Spiegel geht auf',
  'pruefer.grundsatz.spiegel-geht-auf.was':
    'In jedem Spiegel ergibt der Anfangsbestand samt Bewegungen den Endbestand der Bilanz — kein Betrag trägt eine Bewegungsart, für die der Spiegel keine Spalte hat. Gemessen wird mit derselben Vorschrift, nach der die Freigabe prüft, an jedem Konto, das eine Bewegungsart verlangt. Gemessen wird je Monat der geltende Abschluss — der freigegebene, sonst der jüngste —, und zwar seit Beginn seines Geschäftsjahres: Ein Fehler, der bis zum Monat ausgeglichen ist, zählt nicht mehr.',
  'pruefer.grundsatz.spiegel-geht-auf.gut': 'In allen {anzahl} geltenden Abschlüssen gehen die Spiegel auf.',
  'pruefer.grundsatz.spiegel-geht-auf.schlecht':
    'In {fehler} von {anzahl} geltenden Abschlüssen geht ein Spiegel nicht auf. Der größte Betrag außerhalb der Spalten: {groesste}.',
  'pruefer.grundsatz.spiegel-geht-auf.leer': 'Es ist noch kein Abschluß mit einem Spiegelkonto gerechnet.',

  'pruefer.grundsatz.rest-nicht-zugeordnet': 'Kein Rest auf „nicht zugeordnet"',
  'pruefer.grundsatz.rest-nicht-zugeordnet.was':
    'An keinem Konto, das eine Bewegungsart verlangt, bleibt ein Teil des Bestands ohne Bewegungsart. Ein solcher Rest fehlt im Spiegel, obwohl er in der Bilanz steht. Gemessen wird mit derselben Vorschrift, nach der die Freigabe prüft, je Monat am geltenden Abschluss seit Beginn seines Geschäftsjahres.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.gut':
    'In allen {anzahl} geltenden Abschlüssen ist jeder Bestand einer Bewegungsart zugeordnet.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.schlecht':
    'In {fehler} von {anzahl} geltenden Abschlüssen steht ein Rest auf „nicht zugeordnet". Der größte: {groesste}.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.leer': 'Es ist noch kein Abschluß mit einem Spiegelkonto gerechnet.',

  'pruefer.grundsatz.konto-hat-zuordnung': 'Jedes Konto trägt genau eine Zuordnung',
  'pruefer.grundsatz.konto-hat-zuordnung.was':
    'Jedes Konto läuft in genau eine Berichtsposition — keines in zwei, keines in gar keine. Statistische Konten bleiben außen vor: Sie speisen den Anhang und keine Bilanzzeile und tragen deshalb bewußt keine Zuordnung.',
  'pruefer.grundsatz.konto-hat-zuordnung.gut':
    'Alle {anzahl} Konten tragen genau eine Zuordnung.',
  'pruefer.grundsatz.konto-hat-zuordnung.warn':
    '{fehler} von {anzahl} Konten tragen noch keine Zuordnung. Ihre Werte laufen in keine Berichtszeile — eine offene Aufgabe, kein Widerspruch.',
  'pruefer.grundsatz.konto-hat-zuordnung.schlecht':
    'Bei {fehler} von {anzahl} Konten überschneiden sich zwei Zuordnungen. Dann steht nicht fest, in welche Position das Konto läuft.',
  'pruefer.grundsatz.konto-hat-zuordnung.leer': 'Es ist noch kein Konto angelegt.',

  'pruefer.grundsatz.nachweiskette': 'Der Änderungsnachweis ist unversehrt',
  'pruefer.grundsatz.nachweiskette.was':
    'Jeder Eintrag des Änderungsnachweises hängt über einen Prüfcode am vorigen. Wer einen entfernt oder ändert, bricht alles, was danach kommt — und die Probe sagt, ab welcher Nummer.',
  'pruefer.grundsatz.nachweiskette.gut':
    'Die Kette über {anzahl} Einträge ist von Anfang bis Ende nachgerechnet.',
  'pruefer.grundsatz.nachweiskette.schlecht':
    'Die Kette bricht. Nachgerechnet wurden {anzahl} Einträge.',
  'pruefer.grundsatz.nachweiskette.leer': 'Es steht noch kein Eintrag im Nachweis.',

  'pruefer.beispiele': 'Zum Nachsehen: {liste}',
  'pruefer.regeln': 'Dahinter stehen: {liste}',

  'pruefer.ungeprueft': 'Was hier nicht gemessen wird',
  'pruefer.ungeprueft.erlaeuterung':
    'Das Fachkonzept kennt weitere Prüfungen, über die niemand entscheiden kann. Sie hängen an Fachlichkeit, die es noch nicht gibt, und stehen deshalb hier statt nirgends: Eine Prüfung, die nie anschlägt, sähe aus wie eine bestandene.',
  'pruefer.ungeprueft.ic-erfassungsfehler':
    'Ein offener Erfassungsfehler in der Abstimmung zwischen Gesellschaften',
  'pruefer.ungeprueft.ic-differenz-ungeklaert':
    'Eine Differenz zwischen zwei Gesellschaften, die als „nicht geklärt" markiert ist',
  'pruefer.ungeprueft.wechselseitige-beteiligung':
    'Eine wechselseitige Beteiligung, die nicht aufgelöst ist',
  'pruefer.ungeprueft.ukv-funktionsbereich':
    'Ein fehlender Funktionsbereich im Umsatzkostenverfahren',

  'pruefer.lehrfall': 'Der Beispielkonzern',
  'pruefer.lehrfall.erlaeuterung': 'Die folgenden Zahlen sind nicht aufgeschrieben. Sie entstehen bei jedem Aufruf dieser Seite mit derselben Rechnung, die auch den Abschluß dieses Hauses rechnet — wäre eine Regel gebrochen, stimmte das Beispiel nicht mehr. Der Konzern selbst ist erfunden: Er enthält kein Konto und keinen Betrag aus Ihren Daten und sieht deshalb in jeder Anlage gleich aus.',
  'pruefer.lehrfall.periode': 'Abschluß für {periode} · Konzernwährung {waehrung}',
  'pruefer.lehrfall.ledger': 'Bewertungsebene des Beispiels',
  'pruefer.lehrfall.gesellschaften': 'Die Gesellschaften',
  'pruefer.lehrfall.ausgangslage': 'Was die Gesellschaften geliefert haben',
  'pruefer.lehrfall.ausgangslage.erlaeuterung':
    'Soll steht positiv: Vermögen und Aufwand positiv, Kapital, Schulden und Erträge negativ. Die Summe über alle Zeilen einer Gesellschaft ist deshalb genau null — dieselbe Probe wie oben am Beleg. Das Jahresergebnis wird nicht geliefert; es entsteht im zweiten Schritt aus den Erfolgskonten.',
  'pruefer.lehrfall.schritte': 'Was jede Konsolidierungsart daraus macht',
  'pruefer.lehrfall.schritte.erlaeuterung':
    'Die Reihenfolge steht fest und ist nicht Geschmackssache: Jeder Schritt rechnet auf dem Ergebnis der vorherigen auf (die Reihenfolge der Konsolidierungsschritte ist fest).',
  'pruefer.lehrfall.ohne-buchung': 'Dieser Prozess hat im Beispielkonzern nichts gebucht.',
  'pruefer.lehrfall.summe': '{anzahl} Buchungen',
  'pruefer.lehrfall.summe.eine': 'eine Buchung',
  'pruefer.lehrfall.keine-buchung': 'keine Buchung',

  'pruefer.schritt.umrechnung.regel':
    'Vermögen und Schulden werden zum Stichtagskurs umgerechnet, das Eigenkapital zum Kurs des Erwerbstages, die Erfolgsrechnung zum Durchschnittskurs. Weil drei Kurse auf dieselbe Bilanz treffen, geht sie danach nicht mehr auf; der Unterschied wird als Währungsumrechnungsdifferenz ins Eigenkapital gestellt (die Umrechnungsdifferenz bleibt nach ihrer Quelle getrennt). Fehlt ein Kurs, wird nicht geschätzt, sondern angehalten (ein fehlender Kurs blockiert die Umrechnung).',
  'pruefer.schritt.summierung.regel':
    'Die Einzelabschlüsse werden addiert, und das Jahresergebnis jeder Gesellschaft wird aus ihren Erfolgskonten übernommen. Geliefert wird es nie — sonst stünde es zweimal da.',
  'pruefer.schritt.goodwill.regel':
    'Der Firmenwert wird planmäßig abgeschrieben. Die Abschreibung dieser Periode belastet das Jahresergebnis, alles davor die Gewinnrücklagen — sonst ginge die Konzernbilanz um genau diesen Betrag nicht auf. Über wie viele Monate abgeschrieben wird, steht am Regelwerk und damit an der Bewertungsebene: Das Handelsrecht verlangt die planmäßige Abschreibung, die IFRS kennen nur den Wertminderungstest.',
  'pruefer.schritt.schuldenkonsolidierung.regel':
    'Forderungen und Verbindlichkeiten zwischen zwei einbezogenen Gesellschaften werden beide vollständig ausgebucht — nicht bis zur Höhe der kleineren. Genau das macht eine Abstimmdifferenz sichtbar, statt sie als Rest stehenzulassen. Was übrigbleibt, geht auf das Differenzenkonto; über der Wesentlichkeitsgrenze wird es außerdem gemeldet (eine Differenz unter Toleranz wird gebucht und ausgewiesen, ohne vergebenes Differenzenkonto läuft der Prozeß nicht).',
  'pruefer.schritt.aufwandertragkonsolidierung.regel':
    'Innenumsätze verschwinden auf beiden Seiten: der Erlös bei der liefernden, der Aufwand bei der empfangenden Gesellschaft. Am Konzernergebnis ändert das nichts — es kürzt nur die Erfolgsrechnung um das, was der Konzern mit sich selbst umgesetzt hat.',
  'pruefer.schritt.zwischenergebniseliminierung-uv.regel':
    'Was eine Gesellschaft an eine andere geliefert hat und dort noch im Bestand liegt, darf den Gewinn des Konzerns nicht erhöhen. Der Bestand wird um den Zwischengewinn gekürzt. Gerechnet wird aus der Meldung der Gesellschaften und nicht aus einer unterstellten Konzernmarge (die Meldung geht der Konzernmarge vor).',
  'pruefer.schritt.zwischenergebniseliminierung-av.regel':
    'Dasselbe für Gegenstände des Anlagevermögens — dort löst sich der Zwischengewinn nicht mit dem nächsten Verkauf auf, sondern über die Nutzungsdauer. Welche Meldung zum Anlagevermögen gehört, sagt der Konsolidierungsprozeß; wo keiner gepflegt ist, gilt jede Meldung als Umlaufvermögen.',
  'pruefer.schritt.kapitalkonsolidierung.regel':
    'Der Beteiligungsbuchwert der Mutter wird gegen das anteilige Eigenkapital der Tochter zum Erwerbstag verrechnet. Was übrigbleibt, ist der Firmenwert. Das Eigenkapital wird dabei aus der Bilanz der Tochter gelesen und nicht abgetippt (das neubewertete Eigenkapital kommt aus der Handelsbilanz II), und zwar zum Kurs von damals (das Eigenkapital zum Einbezug wird zum historischen Kurs umgerechnet). Aufgedeckt werden darf höchstens, was über dem anteiligen bilanziellen Eigenkapital gezahlt wurde. Der Teil, der nicht der Mutter gehört, wird als Anteil nicht beherrschender Gesellschafter ausgewiesen — gebucht, nicht nur angezeigt.',
  'pruefer.schritt.abschluss.regel':
    'Zum Schluß wird die Bilanz gestellt und geprüft, ob sie aufgeht. Tut sie es nicht, sagt der Lauf es hier — und nicht der Prüfer drei Wochen später.',

  'pruefer.latente-steuern': 'Latente Steuern',
  'pruefer.latente-steuern.regel':
    'Latente Steuern stehen im Vorrat der Konsolidierungswerkzeuge und werden noch nicht gerechnet (latente Steuern entstehen an zwei Stellen). Das steht hier, damit die Lücke nicht wie eine bestandene Prüfung aussieht.',

  'pruefer.tafel.kurse': 'Die Kurse, mit denen gerechnet wird',
  'pruefer.tafel.zwischenergebnisse': 'Was an Zwischengewinnen gemeldet ist',
  'pruefer.tafel.einbezug': 'Das Eigenkapital zum Erwerbstag',

  'pruefer.spalte.gesellschaft': 'Gesellschaft',
  'pruefer.spalte.waehrung': 'Währung',
  'pruefer.spalte.anteil': 'Kapitalanteil',
  'pruefer.spalte.kaufpreis': 'Kaufpreis',
  'pruefer.spalte.erwerb': 'Erworben',
  'pruefer.spalte.konto': 'Konto',
  'pruefer.spalte.bewegungsart': 'Bewegungsart',
  'pruefer.spalte.partner': 'Partner',
  'pruefer.spalte.betrag': 'Betrag',
  'pruefer.spalte.kursart': 'Kursart',
  'pruefer.spalte.kurs': 'Kurs',
  'pruefer.spalte.lieferant': 'Geliefert von',
  'pruefer.spalte.bestand': 'Bestand',
  'pruefer.spalte.zwischengewinn': 'Davon Zwischengewinn',
  'pruefer.spalte.rechnung': 'Wie die Zahl entsteht',

  'pruefer.oberste-mutter': 'oberste Mutter',

  /* --- Agenda und Ausdruck (7.2 Abschnitt 19, R810–R812) --- */
  'agenda.titel': 'Inhalt',
  'agenda.alles-auf': 'Alle Kapitel aufklappen',
  'agenda.alles-zu': 'Alle Kapitel zuklappen',

  'druck.knopf': 'Drucken',
  'druck.anlage': 'CoKo · {anlage}',
  'druck.pruefcode': 'Prüfcode {code}',

  /* --- Die Mappe für die Prüfungsakte (2.13 Abschnitt 9.11) --- */
  'pruefer.pruefcode': 'Prüfcode dieses Beispiels',
  'pruefer.pruefcode.was': 'Derselbe Prüfcode, den jeder Abschlußlauf trägt. Er steht hier, im Fuß jedes Ausdrucks und im Kopf jedes Blattes der Excel-Mappe. Zwei Anlagen mit demselben Code rechnen nachweislich gleich — und er ändert sich, sobald sich an der Rechnung von CoKo etwas ändert. Das ist sein Zweck und kein Fehler.',

  'pruefer.mappe.knopf': 'Als Excel-Mappe',
  'pruefer.mappe.laeuft': 'Die Mappe entsteht …',
  'pruefer.mappe.datei': 'regelnachweis-{datum}.xlsx',
  'pruefer.mappe.erstellt': 'Erstellt am {datum}',
  'pruefer.mappe.pruefcode': 'Prüfcode des Beispielkonzerns: {code}',

  'pruefer.mappe.blatt.grundsaetze': 'Grundsätze',
  'pruefer.mappe.blatt.ausgangslage': 'Ausgangslage',
  'pruefer.mappe.blatt.buchungen': 'Buchungen',

  'pruefer.mappe.titel.grundsaetze': 'Regelnachweis — die Grundsätze, am Bestand gemessen',
  'pruefer.mappe.titel.ausgangslage': 'Regelnachweis — was der Beispielkonzern geliefert hat',
  'pruefer.mappe.titel.buchungen': 'Regelnachweis — was die Konsolidierungsarten daraus machen',

  'pruefer.mappe.spalte.grundsatz': 'Grundsatz',
  'pruefer.mappe.spalte.urteil': 'Urteil',
  'pruefer.mappe.spalte.geprueft': 'Geprüft',
  'pruefer.mappe.spalte.beanstandet': 'Beanstandet',
  'pruefer.mappe.spalte.fundstellen': 'Fundstellen',
  'pruefer.mappe.spalte.regeln': 'Regeln',
  'pruefer.mappe.spalte.herkunft': 'Woher',
  'pruefer.mappe.spalte.art': 'Konsolidierungsart',
  'pruefer.mappe.spalte.nr': 'Nr.',
  'pruefer.mappe.spalte.regel': 'Regel',
  'pruefer.mappe.spalte.erlaeuterung': 'Erläuterung',

  'pruefer.mappe.herkunft.lieferung': 'Geliefert',
  'pruefer.mappe.herkunft.einbezug': 'Eigenkapital zum Erwerbstag',

  /* Die Urteile als Wort — am Bildschirm trägt sie die Ampel, in einer
     Tabelle muß sie jemand lesen und filtern können. */
  'pruefer.urteil.gut': 'in Ordnung',
  'pruefer.urteil.warn': 'zu klären',
  'pruefer.urteil.schlecht': 'beanstandet',
  'pruefer.urteil.leer': 'nichts zu prüfen',
  'pruefer.urteil.ungeprueft': 'nicht maschinell geprüft',

  'abbruch.mappe-leer.meldung': 'Die Mappe hat kein Blatt.',
  'abbruch.mappe-leer.weg': 'Das ist ein Programmfehler. Bitte melden Sie, auf welchem Bildschirm es auftrat.',
  'abbruch.mappe-zu-gross.meldung':
    'Die Mappe hätte {zeilen} Zeilen; mehr als {grenze} werden nicht erzeugt.',
  'abbruch.mappe-zu-gross.weg':
    'Grenzen Sie den Bericht ein und erzeugen Sie die Mappe erneut.',

  /* --- Meldungen (R145): was ist · wie viel es ist · was zu tun ist --- */
  'meldung.rechenabbruch.titel': 'Der Abschluss lässt sich nicht rechnen',
  'meldung.stoerung.unerreichbar': 'CoKo ist gerade nicht erreichbar.',
  'meldung.stoerung.abgebrochen': 'CoKo hat diesen Schritt abgebrochen.',
  'meldung.stoerung.abgewiesen': 'CoKo hat diesen Schritt nicht angenommen.',
  'meldung.stoerung.unbekannt': 'CoKo konnte diesen Schritt nicht abschließen.',
  'meldung.stoerung.weg':
    'Das lässt sich nicht am Bildschirm beheben. Bitte den Betrieb verständigen.',
  /* --- Das Störungsfenster (7.2 Abschnitt 30, R1003–R1006) --- */
  'meldung.stoerung.extern': 'Eine Stelle außerhalb der Anwendung hat nicht geliefert.',
  'stoerung.titel': 'Etwas ist nicht geschehen',
  'stoerung.anzahl': '{anzahl}-mal',
  'stoerung.weg.allgemein':
    'Versuchen Sie es erneut. Besteht es fort, melden Sie es dem Betrieb — mit der Uhrzeit {zeit}.',
  'stoerung.weg.kennung': 'Versuchen Sie es erneut. Besteht es fort, melden Sie es dem Betrieb.',
  // Uhrzeit und Kennung für den Betrieb — über den Knopf, nicht im Satz (7.2 Abschnitt 37, R1145).
  'stoerung.kopieren': 'Für den Betrieb kopieren',
  'stoerung.kopiert': 'kopiert',
  // Ein gescheiterter Abruf ist nicht leer (7.2 Abschnitt 39, R1210).
  'abruf.ausgefallen': 'Ließ sich nicht laden.',
  'abruf.erneut': 'Erneut versuchen',
  'rechte.ausgefallen': 'Welche Handgriffe Ihre Rechte erlauben, ließ sich nicht laden — bis dahin stehen keine schreibenden Knöpfe da.',
  'stoerung.kopie': 'Uhrzeit {zeit} · Kennung {kennung}',
  'stoerung.verstanden': 'Verstanden',
  'warten.hinweis': 'CoKo arbeitet noch — seit {sekunden} Sekunden.',

  /* ======================================================================
     Datenimport (5.1, R296–R301)
     ====================================================================== */

  'import.quellen.leer': 'Es ist noch keine Datenquelle erfasst.',

  'import.art.xlsx': 'Excel-Mappe',
  'import.art.csv': 'CSV-Datei',
  'import.art.sql': 'SQL-Datenbank',
  'import.art.lucanet': 'LucaNet-Export',

  'import.zustand.laeuft': 'läuft',
  'import.zustand.nichtausfuehrbar': 'erfasst, nicht abrufbar',
  'import.zustand.stillgelegt': 'stillgelegt',

  /* ---- Die Datenquelle: Datei und Zuordnung (5.1 Abschnitt 3.7, R521–R527) ---- */

  'quelle.datei.titel': 'Die Datei',
  'quelle.datei.beispiel': 'Beispieldatei',
  'quelle.datei.erlaeuterung': 'Wird nur gelesen, nicht übernommen — sie zeigt die Spalten, damit sie sich zuordnen lassen.',
  'quelle.datei.keine':
    'Laden Sie eine Beispieldatei. Ohne sie lässt sich keine Spalte zuordnen, sondern nur eine Spaltennummer raten.',
  'quelle.datei.gelesen': '{datei}',
  'quelle.datei.vorschau': 'Dateivorschau',
  'quelle.datei.keineDateiart':
    'Diese Quelle ist keine Datei. Sie ist erfasst, damit das Verzeichnis vollständig ist; gelesen wird aus ihr heute nicht.',
  'quelle.blatt': 'Tabellenblatt',
  'quelle.kopfzeilen': 'Kopfzeilen',
  'quelle.kopfzeilen.erlaeuterung': 'Einschließlich der Überschriftzeile — sie ist die letzte davon.',
  'quelle.fusszeilen': 'Fußzeilen',
  'quelle.fusszeilen.erlaeuterung': 'Was am Ende steht und keine Lieferung ist, etwa die Summenzeile.',
  'quelle.periodenherkunft': 'Periode der Lieferung',
  'quelle.periodenherkunft.keine': 'beim Start gestellt',
  'quelle.periodenherkunft.dateiname': 'aus dem Dateinamen',
  'quelle.periodenherkunft.zelle': 'aus einer festen Zelle',
  'quelle.periodenmuster': 'Muster im Dateinamen',
  'quelle.periodenzelle': 'Zelle',
  'quelle.periodenherkunft.erlaeuterung':
    'Ohne Angabe wird die Periode beim Starten gestellt. Mehrere Monate auf einmal gehen dann nicht.',
  'quelle.periodenherkunft.dateiname.erlaeuterung':
    'JJJJ ist das Jahr, MM der Monat: Aus SuSa_2025-01.xlsx wird Januar 2025.',
  'quelle.periodenherkunft.zelle.erlaeuterung':
    'Gezählt wie in der Tabelle: B4 ist die vierte Zeile. Gelesen werden Januar 2025, 01.2025, 2025-01 und 202501.',

  'quelle.trennzeichen': 'Trennzeichen',
  'quelle.dezimal': 'Dezimaltrennzeichen',
  'quelle.faktor': 'Fester Faktor',
  'quelle.faktor.erlaeuterung': '1000, wenn die Datei in Tausend liefert.',
  'quelle.vorzeichen': 'Vorzeichen umkehren',
  'quelle.vorzeichen.erlaeuterung': 'Für die ganze Datei, wenn das Vorsystem Haben positiv führt.',
  'quelle.spalte.belegt': 'liegt auf {feld}',
  'quelle.spalten.titel': 'Die Spalten der Datei',
  'quelle.spalten.ohnedatei': 'Ohne Beispieldatei lässt sich keine Spalte neu legen.',
  'quelle.spiegel.bewegungsart': 'Bewegungsart …',
  'quelle.spiegel.hinweis':
    'Je Wertspalte eine Bewegungsart. Startwert und Buchwert der Periode werden nicht gebucht — sie sind die Probe.',
  'quelle.spiegel.probe': 'Probe, wird nicht gebucht',

  'quelle.zuordnung.titel': 'Die Spaltenzuordnung',
  'quelle.zuordnung.zusatz': '{zugeordnet} von {gesamt} zugeordnet · {nicht} nicht geliefert · {offen} offen',
  'quelle.zuordnung.ohnedatei': 'Ohne Beispieldatei lässt sich die Spaltenzuordnung lesen, aber keine Spalte neu legen.',
  'quelle.zuordnung.oeffnen': 'Spalten zuordnen',
  'quelle.zuordnung.uebernehmen': 'Übernehmen',
  'quelle.zuordnung.abbrechen': 'Abbrechen',
  'quelle.zuordnung.offen': '{anzahl} noch offen: {felder}',
  'quelle.zuordnung.vollstaendig': 'Vollständig zugeordnet',
  'quelle.gruppe.konto': 'Konto',
  'quelle.gruppe.betrag': 'Betrag',
  'quelle.gruppe.kontierung': 'Kontierung',
  'quelle.gruppe.waehrung': 'Währung',
  'quelle.gruppe.beleg': 'Beleg',
  'quelle.gruppe.dimension': 'Optionale Dimensionen',
  'quelle.gruppe.zaehlung': '{zugeordnet} von {gesamt}',

  'quelle.feld.lokalnummer': 'Kontonummer',
  'quelle.feld.betrag': 'Betrag',
  'quelle.feld.betrag_soll': 'Soll',
  'quelle.feld.betrag_haben': 'Haben',
  'quelle.feld.betrag_sh': 'Soll/Haben-Kennzeichen',
  'quelle.feld.ledger': 'Bewertungsebene',
  'quelle.feld.bezeichnung': 'Kontobezeichnung',
  'quelle.feld.bewegungsart': 'Bewegungsart',
  'quelle.feld.ic_partner': 'Partner',
  'quelle.feld.segment': 'Segment',
  'quelle.feld.funktionsbereich': 'Funktionsbereich',
  'quelle.feld.waehrung': 'Währung',
  'quelle.feld.transaktionswaehrung': 'Transaktionswährung',
  'quelle.feld.betrag_tw': 'Betrag in Transaktionswährung',
  'quelle.feld.erlaeuterung': 'Buchungstext',
  'quelle.feld.pflicht': 'Pflicht',
  'quelle.feld.offen': 'offen',
  'quelle.feld.nichtgeliefert': 'nicht geliefert',
  'quelle.feld.wert': '= {wert}',
  'quelle.feld.beispiel': 'z. B. {wert}',
  'quelle.feld.wertwahl': 'fester Wert …',
  'quelle.feld.spaltenwahl': 'Spalte wählen …',
  'quelle.feld.nicht.knopf': 'nicht geliefert',
  'quelle.feld.offen.knopf': 'wieder offen',
  'quelle.feld.loesen': 'lösen',

  'quelle.satz.blatt': 'Blatt „{blatt}"',
  'quelle.satz.kopf': '{kopf} Kopfzeilen, {fuss} Fußzeilen',
  'quelle.satz.trennzeichen': 'Trennzeichen „{zeichen}"',
  'quelle.satz.nicht': 'nicht geliefert: {felder}',
  'quelle.satz.sollhaben': 'Betrag = {soll} − {haben}',
  'quelle.satz.kennzeichen': 'Vorzeichen aus {spalte}',
  'quelle.satz.kontotrennung': '(Nummer und Name, getrennt am {trennung})',
  'quelle.trennung.leerzeichen': 'ersten Leerzeichen',
  'quelle.trennung.zeichen': 'ersten „{zeichen}"',
  'quelle.satz.faktor': 'Beträge mal {faktor}',
  'quelle.satz.vorzeichen': 'Vorzeichen gedreht',
  'quelle.satz.saldo': 'liefert Salden',
  'quelle.satz.verkehrszahl': 'liefert Verkehrszahlen',
  'quelle.betragsart': 'Der Betrag steht',
  'quelle.betragsart.wert': 'in einer Wertspalte',
  'quelle.betragsart.sollhaben': 'in einer Soll- und einer Habenspalte',
  'quelle.betragsart.kennzeichen': 'in einer Wertspalte mit Soll/Haben-Kennzeichen',
  'quelle.betragsart.spiegel': 'in mehreren Spalten — ein Spiegel, je Spalte eine Bewegungsart',
  'quelle.betragsart.erlaeuterung':
    'Soll minus Haben; eine leere Zelle zählt als null. Das Kennzeichen erkennt S · D · Soll · Debit · + und H · C · Haben · Credit · −.',
  'quelle.kontotrennung': 'Nummer und Name in einer Spalte',
  'quelle.kontotrennung.aus': 'nein — die Spalte trägt nur die Nummer',
  'quelle.kontotrennung.leerzeichen': 'ja — getrennt am ersten Leerzeichen',
  'quelle.kontotrennung.zeichen': 'ja — getrennt am ersten „{zeichen}"',
  'quelle.kontotrennung.erlaeuterung': 'Aus „1200 Bank" wird die Kontonummer 1200 und die Kontobezeichnung Bank.',

  'quelle.lieferart': 'Die Zahlen sind',
  'quelle.lieferart.saldo': 'Salden — ein Stand, die Verkehrszahl wird abgeleitet',
  'quelle.betragsart.package': 'Reporting Package — eine ganze Mappe nach der Standardvorlage',
  'quelle.package.titel': 'Das Reporting Package',
  'quelle.package.offen': 'Die Bewertungsebene fehlt noch',
  'quelle.package.fertig': 'Vollständig eingerichtet',
  'quelle.package.vorlage': 'Blätter und Spalten legt die Package-Vorlage fest. Was geliefert wird, ändern Sie unter Datenimport › Package-Vorlagen.',
  'quelle.package.ebene': 'In diese Bewertungsebene bucht jede Mappe dieser Datenquelle.',
  'quelle.lieferart.verkehrszahl': 'Verkehrszahlen — die Bewegung der Periode',
  'quelle.lieferart.erlaeuterung':
    'Eine Saldenliste liefert Stände: Bestandskonten den Bestand, Erfolgskonten den kumulierten Wert seit Geschäftsjahresbeginn. Der Import rechnet die Differenz zum letzten Stand und bucht sie als Verkehrszahl.',

  'quelle.speichern': 'Speichern',
  'quelle.nochnicht': 'Konto und Betrag brauchen eine Spalte, die Bewertungsebene eine Spalte oder einen festen Wert.',

  'quelle.verwendung.titel': 'Verwendung',
  'quelle.verwendung.zusatz': 'zuletzt geändert am {zeit}',
  'quelle.verwendung.zusatz.keine': 'zuletzt geändert am {zeit} · noch keine Lieferung',
  'quelle.verwendung.leer': 'Aus dieser Quelle wurde noch nicht geliefert.',
  'quelle.liste.offen': '{anzahl} offen',

  'zustand.quellen.vollstaendig': 'Alle {anzahl} Datenquellen haben für jedes Feld entschieden, woher es kommt.',
  'zustand.quellen.offen':
    '{anzahl} von {gesamt} Datenquellen haben offene Felder — weder zugeordnet noch als „nicht geliefert" abgehakt. Das blockiert nichts.',
  'kachel.stand.datenquellen': 'Datenquellen',
  'kachel.stand.datenquellen.offen': '{anzahl} von {gesamt} mit offenen Feldern',

  'import.abbildung.befund.meldung': 'Die Spaltenzuordnung wurde nicht gespeichert: {befund}',
  'import.abbildung.befund.weg': 'Ordnen Sie das Feld zu, oder wählen Sie den festen Wert aus dem Vorrat.',
  'import.abbildung.unlesbar.meldung': 'Die Spaltenzuordnung ließ sich nicht lesen.',
  'import.abbildung.unlesbar.weg': 'Laden Sie das Blatt neu und speichern Sie noch einmal.',
  'import.quelle.unbekannt.meldung': 'Die Datenquelle {quelle} gibt es nicht.',
  // R1826: Verkehrszahlen nur über eine Direktanbindung.
  'import.verkehrszahl.datei.meldung':
    'Eine Datenquelle aus Excel oder CSV liefert Salden, einen Spiegel oder ein Package. Verkehrszahlen kommen nur über eine Direktanbindung (SQL, Lucanet).',
  'import.verkehrszahl.datei.weg': 'Stellen Sie die Lieferart auf Saldo, oder richten Sie die Quelle als Direktanbindung ein.',

  // Die Package-Vorlagen (5.1 Abschnitt 3.37, R892–R897): sechs Fragen, jede vorbelegt.
  'packagevorlage.quelle.vorschlag': 'Reporting Package',
  'packagevorlage.frage.quelle': '1. Für welche Datenquelle?',
  'packagevorlage.quelle.neu': 'Neue Datenquelle entwerfen',
  'packagevorlage.quelle.name': 'Name der neuen Datenquelle',
  'packagevorlage.frage.gesellschaften': '2. Für welche Gesellschaften und welche Periode?',
  'packagevorlage.frage.ebene': '3. Auf welcher Ebene melden die Lieferer?',
  'packagevorlage.ebene.lokal': 'Eigene Konten der Gesellschaft – aus ihrer Kontenzuordnung',
  'packagevorlage.ebene.konzern': 'Konzernkonten',
  'packagevorlage.ebene.position': 'Positionen – wenige Zeilen, für kleine Lieferer',
  'packagevorlage.stellvertreter.frage': 'Diese Positionen tragen mehrere Konten. Auf welches bucht CoKo den gelieferten Betrag?',
  'packagevorlage.stellvertreter.offen': 'bitte wählen',
  'packagevorlage.frage.partner': '4. Sollen die Lieferer Salden auf Konzernpartner aufteilen?',
  'packagevorlage.partner.ja': 'Ja – das Blatt Intercompany kommt dazu',
  'packagevorlage.partner.nein': 'Nein',
  'packagevorlage.frage.spiegel': '5. Welche Spiegel sollen geliefert werden?',
  'packagevorlage.spiegel.anlagen': 'Anlagenspiegel',
  'packagevorlage.spiegel.verbindlichkeiten': 'Kreditspiegel',
  'packagevorlage.spiegel.rueckstellungen': 'Rückstellungsspiegel',
  'packagevorlage.spiegel.eigenkapital': 'Eigenkapitalspiegel',
  'packagevorlage.frage.zusaetze': '6. Was soll die Mappe noch tragen?',
  'packagevorlage.zusatz.eroeffnung': 'Bestand 01.01. – nur für das erste Package einer Gesellschaft',
  'packagevorlage.zusatz.segment': 'Segment',
  'packagevorlage.zusatz.funktionsbereich': 'Funktionsbereich',
  'packagevorlage.zusatz.kostenstelle': 'Kostenstelle',
  'packagevorlage.faktor.eins': 'Beträge in ganzen Einheiten',
  'packagevorlage.faktor.tausend': 'Beträge in Tausend',
  'packagevorlage.vorschau': 'Was der Lieferer in die Hand bekommt',
  'packagevorlage.blatt.zeilen': '{blatt}',
  'packagevorlage.erzeugen': 'Erzeugen',
  'packagevorlage.fehler': 'Die Vorlage wurde nicht erzeugt. {grund}',
  'packagevorlage.fehler.speichern': 'Die Vorlage ist erzeugt, ließ sich aber nicht als {datei} speichern: {grund} Wählen Sie einen anderen Ort und drücken Sie „Erzeugen" erneut.',
  'packagevorlage.vorschau.fehler': 'Die Vorschau ließ sich nicht abrufen. {grund} Was hier steht, gilt für die vorige Auswahl.',
  'packagevorlage.grund.abgemeldet': 'Ihre Anmeldung ist abgelaufen. Melden Sie sich neu an und drücken Sie „Erzeugen" erneut.',
  'packagevorlage.grund.recht': 'Das Erzeugen steht Ihrer Anmeldung nicht offen – dafür braucht es das Recht „Importieren".',
  'packagevorlage.grund.server': 'Die Vorlage ließ sich nicht erzeugen. Sehen Sie unter Frage 1 nach, ob die Datenquelle schon angelegt ist, bevor Sie es erneut versuchen.',
  'packagevorlage.grund.ort': 'Der gewählte Ort nimmt die Datei nicht an — wählen Sie einen anderen.',
  'packagevorlage.schritt.meldung': 'Die Vorlage wurde nicht erzeugt – es hakte {schritt}: {grund}',
  'packagevorlage.schritt.weg': 'Beheben Sie die Ursache und drücken Sie „Erzeugen" erneut.',
  'packagevorlage.schritt.angelegt': 'Die Datenquelle „{quelle}" ist bereits angelegt und steht unter Frage 1 zur Wahl – wählen Sie sie beim nächsten Erzeugen, statt eine neue zu entwerfen.',
  'packagevorlage.befund.meldung': 'Die Vorlage wurde nicht erzeugt: {befund}',
  'packagevorlage.befund.weg': 'Beantworten Sie die offenen Fragen im Fragebogen.',

  'import.spalte.code': 'Kurzzeichen',
  'import.spalte.name': 'Bezeichnung',
  'import.spalte.art': 'Art',
  'import.spalte.zustand': 'Zustand',
  'import.spalte.einheit': 'Gesellschaft',
  'import.spalte.zustaendig': 'Zuständig',
  'import.spalte.herkunft': 'Herkunft',
  'import.spalte.quelle': 'Quelle',
  'import.spalte.periode': 'Periode',
  'import.spalte.gelesen': 'gelesen',
  'import.spalte.wann': 'angenommen am',
  'import.spalte.zeile': 'Zeile',
  'import.spalte.lokalkonto': 'lokales Konto',
  'import.spalte.bezeichnung': 'Bezeichnung',
  'import.spalte.konzernkonto': 'Konzernkonto',
  'import.spalte.betrag': 'Betrag',
  'import.spalte.bewegungsart': 'Bewegungsart',
  'import.spalte.befund': 'Befund',
  'import.spalte.bisher': 'letzter Stand',
  'import.spalte.verkehrszahl': 'Verkehrszahl',
  'import.wirkung.frage': 'Für {perioden} liegen schon Lieferungen vor. Worauf soll diese wirken?',
  'import.wirkung.periode': 'Rückwirkend: {periode} ersetzen, {folge} wird neu abgeleitet',
  'import.wirkung.zielperiode': 'Nachbuchen in',
  'import.wirkung.nachbuchung': 'Als Nachbuchung: die Differenz in {ziel} buchen, {periode} bleibt',

  'import.quelle.neu': 'Neue Datenquelle',
  'import.quelle.waehlen': 'Quelle wählen …',
  'import.quelle.fehler': 'Das ließ sich nicht speichern. Trägt der Code schon eine Quelle?',
  'import.herkunft.erlaeuterung':
    'Ordner, Server, Ansprechpartner — für Menschen. Keine Zugangsdaten: Die gehören in die Ablage des Betriebs.',
  'import.aktiv.erlaeuterung': 'Eine stillgelegte Quelle bleibt im Verzeichnis und wird nicht mehr angeboten.',
  'import.speichern': 'Speichern',
  'import.schliessen': 'Schließen',

  'import.schritt.quelle': 'Datenquelle',
  'import.schritt.periode': 'Periode',
  'import.periode.kommt.dateiname': 'kommt aus dem Dateinamen',
  'import.periode.kommt.zelle': 'kommt aus Zelle {zelle}',
  'import.periode.gelesen.dateiname': '{periode} — aus dem Dateinamen',
  'import.periode.gelesen.zelle': '{periode} — aus Zelle {zelle}',
  'import.schritt.datei': 'Datei',
  'dateiwahl.waehlen': 'Datei wählen',
  'dateiwahl.waehlen.mehrere': 'Dateien wählen',
  'dateiwahl.anzahl': '{anzahl} Dateien',
  /* R871–R874 — Die Datenquelle schlägt Bewegungsarten vor, der Anwender antwortet bei jedem Import. */
  'import.vorschlag.titel.eins': '1 Vorschlag der Datenquelle',
  'import.vorschlag.titel.mehrere': '{anzahl} Vorschläge der Datenquelle',
  'import.vorschlag.satz': '{richtung} auf {konto} als {bewegungsart}',
  'import.vorschlag.zeilen.eins': '1 Zeile, {betrag}',
  'import.vorschlag.zeilen.mehrere': '{anzahl} Zeilen, {betrag}',
  'import.vorschlag.annehmen': 'Annehmen',
  'import.vorschlag.ablehnen': 'Ablehnen',
  'import.uebernehmen.vorschlaege': 'Erst jeden Vorschlag der Datenquelle annehmen oder ablehnen.',
  'import.vorschlag.befund.meldung': 'Die Vorschläge wurden nicht gespeichert: {befund}',
  'import.vorschlag.befund.weg':
    'Wählen Sie Konzernkonto, Richtung und Bewegungsart aus dem Vorrat — jedes Konto mit derselben Richtung nur einmal.',
  'quelle.vorschlaege.titel': 'Vorschläge zur Bewegungsart',
  'quelle.vorschlaege.zusatz.keine': 'keine',
  'quelle.vorschlaege.zusatz.eins': 'ein Vorschlag',
  'quelle.vorschlaege.zusatz.mehrere': 'mehrere Vorschläge',
  'quelle.vorschlaege.konto': 'Konzernkonto',
  'quelle.vorschlaege.richtung': 'Richtung',
  'quelle.vorschlaege.bewegungsart': 'Bewegungsart',
  'quelle.vorschlaege.zunahme': 'Zunahme',
  'quelle.vorschlaege.abnahme': 'Abnahme',
  'quelle.vorschlaege.neu': 'Vorschlag hinzufügen',
  'quelle.vorschlaege.entfernen': 'Vorschlag entfernen',
  'quelle.vorschlaege.unvollstaendig': 'Ein Vorschlag braucht Konzernkonto und Bewegungsart.',
  'quelle.vorschlaege.fest':
    'Die Bewegungsart steht an dieser Quelle als fester Wert. Jede Zeile bringt damit eine mit — die Vorschläge greifen nie.',
  'import.pruefen': 'Prüfen',
  'import.pruefterade': 'Prüft …',
  'import.nichtausfuehrbar.titel': 'Aus dieser Quelle kann heute nicht gelesen werden ({art}).',

  'import.vorschau.titel': 'Was in {datei} steht',
  'import.vorschau.gueltig': 'zuordenbar',
  'import.vorschau.fehlerhaft': 'offen',
  'import.vorschau.stehtaus': 'steht aus',
  'import.vorschau.probe': 'Probe',
  'import.vorschau.gehtauf': 'geht auf',
  'import.vorschau.differenz': 'Differenz {betrag}',
  'import.vorschlag': 'Vorschlag: {konto}',
  'import.uebernehmen': 'Übernehmen',
  'import.uebernehmen.eins': 'Eine Zeile übernehmen',
  'import.sperre.nochnicht': 'Noch nicht:',
  'import.sperre.konto.eins': 'Eine Zeile hat kein Konzernkonto',
  'import.sperre.konto.mehrere': '{anzahl} Zeilen haben kein Konzernkonto',
  'import.sperre.zuordnung.eins': 'Bei einer Zeile fehlt eine Zuordnung',
  'import.sperre.zuordnung.mehrere': 'Bei {anzahl} Zeilen fehlt eine Zuordnung',
  'import.sperre.befund.eins': 'Eine Zeile trägt einen Befund',
  'import.sperre.befund.mehrere': '{anzahl} Zeilen tragen einen Befund',
  'import.sperre.zeilenzeigen': 'Nur diese Zeilen zeigen',
  'import.sperre.allezeigen': 'Alle Zeilen zeigen',
  'import.sperre.schluss': 'Ein unvollständiger Datensatz betritt das System nicht.',
  'import.uebernehmen.differenz':
    'Noch nicht: Soll und Haben stimmen um {betrag} nicht überein. Die Bilanzgleichung ist Grundbedingung, nicht Befund.',
  'import.laeufe.titel': 'Gespeicherte Datenimporte',
  'import.laeufe.leer': 'Es ist noch kein Datenimport gespeichert.',

  /* Die gespeicherten Datenimporte — was gilt, nicht was geschah (R633–R638). */
  'import.neu': 'Neuer Datenimport',
  'import.bereich.hinweis':
    'In diesem Bereich können Sie neue Datenimporte durchführen, bestehende Importe ' +
    'aktualisieren und — sofern der Periodenschutz es zulässt — löschen.',
  'import.filter.kopf': 'Datenimporte nach Periode, Gesellschaft oder Bewertungsebene filtern',
  'import.filter.aktiv': 'Diese Spalte ist eingegrenzt',

  /* R374 — Jeder Datenimport trägt einen Namen, auch der maschinelle. */
  'import.spalte.belegname': 'Name',
  'import.name.erlaeuterung':
    'Worunter Sie diesen Datenimport wiederfinden. Der Vorschlag steht drin — ändern Sie ihn, ' +
    'wenn mehrere Lieferungen in dieselbe Periode gehen.',
  'import.name.fehlt': 'Tragen Sie einen Namen ein — jeder Datenimport hat einen.',
  'import.staende.leer': 'Es ist noch kein Datenimport gespeichert.',
  'import.staende.leer.eingrenzung': 'Kein gespeicherter Datenimport passt zu dieser Eingrenzung.',
  'import.spalte.schicht': 'Bewertungsebene',
  // Das Reporting Package: eine Mappe, viele Blätter (5.1 Abschnitt 3.26).
  // Die gelieferte Sicht (2.12 Abschnitt 3.12).
  'bericht.bezug.unbekannt': 'Den Bezug {bezug} gibt es nicht.',
  'bericht.bezug.unbekannt.weg': 'Waehlen Sie eine Gesellschaft oder eine Gruppe aus der Liste.',
  'bericht.bezug.kreis': 'Ein Konsolidierungskreis wird gerechnet und nicht gelesen.',
  'bericht.bezug.kreis.weg': 'Fuer ihn gibt es den Abschlusslauf; gelieferte Zahlen stehen unter einer Gesellschaft oder Gruppe.',
  'bericht.bezug.leer': 'Unter {bezug} steht in dieser Periode keine Gesellschaft.',
  'bericht.bezug.leer.weg': 'Nehmen Sie Gesellschaften in die Gruppe auf oder waehlen Sie eine einzelne.',
  'bericht.bezug.fehlt': 'Ohne Konsolidierungskreis oder Bezug gibt es nichts nachzuweisen.',
  'stammdaten.feld.uebergeordneter_kreis': 'Übergeordneter Konsolidierungskreis',
  'bereich.abschluss.konzernabschluesse': 'Konzernabschlüsse',
  'konzernabschluss.spalte.name': 'Konzernabschluss',
  'konzernabschluss.spalte.kreis': 'Konsolidierungskreis',
  'konzernabschluss.spalte.geschaeftsjahr': 'Geschäftsjahr',
  'konzernabschluss.spalte.prozesse': 'Prozesse',
  'konzernabschluss.spalte.gerechnet': 'Gerechnet',
  'konzernabschluss.spalte.zustand': 'Zustand',
  'konzernabschluss.spalte.aktualisiert': 'Aktualisiert',
  'konzernabschluss.zustand.gerechnet': '{anzahl} von zwölf Monaten gerechnet',
  'konzernabschluss.zustand.offen': 'Noch nicht gerechnet',
  'konzernabschluss.nichtgerechnet': 'noch nicht gerechnet',
  'konzernabschluss.von': 'von {wer}',
  'prozess.zustand.fertig': 'Gerechnet',
  'prozess.zustand.unvollstaendig': 'Nicht in allen gerechneten Monaten gelaufen',
  'prozess.zustand.offen': 'CoKo rechnet diesen Prozess noch nicht',
  'konzernabschluss.freigegeben': '{anzahl} freigegeben',
  'konzernabschluss.leer': 'Noch kein Konzernabschluss angelegt. Ohne ihn rechnet nur die Vorberechnung, und zwar ohne Ihre Prozesse.',
  'konzernabschluss.neu': 'Konzernabschluss anlegen',
  'konzernabschluss.anlegen': 'Anlegen',
  'konzernabschluss.abbrechen': 'Abbrechen',
  'konzernabschluss.feld.name': 'Bezeichnung',
  'konzernabschluss.feld.name.hinweis': 'Woran der Prüfer ihn erkennt, zum Beispiel „Konzernabschluss 2026\".',
  'konzernabschluss.feld.kreis': 'Konsolidierungskreis',
  'konzernabschluss.feld.kreis.leer': 'Kreis wählen …',
  'konzernabschluss.feld.ledger': 'Bewertungsebene',
  'konzernabschluss.feld.ledger.leer': 'Bewertungsebene wählen …',
  'konzernabschluss.feld.ledger.hinweis': 'Nach welchem Recht gerechnet wird. Derselbe Konzern legt nach HGB und IFRS getrennt ab — das sind zwei Abschlüsse.',
  'konzernabschluss.feld.von': 'Erster Monat des Geschäftsjahres',
  'konzernabschluss.feld.von.hinweis': 'Die übrigen elf folgen. Es wird jeder Monat gerechnet; Lücken gibt es nicht.',
  'konzernabschluss.feld.vorlage': 'Prozesse übernehmen von',
  'konzernabschluss.feld.vorlage.hinweis': 'Die Prozesse werden kopiert, nicht verknüpft. Wer hier später einen herausnimmt, ändert die Vorlage nicht.',
  'konzernabschluss.vorlage.werk': 'Fassung ab Werk',
  'konzernabschluss.rechnen': 'Rechnen',
  'konzernabschluss.rechnen.titel': 'Konzernabschluss rechnen',
  'konzernabschluss.rechnen.frage': '{name} jetzt rechnen?',
  'konzernabschluss.rechnen.hinweis': 'Alle zwölf Monate werden gerechnet. Ein Monat, der sich nicht rechnen lässt, hält die übrigen nicht auf — er steht danach mit seiner Meldung da.',
  'konzernabschluss.ablage.meldung':
    'Der Konzernabschluss ist gerechnet, aber die Befunde für die Überwachung der Arbeitsliste ließen sich nicht ablegen. Rechnen Sie ihn noch einmal; besteht es fort, melden Sie es dem Betrieb.',
  'ablage.spiegel': 'Davon ohne Spalte im Spiegel: {ohne_spalte}.',
  'uebersicht.konzernbestand.nicht-konsolidiert':
    'Konzernbestände nicht geprüft: {monat} ist nicht konsolidiert. Rechnen Sie den Monat im Bereich [[Konzernabschlüsse]]; danach werden sie geprüft.',
  'uebersicht.konzernbestand.ablage-folgt':
    'Konzernbestände für {monat} noch nicht geprüft: Die Befunde des Laufs werden gerade nachgetragen und stehen in einer Minute hier.',
  'konzernabschluss.rechnet': 'Rechnet … Monat {fertig} von {monate}',
  'konzernabschluss.speichert': 'Speichert …',
  'konzernabschluss.rechnet.wer': 'angestoßen {wann} von {wer}',
  'konzernabschluss.nichtgespeichert': '{name} nicht gespeichert',
  'konzernabschluss.nichtgespeichert.monate': 'Nicht jeder Monat ließ sich rechnen.',
  'konzernabschluss.nichtgespeichert.abbruch': 'Die Rechnung endete mit einer Meldung.',
  'konzernabschluss.ansehen': 'Ansehen',
  'konzernabschluss.verschwunden': '{name} nicht zu Ende gerechnet',
  'konzernabschluss.verschwunden.satz': 'Der Dienst wurde währenddessen neu gestartet; gespeichert ist nichts.',
  'konzernabschluss.neurechnen': 'Neu rechnen',
  'konzernabschluss.verweigert': 'Nicht gespeichert: {anzahl} Monate tragen einen Fehler. Es wird entweder das ganze Geschäftsjahr geschrieben oder keines — sonst stünde ein Abschluss da, bei dem Monate fehlen, und er sähe aus wie ein fertiger.',
  'konzernabschluss.verweigert.eins': 'Nicht gespeichert: Ein Monat trägt einen Fehler. Es wird entweder das ganze Geschäftsjahr geschrieben oder keines — sonst stünde ein Abschluss da, bei dem ein Monat fehlt, und er sähe aus wie ein fertiger.',
  'konzernabschluss.laufhin.schliessen': 'Schliessen',
  'konzernabschluss.fehlt.meldung': 'Diesen Konzernabschluss gibt es nicht.',
  'konzernabschluss.fehlt.weg': 'Legen Sie ihn an, bevor Sie ihn anstossen.',
  'konzernabschluss.ohnename.meldung': 'Der Konzernabschluss braucht einen Namen.',
  'konzernabschluss.ohnename.weg': 'Geben Sie einen sprechenden Namen ein, zum Beispiel „Konzernabschluss 2026\". Daran erkennt ihn der Prüfer im Bericht.',
  'konzernabschluss.namedoppelt.meldung': 'Ein Konzernabschluss heisst bereits {name}.',
  'konzernabschluss.namedoppelt.weg': 'Wählen Sie einen anderen Namen. Zwei Abschlüsse desselben Zeitraums sind erlaubt — der ursprüngliche und der angepasste —, zwei gleiche Namen nicht.',
  'kreis.mitglieder.titel': 'Wer dazugehört',
  'kreis.mitglieder.leer': 'Noch keine Gesellschaft aufgenommen. Ohne Mitglieder rechnet der Kreis nichts.',
  'kreis.mitglied.gesellschaft': 'Gesellschaft',
  'kreis.mitglied.ab': 'Gehört dazu ab',
  'kreis.mitglied.ab.hinweis': 'Ab dieser Periode fliessen ihre Zahlen in den Konzern. Davor nicht — auch dann nicht, wenn sie gebucht sind.',
  'kreis.mitglied.bis': 'Bis',
  'kreis.mitglied.offen': 'offen',
  'kreis.mitglied.art': 'Einbezogen als',
  'kreis.mitglied.oberste': 'oberste Gesellschaft',
  'kreis.mitglied.waehlen': 'Gesellschaft wählen …',
  'kreis.mitglied.hinzufuegen': 'Gesellschaft hinzufügen',
  'kreis.mitglied.aufnehmen': 'Aufnehmen',
  'kreis.mitglied.abbrechen': 'Abbrechen',
  'lieferrhythmus.spalte.ab': 'Gilt ab',
  'lieferrhythmus.leer': 'Kein Rhythmus gepflegt; es gilt „monatlich“.',
  'lieferrhythmus.aendern': 'Rhythmus ändern',
  'lieferrhythmus.waehlen': 'Rhythmus wählen …',
  'lieferrhythmus.ab.hinweis': 'Ab diesem Monat gilt der neue Rhythmus; die Zeit davor bleibt, wie sie war.',
  'lieferrhythmus.speichern': 'Speichern',
  'lieferrhythmus.abbrechen': 'Abbrechen',
  'lieferrhythmus.entfernen': 'Entfernen',
  'kreis.mitglied.eintrittskurs': 'Eintrittskurs',
  'kreis.eintrittskurs.fehlt': 'fehlt',
  'kreis.eintrittskurs.nachtragen.kurz': 'liegt nicht vor — Freigabe blockiert',
  'kreis.eintrittskurs.stichtag.kurz': '{kurs} · Stichtag {periode}, nach Rücksprache mit dem Wirtschaftsprüfer',
  'kreis.eintrittskurs.waehlen': 'wählen',
  'kreis.eintrittskurs.aendern': 'ändern',
  'kreis.eintrittskurs.speichern': 'Eintrittskurs speichern',
  'kreis.eintrittskurs.frage':
    '{einheit} bilanziert in {waehrung}, der Kreis in {konzernwaehrung}. Der Eintritt steht zu einem Kurs für die ganze Eröffnungsbilanz, fixiert. Eingebracht wird der Stand von Ende {eingebracht}.',
  'kreis.eintrittskurs.weg.eingetragen': 'Kurs eintragen',
  'kreis.eintrittskurs.weg.nachtragen':
    'Historische Kurse liegen mir nicht vor. Erzeuge eine Aufgabe, dies nachzutragen, und blocke den Abschluss, bis das erledigt ist.',
  'kreis.eintrittskurs.weg.stichtag':
    'Nach Rücksprache mit dem Wirtschaftsprüfer darf der Stichtagskurs des Monats als historischer Kurs unterstellt werden. Bitte übernimm diesen aus der Wechselkurstabelle.',
  'kreis.eintrittskurs.kurs': '1 {waehrung} = … {konzernwaehrung}',
  'kreis.eintrittskurs.stichtag.wert': 'Stichtagskurs {periode} übernehmen: {kurs}',
  'kreis.eintrittskurs.stichtag.keiner': 'Für {periode} steht kein Stichtagskurs {waehrung} in der Kurstabelle.',
  'kreis.eintrittskurs.zentralbank': 'Kurse {periode} von der EZB holen',
  'kreis.eintrittskurs.zentralbank.ergebnis': 'Die EZB liefert für {periode} den Stichtagskurs {kurs}.',
  'kreis.eintrittskurs.zentralbank.nichts': 'Die EZB liefert für {periode} keinen Stichtagskurs.',
  'kreis.eintrittskurs.zentralbank.uebernehmen': 'In die Kurstabelle übernehmen',
  'kreis.eintrittskurs.pflicht.meldung':
    '{einheit} bilanziert in {waehrung}, der Kreis in {konzernwaehrung}: Der Eintrittskurs ist Pflicht.',
  'kreis.eintrittskurs.pflicht.weg':
    'Wählen Sie einen der drei Wege: Kurs eintragen, „Historische Kurse liegen mir nicht vor“ oder den Stichtagskurs übernehmen.',
  'kreis.eintrittskurs.wert.meldung': 'Ein eingetragener Eintrittskurs für {einheit} braucht einen Wert über null.',
  'kreis.eintrittskurs.wert.weg': 'Tragen Sie den Kurs ein: 1 {waehrung} = … {konzernwaehrung}.',
  'kreis.eintrittskurs.nachtragen.meldung': 'Wer „Historische Kurse liegen mir nicht vor“ wählt, trägt für {einheit} keinen Kurs ein.',
  'kreis.eintrittskurs.nachtragen.weg': 'Lassen Sie das Kursfeld leer oder wählen Sie „Kurs eintragen“.',
  'kreis.eintrittskurs.nichtfremd.meldung': '{einheit} bilanziert in der Konzernwährung {konzernwaehrung} und braucht keinen Eintrittskurs.',
  'kreis.eintrittskurs.nichtfremd.weg': 'Lassen Sie den Eintrittskurs leer.',
  'kreis.eintrittskurs.stichtag.anders.meldung':
    'Der Stichtagskurs für {einheit} kommt aus der Kurstabelle und lässt sich nicht von Hand ändern.',
  'kreis.eintrittskurs.stichtag.anders.weg': 'Wählen Sie „Kurs eintragen“, wenn ein anderer Kurs gelten soll.',
  'kreis.eintrittskurs.stichtag.fehlt.meldung': 'Für {periode} steht kein Stichtagskurs {waehrung} in der Kurstabelle.',
  'kreis.eintrittskurs.stichtag.fehlt.weg': 'Holen Sie die Kurse {periode} von der EZB oder tragen Sie sie unter Währungskurse ein.',
  'abbruch.eintrittskurs.fehlt.meldung':
    '{einheit} tritt in fremder Währung in den Kreis ein, und ihr Eintrittskurs ist nicht gewählt. Der Kreis rechnet in {konzernwaehrung}.',
  'abbruch.eintrittskurs.fehlt.weg':
    'Wählen Sie unter [[Konsolidierungskreise]] bei den Mitgliedern den Eintrittskurs. Danach rechnen Sie neu.',
  // R1472: Die Prüfsumme der historischen Kurse.
  'abbruch.historischer-kurs.fehlt.meldung':
    '{einheit}, {periode}: Für Konto {konto}, {bewegungsart}, fehlt die Zeile der historischen Kurse — geliefert sind {differenz} {waehrung}.',
  'abbruch.historischer-kurs.summe.meldung':
    '{einheit}, {periode}: Die historischen Kurse auf Konto {konto}, {bewegungsart}, passen nicht zur Lieferung — Lieferung minus Tabelle: {differenz} {waehrung}.',
  // R1592: Ohne Stand vor dem Eintritt kein Zugang.
  'abbruch.kreiseintritt.ohne-stand.meldung':
    '{einheit} tritt im {periode} in den Konsolidierungskreis ein, aber für das Ende des Vormonats liegt kein Stand vor. Den Zugang bildet CoKo aus diesem Stand.',
  'abbruch.kreiseintritt.ohne-stand.weg':
    'Lesen Sie unter [[Datenimporte]] die Saldenliste {vormonat} der {einheit} ein. Danach rechnen Sie neu.',
  'abbruch.historischer-kurs.weg':
    'Legen Sie die Zeilen unter [[Historische Kurse]] an oder passen Sie sie dort an. Danach rechnen Sie neu.',
  'historischer-kurs.startwert': 'Startwert',
  'historischer-kurs.sachverhalt.pflicht': 'Eine Zeile der historischen Kurse braucht den Namen ihres Sachverhalts.',
  'historischer-kurs.unbekannt': 'Diese Zeile der historischen Kurse gibt es nicht mehr. Bitte die Seite neu laden.',
  'historischer-kurs.kurs.pflicht': 'Ein historischer Kurs ist größer als null.',

  /* --- Historische Kurse (2.6 Abschnitt 2.12) --- */
  'historisch.offen.titel': 'Offen im {periode}',
  'historisch.offen.erlaeuterung':
    'Was die Lieferung im Monat bringt und die Tabelle noch nicht trägt. Bis es passt, rechnet der Abschluss nicht.',
  'historisch.offen.summe': 'Zeilen anpassen',
  'historisch.spalte.gesellschaft': 'Gesellschaft',
  'historisch.spalte.bewertungsebene': 'Bewertungsebene',
  'historisch.spalte.konto': 'Konto',
  'historisch.spalte.bewegungsart': 'Bewegungsart',
  'historisch.spalte.geliefert': 'Geliefert',
  'historisch.spalte.intabelle': 'In der Tabelle',
  'historisch.spalte.kurs': 'Kurs',
  'historisch.spalte.monat': 'Monat',
  'historisch.spalte.sachverhalt': 'Sachverhalt',
  'historisch.spalte.betrag': 'Betrag',
  'historisch.spalte.konzern': 'In Konzernwährung',
  'historisch.spalte.herkunft': 'Herkunft',
  'historisch.vorschlag.stichtag': 'Stichtagskurs des Monats',
  'historisch.vorschlag.ergebnisjahr': 'Kurs des Ergebnisjahres {jahr}',
  'historisch.vorschlag.eintrittskurs': 'Eintrittskurs der Kreismitgliedschaft',
  'historisch.vorschlag.keiner': 'Kein Vorschlag',
  'historisch.vorschlag.ueberschreiben': 'Anderen Kurs eintragen',
  'historisch.eingabe.kurs': '1 {waehrung} = … {konzernwaehrung}',
  'historisch.eingabe.mischkurs': 'Mischkurs: 1 {waehrung} = … {konzernwaehrung}',
  'historisch.anlegen': 'Zeilen anlegen',
  'historisch.tabelle.titel': 'Tabelle der Gesellschaft',
  'historisch.tabelle.leer': 'Für {gesellschaft} steht noch keine Zeile in der Tabelle.',
  'historisch.gesellschaft.waehlen': 'Gesellschaft wählen',
  'historisch.herkunft.eintrittskurs': 'Eintrittskurs',
  'historisch.herkunft.vorgeschlagen': 'vorgeschlagen',
  'historisch.herkunft.eingetragen': 'eingetragen',
  'historisch.speichern': 'Speichern',
  'historisch.abbrechen': 'Abbrechen',
  'historisch.aendern': 'Ändern',
  'historisch.teilen': 'Teilen',
  'historisch.loeschen': 'Löschen',
  'kreis.mitglied.entfernen': 'Entfernen',
  'kreis.zirkel.meldung': 'Der Kreis stünde damit unter sich selbst.',
  'kreis.zirkel.weg': 'Wählen Sie einen Kreis, der nicht schon unter diesem hängt. Leer lassen heisst: Dies ist der oberste Kreis.',
  'werkzeug.datenuebernahme': 'Datenübernahme in den Konzern',
  'werkzeug.goodwillfortschreibung': 'Firmenwert fortschreiben',
  'werkzeug.ic-abstimmung': 'IC-Abstimmung',
  'werkzeug.schuldenkonsolidierung': 'Schuldenkonsolidierung',
  'werkzeug.aufwandertragkonsolidierung': 'Aufwands- und Ertragskonsolidierung',
  'werkzeug.zwischenergebniseliminierung-uv': 'Zwischenergebniseliminierung Umlaufvermögen',
  'werkzeug.zwischenergebniseliminierung-av': 'Zwischenergebniseliminierung Anlagevermögen',
  'werkzeug.kapitalkonsolidierung': 'Kapitalkonsolidierung',
  'werkzeug.latente-steuern': 'Latente Steuern auf Konsolidierungsmaßnahmen',
  'werkzeug.anteile-nicht-beherrschend':
    'Ausweis laufendes Ergebnis nicht beherrschender Gesellschafter',
  'werkzeug.dividendenkonsolidierung': 'Konsolidierung von IC-Ausschüttungen',
  'werkzeug.datenuebernahme.liest': 'Liest die gelieferten Einzelabschlüsse, rechnet Fremdwährung in Konzernwährung um und stellt die Gesellschaften nebeneinander.',
  'werkzeug.goodwillfortschreibung.liest': 'Liest den bei der Erstkonsolidierung entstandenen Firmenwert und schreibt ihn ab, soweit die Bewertungsebene eine planmässige Abschreibung verlangt.',
  'werkzeug.ic-abstimmung.liest': 'Liest die konzerninternen Forderungen und Verbindlichkeiten beider Seiten und stellt sie gegenüber.',
  'werkzeug.schuldenkonsolidierung.liest': 'Liest konzerninterne Forderungen und Verbindlichkeiten und eliminiert sie gegeneinander.',
  'werkzeug.aufwandertragkonsolidierung.liest': 'Liest konzerninterne Aufwendungen und Erträge und eliminiert sie gegeneinander.',
  'werkzeug.zwischenergebniseliminierung-uv.liest': 'Liest die Vorräte mit Partnercode und rechnet mit der Marge des Lieferanten den Zwischengewinn heraus.',
  'werkzeug.zwischenergebniseliminierung-av.liest': 'Liest konzernintern gelieferte Anlagegüter und rechnet den darin enthaltenen Zwischengewinn über deren Nutzungsdauer heraus.',
  'werkzeug.kapitalkonsolidierung.liest': 'Liest den Beteiligungsbuchwert der Mutter und das Eigenkapital der Tochter zum Einbezug.',
  'werkzeug.latente-steuern.liest': 'Liest die Buchungen der übrigen Konsolidierungsprozesse und bewertet ihre steuerlichen Folgen.',
  'werkzeug.anteile-nicht-beherrschend.liest':
    'Liest das Ergebnis der Tochter und weist den Anteil fremder Gesellschafter daran gesondert aus. Die Aufteilung des übrigen Eigenkapitals gehört zur Erstkonsolidierung und steht im Beleg der Kapitalkonsolidierung.',
  'werkzeug.dividendenkonsolidierung.liest':
    'Liest den Beteiligungsertrag der Mutter und die Ausschüttung der Tochter, nimmt beide mit ihrem eigenen Betrag heraus und bucht den Unterschied auf das Differenzenkonto; den Anteil der Minderheiten an der Ausschüttung bucht sie gegen ihren Ausgleichsposten.',
  // Zwei Schritte bis zum ersten Anwendertest (2.9 Abschnitt 15.9, R1615).
  'werkzeug.ausweiskorrektur': 'Ausweiskorrekturen im Konzern',
  'werkzeug.ausweiskorrektur.liest': 'Liest Positionen oder Konten, die im Konzern anders auszuweisen sind als im Einzelabschluss, und gliedert ihren ganzen Wert auf das Zielkonto um.',
  'werkzeug.saldierung': 'Saldierung von Werten im Konzern',
  'werkzeug.saldierung.liest': 'Liest zwei Positionen, die im Konzern gegeneinander stehen, und bucht die kleinere Konzernsumme auf das Sammelkonto der grösseren.',
  'regel.umgliederung': 'Umgliederung',
  'regel.konzernsaldierung': 'Saldierung im Konzern',
  'meldung.ausweiskorrektur.keine': 'Für diesen Konzernabschluss ist keine Ausweiskorrektur gepflegt.',
  'meldung.saldierung.keine': 'Für diesen Konzernabschluss ist keine Saldierung gepflegt.',
  'meldung.ausweiskorrektur.entlastung-fremd': 'Das Konto {konto} liegt nicht unter {position}. Entlastet wird nur innerhalb der Quelle — diese Korrektur ist nicht gebucht.',
  // Ein Konsolidierungsbeleg, der nicht aufgeht, wird nicht gespeichert (2.13 Abschnitt 21, R1638).
  'meldung.konsolidierungsbeleg.nichtauf':
    'Beleg {nummer} im Schritt „{schritt}“ geht in {waehrung} nicht auf: Es fehlen {betrag}. Der Lauf wurde nicht gespeichert, der vorige Stand gilt weiter.',
  'meldung.konsolidierungsbeleg.nichtauf.weg':
    'Das liegt nicht an Ihren Daten. Bitte melden Sie es mit der Belegnummer.',
  'meldung.ausweiskorrektur.ohne-konten': 'Der Position {position} ist kein Konto zugeordnet. Es gibt nichts umzubuchen.',
  'meldung.ausweiskorrektur.ohne-gegenseite': 'Die Saldierung an {position} nennt keine Gegenseite. Welche Seite überlebt, entscheidet sich erst beim Rechnen — deshalb braucht er beide.',
  'buchung.umgliederung.heraus': '{betrag} {waehrung} von {konto} herausgenommen — im Konzern wird die Position anders ausgewiesen.',
  'buchung.umgliederung.hinein': '{betrag} {waehrung} auf {konto} übernommen — der konzerneinheitliche Ausweis dieser Position.',
  'buchung.konzernsaldierung.heraus': '{betrag} {waehrung} von {konto} herausgenommen — im Konzern überlebt die grössere der beiden Seiten.',
  'buchung.konzernsaldierung.hinein': '{betrag} {waehrung} auf {konto} übernommen — der Konzern weist diese Seite aus.',
  'schritte.spalte.stelle': 'Stelle',
  'prozess.blatt.einstellungen': 'Einstellungen',
  'prozess.blatt.belege': 'Erzeugte Belege',
  'prozess.bucht': 'Bucht auf',
  'prozess.angabe': 'Angabe',
  'prozess.ausHandbuch': 'Aus dem Konzernhandbuch',
  'prozess.ausHandbuch.ausgabe': 'Die Werte stammen aus Ausgabe {nummer} des Konzernhandbuchs und gelten für alle Konzernabschlüsse.',
  'prozess.ausHandbuch.ohne': 'Für diesen Zeitraum gilt keine Ausgabe des Konzernhandbuchs.',
  'prozess.ausHandbuch.leer': 'nicht gesetzt',
  'prozess.abweichend': 'Dieser Prozess bucht stattdessen',
  'prozess.abweichend.leer': 'wie im Konzernhandbuch',
  'prozess.abweichend.schalter': 'Abweichend vom Konzernhandbuch buchen',
  'prozess.abgrenzung.vorhanden': 'Dieser Prozess arbeitet an abgegrenzten Positionen. Gepflegt wird die Abgrenzung im Vorlagenbereich.',
  'prozess.ohnevorgang': 'Diesen Prozess rechnet CoKo noch nicht. Er steht in der Folge, weil es ihn fachlich gibt — Belege erzeugt er keine.',
  'prozess.belege.keine': 'Im {monat} hat dieser Prozess nichts gebucht.',
  // Konsolidierungsbelege (2.13 Abschnitt 21, R1639, R1641, 21.6).
  'prozess.belege.ohne':
    'Dieser Monat ist vor den Konsolidierungsbelegen gerechnet. Rechnen Sie den Konzernabschluss neu, dann stehen hier seine Belege.',
  'prozess.beleg.ausweis': 'Ergebnis in der Bilanz',
  'prozess.beleg.ausnahme': 'Gegenseite noch nicht gebucht',
  'prozess.belege.suche': 'Gesellschaft oder Sachverhalt suchen …',
  'prozess.belege.ordnung': 'Sortiert nach',
  'prozess.belege.nachgesellschaft': 'Gesellschaft',
  'prozess.belege.nachregel': 'Sachverhalt',
  'prozess.belege.nachbetrag': 'Betrag, größter zuerst',
  'prozess.belege.zeile': 'eine Zeile',
  'prozess.belege.gehtauf': 'geht auf',
  'prozess.speichern': 'Speichern',
  'prozess.schliessen': 'Schliessen',
  'prozess.weichtab': 'weicht ab',
  // Steht als erster Punkt im Einstellungsblatt (Auftrag vom 2026-09-27).
  // Die frühere Überschrift „Woran dieser Prozess arbeitet" sagte dasselbe
  // in einer Frage, die die Zeile darunter schon beantwortet.
  'prozess.einbezogen': 'Einbezogene Positionen/Konten',
  // Die IC-Abstimmung (2.10 Abschnitt 9, R827, R828). Sie zeigt und bucht
  // nicht -- gebucht wird in der SchuKo und der AEKO.
  'ic.titel': 'IC-Abstimmung',
  'ic.zeigtnur':
    'Dieses Blatt zeigt die paarweisen Differenzen. Gebucht wird nichts — das tun die Schuldenkonsolidierung und die Aufwands- und Ertragskonsolidierung.',
  'ic.abschluss': 'Konzernabschluss',
  'ic.blatt.schuldenkonsolidierung': 'Differenzen SchuKo',
  'ic.blatt.aufwandertragkonsolidierung': 'Differenzen AEKO',
  'ic.sicht.saldo': 'Saldo',
  'ic.sicht.ytd': 'Kumuliert seit Jahresbeginn',
  'ic.sicht.verkehr': 'Verkehrszahl',
  'ic.spalte.paar': 'Gesellschaftspaar',
  'ic.schwelle': 'Tolerierte Schwelle: {betrag} {waehrung} — aus dem Konzernhandbuch.',
  // R1483: Der Währungsanteil steht unter dem Paar und zählt nicht gegen die Schwelle.
  'ic.waehrung': 'davon Währungsdifferenz',
  // Eine eigene Schwelle muss man sehen, sonst staende eine Zahl gruen da,
  // die zwei Zeilen tiefer rot waere (R828).
  'ic.eigeneschwelle': 'eigene Grenze {betrag}',
  'ic.eigeneschwelle.titel':
    'Für dieses Paar gilt eine eigene Grenze aus dem Konzernhandbuch, nicht die allgemeine.',
  'ic.laedt': 'Die Differenzen werden gerechnet …',
  'ic.befund.stimmt': 'stimmt auf den Cent',
  'ic.befund.innerhalb': 'innerhalb der tolerierten Schwelle',
  'ic.befund.ausserhalb': 'außerhalb der tolerierten Schwelle',
  // Das Wort zur Seite haengt am Blatt: Auf dem Bilanzblatt heisst die eine
  // Seite Forderung, auf dem Erfolgsblatt Ertrag. Beides zugleich waere ein
  // Schraegstrich, den der Leser jedes Mal aufloesen muss.
  'ic.leer.ohne-prozess':
    'Dieser Konzernabschluss führt den Prozess nicht. Ohne ihn gibt es nichts abzustimmen — aufgenommen wird er unter Konsolidierungsschritte.',
  'ic.leer.ohne-positionen':
    'Am Prozess stehen keine einbezogenen Positionen. Leer heißt dort „alle konzerninternen Konten“, und „alle“ lässt sich nicht in zwei Seiten teilen — die Positionen werden im Prozessfenster gesetzt.',
  'ic.leer.ohne-konten':
    'Unter den einbezogenen Positionen hängt kein Konto. Die Zuordnung steht in der Kontenzuordnung.',
  'ic.leer.ohne-paare':
    'Keine Gesellschaft hat auf diesen Positionen eine Gegengesellschaft gebucht.',
  'ic.ohnepartner':
    'Ohne Partner — keine Gegenseite, deshalb keine Differenz, sondern ein offener Betrag',
  // Der Rechenkern nimmt das Konto des Prozesses vor dem des Regelwerks
  // (R800). Ohne diese Marke waere die Zeile darueber eine Auskunft, die
  // nicht stimmt.
  'prozess.weichtab.titel': 'Dieser Prozess bucht abweichend vom Konzernhandbuch.',
  'prozess.oeffnen': 'Prozess öffnen',
  'prozess.korrektur.titel': 'Ausweiskorrekturen',
  'prozess.grundlage.titel': 'Woran dieser Prozess rechnet',
  'prozess.grundlage.zaehlung': '{gesamt} Beteiligungen im Kreis · {voll} davon voll einbezogen · {erfasst} mit erfasstem Eigenkapital zum Einbezug.',
  'prozess.grundlage.zaehlung.eine': 'Eine Beteiligung im Kreis, {voll} davon voll einbezogen, {erfasst} mit erfasstem Eigenkapital zum Einbezug.',
  'prozess.grundlage.suche': 'Gesellschaft suchen …',
  'prozess.grundlage.alle': 'Die übrigen {anzahl} zeigen',
  'prozess.grundlage.nichts': 'Keine Beteiligung gefunden.',
  'prozess.grundlage.tochter': 'Tochtergesellschaft',
  'prozess.grundlage.quote': 'Kapitalanteil',
  'prozess.grundlage.erwerb': 'Erworben',
  'prozess.grundlage.quelle': 'Eigenkapital zum Einbezug',
  'prozess.grundlage.quelle.erfasst': 'erfasst im Beteiligungsregister',
  'prozess.grundlage.quelle.geliefert': 'aus der Lieferung des Erwerbsmonats',
  'prozess.grundlage.quelle.staende': 'Stände zum Aufsatz, erfasst im Beteiligungsregister',
  'prozess.grundlage.quelle.manuell': 'manuell konsolidiert — Ausnahme, der Prozess bucht nichts',
  'prozess.grundlage.von': 'gehalten von {mutter}',
  'prozess.grundlage.ohneerwerb': 'kein Erwerbszeitpunkt',
  'prozess.grundlage.nichtvoll': 'wird hier nicht gerechnet ({art})',
  'prozess.grundlage.hinweis': 'Erfasst wird nur im Ausnahmefall — wenn die Lieferung des Erwerbsmonats keine richtige Ableitung erlaubt. Gepflegt werden die Werte im Beteiligungsregister, wo sie mit ihrer Historie stehen.',
  'einbeziehungsart.voll': 'Vollkonsolidierung',
  'einbeziehungsart.quotal': 'Quotenkonsolidierung',
  'einbeziehungsart.equity': 'at Equity',
  'einbeziehungsart.nicht': 'nicht einbezogen',
  'prozess.korrektur.leer': 'Noch keine Korrektur gepflegt. Ohne Korrektur weist der Konzern aus, was die Gesellschaften geliefert haben.',
  'prozess.korrektur.neu': 'Korrektur hinzufügen',
  'prozess.korrektur.quelle': 'Position oder Konto, dessen Wert bewegt wird',
  'prozess.korrektur.entlastung': 'Entlastet auf',
  'prozess.korrektur.eigene': 'Auf den eigenen Konten',
  'prozess.korrektur.aufquelle': 'Auf dem Konto selbst',
  'prozess.korrektur.ziel': 'Konto, auf das er läuft',
  // Die Saldierung als eigener Schritt (2.9 Abschnitt 15.9, R1615).
  'prozess.saldierung.titel': 'Saldierungen',
  'prozess.saldierung.leer': 'Noch keine Saldierung gepflegt. Ohne Saldierung stehen beide Seiten im Konzern, wie die Gesellschaften sie geliefert haben.',
  'prozess.saldierung.neu': 'Saldierung hinzufügen',
  'prozess.saldierung.seite': 'Die beiden Positionen',
  'prozess.saldierung.sammelkonto': 'Ihre Sammelkonten',
  'prozess.saldierung.quelle': 'Erste Seite: die Position',
  'prozess.saldierung.ziel': 'Erste Seite: ihr Sammelkonto',
  'prozess.saldierung.hinweis': 'Saldiert wird nur, wenn im Konzern beide Seiten belegt sind. Die kleinere Konzernsumme läuft auf das Sammelkonto der grösseren — welche das ist, entscheidet sich beim Rechnen.',
  // Beide rechnen am Ende, untereinander in ihrer Folge (R1618).
  'schritte.amende': 'rechnet am Ende, in dieser Folge',
  'prozess.amende.vermerk': 'Rechnet nach allen übrigen Prozessen. Ausweiskorrekturen und Saldierung rechnen untereinander in der Folge, in der sie hier stehen.',
  'prozess.korrektur.gegenquelle': 'Gegenseite: die zweite Position',
  'prozess.korrektur.gegenziel': 'Gegenseite: ihr Sammelkonto',
  'prozess.korrektur.hinweis': 'Umgliedern bewegt den ganzen Wert immer. Eine Position nimmt jedes Konto unter sich mit, auch ein später angelegtes; entlastet wird auf ihren eigenen Konten oder in einer Summe auf einem Konto der Position.',
  'konzernabschluss.prozesse.laedt': 'Die Prozesse werden geholt …',
  'schritte.spalte.name': 'Schritt',
  'schritte.leer': 'Noch kein Schritt angelegt. Ohne Schritte rechnet der Konzernabschluss nichts.',
  'schritte.neu': 'Schritt hinzufügen',
  'schritte.anlegen': 'Anlegen',
  'schritte.abbrechen': 'Abbrechen',
  'schritte.speichern': 'Speichern',
  'schritte.entfernen': 'Entfernen',
  'schritte.entfernen.titel': 'Schritt entfernen',
  'schritte.entfernen.frage': 'Den Schritt {name} aus der Folge nehmen?',
  'schritte.entfernen.hinweis':
    'Ältere Abschlüsse behalten ihn — sie wurden mit ihm gerechnet. Der Prozess rechnet danach weiter, aber mit den Werten aus dem Konzernhandbuch und ohne die Einstellungen dieses Schritts.',
  'schritte.feld.werkzeug': 'Prozess',
  'schritte.feld.werkzeug.leer': 'Prozess wählen …',
  'schritte.feld.werkzeug.hinweis':
    'Was der Schritt fachlich tut. Derselbe Prozess darf mehrfach vorkommen — etwa zweimal die Aufwands- und Ertragskonsolidierung, einmal für Umsatz und Aufwand, einmal für das Finanzergebnis. Der Schritt ist sein Platz in der Folge.',
  'schritte.feld.name': 'Bezeichnung',
  'schritte.feld.name.hinweis': 'Wie der Schritt in Ihrem Abschluss heisst. Die Nummer steht nicht im Namen — sie ergibt sich aus der Stelle.',
  'schritte.rechnetnicht': 'rechnet noch nicht',
  'schritte.rechnetnicht.erlaeuterung':
    'Für diesen Prozess gibt es noch keine Rechnung. Er steht in der Folge, damit sie vollständig ist.',
  'schritte.befund.doppelt':
    '„{werkzeug}“ steht {anzahl}-mal in der Folge, und kein Schritt grenzt ab — dann wird zweimal eliminiert. Grenzen Sie die Schritte unter [[Abgrenzen]] gegeneinander ab; dann verschwindet diese Meldung.',
  'schritte.befund.fehlt':
    'Der Prozess „{werkzeug}“ wurde noch nicht eingerichtet. Klicken Sie auf [[Schritt hinzufügen]] und legen Sie die Standardeinstellung einmalig fest. Dadurch verschwindet diese Meldung.',
  'schritte.befund.ohne-differenzkonto':
    '„{werkzeug}“ hat kein Differenzenkonto; ohne dieses Konto lässt sich der Konzernabschluss nicht rechnen. Tragen Sie es unter [[Abgrenzen]] ein, dann verschwindet diese Meldung.',
  'schritte.befund.ohne-differenzkonto-waehrung':
    '„{werkzeug}“ hat kein Differenzenkonto für Währungsdifferenzen; ohne dieses Konto lässt sich der Konzernabschluss nicht rechnen. Tragen Sie es unter [[Abgrenzen]] ein — es darf dasselbe sein wie das Differenzenkonto. Dann verschwindet diese Meldung.',
  'schritte.befund.unbekannt':
    'Ein Schritt nennt den Prozess {werkzeug}, den es nicht gibt, und wird nie gerechnet. Klicken Sie auf [[Entfernen]], dann verschwindet diese Meldung.',
  // Die Seite pflegt nur die Vorlage ab Werk (2.13 Abschnitt 20, R1610).
  'schritte.nurvorlage':
    'Mit diesen Schritten beginnt jeder neue Konzernabschluss. Die Schritte eines bestehenden Abschlusses pflegen Sie an ihm, unter [[Konzernabschlüsse]].',
  'schritte.nachweis': 'Nachweis',
  'schritte.nachweis.zu': 'Nachweis zu',
  'schritte.nachweis.titel': 'Was {werkzeug} gebucht hat',
  'schritte.nachweis.monat': 'Monat',
  'schritte.nachweis.buchungen': 'Buchungen',
  'schritte.nachweis.meldung': 'Meldung',
  'schritte.nachweis.gesellschaft': 'Gesellschaft',
  'schritte.nachweis.konto': 'Konto',
  'schritte.nachweis.betrag': 'Betrag',
  'schritte.nachweis.erlaeuterung': 'Erläuterung',
  'schritte.nachweis.nielief': 'Dieser Prozess lief noch nie — der Konzernabschluss wurde nicht angestossen.',
  'schritte.fehlt.meldung': 'Diesen Schritt gibt es nicht mehr.',
  'schritte.fehlt.weg': 'Laden Sie die Seite neu — er wurde vermutlich entfernt.',
  'schritte.uebersteuerung.fremd.meldung':
    'Der Prozess „{werkzeug}“ liest {angabe} nicht.',
  'schritte.korrektur.fremd.meldung': 'Der Prozess „{werkzeug}“ liest keine Ausweiskorrekturen.',
  'schritte.korrektur.fremd.weg': 'Umgliedern kann nur der Prozess Ausweiskorrekturen im Konzern, saldieren nur der Prozess Saldierung von Werten im Konzern. Legen Sie ihn in dieser Folge an.',
  'schritte.korrektur.ohnegegenseite.meldung': 'Die Saldierung an {position} nennt keine Gegenseite.',
  'schritte.korrektur.ohnegegenseite.weg': 'Welche Seite im Konzern überlebt, entscheidet sich erst beim Rechnen. Tragen Sie die zweite Position und ihr Sammelkonto ein.',
  'schritte.uebersteuerung.fremd.weg':
    'Übersteuert werden kann nur, was dieser Prozess auch rechnet. Alles Übrige bleibt im Konzernhandbuch.',
  'schritte.werkzeug.unbekannt': 'Den Prozess {werkzeug} gibt es nicht.',
  'schritte.werkzeug.unbekannt.weg':
    'Wählen Sie einen aus der Liste. Der Vorrat ist fest — ein neuer Prozess ist eine Fachentscheidung und keine Einstellung.',
  'schritte.unbekannt': 'Den Schritt {schritt} gibt es in dieser Folge nicht.',
  'schritte.unbekannt.weg': 'Laden Sie die Seite neu. Der Schritt wurde vermutlich an anderer Stelle entfernt.',
  'schritte.position.unbekannt': 'Diese Positionen gibt es nicht: {positionen}.',
  'schritte.position.unbekannt.weg': 'Wählen Sie Positionen aus der Gliederung aus.',
  'schritte.differenzkonto.unbekannt': 'Das Konto {konto} gibt es nicht.',
  'schritte.differenzkonto.unbekannt.weg': 'Wählen Sie ein vorhandenes Konto. Bleibt das Feld leer, wird das Differenzenkonto aus dem Regelwerk verwendet.',
  'schritte.abgrenzen': 'Abgrenzen',
  'schritte.abgrenzen.zu': 'Abgrenzung schließen',
  'schritte.abgrenzung.titel': 'Woran dieser Prozess arbeitet',
  'schritte.abgrenzung.positionen': 'Positionen',
  'schritte.abgrenzung.positionen.hinweis': 'Leer heißt: alle konzerninternen Konten. Eine gewählte Position schließt die Positionen unter ihr ein.',
  'schritte.abgrenzung.differenzkonto': 'Differenzenkonto',
  'schritte.abgrenzung.differenzkonto.hinweis': 'Wohin die verbleibende Differenz gebucht wird. Leer heißt: das Konto aus dem Regelwerk.',
  'schritte.abgrenzung.toleranz': 'Toleranz',
  'schritte.abgrenzung.toleranz.hinweis': 'Ab diesem Betrag wird eine Differenz als wesentlich gemeldet. Gebucht wird sie in jedem Fall. Leer heißt: die Grenze aus dem Regelwerk.',
  'schritte.abgrenzung.leer': 'Keine Position gewählt — der Prozess arbeitet an allen konzerninternen Konten.',
  'schritte.marge': 'Konzernmarge',
  'schritte.marge.definition':
    'Die Marge ist der Anteil des Gewinns am Verrechnungspreis. Verkauft die Holding für 100, was sie 80 gekostet hat, beträgt die Marge 20 %. Eliminiert wird der Vorrat mit Partnercode des Lieferanten mal dieser Marge: 50.000 im Lager ergeben bei 20 % einen Zwischengewinn von 10.000. Wer in Aufschlägen rechnet, rechnet um: 25 % Aufschlag auf die Kosten sind 20 % Marge.',
  'schritte.marge.positionen': 'Vorräte',
  'schritte.ertrag.positionen': 'Beteiligungserträge',
  'schritte.ertrag.positionen.leer': 'Keine Position gewählt — ohne sie erkennt der Prozess keinen Beteiligungsertrag.',
  'schritte.ertrag.positionen.hinweis': 'Die Positionen, unter denen die Erträge aus Beteiligungen stehen. Eine gewählte Position schließt die Positionen unter ihr ein.',
  'schritte.kapko.beteiligungen': 'Beteiligungen',
  'schritte.kapko.beteiligungen.leer': 'Keine Position gewählt — ohne sie rechnet der Konzernabschluss nicht.',
  'schritte.kapko.beteiligungen.hinweis': 'Die Positionen, unter denen die Anteile an den Töchtern stehen. Eine gewählte Position schließt die Positionen unter ihr ein.',
  'schritte.kapko.eigenkapital': 'Eigenkapital',
  'schritte.kapko.eigenkapital.leer': 'Keine Position gewählt — ohne sie rechnet der Konzernabschluss nicht.',
  'schritte.kapko.eigenkapital.hinweis': 'Die Positionen des Eigenkapitals, das gegen die Beteiligung aufgerechnet wird — ohne die Anteile nicht beherrschender Gesellschafter.',
  'schritte.marge.positionen.leer': 'Keine Position gewählt — ohne Vorräte eliminiert der Prozess nichts.',
  'schritte.marge.positionen.hinweis': 'Die Positionen, unter denen die Vorräte stehen. Eine gewählte Position schließt die Positionen unter ihr ein.',
  'schritte.marge.standard': 'Standardmarge',
  'schritte.marge.standard.hinweis': 'Gilt für jede Lieferbeziehung ohne eigene Marge. Fehlt sie, und es liegt Vorrat mit Partnercode, hält der Lauf an.',
  'schritte.marge.keine': 'keine',
  'schritte.marge.paare': 'Marge je Lieferbeziehung',
  'schritte.marge.paare.leer': 'Keine eigene Marge — es gilt für alle die Standardmarge.',
  'schritte.marge.paare.hinweis': 'Je Richtung eine Zeile: die Marge, mit der der Lieferant an den Empfänger verkauft. Sie geht der Standardmarge vor.',
  'schritte.marge.lieferant': 'Lieferant',
  'schritte.marge.empfaenger': 'Empfänger',
  'schritte.marge.marge': 'Marge in Prozent',
  'schritte.marge.paar.neu': 'Lieferbeziehung aufnehmen',
  'schritte.marge.paar.entfernen': 'Lieferbeziehung entfernen',
  'schritte.marge.quelle.standard': 'Standardmarge des Prozesses',
  'schritte.marge.quelle.paar': 'Marge der Lieferbeziehung',
  'schritte.marge.ungueltig.meldung': 'Eine Marge liegt nicht zwischen 0 und unter 100 Prozent.',
  'schritte.marge.ungueltig.weg': 'Die Marge ist der Anteil des Gewinns am Verrechnungspreis — tragen Sie einen Wert ab 0 und unter 100 ein.',
  'schritte.marge.paar.ungueltig.meldung': 'Eine Lieferbeziehung ist unvollständig, doppelt oder nennt dieselbe Gesellschaft zweimal.',
  'schritte.marge.paar.ungueltig.weg': 'Wählen Sie für jede Zeile einen Lieferanten und einen anderen Empfänger, und jedes Paar je Richtung nur einmal.',
  'schritte.abgrenzung.hinzufuegen': 'Position aufnehmen',
  'schritte.abgrenzung.entfernen': 'Entfernen',
  'schritte.abgrenzung.nur-eliminierung': 'Abgegrenzt wird nur bei den Prozessen, die konzerninterne Beziehungen ausbuchen.',
  // Gewaehlt wird aus Bilanz und GuV und nicht aus einer flachen Liste (R826).
  // Der Kopf nennt den Prozess, weil das Fenster darunter verdeckt ist.
  'schritte.abgrenzung.positionen.waehlen': 'Positionen wählen',
  'schritte.abgrenzung.differenzkonto.keins': 'Konto aus dem Regelwerk',
  'schritte.abgrenzung.differenzkontoWaehrung': 'Differenzenkonto für Währungsdifferenzen',
  'schritte.abgrenzung.differenzkontoWaehrung.hinweis':
    'Wohin der Währungsanteil der Differenz gebucht wird — was beide Seiten als Währungsdifferenz tragen. Pflicht; darf dasselbe Konto sein wie das Differenzenkonto.',
  'schritte.abgrenzung.differenzkontoWaehrung.keins': 'nicht gewählt',
  // Ein abgeschalteter Knopf ohne Grund ist die Sackgasse, die dieser Bau
  // gerade abschafft (R825).
  'schritte.abgrenzung.ohnebezug':
    'Positionen aus Bilanz und GuV wählen Sie am Konzernabschluss: Er sagt, welche Bewertungsebene und welche Periode gelten — und danach richtet sich, welche Position es gibt.',
  'gliederungswahl.titel.positionen': 'Positionen aus Bilanz und GuV',
  'gliederungswahl.hinweis.positionen':
    'Mehrere auf einmal. Eine gewählte Position schließt die Positionen unter ihr ein.',
  'gliederungswahl.bericht.bilanz': 'Bilanz',
  'gliederungswahl.bericht.guv': 'Gewinn- und Verlustrechnung',
  'gliederungswahl.uebernehmen': 'Übernehmen',
  'gliederungswahl.abbrechen': 'Abbrechen',
  'gliederungswahl.aufklappen': '{position} aufklappen',
  'gliederungswahl.zuklappen': '{position} zuklappen',
  'gliederungswahl.leer': 'Hier steht keine Position.',
  'handbuch.spalte.ausgabe': 'Ausgabe',
  'handbuch.spalte.name': 'Bezeichnung',
  'handbuch.spalte.gueltig': 'Gültig',
  'handbuch.spalte.abschluesse': 'Abschlüsse',
  'handbuch.geltung.offen': 'ab {von}',
  'handbuch.geltung.bis': '{von} bis {bis}',
  'handbuch.leer': 'Noch keine Ausgabe. Ohne sie rechnet kein Konzernabschluss.',
  'handbuch.neu': 'Neue Ausgabe',
  'handbuch.neu.name': 'Konzernhandbuch, Ausgabe {nummer}',
  'handbuch.gesperrt': 'freigegeben',
  'handbuch.speichern': 'Speichern',
  'handbuch.entfernen': 'Entfernen',
  'handbuch.entfernen.titel': 'Ausgabe entfernen',
  'handbuch.entfernen.frage': 'Ausgabe {nummer} — {name} — vollständig entfernen?',
  'handbuch.entfernen.hinweis': 'Die Ausgabe und ihre Regeln werden gelöscht, nicht stillgelegt. Abschlüsse, die mit ihr gerechnet wurden, sind danach neu zu rechnen.',
  'handbuch.abschnitt.ausgabe': 'Die Ausgabe',
  'handbuch.zusatz.ausgabe': 'Nummer {nummer}',
  'handbuch.abschnitt.rechnen': 'Runden und Grenzen',
  'handbuch.zusatz.rechnen': '{stellen} Nachkommastellen',
  'handbuch.abschnitt.konten': 'Konten der Konsolidierung',
  'handbuch.feld.name': 'Bezeichnung',
  'handbuch.feld.name.hinweis': 'Was der Prüfer im Bericht liest.',
  'handbuch.feld.gueltigVon': 'Gültig ab',
  'handbuch.feld.gueltigVon.hinweis': 'Ab dieser Periode rechnet jeder Konzernabschluss nach dieser Ausgabe.',
  'handbuch.feld.gueltigBis': 'Gültig bis',
  'handbuch.feld.gueltigBis.hinweis': 'Leer lassen, solange die Ausgabe weiter gilt.',
  'handbuch.feld.nachkommastellen': 'Nachkommastellen',
  'handbuch.feld.nachkommastellen.hinweis': 'Auf wie viele Stellen jeder gerechnete Betrag gerundet wird.',
  'handbuch.feld.icGrenze': 'Grenze für IC-Differenzen',
  // Eine eigene Grenze je Gesellschaftspaar (R828): Zwei Gesellschaften
  // derselben Waehrung muessen auf den Cent stimmen, zwei mit verschiedenen
  // tragen den umrechnungsbedingten Anteil immer mit sich.
  'handbuch.abschnitt.icpaare': 'Grenzen für einzelne Gesellschaftspaare',
  'handbuch.zusatz.icpaare.leer': 'für alle gilt die allgemeine Grenze',
  'handbuch.icpaare.leer':
    'Kein Paar hat eine eigene Grenze — für alle gilt die Grenze für IC-Differenzen von oben.',
  'handbuch.icpaare.hinweis':
    'Die Grenze eines Paares geht der allgemeinen vor und gilt in beide Richtungen. Zwei Gesellschaften derselben Währung sollten auf den Cent stimmen; zwei mit verschiedenen Währungen tragen den umrechnungsbedingten Anteil immer mit sich.',
  'handbuch.icpaar.a': 'Gesellschaft',
  'handbuch.icpaar.b': 'Gegengesellschaft',
  'handbuch.icpaar.grenze': 'Grenze',
  'handbuch.icpaar.neu': 'Paar aufnehmen',
  'handbuch.icpaar.entfernen': 'Entfernen',
  'handbuch.feld.icGrenze.hinweis': 'Bis zu diesem Betrag wird eine Differenz zwischen zwei Gesellschaften ausgebucht statt gemeldet.',
  'handbuch.feld.nutzungsdauer': 'Firmenwert abschreiben über (Monate)',
  'handbuch.feld.nutzungsdauer.hinweis': 'Das sind {jahre} Jahre planmässige Abschreibung.',
  'handbuch.feld.nutzungsdauer.ohne': 'Pflicht: Das HGB verlangt eine Abschreibung über eine Nutzungsdauer in Monaten. Leer lässt sich die Ausgabe nicht speichern.',
  'handbuch.feld.firmenwertKonto': 'Firmenwert',
  'handbuch.feld.firmenwertKonto.hinweis': 'Wohin der Mehrbetrag des Kaufpreises über das anteilige Eigenkapital gebucht wird.',
  'handbuch.feld.unterschiedsbetragPassivKonto': 'Passivischer Unterschiedsbetrag',
  'handbuch.feld.unterschiedsbetragPassivKonto.hinweis':
    'Wohin gebucht wird, wenn das anteilige Eigenkapital über dem Kaufpreis liegt. Nach HGB ein Passivkonto, als Posten nach dem Eigenkapital; nach IFRS ein Ertragskonto. Pflicht, auch wenn der Fall nie eintritt.',
  'handbuch.feld.firmenwertAbschreibungKonto': 'Abschreibung auf den Firmenwert',
  'handbuch.feld.firmenwertAbschreibungKonto.hinweis': 'Der Aufwand der laufenden Periode.',
  'handbuch.konto.pflicht.meldung': 'Das Feld „{feld}“ ist leer. Ohne dieses Konto rechnet kein Konzernabschluss.',
  'handbuch.konto.pflicht.weg': 'Wählen Sie ein Konto und speichern Sie dann.',
  'handbuch.feld.gewinnvortragKonto': 'Gewinn-/Verlustvortrag',
  'handbuch.feld.gewinnvortragKonto.hinweis':
    'Wohin Konzernbuchungen früherer Geschäftsjahre gehen, etwa die Abschreibung früherer Perioden — sie hat frühere Jahre belastet, nicht dieses.',
  'handbuch.feld.abschreibungFinanzanlagenKonto': 'Abschreibung auf Finanzanlagen',
  'handbuch.feld.abschreibungFinanzanlagenKonto.hinweis':
    'Nur nötig, wenn eine Mutter ihre Beteiligung an einer Tochter ab- oder zuschreibt: Dorthin nimmt die Kapitalkonsolidierung die Abschreibung zurück. Leer zulässig — fehlt es im Fall, hält der Lauf an.',
  'handbuch.feld.abgangBeteiligungenKonto': 'Ergebnis aus dem Abgang von Beteiligungen',
  'handbuch.feld.abgangBeteiligungenKonto.hinweis':
    'Nur nötig beim Teilverkauf einer Tochter: Dorthin geht die Berichtigung des Gewinns der Mutter auf den Gewinn des Konzerns. Leer zulässig — fehlt es im Fall, hält der Lauf an.',
  'handbuch.feld.nichtBeherrschendKonto': 'Anteile anderer Gesellschafter',
  'handbuch.feld.nichtBeherrschendKonto.hinweis': 'Der Teil des Eigenkapitals, der nicht dem Konzern gehört.',
  'handbuch.feld.icDifferenzKonto': 'IC-Differenz',
  'handbuch.feld.icDifferenzKonto.hinweis': 'Wohin eine Differenz unterhalb der Grenze ausgebucht wird.',
  // R1482: Der Währungsanteil einer konzerninternen Differenz hat ein eigenes Konto.
  'handbuch.feld.icDifferenzKontoWaehrung': 'IC-Differenz aus Währung',
  'handbuch.feld.icDifferenzKontoWaehrung.hinweis':
    'Wohin der Währungsanteil einer konzerninternen Differenz ausgebucht wird. Darf dasselbe Konto sein wie für die IC-Differenz.',
  'handbuch.feld.zwischenergebnisKonto': 'Zwischenergebnis',
  'handbuch.feld.zwischenergebnisKonto.hinweis': 'Wohin der Gewinn aus einer konzerninternen Lieferung herausgerechnet wird.',
  'handbuch.abschnitt.vergleich': 'Was sich gegenüber Ausgabe {nummer} geändert hat',
  'handbuch.vergleich.zusatz': '{anzahl} Angaben',
  'handbuch.vergleich.zusatz.gleich': 'unverändert',
  'handbuch.vergleich.gleich': 'Beide Ausgaben treffen in jeder Angabe dieselbe Festlegung.',
  'handbuch.vergleich.angabe': 'Angabe',
  'handbuch.vergleich.vorher': 'Ausgabe {nummer}',
  'handbuch.vergleich.nachher': 'Ausgabe {nummer}',
  'handbuch.vergleich.offen': 'offen',
  'handbuch.vergleich.keineAbschreibung': 'keine planmässige Abschreibung',
  'handbuch.freigegeben': 'Ein Abschluss mit dieser Ausgabe ist freigegeben.',
  'handbuch.freigegeben.meldung': 'Ausgabe {ausgabe} traegt den freigegebenen Abschluss {periode}.',
  'handbuch.freigegeben.weg': 'Legen Sie eine neue Ausgabe an. Was ein Pruefer gesehen hat, bleibt unveraendert.',
  'import.package.titel': 'Reporting Package',
  'import.package.blaetter': 'Blätter',
  'import.package.eroeffnung': 'Bestand 01.01.',
  'import.package.eroeffnung.gebucht': 'wird gebucht',
  'import.package.eroeffnung.verprobt': 'wird verprobt',
  'import.package.bereit': 'bereit',
  'import.package.befunde': '{anzahl} offen',
  'import.package.befund': 'Befund',
  'import.package.nurverprobt': 'nur verprobt',
  'import.package.spalte.blatt': 'Blatt',
  'import.package.spalte.wirkung': 'Wofür',
  'import.package.rolle.eroeffnung': 'Anfangsbestand',
  'import.package.rolle.financials': 'Saldenliste',
  'import.package.rolle.intercompany': 'Partner anreichern',
  'import.package.rolle.spiegel': 'Spiegel',
  'import.package.proben': '{anzahl} Proben gehen nicht auf:',
  'import.package.uebernehmen': 'Ganzes Package übernehmen',
  'import.package.buchtgerade': 'Bucht die Mappe …',
  'import.package.gesperrt':
    'Ein Package wird ganz gebucht oder gar nicht — erst wenn jede Probe aufgeht und jede Zeile zugeordnet ist.',
  'import.package.bericht.halt':
    'Angehalten bei {blatt}. Die {gebucht} Blätter davor bleiben gebucht, die danach sind nicht angefasst.',
  'import.stapel.titel': 'Stapel',
  'import.stapel.alle': 'alle Dateien',
  'import.stapel.dateien': 'Dateien',
  'import.stapel.monate': 'Monate',
  'import.stapel.ersetzt': 'Ersetzt',
  'import.stapel.bereit': 'bereit',
  'import.stapel.befunde': '{anzahl} mit Befund',
  'import.stapel.spalte.monat': 'Monat',
  'import.stapel.spalte.datei': 'Datei',
  'import.stapel.spalte.summe': 'Differenz',
  'import.stapel.spalte.stand': 'Stand',
  'import.stapel.stand.neu': 'neu',
  'import.stapel.stand.ersetzt': 'ersetzt {name}',
  'import.stapel.stand.gebucht': 'gebucht',
  'import.stapel.uebernehmen': 'Alle {anzahl} Monate übernehmen',
  'import.stapel.buchtgerade': 'Bucht {anzahl} Monate …',
  'import.stapel.gesperrt': 'Erst wenn jede Datei ihren Monat nennt, aufgeht und zugeordnet ist.',
  'import.stapel.bericht.halt':
    'Angehalten bei {datei}. Die {gebucht} Monate davor bleiben gebucht, die danach sind nicht angefasst.',
  'import.stapel.hindernis.periodenherkunft':
    'Diese Datenquelle sagt nicht, woher die Periode kommt. Solange das offen ist, gilt eine Datei je Monat — die Herkunft steht an der Quelle.',
  'import.stapel.hindernis.leer': 'Es wurde keine Datei übergeben.',
  /* R1407 — Ohne lesbare Spaltenzuordnung liest der Datenimport nichts und nennt die Datenquelle. */
  'import.abbildung.quellefehlt': 'Die Datenquelle {quelle} gibt es nicht mehr.',
  'import.abbildung.unlesbar': 'Die Datenquelle „{quelle}“ hat keine lesbare Spaltenzuordnung.',
  'import.abbildung.vorlageunlesbar': 'Die Importvorlage {vorlage} hat keine lesbare Spaltenzuordnung.',
  'import.abbildung.ohnequelle': 'Es ist keine Datenquelle genannt, nach deren Spaltenzuordnung gelesen werden könnte.',
  /* R1412, R1413, R1418 — Ein Konto ohne Bezeichnung wird abgelehnt. */
  'import.bezeichnung.fehlt.meldung':
    'Die Lieferung wird nicht übernommen: {anzahl} Zeilen tragen eine Kontonummer, aber keine Bezeichnung (Zeilen {zeilen}). Ohne Bezeichnung lässt sich nicht prüfen, ob eine Nummer hier dasselbe Konto meint wie bei einer anderen Gesellschaft — eine falsche Zuordnung fiele erst in der Konzern-GuV auf.',
  'import.bezeichnung.fehlt.weg':
    'Ergänzen Sie die Bezeichnung in der Datei, oder ordnen Sie in der Spaltenzuordnung die Spalte mit der Bezeichnung zu.',
  'import.bezeichnung.nurnummer.meldung':
    'Die Lieferung wird nicht übernommen: Die Spalte ist als „Nummer und Bezeichnung in einer Spalte“ zugeordnet, enthält in {anzahl} Zeilen aber nur eine Nummer (Zeilen {zeilen}).',
  'import.bezeichnung.nurnummer.weg':
    'Prüfen Sie die Trennung an der Kontonummer in der Spaltenzuordnung, oder ergänzen Sie die Bezeichnung in der Datei.',
  'import.bezeichnung.package.meldung':
    'Das Package wird nicht übernommen: Im Blatt Intercompany stehen {anzahl} Kontonummern, die das Blatt Financials derselben Mappe nicht führt (Zeilen {zeilen}). Ohne sie fehlt der Kontoname, und die Nummer allein ordnet nicht zu.',
  'import.bezeichnung.package.weg':
    'Führen Sie jedes Konto des Blatts Intercompany auch im Blatt Financials, mit seiner Bezeichnung.',
  'import.abbildung.weg':
    'Legen Sie die Spaltenzuordnung an der Datenquelle fest. Ohne sie liest CoKo keine Datei — ein angenommenes Muster könnte eine falsche Spalte als Betrag lesen.',
  /* R1409 — Eine gespeicherte Einstellung, nach der gerechnet wird und die sich nicht lesen lässt, hält den Lauf an. */
  'lauf.einstellung.unlesbar.meldung': 'Die Einstellung „{einstellung}“ des Prozesses „{prozess}“ lässt sich nicht lesen.',
  'lauf.einstellung.unlesbar.weg':
    'Öffnen und speichern Sie den Prozess neu. Bis dahin rechnet CoKo nicht: Ohne die Einstellung stünden Zahlen da, die mit den Werten des Konzernhandbuchs statt mit denen des Prozesses gerechnet sind.',
  'lauf.einstellung.abgrenzung': 'Abgrenzung',
  'lauf.einstellung.kaufpreisallokation': 'Kaufpreisallokation',
  'lauf.einstellung.uebersteuerung': 'Übersteuerung von Werten des Konzernhandbuchs',
  'lauf.einstellung.ausweiskorrektur': 'Umgliederungen und Saldierungen',
  'lauf.uebersteuerung.keinezahl.meldung':
    'Die Übersteuerung „{wert}“ für „{angabe}“ im Prozess „{prozess}“ lässt sich nicht als Zahl lesen.',
  'lauf.uebersteuerung.icDifferenzGrenze': 'Grenze für IC-Differenzen',
  'lauf.uebersteuerung.goodwillNutzungsdauerMonate': 'Nutzungsdauer des Firmenwerts in Monaten',
  /* R1394 — Für den Konzernabschluss gibt es keine Werkswerte. */
  'abbruch.handbuch.unlesbar.meldung': 'Die Ausgabe {ausgabe} des Konzernhandbuchs lässt sich nicht lesen.',
  'abbruch.handbuch.angabefehlt.meldung': 'Im Konzernhandbuch, Ausgabe {ausgabe}, fehlt die Angabe „{angabe}“.',
  'abbruch.handbuch.kontofehlt.meldung':
    'Die Angabe „{angabe}“ im Konzernhandbuch, Ausgabe {ausgabe}, nennt das Konto {konto}, das nicht im Kontenplan steht.',
  'abbruch.handbuch.weg':
    'Ergänzen Sie die Angabe im Konzernhandbuch. Bis dahin rechnet CoKo nicht: Ein eingebauter Wert könnte auf ein Konto zeigen, das bei Ihnen etwas anderes bedeutet.',
  /* R1396 — Die Nutzungsdauer des Firmenwerts ist Pflicht: Monate oder „Keine Abschreibung“ (nur IFRS). */
  'abbruch.handbuch.keineabschreibung.meldung':
    'Im Konzernhandbuch, Ausgabe {ausgabe}, steht für den Firmenwert „Keine Abschreibung“. Nach {rechnungslegung} ist eine Nutzungsdauer in Monaten Pflicht.',
  'handbuch.nutzungsdauer.pflicht.meldung': 'Die Nutzungsdauer des Firmenwerts fehlt.',
  'handbuch.nutzungsdauer.pflicht.weg':
    'Tragen Sie die Nutzungsdauer in Monaten ein. Das HGB verlangt eine Abschreibung; ein leeres Feld hieße sonst nicht „keine Abschreibung“, sondern „vergessen“.',
  'import.stapel.befund.doppelt':
    'Zwei Dateien nennen {periode}. Die eine würde die andere ersetzen — welche gelten soll, sagen die Dateien nicht.',
  'import.stapel.befund.periode': 'Keine Periode zu erkennen — gesucht wurde bei {stelle}.',
  'import.stapel.befund.unlesbar': 'Die Datei lässt sich nicht lesen: {grund}',
  'import.stapel.befund.pruefung': '{offen} Zeilen ohne Zuordnung, Differenz {summe}.',
  'import.stapel.halt': 'Bei {periode} angehalten: {grund}',
  'import.stand.aktualisieren': 'Aktualisieren',
  // Die Datenimporte im Ordnerbaum des Buchungsjournals (5.1 Abschnitt 3.45, R970–R972).
  'import.ordner.alle': 'Alle Datenimporte',
  'import.ordner.markieren': 'Markieren',
  'import.ordner.allemarkieren': 'Alle gezeigten markieren',
  'import.ordner.markiert': '{anzahl} markiert',
  'import.ordner.ziel': 'Verschieben nach',
  'import.ordner.verschieben': 'Verschieben',
  'import.ordner.aufheben': 'Markierung aufheben',
  'import.ordner.mehrere':
    'Ein Ordner gehört genau einer Gesellschaft. Markieren Sie zum Verschieben Datenimporte nur einer Gesellschaft.',
  'import.ordner.leer': 'In diesem Ordner liegt kein Datenimport.',

  'import.stand.loeschen': 'Löschen',
  'import.stand.gilt': 'gilt',
  'import.stand.geschlossen': '{kreis} ist geschlossen',
  'import.stand.freigegeben': '{kreise} freigegeben',
  'import.stand.nachbuchung': 'Nachbuchung für {periode}',
  /* R880 — am Zeitstempel gelesen: Ein früherer Datenimport ist jünger als dieser. */
  'import.stand.aktualisierung': 'Aktualisierung erforderlich — {periode} wurde danach aktualisiert',
  /* R880 — die Warnung im Konzernabschluss-Monitor, im Wortlaut des Auftraggebers vom 2026-10-03. */
  'monitor.aktualisierung.eins':
    'Es liegt 1 Import vor, bei dem eine Vorperiode aktualisiert wurde, ohne den Folgemonat zu aktualisieren. Dies kann die Datenkonsistenz gefährden. Ich empfehle eine Aktualisierung des betroffenen Imports:',
  'monitor.aktualisierung.mehrere':
    'Es liegen {anzahl} Importe vor, bei denen eine Vorperiode aktualisiert wurde, ohne die Folgemonate zu aktualisieren. Dies kann die Datenkonsistenz gefährden. Ich empfehle eine Aktualisierung der betroffenen Importe:',
  'import.stand.verlauf': 'Verlauf',
  'import.stand.beleg': 'Beleg',
  'journal.zurueck.datenimporte': 'Zurück zu den Datenimporten',
  'import.stand.laeufe.leer': 'Zu diesem Stand ist kein Lauf mehr verzeichnet.',
  'import.stand.zurueck': 'Zurück zur Liste',

  /* R931–R933 — Aktualisieren lädt die aufbewahrte Datei (5.1 Abschnitt 3.39). */
  'import.aktualisierung.eingelegt':
    'Eingelegt ist die aufbewahrte Datei {datei}. Wer eine neuere Fassung hat, wählt sie im Feld Datei.',
  'import.aktualisierung.liest': 'Die aufbewahrten Dateien werden gelesen …',
  'import.aktualisierung.alle': 'Alle {anzahl} angemahnten aktualisieren',
  'import.aktualisierung.titel': 'Alle angemahnten Datenimporte aktualisieren',
  'import.aktualisierung.hinweis':
    'Gelesen werden die aufbewahrten Dateien. Gebucht wird je Gesellschaft und Datenquelle aufsteigend nach Periode; die Verkehrszahl jedes Monats entsteht dabei neu gegen den Stand davor.',
  'import.aktualisierung.uebernehmen': '{anzahl} Datenimporte aktualisieren',
  'import.aktualisierung.buchtgerade': 'Aktualisiert {anzahl} Datenimporte …',
  'import.aktualisierung.gesperrt':
    'Erst wenn keine Zeile mehr einen Befund trägt. Einen Datenimport mit Befund aktualisieren Sie an seiner Zeile in der Liste.',
  'import.aktualisierung.weg': 'Aktualisieren Sie den Datenimport an seiner Zeile und wählen Sie die Datei dort von Hand.',
  'import.aktualisierung.befund.ohnedatei': 'Zu diesem Datenimport liegt keine Datei.',
  'import.aktualisierung.befund.package': 'Ein Reporting Package wird als ganze Mappe aktualisiert.',
  'import.aktualisierung.befund.pruefung': 'Die Datei ist so nicht buchbar — {offen} Zeilen ohne Zuordnung.',
  'import.aktualisierung.bleibt': 'bleibt, weil {kreis} geschlossen ist',
  'import.aktualisierung.halt': '{gebucht} Datenimporte aktualisiert, dann angehalten. Der Rest bleibt angemahnt.',

  /* R638 — Die Warnabfrage benennt, was verschwindet. Kein „Wirklich löschen?". */
  'import.loeschen.titel': 'Diesen Datenimport löschen?',
  'import.loeschen.was':
    '{einheit}, {periode}, {schicht} — {zeilen} Zeilen über {summe}. Beleg und Buchungen ' +
    'verschwinden; was gelöscht ist, kommt nicht als Vorgänger zurück.',
  'import.loeschen.spur':
    'Der Lauf und die gelieferte Datei bleiben unberührt stehen, und das Änderungsprotokoll ' +
    'führt jede Zeile mit ihrem Betrag weiter. Die Spur zur Ursprungsdatei bricht nicht ab.',
  'import.loeschen.zuordnung':
    'Was jemand zugeordnet hat, bleibt liegen und steht danach vollständig auf „nicht ' +
    'zugeordnet". Das blockiert die Freigabe, bis es aufgelöst ist.',
  'import.loeschen.tun': 'Endgültig löschen',
  'import.loeschen.abbrechen': 'Abbrechen',
  'import.loeschen.laeuft': 'Löscht …',

  'datenimport.unbekannt.meldung': 'Diesen Datenimport gibt es nicht mehr (Beleg {beleg}).',
  'datenimport.unbekannt.weg':
    'Laden Sie die Liste neu — möglicherweise hat ihn jemand anderes bereits gelöscht.',
  'datenimport.hindernis.geschlossen.meldung':
    'Der Abschluss von {kreis} für {periode} ist geschlossen. Gelöscht und ersetzt wird nur in ' +
    'einer offenen Periode.',
  'datenimport.hindernis.geschlossen.weg':
    'Eröffnen Sie die Periode wieder — oder schicken Sie die neue Datei als Nachbuchung in den ' +
    'nächsten offenen Monat. Angenommen wird sie in jedem Fall.',
  'datenimport.hindernis.folgeperiode.meldung':
    'Für {periode} ist bereits geliefert worden. Der Eröffnungsbestand dort steht auf dem ' +
    'Endbestand dieses Datenimports.',
  /* R639 — Ersetzt wird, was markiert ist. */
  'import.ersetzt.fremd.meldung':
    'Der Datenimport, der ersetzt werden soll, gehört nicht zu {einheit} in {periode}.',
  'import.ersetzt.fremd.weg':
    'Laden Sie die Liste neu und drücken Sie Aktualisieren an der Zeile, die Sie meinen.',
  'import.neu.hinweis':
    'Dieser Datenimport kommt hinzu; er ersetzt nichts. Wollen Sie einen vorhandenen ersetzen, ' +
    'gehen Sie zurück und drücken Sie an seiner Zeile Aktualisieren.',
  'import.ersetzt.hinweis': 'Ersetzt wird: {bezeichnung}. Alles andere in {periode} bleibt stehen, auch eine Nachbuchung.',

  'datenimport.hindernis.folgeperiode.weg':
    'Löschen Sie zuerst den Datenimport für {periode}, dann diesen.',


  /* ---- Die Zuordnungen (B8, R138, R303) ---- */

  'mapping.suche.beispiel': 'Konto, Code oder Wert',
  'mapping.suche': 'Suchen',
  'mapping.leer': 'Es ist noch kein Konto zugeordnet.',
  /* ---- Die Gesellschaft und die Mappe (5.1 Abschnitt 3.49, R1153, R1155) ---- */
  'mapping.gesellschaft': 'Gesellschaft',
  'mapping.gesellschaft.alle': 'Alle Gesellschaften',
  'mapping.gesellschaft.leer': 'Die gespeicherten Lieferungen dieser Gesellschaft verwenden keine dieser Zuordnungen.',
  'mapping.export': 'Als Excel-Mappe',
  /* ---- Stilllegen ab einem Monat, im Zeilenmenü (5.1 Abschnitt 3.50, R1160–R1164) ---- */
  'mapping.spalte.gueltig': 'gültig',
  'mapping.gueltig.ab': 'ab {von}',
  'mapping.gueltig.bis': '{von} bis {bis}',
  'mapping.menue.stilllegen': 'Stilllegen …',
  'mapping.menue.inkraft': 'Wieder in Kraft setzen',
  'mapping.stilllegen.titel': '{quelle} → {ziel} stilllegen',
  'mapping.stilllegen.laedt': 'Wird nachgesehen, wer sie verwendet …',
  'mapping.stilllegen.verwender': 'Verwendet von {liste} — stillgelegt wird für alle.',
  'mapping.stilllegen.niemand': 'Von keiner gespeicherten Lieferung verwendet.',
  'mapping.stilllegen.unbekannt': 'Wer sie verwendet, ließ sich nicht nachsehen. Stillgelegt wird für alle Gesellschaften.',
  'mapping.stilllegen.ab': 'Nicht mehr gültig ab',
  'mapping.stilllegen.bis': 'Bis {monat} gilt sie weiter.',
  'mapping.stilllegen.ganz': 'Sie gilt dann für keinen Monat mehr und verschwindet aus der Liste.',
  /* ---- Berichtigen, Verlauf und der Monat des Wirksamwerdens (5.1 Abschnitt 3.51, R1170–R1175) ---- */
  'mapping.menue.berichtigen': 'Berichtigen …',
  'mapping.menue.verlauf': 'Verlauf …',
  'mapping.berichtigen.titel': '{quelle} berichtigen',
  'mapping.berichtigen.partner': 'Partner',
  'mapping.berichtigen.ohnepartner': 'kein fester Partner',
  'mapping.berichtigen.ab': 'Wirksam ab',
  'mapping.berichtigen.bis': 'Bis {monat} gilt weiter {ziel}.',
  'mapping.berichtigen.ganz': 'Die Zeile bekommt das neue Ziel ab ihrem Beginn.',
  'mapping.berichtigen.davor': 'Der Monat liegt vor dem Beginn dieser Zeile ({von}).',
  'mapping.berichtigen.tun': 'Ab {monat} berichtigen',
  'mapping.berichtigen.tun.leer': 'Berichtigen',
  'mapping.verlauf.titel': 'Verlauf: {quelle}',
  'mapping.verlauf.laedt': 'Wird gelesen …',
  'mapping.verlauf.spalte.wann': 'Wann',
  'mapping.verlauf.spalte.aenderung': 'Änderung',
  'mapping.verlauf.tat.angelegt': 'angelegt',
  'mapping.verlauf.tat.berichtigt': 'berichtigt',
  'mapping.verlauf.tat.stillgelegt': 'stillgelegt',
  'mapping.verlauf.tat.inkraft': 'wieder in Kraft',
  'mapping.verlauf.tat.aufgehoben': 'für keinen Monat mehr',
  'mapping.verlauf.tat.geaendert': 'geändert',
  'mapping.wirksam.titel': 'Ab wann wirkt die neue Zuordnung?',
  'mapping.wirksam.ab': 'Wirksam ab',
  'mapping.wirksam.tun': 'Ab {monat} zuordnen',
  'mapping.wirksam.tun.leer': 'Zuordnen',
  'import.zuordnung.monatfehlt.bis.meldung': '{lokal} war von {von} bis {bis} auf {ziel} zugeordnet.',
  'import.zuordnung.monatfehlt.ab.meldung': '{lokal} ist ab {von} auf {ziel} zugeordnet.',
  'import.zuordnung.monatfehlt.weg': 'Tragen Sie den Monat ein, ab dem die neue Zuordnung wirkt.',
  'import.zuordnung.spaeter.meldung': 'Für {lokal} gilt ab {von} schon eine spätere Zuordnung auf {ziel}.',
  'import.zuordnung.spaeter.weg': 'Berichtigen Sie die spätere Zuordnung unter Datenimport → Zuordnungen.',
  'mapping.keintreffer': 'Kein Treffer.',
  /* ---- Ein Konto neu erzeugen (R307, R308) ---- */

  'import.konto.zwilling.meldung':
    'Das Konto {nummer} {bezeichnung} gibt es bereits im Konzernkontenrahmen.',
  'import.konto.zwilling.weg':
    'Ziehen Sie das lokale Konto darauf, statt ein zweites gleichen Namens anzulegen. Dieselbe Nummer unter anderem Namen ist dagegen zulässig.',
  'import.konto.ohnegeschwister.meldung':
    'Unter {position} hängt noch kein Konto, von dem sich die Kontengruppe ableiten ließe.',
  'import.konto.uneins.meldung':
    'Die Konten unter {position} gehören zu verschiedenen Kontengruppen ({gruppen}).',
  'import.konto.gruppe.weg':
    'Wählen Sie die Kontengruppe ausdrücklich — an ihr hängen Umrechnungsmethode, Bewegungsartpflicht und Fondszugehörigkeit.',
  'import.konto.keineposition.meldung': 'Die Position {position} gibt es nicht.',
  'import.konto.unbekannt.meldung': '{konto} ist kein Konto des Konzernkontenrahmens.',
  'import.konto.nurcoko.meldung': '{konto} ist ein technisches Konto ab Werk — ihm lässt sich kein lokales Konto zuordnen.',
  'import.konto.nurcoko.weg': 'Ordnen Sie das lokale Konto einem gewöhnlichen Konto zu.',
  'import.konto.unbekannt.weg':
    'Zugeordnet wird auf ein vorhandenes Konto — oder Sie erzeugen ein neues, indem Sie auf eine Position ziehen.',

  'mapping.gliederung.leer': 'Für diese Bewertungsebene ist keine Gliederung hinterlegt.',
  'mapping.position.ausgefallen': 'Die Spalte „Position“ ließ sich nicht laden.',
  'mapping.offeneplaene':
    'Im Positionsplan {plaene} ist das neue Konto noch nicht eingeordnet; dort fällt es unter „nicht zugeordnet".',
  'mapping.gruppe.frage': 'Unter {position} hängt noch kein Konto. Welche Kontengruppe soll {lokal} bekommen?',
  'mapping.gruppe.wahl': 'Kontengruppe …',
  'mapping.gruppe.anlegen': 'Konto anlegen und zuordnen',
  'mapping.gruppe.abbrechen': 'Abbrechen',
  'mapping.rechenposition': 'Rechenposition — ihr Wert ist eine Formel, sie trägt keine Konten',
  'mapping.rahmen.titel': 'Konzernkontenrahmen',
  'mapping.hierablegen': '{lokal} hier ablegen',
  'mapping.tabelle.titel': 'Konten',
  'mapping.bewegungsart.titel': 'Bewegungsarten',
  'mapping.bewegungsart.leer': 'Es ist noch kein Bewegungsartcode zugeordnet.',
  'mapping.werte.titel': 'Weitere Werte',
  'mapping.werte.leer': 'Es ist noch kein weiterer Wert zugeordnet.',
  'mapping.spalte.code': 'gelieferter Wert',
  'mapping.spalte.feld': 'Dimension',
  'mapping.spalte.ziel': 'Ziel',
  'mapping.spalte.bewegungsart': 'Bewegungsart',

  'mapping.spalte.lokalnummer': 'lokales Konto',
  'mapping.spalte.kontonummer': 'Kontonummer',
  'mapping.spalte.konzernkonto': 'Konzernkonto',
  'mapping.spalte.position': 'Position',
  'mapping.konto.waehlen': 'Konzernkonto wählen …',
  'mapping.speichern': 'Zuordnung speichern',
  'import.zuordnen': 'Zuordnen: {konto}',
  'import.ordnetzu': 'Ordnet zu …',

  /* ---- Zuordnen im Lauf (5.1 Abschnitt 3.11, R589–R592) ---- */

  'import.zuordnung.oeffnen.eins': 'Eine Zuordnung vornehmen',
  'import.zuordnung.oeffnen.mehrere': '{anzahl} Zuordnungen vornehmen',
  'import.zuordnung.zeile': 'Zuordnen …',
  'import.zuordnung.titel': 'Konten dieser Lieferung zuordnen',
  'import.zuordnung.klammer': '{datei} · ein neues Konto entsteht im Kontenrahmen der Bewertungsebene {ledger}',
  'import.zuordnung.ohneplan':
    'Über der Rohdatenschicht {ledger} führt keine Bewertungsebene einen Positionsplan — hier lässt sich kein neues Konto anlegen. Hängen Sie die Schicht unter eine Gruppe, die einen führt (Stammdaten → Bewertungsebenen). Zuordnen auf ein bestehendes Konto geht trotzdem.',
  'import.zuordnung.fortschritt': '{erledigt} von {gesamt} zugeordnet',
  'import.zuordnung.leer': 'In dieser Lieferung ist jedes Konto zugeordnet.',
  'import.zuordnung.zeilen': '{anzahl} Zeilen',
  'import.zuordnung.zugeordnet': 'zugeordnet auf {konto}',
  'import.zuordnung.angelegt': 'neu angelegt als {konto}',
  'import.zuordnung.neuanlegen': 'Neues Konzernkonto anlegen',
  'import.zuordnung.position': 'Position wählen …',
  'import.zuordnung.schliessen': 'Schließen und neu prüfen',
  'import.zuordnung.schliessen.ohne': 'Schließen',
  'import.zuordnung.veraltet':
    'Die Zahlen oben sind vom letzten Lesen. Sie werden nach dem Schließen neu geprüft.',
  /* ---- Die Bewegungsart im Lauf (5.1 Abschnitt 3.12, R593–R596) ---- */

  'import.zuordnung.abschnitt.konten': 'Konten',
  'import.zuordnung.abschnitt.bewegungsarten': 'Bewegungsarten',
  'import.zuordnung.abschnitt.werte': 'Weitere Werte',

  /* ---- Zwei Bereiche und ein Baum (5.1 Abschnitt 3.15, R602–R606) ---- */

  'import.zuordnung.sorten': 'Was in dieser Lieferung offen ist',
  'import.zuordnung.quelle': 'Aus der Lieferung',
  'import.zuordnung.ziel': 'Wohin es gehört',
  'import.zuordnung.grenze': 'Grenze zwischen Lieferung und Abschluss verschieben',
  'import.zuordnung.gewaehlt': '{anzahl} gewählt',
  'import.zuordnung.dazunehmen': '{konto} zur Auswahl dazunehmen',
  'import.zuordnung.bericht': 'Bericht',
  'import.zuordnung.bericht.bilanz': 'Bilanz',
  'import.zuordnung.bericht.guv': 'GuV',
  'import.zuordnung.ziel.suche': 'Position oder Konto suchen …',
  'import.zuordnung.ziel.keintreffer': 'Weder eine Position noch ein Konto passt dazu.',
  'import.zuordnung.erstwaehlen': 'Wählen Sie links ein Konto der Lieferung.',
  'import.zuordnung.einhaengen.eins': 'Dieses Konto unter {position} neu anlegen',
  'import.zuordnung.einhaengen.mehrere': '{anzahl} Konten unter {position} neu anlegen',
  'import.zuordnung.ziel.ohnestelle': 'Ohne Stelle in diesem Plan ({anzahl})',
  'import.zuordnung.ziel.ohnestelle.grund':
    'Diese Konzernkonten hängen an keiner Position dieses Positionsplans. Verschmelzen geht trotzdem — im Bericht erscheinen sie erst, wenn sie eine Stelle haben.',
  'import.zuordnung.aufklappen': '{position} aufklappen',
  'import.zuordnung.zuklappen': '{position} zuklappen',
  'import.zuordnung.art.waehlen': 'Bewegungsart wählen …',
  'import.zuordnung.art.neuanlegen': 'Neue Bewegungsart anlegen',
  'import.zuordnung.art.kern': 'Kern-Bewegungsart wählen …',
  'import.zuordnung.art.zugeordnet': 'zugeordnet auf {konto}',
  'import.zuordnung.art.angelegt': 'neu angelegt als {konto}',
  'import.bewegungsart.unbekannt.meldung': 'Die Bewegungsart {bewegungsart} gibt es nicht.',
  'import.bewegungsart.unbekannt.weg':
    'Wählen Sie eine aus dem Vorrat, oder legen Sie eine unter ihrem Kern an.',
  'import.bewegungsart.widerspruch.meldung':
    'Der Code {code} ist bereits auf {bewegungsart} zugeordnet.',
  'import.bewegungsart.widerspruch.weg':
    'Legen Sie die bestehende Zuordnung still, wenn der Code künftig etwas anderes bedeuten soll — eine gebuchte Periode behält ihre Bedeutung.',
  'import.bewegungsart.keinkern.meldung': '{bewegungsart} ist selbst schon ein Detail.',
  'import.bewegungsart.keinkern.weg':
    'Eine neue Bewegungsart hängt unter einem Kern; ein Detail unter einem Detail gibt es nicht.',
  // R1061: Der Vortrag ist keine Bewegungsart für den Anwender (2.5 Abschnitt 15).
  'import.bewegungsart.vortrag.meldung': '{bewegungsart} trägt den Vormonat vor und ist nicht wählbar.',
  'import.bewegungsart.vortrag.weg':
    'CoKo trägt den Stand des Vormonats selbst vor. Ordnen Sie den Code einer Bewegung zu, sonst Default.',
  // R1081: Kein Spaltenkopf steht in einem Spiegel zweimal (2.5 Abschnitt 16).
  'stammdaten.spaltenkopf.doppelt.meldung': '„{kopf}" steht im selben Spiegel schon über der Spalte von {bewegungsart}.',
  'stammdaten.spaltenkopf.doppelt.weg':
    'Geben Sie dieser Bewegungsart ein eigenes Kürzel — im Spiegel steht das Kürzel, sonst der Name ohne Nummer.',
  'import.bewegungsart.schonda.meldung': 'Den Code {code} trägt bereits eine Bewegungsart.',
  'import.bewegungsart.schonda.weg':
    'Ordnen Sie den gelieferten Code dieser Bewegungsart zu, statt eine zweite mit demselben Code anzulegen.',

  /* ---- Die uebrigen Vorratsfelder (5.1 Abschnitt 3.13, R597–R600) ---- */

  'import.zuordnung.wert.waehlen': 'Wert wählen …',
  'import.zuordnung.wert.neuanlegen': 'Neu anlegen',
  'import.zuordnung.wert.zugeordnet': 'zugeordnet auf {konto}',
  'import.zuordnung.wert.angelegt': 'neu angelegt als {konto}',
  'import.zuordnung.wert.keinvorrat':
    'Dieser Vorrat wächst nicht im Lauf mit — der Wert wird in der Stammdatenpflege angelegt und hier zugeordnet.',
  'import.wert.waehrung.meldung': 'Eine Währung wird nicht zugeordnet.',
  'import.wert.waehrung.weg':
    'Sie wird gegen das Stammdatum der Gesellschaft verprobt. Ein abweichender Code heißt: Die Datei gehört zu einer anderen Gesellschaft.',
  'import.wert.widerspruch.meldung': 'Der Wert {code} ist bereits auf {ziel} zugeordnet.',
  'import.wert.widerspruch.weg':
    'Legen Sie die bestehende Zuordnung still, wenn der Wert künftig etwas anderes bedeuten soll.',
  'import.wert.keinvorrat.meldung': 'Für {feld} lässt sich kein Wert im Lauf anlegen.',
  'import.wert.keinvorrat.weg':
    'Offen sind die Vorräte für Partnercodes, Segmente und optionale Dimensionen. Was Bedeutung für die Rechnung trägt, entsteht in der Pflege.',


  /* ---- Hilfe (7.1, R304) ---- */

  'bereich.hilfe.name': 'Hilfe',
  'bereich.hilfe.zweck': 'Schritt für Schritt durch die Wege, die man selten geht.',
  'bereich.hilfe.import': 'Eine Lieferung importieren',
  'bereich.hilfe.struktur': 'Ein Konto anlegen und umbenennen',
  'hilfe.anleitung.zweck':
    'Der ganze Weg vom leeren System bis zu Zahlen im Bericht. Jeder Schritt zeigt den Bildschirm, auf dem er stattfindet, und markiert, was zu drücken ist.',
  'hilfe.schritt': 'Schritt {nummer} von {gesamt}',
  'hilfe.zurueck': 'Zurück',
  'hilfe.weiter': 'Weiter',
  'hilfe.dorthin': 'Diesen Bildschirm öffnen',
  'hilfe.bildfehlt':
    'Für diesen Schritt liegt noch keine Aufnahme vor. Der Text sagt trotzdem, was zu tun ist.',
  'hilfe.alleschritte': 'Alle Schritte',
  'hilfe.aufnahme': 'Aufnahme vom {datum}',

  /* ---- Der Begleiter (7.1 Abschnitt 10, R545–R549) ---- */

  'bereich.hilfe.gesellschaft': 'Eine Gesellschaft anlegen und einbeziehen',
  'bereich.hilfe.begleiter': 'Der Begleiter',
  'begleiter.weg.neuegesellschaft': 'Eine neue Gesellschaft liefert zum ersten Mal',
  'begleiter.schonda': 'steht schon in der Liste',
  'begleiter.anhaengen': 'Weiteren Weg anhängen',
  /* Das Modell rät nie (R142): Der Satz sagt, woran es den Schritt erkannt
     hat — an einer Menge, die gestiegen ist, nicht an einer Vermutung. */
  'begleiter.erkannt': 'Vom Modell erkannt: Seit dem Anlegen der Liste ist hier etwas dazugekommen.',
  'begleiter.stand': '{erledigt} von {gesamt} erledigt',
  'begleiter.fertig': 'Alles erledigt.',
  'begleiter.verwerfen': 'Liste verwerfen',
  'begleiter.daneben': 'Daneben legen',
  'begleiter.unbekannt': 'Diesen Schritt gibt es in CoKo nicht mehr. Die Liste ist älter als der heutige Stand.',

  'keinedaten.titel': 'Für diese Auswahl liegen keine Daten vor.',
  'keinedaten.text':
    'Zu diesem Organisationselement, dieser Bewertungsebene und diesem Zeitraum ist nichts geliefert worden.',
  'keinedaten.weg': 'Oben eine andere Auswahl treffen.',
  'keinedaten.konzern.titel': 'Für diese Auswahl ist kein Konzernabschluss gerechnet.',
  'keinedaten.konzern.text':
    'Konzernzahlen entstehen nur in einem Konzernabschluss. Die Lieferdaten jeder Gesellschaft stehen oben in der Auswahl unter „Lieferdaten".',
  'keinedaten.konzern.weg': 'Der Weg zum Konzernabschluss steht über diesem Satz.',

  /* ======================================================================
     Die Texte des Rechenkerns
     ======================================================================

     Der Kern schickt Schlüssel und Werte, den Satz setzt die Oberfläche.
     Damit wirkt ein Sprachwechsel sofort — ohne Nachladen und ohne den
     Abschluss neu zu rechnen. */

  /** Ein Satz aus der Zeit vor der Umstellung, unverändert ausgegeben. */
  'text.roh': '{text}',
  'text.unbekannt': 'Zu diesem Schritt liegt kein Text vor.',

  /* --- Die Regelnamen. Der Code steht im Prüfcode, der Name hier. --- */
  'regel.umrechnungsdifferenz': 'Umrechnungsdifferenz',
  'regel.umrechnungsanpassung': 'Anpassung an den Kurs des Kontos',
  // Der Name des Umrechnungsbelegs im Belegreiter (2.13 Abschnitt 21.7).
  'regel.waehrungsumrechnung': 'Währungsumrechnung',
  'regel.ergebnisuebernahme': 'Ergebnisübernahme',
  'regel.ergebnisspiegelung': 'Ergebnisspiegelung',
  'regel.schuldenkonsolidierung': 'Schuldenkonsolidierung',
  'regel.schuldenkonsolidierung-differenz': 'Schuldenkonsolidierung — Differenz',
  'regel.aufwandertragkonsolidierung': 'Aufwands- und Ertragskonsolidierung',
  'regel.aufwandertragkonsolidierung-differenz': 'Aufwands- und Ertragskonsolidierung — Differenz',
  'regel.dividendenkonsolidierung': 'Konsolidierung von IC-Ausschüttungen',
  'buchung.dividende.ertrag':
    '{konto} bei {einheit}: Die Dividende von {partner} über {betrag} {waehrung} ist aus Konzernsicht kein Ertrag und wird ausgebucht.',
  'buchung.rundungsdifferenz.ausschuettung': 'Rundungsdifferenz aus der Verteilung der Ausschüttung von {einheit}: {betrag} {waehrung}.',
  'buchung.dividende.ruecklage':
    '{konto} bei {einheit}: Die Ausschüttung wird um {betrag} {waehrung} zurückgenommen — sie floss innerhalb des Konzerns oder steht beim Ausgleichsposten der Minderheiten.',
  'buchung.dividende.vortrag':
    '{konto} bei {einheit}: Die Dividendenkonsolidierung der Vormonate wird mit {betrag} {waehrung} fortgeschrieben.',
  'buchung.aufwandertrag.vortrag':
    '{konto} bei {einheit}: Die Aufwands- und Ertragskonsolidierung der Vormonate wird mit {betrag} {waehrung} fortgeschrieben.',
  'buchung.bestand.vortrag':
    '{konto} bei {einheit}: Der Bestand des Vormonats eröffnet mit {betrag} {waehrung}.',
  'buchung.bestand.veraenderung':
    '{konto} bei {einheit}: Veränderung zum Vormonat ohne eigene Bewegungsart, {betrag} {waehrung}.',
  'buchung.bestand.vormonat':
    '{konto} bei {einheit}: {betrag} {waehrung} standen schon im Vormonat und gehen nicht noch einmal in die GuV.',
  'buchung.dividende.minderheiten':
    '{einheit}: Der Anteil der Minderheiten ({quote}) an der Ausschüttung, {betrag} {waehrung}, mindert ihren Ausgleichsposten.',
  'meldung.dividende.differenz':
    '{einheit} weist von {partner} einen Beteiligungsertrag von {ertrag} {waehrung} aus; die Ausschüttung der Tochter ({ausschuettung} {waehrung}) mal Quote ({quote}) ergibt {erwartet} {waehrung}. Differenz {differenz} {waehrung} — eliminiert wurde der Ertrag der Mutter.',
  'meldung.dividende.ohne-ausschuettung':
    '{einheit} weist von {partner} einen Beteiligungsertrag von {ertrag} {waehrung} aus, die Tochter aber keine Ausschüttung. Eliminiert wurde der Ertrag gegen das Rücklagenkonto des Konzernhandbuchs.',
  // Seit dem 2026-10-10 (2.9 Abschnitt 13.13, R1509, R1510); die beiden
  // Meldungen darüber stehen in Läufen davor.
  'meldung.dividende.wesentlich':
    '{einheit} weist von {partner} einen Beteiligungsertrag von {ertrag} {waehrung} aus; die Ausschüttung der Tochter ({ausschuettung} {waehrung}) mal Quote ({quote}) ergibt {erwartet} {waehrung}. Der Unterschied von {differenz} {waehrung} steht auf dem Differenzenkonto und liegt über der Toleranz von {grenze} {waehrung} — er ist nicht geklärt.',
  'meldung.dividende.unwesentlich':
    '{einheit} weist von {partner} einen Beteiligungsertrag von {ertrag} {waehrung} aus; die Ausschüttung der Tochter ({ausschuettung} {waehrung}) mal Quote ({quote}) ergibt {erwartet} {waehrung}. Der Unterschied von {differenz} {waehrung} steht auf dem Differenzenkonto und liegt unter der Toleranz von {grenze} {waehrung}.',
  'buchung.dividende.differenz':
    '{einheit} gegen {partner}: Beteiligungsertrag und Ausschüttung mal Quote unterscheiden sich um {differenz} {waehrung}; der Unterschied steht auf dem Differenzenkonto.',
  'regel.zwischenergebniseliminierung-uv': 'Zwischenergebniseliminierung Umlaufvermögen',
  'regel.zwischenergebniseliminierung-av': 'Zwischenergebniseliminierung Anlagevermögen',
  'regel.beteiligungsbuchwert': 'Beteiligungsbuchwert',
  'regel.erstkonsolidierung': 'Erstkonsolidierung',
  'regel.unterschiedsbetrag': 'Unterschiedsbetrag',
  'regel.neubewertung': 'Neubewertung fortschreiben',
  'regel.goodwillfortschreibung': 'Firmenwert fortschreiben',
  'regel.anteile-nicht-beherrschend':
    'Ausweis laufendes Ergebnis nicht beherrschender Gesellschafter',

  /* --- Die Felder der Herleitung (K7) --- */
  'herleitung.monate': 'Abgelaufene Monate',
  'herleitung.rate': 'Abschreibung je Monat',
  'herleitung.nutzungsdauer': 'Nutzungsdauer in Monaten',
  'herleitung.firmenwert': 'Firmenwert beim Erwerb',
  'herleitung.einheit': 'Gesellschaft',
  'herleitung.partner': 'Partner',
  'herleitung.konto': 'Konto',
  'herleitung.waehrung': 'Währung',
  'herleitung.schicht': 'Schicht',
  'herleitung.landeswaehrung': 'Landeswährung',
  'herleitung.erfolgswirkung': 'Erfolgswirkung',
  'herleitung.summe-umgerechnet': 'Summe der umgerechneten Salden',
  'herleitung.vormonat': 'Stand im Vormonat',
  'herleitung.betrag-lokal': 'Betrag in Landeswährung',
  'herleitung.kursart': 'Kursart',
  'herleitung.periode': 'Monat',
  'herleitung.zum-stichtag': 'Zum Stichtagskurs',
  'herleitung.zum-kurs': 'Zum Kurs des Kontos',
  'herleitung.quellkonto': 'Quellkonto',
  'herleitung.rundung': 'Rundung',
  'herleitung.stand': 'Stand',
  'herleitung.bewegungen': 'Bewegungen des Monats',
  'herleitung.summe-erfolgskonten': 'Summe der Erfolgskonten',
  'herleitung.stichtagskurs': 'Stichtagskurs',
  'herleitung.durchschnittskurs': 'Durchschnittskurs',
  'herleitung.historischer-kurs': 'Historischer Kurs',
  'herleitung.kurs': 'Kurs',
  'herleitung.ausgangswert': 'Ausgangswert',
  'herleitung.differenz': 'Differenz',
  'herleitung.wesentlichkeitsgrenze': 'Wesentlichkeitsgrenze',
  'herleitung.waehrungsanteil': 'Davon Währungsdifferenz',
  'herleitung.lieferant': 'Lieferant',
  'herleitung.marge': 'Marge in Prozent',
  'herleitung.margenquelle': 'Woher die Marge stammt',
  'herleitung.bestand': 'Bestand',
  'herleitung.zwischenergebnis': 'Zwischenergebnis',
  'herleitung.mutter': 'Mutter',
  'herleitung.tochter': 'Tochter',
  'herleitung.buchwert': 'Buchwert',
  'herleitung.beteiligungsbuchwert': 'Beteiligungsbuchwert',
  'herleitung.wert-am-erwerbstag': 'Wert am Erwerbstag',
  /* Gefunden am 2026-09-26 in der Prüferansicht: Der Rechenkern schreibt dieses
     Feld an jede Buchung der Erstkonsolidierung, und bis heute stand im
     Wertenachweis der nackte Schlüssel darüber (R792). */
  'herleitung.stichtag': 'Monat, dessen Eigenkapital verrechnet wird',
  'herleitung.kurs-am-erwerbstag': 'Kurs am Erwerbstag',
  'herleitung.quote': 'Kapitalanteil',
  'herleitung.ausschuettung': 'Ausschüttung der Tochter',
  'herleitung.beteiligungsertrag': 'Beteiligungsertrag der Mutter',
  'herleitung.anteil': 'Anteiliger Betrag',
  'herleitung.anteiliges-eigenkapital': 'Anteiliges Eigenkapital am Erwerbstag',
  'herleitung.unterschiedsbetrag': 'Unterschiedsbetrag',
  'herleitung.eigenkapital': 'Eigenkapital',
  'herleitung.fremdquote': 'Fremdquote',
  'herleitung.betrag': 'Betrag',

  /* --- Buchungserläuterungen --- */
  'herleitung.beginn': 'Abgeschrieben ab',
  'herleitung.verbleibend': 'Verbliebener Anteil in Prozent',
  'herleitung.quotevorher': 'Kapitalanteil vorher',
  'herleitung.quotedanach': 'Kapitalanteil danach',
  'herleitung.ausgleichsposten': 'Ausgleichsposten der anderen Gesellschafter',
  'herleitung.kaufpreis': 'Kaufpreis',
  'herleitung.firmenwertabgang': 'Abgegangener Firmenwert',
  'buchung.umrechnungsdifferenz':
    'Umrechnung von {einheit} aus {landeswaehrung}: Bilanzposten zum Stichtagskurs, Eigenkapital zum historischen Kurs, Erfolgsrechnung zum Durchschnittskurs. Die verbleibende Differenz von {differenz} {waehrung} geht erfolgsneutral ins Eigenkapital.',
  // R1082, R1083: Seit dem 2026-10-05 Vortrag plus Bewegung, mit Kursen (2.6 Abschnitt 2.9).
  // R1471: Eine Bewertungsebene ohne Zeile der historischen Kurse nennt keinen.
  'buchung.umrechnungsdifferenz.bewegung.ohne-historisch':
    'Umrechnungsdifferenz {einheit} ({landeswaehrung}): Vermögen und Schulden zum Stichtagskurs {kursStichtag}.',
  'buchung.umrechnungsdifferenz.bewegung':
    'Umrechnungsdifferenz {einheit} ({landeswaehrung}): Eigenkapital zum historischen Kurs {kursHistorisch}, Vermögen und Schulden zum Stichtagskurs {kursStichtag}.',
  'buchung.umrechnungsdifferenz.vortrag': 'Umrechnungsdifferenz {einheit}: Stand des Vormonats, vorgetragen.',
  // Der Umrechnungsbeleg (2.13 Abschnitt 21.7, R1670-R1673), die Namen nach R1653.
  'buchung.umrechnung.anpassung.stichtag': 'Anpassung an den Stichtagskurs: {konto} bei {einheit}.',
  'buchung.umrechnung.anpassung.historisch': 'Anpassung an den historischen Kurs: {konto} bei {einheit}.',
  'buchung.umrechnung.anpassung.durchschnitt': 'Anpassung an den Durchschnittskurs: {konto} bei {einheit}.',
  'buchung.umrechnung.gegenbuchung': 'Gegenbuchung der Umrechnung: {quellkonto} bei {einheit}.',
  'buchung.umrechnung.gegenbuchung.vortrag': 'Gegenbuchung der Umrechnung: {quellkonto} bei {einheit}, Stand des Vormonats.',
  'buchung.umrechnung.gegenbuchung.ohne-quellkonto': 'Gegenbuchung der Umrechnung bei {einheit}: Stand von vor dem Umrechnungsbeleg, auf die Quellkonten aufgeteilt.',
  'buchung.umrechnung.gegenbuchung.ohne-quellkonto.vortrag': 'Gegenbuchung der Umrechnung bei {einheit}: Stand des Vormonats, noch ohne Quellkonto.',
  // Im Monat des Wechsels wandert der Stand des Vormonats (R1657).
  'buchung.umrechnung.umbuchung': 'Umbuchung der Umrechnung: {quellkonto} bei {einheit}, von {von} auf {nach}.',
  'buchung.umrechnung.rundung': 'Rundung der Umrechnung bei {einheit}.',
  'buchung.umrechnung.rundung.vortrag': 'Rundung der Umrechnung bei {einheit}, Stand des Vormonats.',
  'buchung.umrechnung.rundung.konto': 'Rundung der Umrechnung: {konto} bei {einheit}.',
  'buchung.ergebnisuebernahme.ueberschuss':
    '{einheit} weist einen Jahresüberschuss von {betrag} {waehrung} aus; er wird in die Bilanz übernommen.',
  'buchung.ergebnisuebernahme.fehlbetrag':
    '{einheit} weist einen Jahresfehlbetrag von {betrag} {waehrung} aus; er wird in die Bilanz übernommen.',
  'buchung.ergebnisspiegelung.minderung':
    'Der Vorgang mindert das Ergebnis um {betrag} {waehrung}; das Jahresergebnis in der Bilanz folgt der Gewinn- und Verlustrechnung.',
  'buchung.ergebnisspiegelung.erhoehung':
    'Der Vorgang erhöht das Ergebnis um {betrag} {waehrung}; das Jahresergebnis in der Bilanz folgt der Gewinn- und Verlustrechnung.',
  'buchung.ic-eliminierung':
    '{konto} bei {einheit} gegenüber {partner}: {betrag} {waehrung} sind aus Konzernsicht ein Geschäft mit sich selbst und werden ausgebucht.',
  'buchung.ic-differenz.schuldenkonsolidierung':
    '{einheit} und {partner} weisen ihre Forderungen und Verbindlichkeiten nicht in gleicher Höhe aus. Die Differenz von {differenz} {waehrung} bleibt nach der Eliminierung stehen und wird erfolgswirksam erfasst.',
  // R1481: Der Währungsanteil der Differenz, mit der Bewegungsart Währungsdifferenz.
  'buchung.ic-differenz.schuldenkonsolidierung.waehrung':
    'Währungsanteil der Differenz zwischen {einheit} und {partner} bei Forderungen und Verbindlichkeiten: {differenz} {waehrung}, aus der Umrechnung zum Stichtagskurs.',
  'buchung.ic-differenz.aufwandertragkonsolidierung.waehrung':
    'Währungsanteil der Differenz zwischen {einheit} und {partner} bei Innenumsätzen und Innenaufwendungen: {differenz} {waehrung}, aus der Umrechnung.',
  'buchung.ic-differenz.aufwandertragkonsolidierung':
    '{einheit} und {partner} weisen ihre Innenumsätze und Innenaufwendungen nicht in gleicher Höhe aus. Die Differenz von {differenz} {waehrung} bleibt nach der Eliminierung stehen und wird erfolgswirksam erfasst.',
  'buchung.zwischenergebnis':
    'Im Bestand {konto} von {einheit} stecken {betrag} {waehrung} Gewinn aus einer Lieferung von {lieferant}. Aus Konzernsicht ist er nicht realisiert, solange die Ware den Konzern nicht verlassen hat.',
  'buchung.zwischenergebnis.gegenbuchung':
    'Gegenbuchung zur Zwischenergebniseliminierung im Bestand von {einheit}.',
  // R1810: Anteil der anderen Gesellschafter am Zwischengewinn aus Lieferungen einer Tochter.
  'buchung.zwischengewinn.andere':
    '{tochter}: {quote} Prozent des Zwischengewinns aus ihren Lieferungen tragen die anderen Gesellschafter — ihr Ausgleichsposten sinkt um {betrag} {waehrung}.',
  'buchung.zwischengewinn.andere.vortrag':
    '{tochter}: Anteil der anderen Gesellschafter am Zwischengewinn aus dem Vormonat, {betrag} {waehrung}.',
  'buchung.zwischengewinn.andere.ergebnis':
    '{tochter}: Der Ergebnisanteil der anderen Gesellschafter sinkt um ihren Anteil am Zwischengewinn, {betrag} {waehrung}.',
  'buchung.zwischengewinn.marge':
    '{konto} bei {einheit}: Im Vorrat aus Lieferungen von {lieferant} stecken bei {marge} Prozent Marge {betrag} {waehrung} Zwischengewinn — eliminiert.',
  'buchung.beteiligungsbuchwert':
    'Der Beteiligungsbuchwert von {betrag} {waehrung}, den {mutter} für {tochter} führt, wird gegen das anteilige Eigenkapital der Tochter aufgerechnet.',
  'buchung.erstkonsolidierung':
    '{konto} von {tochter} stand am Erwerbstag bei {betrag} {waehrung}. Davon entfallen {quote} Prozent auf {mutter} und werden gegen den Beteiligungsbuchwert aufgerechnet.',
  'buchung.unterschiedsbetrag.firmenwert':
    '{mutter} hat für {tochter} {buchwert} {waehrung} bezahlt und dafür anteiliges Eigenkapital von {eigenkapital} {waehrung} erhalten. Der Mehrbetrag von {unterschied} {waehrung} ist der Geschäfts- oder Firmenwert.',
  'buchung.unterschiedsbetrag.passivisch':
    '{mutter} hat für {tochter} weniger bezahlt, als das anteilige Eigenkapital wert war. Der passivische Unterschiedsbetrag beträgt {unterschied} {waehrung} und steht als eigener Posten nach dem Eigenkapital.',
  'buchung.unterschiedsbetrag.ertrag':
    '{mutter} hat für {tochter} weniger bezahlt, als das anteilige Eigenkapital wert war. Nach IFRS ist der Unterschied von {unterschied} {waehrung} im Erwerbsmonat Ertrag.',
  'buchung.unterschiedsbetrag.ertrag-vortrag':
    'Der Ertrag aus dem Erwerb von {tochter} — {unterschied} {waehrung} — stammt aus einem Vormonat dieses Geschäftsjahres und steht als Ergebnisvortrag im Jahresergebnis.',
  'buchung.unterschiedsbetrag.ertrag-ruecklage':
    'Der Ertrag aus dem Erwerb von {tochter} — {unterschied} {waehrung} — stammt aus einem früheren Geschäftsjahr und steht in den Gewinnrücklagen.',
  'buchung.neubewertung':
    'Beim Erwerb von {tochter} wurden auf {konto} stille Reserven von {betrag} {waehrung} aufgedeckt. Sie stehen im Konzern als Vermögenswert, im Einzelabschluss der Tochter nicht.',
  'buchung.neubewertung.kumuliert':
    'Auf die aufgedeckte Reserve in {konto} von {tochter} sind bis zum Beginn des Geschäftsjahres {betrag} {waehrung} abgeschrieben.',
  'buchung.neubewertung.abschreibung':
    'Die aufgedeckte Reserve in {konto} von {tochter} wird planmäßig abgeschrieben: {betrag} {waehrung} in dieser Periode.',
  'buchung.neubewertung.aufwand':
    'Gegenbuchung: Die Abschreibung auf die aufgedeckte Reserve bei {tochter} mindert das Konzernergebnis um {betrag} {waehrung}.',
  'buchung.fortschreibung.ruecklage':
    'Gegenbuchung: {betrag} {waehrung} entfallen auf frühere Geschäftsjahre und stehen in den Gewinnrücklagen, nicht im Jahresergebnis.',
  'buchung.fortschreibung.ergebnisvortrag':
    'Gegenbuchung: {betrag} {waehrung} entfallen auf {monate} Vormonate dieses Geschäftsjahres und stehen als Ergebnisvortrag im Jahresergebnis.',
  'buchung.goodwill.vortrag':
    '{konto} bei {einheit}: Firmenwert und aufgelaufene Abschreibung des Vormonats werden mit {betrag} {waehrung} vorgetragen.',
  'buchung.kapitalkonsolidierung.vortrag':
    '{konto} bei {einheit}: Die Kapitalkonsolidierung des Vormonats wird mit {betrag} {waehrung} vorgetragen.',
  'buchung.aufsatz.eigenkapital':
    '{konto} bei {tochter}: Das Eigenkapital zu Beginn des Aufsatzmonats, {betrag} {waehrung}, wird ausgebucht.',
  'buchung.aufsatz.firmenwert': 'Firmenwert aus dem Erwerb von {tochter}, Restbuchwert zum Aufsatz: {betrag} {waehrung}.',
  'buchung.aufsatz.ausgleichsposten': 'Ausgleichsposten der anderen Gesellschafter an {tochter} zum Aufsatz: {betrag} {waehrung}.',
  // R1829: Was die Tochter seit dem Erwerb erwirtschaftet hat, steht im Gewinnvortrag.
  'buchung.erstkonsolidierung.erwirtschaftet':
    'Seit dem Erwerb hat {tochter} auf {konto} {betrag} {waehrung} für den Konzern erwirtschaftet. Der Betrag geht aus dem Konto heraus …',
  'buchung.erstkonsolidierung.erwirtschaftet.vortrag':
    '… und steht im Gewinnvortrag des Konzerns: was der Konzern an {tochter} seit dem Erwerb erwirtschaftet hat, {betrag} {waehrung}.',
  'buchung.aufsatz.ruecklagen':
    'Was der Konzern an {tochter} seit dem Erwerb erwirtschaftet hat, abzüglich der bisherigen Firmenwertabschreibung: {betrag} {waehrung} in die Gewinnrücklagen.',
  'meldung.aufsatz.staende':
    '{tochter} ist mit den Ständen zum Aufsatz in den Konzern gekommen: Firmenwert {firmenwert} {waehrung}, Ausgleichsposten der anderen Gesellschafter {ausgleichsposten} {waehrung}.',
  'meldung.kapitalkonsolidierung.manuell': '{tochter} wird manuell konsolidiert. Der Prozess bucht für sie nichts.',
  // R1823: vor dem Aufsatzmonat nicht konsolidiert.
  'meldung.kapitalkonsolidierung.vor-aufsatz':
    '{tochter} wird erst ab {monat} aufgesetzt. Bis dahin konsolidiert der Prozess sie nicht.',
  'buchung.goodwill.kumuliert':
    'Auf den Firmenwert aus dem Erwerb von {tochter} sind bis zum Beginn des Geschäftsjahres {betrag} {waehrung} abgeschrieben.',
  'buchung.goodwill.abschreibung':
    'Der Firmenwert aus dem Erwerb von {tochter} wird über {monate} Monate planmäßig abgeschrieben: {betrag} {waehrung} in dieser Periode.',
  'buchung.goodwill.aufwand':
    'Gegenbuchung: Die Abschreibung auf den Firmenwert von {tochter} mindert das Konzernergebnis um {betrag} {waehrung}.',
  'buchung.goodwill.zukauf':
    'Der Firmenwert aus dem Zukauf an {tochter} im {beginn} wird über {monate} Monate planmäßig abgeschrieben: {betrag} {waehrung} in dieser Periode.',
  'buchung.kapitalkonsolidierung.ruecknahme':
    '{konto}: Die Ab- oder Zuschreibung der Mutter auf ihre Beteiligung an {tochter}, {betrag} {waehrung}, wird im Konzern zurückgenommen.',
  'buchung.kapitalkonsolidierung.ruecknahme-ergebnis':
    'Gegenbuchung auf {konto}: Im Konzern steht an Stelle der Beteiligung an {tochter} ihr Reinvermögen; die Ab- oder Zuschreibung von {betrag} {waehrung} entfällt.',
  'buchung.zukauf.beteiligung':
    '{konto}: Der Kaufpreis für weitere Anteile an {tochter}, {betrag} {waehrung}, wird gegen Ausgleichsposten und Firmenwert verrechnet.',
  'buchung.zukauf.ausgleichsposten':
    'Die Mutter übernimmt den hinzugekauften Anteil am Ausgleichsposten der anderen Gesellschafter an {tochter}: {betrag} {waehrung}.',
  'buchung.zukauf.firmenwert': 'Weiterer Firmenwert aus dem Zukauf an {tochter}: {betrag} {waehrung} — der Kaufpreis über dem übernommenen Anteil.',
  'buchung.teilverkauf.beteiligung':
    '{konto}: Der Abgang von Anteilen an {tochter}, {betrag} {waehrung}, wird im Konzern zurückgenommen — verkauft sind Reinvermögen und Firmenwert, nicht die Beteiligung.',
  'buchung.teilverkauf.ausgleichsposten':
    'Der Ausgleichsposten der anderen Gesellschafter an {tochter} steigt um den verkauften Anteil am Eigenkapital: {betrag} {waehrung}.',
  'buchung.teilverkauf.firmenwert': 'Der verkaufte Anteil am Firmenwert aus {tochter} geht ab: {betrag} {waehrung} Anschaffungskosten.',
  'buchung.teilverkauf.firmenwert-abschreibung': 'Mit ihm geht die aufgelaufene Abschreibung auf den Firmenwert aus {tochter} ab: {betrag} {waehrung}.',
  'buchung.teilverkauf.ergebnis-mehr':
    '{konto}: Der Gewinn des Konzerns aus dem Teilverkauf von {tochter} liegt {betrag} {waehrung} über dem Gewinn der Mutter.',
  'buchung.teilverkauf.ergebnis-weniger':
    '{konto}: Der Gewinn des Konzerns aus dem Teilverkauf von {tochter} liegt {betrag} {waehrung} unter dem Gewinn der Mutter.',
  'buchung.nicht-beherrschend.konto':
    'Aus der Erstkonsolidierung: Von {konto} bei {tochter} entfallen {quote} Prozent auf Gesellschafter außerhalb des Konzerns und werden gesondert ausgewiesen.',
  'buchung.nicht-beherrschend.summe':
    'Aus der Erstkonsolidierung: {quote} Prozent des Eigenkapitals von {tochter} — {betrag} {waehrung} — gehören nicht dem Konzern und stehen gesondert im Eigenkapital.',
  'buchung.nicht-beherrschend.ergebnis':
    'Laufendes Ergebnis: Von {konto} bei {tochter} entfallen {quote} Prozent auf Gesellschafter außerhalb des Konzerns.',
  'buchung.nicht-beherrschend.ergebnis-summe':
    '{quote} Prozent des Ergebnisses dieses Monats von {tochter} — {betrag} {waehrung} — gehören nicht dem Konzern und stehen gesondert im Eigenkapital.',
  'buchung.nicht-beherrschend.ergebnis-vormonate':
    '{quote} Prozent des Ergebnisses der Vormonate von {tochter} — {betrag} {waehrung} — gehören nicht dem Konzern und stehen gesondert im Eigenkapital.',
  'buchung.nicht-beherrschend.umrechnung':
    'Umrechnungsrücklage: Von {konto} bei {tochter} entfallen {quote} Prozent auf Gesellschafter außerhalb des Konzerns — mit derselben Bewegungsart wie die Rücklage selbst.',
  'buchung.nicht-beherrschend.umrechnung-summe':
    '{quote} Prozent der Umrechnungsrücklage von {tochter} — {betrag} {waehrung} — gehören nicht dem Konzern und stehen gesondert im Eigenkapital.',
  'buchung.nicht-beherrschend.bewegung':
    'Von der Veränderung auf {konto} bei {tochter} entfallen {quote} Prozent auf Gesellschafter außerhalb des Konzerns — mit derselben Bewegungsart wie bei der Tochter.',
  'buchung.kapitalerhoehung.monat':
    'Kapitalerhöhung: Von {konto} bei {tochter} gehören {quote} Prozent der Mutter — {betrag} {waehrung}, verrechnet gegen ihre Einzahlung.',
  'buchung.kapitalerhoehung.frueher':
    'Kapitalerhöhung aus einem früheren Monat: Von {konto} bei {tochter} gehören {quote} Prozent der Mutter — {betrag} {waehrung}, verrechnet gegen ihre Einzahlung.',
  'buchung.kapitalerhoehung.einzahlung':
    'Die Einzahlung der Mutter auf die Kapitalerhöhung von {tochter} — {betrag} {waehrung} — wird gegen ihren Anteil an der Erhöhung verrechnet; ein Firmenwert entsteht nicht.',
  'buchung.kapitalerhoehung.rest':
    'Was die Mutter auf frühere Kapitalerhöhungen von {tochter} mehr oder weniger eingezahlt hat, als ihrer Quote entspricht — {betrag} {waehrung} —, bleibt als Beteiligung stehen.',
  'buchung.nicht-beherrschend.bewegung-summe':
    '{quote} Prozent dieser Veränderung des Eigenkapitals von {tochter} — {betrag} {waehrung} — gehören nicht dem Konzern und stehen gesondert im Eigenkapital.',

  /* --- Meldungen der Vorgänge --- */
  'meldung.salden-gehen-nicht-auf':
    'Die Salden von {einheit} gehen um {differenz} {waehrung} nicht auf. Die Gesellschaft bilanziert in Konzernwährung, ein Währungseffekt kommt als Erklärung nicht in Frage.',
  'meldung.umrechnungsdifferenz':
    '{einheit}: Umrechnungsdifferenz {differenz} {waehrung} in die Währungsumrechnungsrücklage.',
  'meldung.summierung': 'Die Gesellschaften sind summiert, ihr Ergebnis steht je Gesellschaft in der Bilanz.',
  'meldung.ic-differenz.wesentlich':
    'WESENTLICH: {einheit} gegen {partner} — Differenz {differenz} {waehrung}. Die Grenze von {grenze} {waehrung} ist überschritten; die Freigabe prüft die Differenz als nicht geklärt.',
  'meldung.ic-differenz.unwesentlich':
    '{einheit} gegen {partner} — Differenz {differenz} {waehrung}, unterhalb der Wesentlichkeitsgrenze erfolgswirksam erfasst.',
  'meldung.ic-differenz.nur-waehrung':
    '{einheit} gegen {partner} — nur eine Währungsdifferenz von {waehrungsanteil} {waehrung}; sie ist getrennt gebucht und sperrt nichts.',
  'meldung.ic-konto-nicht-abgegrenzt':
    'Diese Konten tragen konzerninterne Bestände, stehen aber in keiner Position dieses Prozesses und bleiben deshalb stehen: {konten}.',
  'meldung.ic-position-ohne-konten':
    'Diesen Positionen ist kein konzerninternes Konto zugeordnet; der Prozess arbeitet an weniger, als ausgewählt wurde: {positionen}.',
  'meldung.handbuch-uebersteuert':
    'Der Prozess „{werkzeug}“ setzt {angabe} auf {wert} — abweichend vom Konzernhandbuch.',
  'meldung.ic-differenzkonto-uneinig':
    'Mehrere Prozesse der Art „{werkzeug}" nennen verschiedene Differenzenkonten ({konten}). Gebucht wird auf {gewaehlt} — das Konto des Prozesses mit dem kleinsten Rang.',
  'meldung.ic-differenzkonto-unbekannt':
    'Das Differenzenkonto {konto} gibt es nicht. Gebucht wird auf {ersatz} aus dem Regelwerk.',
  'meldung.zwischenergebnis':
    '{einheit}: {betrag} {waehrung} Zwischenergebnis aus Lieferungen von {lieferant} eliminiert.',
  'meldung.zwischenergebnis.keine-uv': 'Keine Zwischenergebnisse im Umlaufvermögen gemeldet.',
  'meldung.zwischenergebnis.keine-av': 'Keine Zwischenergebnisse im Anlagevermögen gemeldet.',
  'meldung.einbeziehungsart.quotal': '{einheit} ist als „quotal" gekennzeichnet. CoKo rechnet bisher nur die Vollkonsolidierung; die Gesellschaft bleibt unkonsolidiert stehen.',
  'meldung.einbeziehungsart.equity': '{einheit} ist als „at equity" gekennzeichnet. CoKo rechnet bisher nur die Vollkonsolidierung; die Gesellschaft bleibt unkonsolidiert stehen.',
  'meldung.einbeziehungsart.nicht-einbezogen': '{einheit} ist als „nicht einbezogen" gekennzeichnet. CoKo rechnet bisher nur die Vollkonsolidierung; die Gesellschaft bleibt unkonsolidiert stehen.',
  'meldung.keine-beteiligung': 'Im Kreis steht keine Beteiligung, die zu konsolidieren wäre.',
  'meldung.kein-beteiligungsbuchwert':
    '{mutter} führt keinen Beteiligungsbuchwert für {tochter}. Ohne Buchwert ist kein Unterschiedsbetrag ermittelbar; die Kapitalkonsolidierung unterbleibt für diese Beteiligung.',
  'meldung.fortgeschrieben':
    '{gesellschaft} hat für diesen Monat nichts geliefert. Gerechnet ist mit dem Bestand aus {aus}.',
  'meldung.ohne-lieferung':
    '{gesellschaft} hat für diesen Monat nichts geliefert, und im Geschäftsjahr gibt es keinen Bestand fortzuschreiben. Letzte Lieferung: {aus}. Die Gesellschaft steht mit null im Abschluss.',
  'meldung.nie-geliefert': '{gesellschaft} hat noch nie geliefert und steht mit null im Abschluss.',
  'meldung.nicht-faellig':
    '{gesellschaft}: Lieferung für diesen Monat nicht fällig ({rhythmus}). Sie steht mit null im Abschluss, ohne Befund.',
  // R1806: Beteiligung zum Ende, Kreismitgliedschaft in einem anderen Monat.
  'meldung.zum-ende.mitgliedschaft':
    '{tochter} wird laut Beteiligung zum Ende des Monats {erwerb} einbezogen, die Mitgliedschaft im Konsolidierungskreis beginnt aber nicht in diesem Monat. Bitte gleichen Sie Beteiligung und Konsolidierungskreis an.',
  'meldung.goodwill-keine-abschreibung':
    'In dieser Rechnungslegung wird der Firmenwert nicht planmäßig abgeschrieben (IFRS 3.B63a). Ob er wertgemindert ist, entscheidet der Wertminderungstest — er verlangt einen erzielbaren Betrag und ist deshalb eine Erfassung, keine Rechnung. Erfasst werden kann er noch nicht.',
  'meldung.firmenwert': '{tochter}: Geschäfts- oder Firmenwert {betrag} {waehrung}.',
  'meldung.zwischenergebnis.ohne-schritt':
    '{einheit}: Das gemeldete Zwischenergebnis von {betrag} {waehrung} auf {konto}, geliefert von {lieferant}, steht nicht unter dem Prozess für das Anlagevermögen und ist nicht eliminiert. Im Umlaufvermögen rechnet die Konzernmarge, nicht die Meldung.',
  'meldung.zwischengewinn.ohne-positionen': 'Der Prozess Zwischenergebniseliminierung Umlaufvermögen nennt keine Vorräte und eliminiert nichts.',
  'abbruch.zwischengewinn.marge-fehlt.meldung':
    '{einheit} führt auf {konto} Vorrat mit Partnercode {lieferant}, und der Prozess nennt keine Marge dafür.',
  'abbruch.zwischengewinn.marge-fehlt.weg':
    'Tragen Sie am Prozess Zwischenergebniseliminierung Umlaufvermögen eine Standardmarge ein oder eine Marge für diese Lieferbeziehung.',
  'meldung.kapitalerhoehung.quote':
    '{tochter}: Die Mutter hat auf Kapitalerhöhungen {einzahlung} {waehrung} eingezahlt, ihr Anteil an den Erhöhungen beträgt {anteil} {waehrung}; {rest} {waehrung} bleiben als Beteiligung stehen.',
  'meldung.zukauf-von-anteilen':
    '{tochter}: Zugang auf der Beteiligung von {betrag} {waehrung} ohne Kapitalerhöhung der Tochter — gerechnet als Zukauf von Anteilen.',
  'meldung.unterschiedsbetrag-passivisch':
    '{tochter}: passivischer Unterschiedsbetrag {betrag} {waehrung}.',
  'meldung.nicht-beherrschend':
    '{tochter}: Anteile nicht beherrschender Gesellschafter {betrag} {waehrung} ({quote} Prozent).',
  'meldung.konto-ohne-position':
    'Konto {konto} trägt einen Betrag, ist in der Periode {periode} aber keiner Position zugeordnet. Der Betrag erscheint in keinem Bericht.',
  'meldung.bilanz-geht-auf':
    'Die Konzernbilanz geht auf: Aktiva und Passiva stimmen auf den Cent überein.',
  'meldung.bilanz-geht-nicht-auf':
    'ACHTUNG: Die Konzernbilanz geht um {differenz} {waehrung} nicht auf.',

  /* --- Hinweise zum Lauf --- */
  'hinweis.konto-mehrdeutig':
    'Konto {konto} ist in der Periode {periode} mehreren Positionen zugeordnet. Die Zuordnungszeiträume überschneiden sich. Das Konto bleibt ungerechnet, bis das entschieden ist.',
  'hinweis.konto-unbekannt':
    'Zu Konto {konto} liegen Salden vor, das Konto steht aber nicht im Konzernkontenrahmen.',

  /* --- Abbrüche: was ist, wie viel es ist, was zu tun ist (R145) --- */
  'abbruch.kreis-ohne-einheit.meldung':
    'Zum Konsolidierungskreis gehört in der Periode {periode} keine Gesellschaft.',
  'abbruch.kreis-ohne-einheit.weg':
    'Prüfen Sie unter Konzernstruktur → Konsolidierungskreise, ab welcher Periode die Gesellschaften dem Kreis zugeordnet sind.',
  'abbruch.keine-salden.meldung': 'Für die Periode {periode} liegen keine Salden vor.',
  'abbruch.keine-salden.weg':
    'Liefern Sie zuerst die Salden der Gesellschaften, über Import oder Erfassung.',
  'abbruch.kurs-fehlt.stichtag':
    'Für {waehrung} nach {konzernwaehrung} fehlt in der Periode {periode} der Stichtagskurs.',
  'abbruch.kurs-fehlt.durchschnitt':
    'Für {waehrung} nach {konzernwaehrung} fehlt in der Periode {periode} der Durchschnittskurs.',
  'abbruch.kurs-fehlt.historisch':
    'Für {waehrung} nach {konzernwaehrung} fehlt in der Periode {periode} der historische Kurs.',
  'abbruch.kurs-fehlt.weg':
    'Tragen Sie den Kurs unter Stammdaten → Währungskurse nach und starten Sie den Lauf erneut.',
  'abbruch.erstkonsolidierung-fehlt.meldung':
    'Für die Beteiligung von {mutter} an {tochter} fehlen die Erstkonsolidierungswerte.',
  'abbruch.erstkonsolidierung-fehlt.weg':
    'Tragen Sie unter Konzernstruktur → Beteiligungsregister ein, wie hoch das Eigenkapital der Tochter am Erwerbstag war. Ohne diese Angabe lässt sich der Unterschiedsbetrag nicht ermitteln.',
  'abbruch.kreis-unbekannt.meldung': 'Den Konsolidierungskreis „{kreis}" gibt es nicht.',
  'abbruch.kreis-unbekannt.weg':
    'Wählen Sie einen Kreis aus der Liste unter Konzernstruktur → Konsolidierungskreise.',
  'abbruch.ledger-unbekannt.meldung': 'Die Bewertungsebene „{ledger}" gibt es nicht.',
  'abbruch.ledger-unbekannt.weg': 'Wählen Sie eine Bewertungsebene aus der Liste unter Stammdaten → Bewertungsebenen.',
  /* R851 — In Fremdwährung bucht der Januarlauf den Jahreswechsel mit dem Euro-Wert aus dem Dezemberlauf. */
  'abbruch.jahreswechsel-ohne-dezember.meldung':
    'Für {einheit} gibt es in {periode} einen Jahreswechsel in Fremdwährung, aber keinen Dezemberlauf des Vorjahres, aus dem der Euro-Wert des Vorjahresergebnisses kommt.',
  'abbruch.jahreswechsel-ohne-dezember.weg':
    'Das Vorjahresergebnis wird auf Ergebniskonto und Rücklagen mit dem Wert eingestellt, der im Dezember in der Konzernbilanz stand — geraten wird er nicht. Rechnen Sie zuerst den Dezember des Vorjahres für diesen Konsolidierungskreis.',
  'abbruch.kapitalerhoehung.kurs.meldung':
    'Für die Kapitalerhöhung von {einheit} auf {konto} in {periode} fehlt die Zeile in der Tabelle der historischen Kurse.',
  'abbruch.kapitalerhoehung.kurs.weg':
    'Tragen Sie die Kapitalerhöhung mit ihrem Kurs in die Tabelle der historischen Kurse der Gesellschaft ein und rechnen Sie neu.',
  'abbruch.unterschiedsbetrag-konto.meldung':
    'Für {tochter} entsteht ein passivischer Unterschiedsbetrag, aber {konto} ist weder ein Passivkonto noch ein Ertragskonto.',
  'abbruch.unterschiedsbetrag-konto.weg':
    'Tragen Sie im Konzernhandbuch beim passivischen Unterschiedsbetrag ein Passivkonto (HGB) oder ein Ertragskonto (IFRS) ein — oder am Schritt Kapitalkonsolidierung, wenn er das Konto übersteuert.',
  'abbruch.unterschiedsbetrag-kontoart.hgb.meldung':
    'Der passivische Unterschiedsbetrag soll auf {konto} gebucht werden. In einer Bewertungsebene nach {rechnungslegung} steht er als Posten nach dem Eigenkapital und braucht ein Passivkonto.',
  'abbruch.unterschiedsbetrag-kontoart.ifrs.meldung':
    'Der passivische Unterschiedsbetrag soll auf {konto} gebucht werden. In einer Bewertungsebene nach {rechnungslegung} ist er Ertrag und braucht ein Ertragskonto.',
  'abbruch.unterschiedsbetrag-kontoart.weg':
    'Wählen Sie im Konzernhandbuch beim passivischen Unterschiedsbetrag ein Konto der passenden Art — oder am Schritt Kapitalkonsolidierung, wenn er das Konto übersteuert.',
  'abbruch.kein-eigenkapital.meldung':
    'Für {tochter} steht in {stichtag} kein Eigenkapital in der Bewertungsebene des Abschlusses.',
  'abbruch.kein-eigenkapital.weg':
    'Gegen dieses Eigenkapital wird der Beteiligungsbuchwert verrechnet. Prüfen Sie, ob für diesen Monat Zahlen der Gesellschaft geliefert sind — und ob der Einbeziehungszeitpunkt im Beteiligungsregister stimmt (zu Beginn der Periode heisst: der Stand des Vormonats gilt).',
  'abbruch.kapitalkonsolidierung.vormonat-fehlt.meldung':
    'Für {tochter} fehlt der Lauf von {vormonat}. Die Kapitalkonsolidierung trägt den Stand des Vormonats vor und rechnet ihn nicht neu.',
  'abbruch.kapitalkonsolidierung.vormonat-fehlt.weg':
    'Rechnen Sie zuerst {vormonat}. Wurde die Tochter vor ihrem ersten Monat in CoKo erworben, erfassen Sie im Beteiligungsregister den Aufsatz.',
  'abbruch.bestand.vormonat-fehlt.meldung':
    'Für die {schritt} fehlt der Lauf von {vormonat}. Sie trägt ihre Bilanzseite aus dem Vormonat vor und rechnet sie nicht neu.',
  'abbruch.bestand.vormonat-fehlt.weg': 'Rechnen Sie zuerst {vormonat}.',
  'abbruch.kapitalkonsolidierung.buchwert-bewegt.meldung':
    'Der Beteiligungsbuchwert von {mutter} an {tochter} hat sich in diesem Monat um {betrag} {waehrung} mit einer Bewegungsart bewegt, die CoKo keinem Ereignis zuordnet.',
  'abbruch.kapitalkonsolidierung.buchwert-bewegt.weg':
    'Ein Zugang ist ein Zukauf, ein Abgang ein Teilverkauf, eine Ab- oder Zuschreibung wird zurückgenommen. Andere Bewegungsarten — und einen Abgang oder eine Abschreibung im Monat einer Kapitalerhöhung — rechnet CoKo noch nicht. Prüfen Sie die Lieferung der Mutter oder konsolidieren Sie die Tochter manuell.',
  'abbruch.aufsatz.angabe-fehlt.meldung': 'Für den Aufsatz von {tochter} fehlt die Angabe „{angabe}“.',
  'abbruch.aufsatz.angabe-fehlt.weg': 'Tragen Sie sie im Beteiligungsregister bei der Beteiligung ein und rechnen Sie neu.',
  'abbruch.kapitaltransaktion.vor-dem-start.meldung':
    'Im Beteiligungsregister steht für {mutter} an {tochter} eine Kapitaltransaktion im {transaktion} — nicht nach dem ersten Monat in CoKo, {start}.',
  'abbruch.kapitaltransaktion.vor-dem-start.weg':
    'Bis zum Erwerb oder Aufsatz steckt jede Transaktion im Stand, und der Kapitalanteil an der Beteiligung gilt zu diesem Zeitpunkt. Löschen Sie den Eintrag oder berichtigen Sie den Monat.',
  'abbruch.kapitaltransaktion.erhoehung-ohne-eintrag.meldung':
    '{tochter} liefert im {monat} eine Kapitalerhöhung von {betrag} {waehrung}; im Beteiligungsregister steht dazu keine.',
  'abbruch.kapitaltransaktion.erhoehung-ohne-eintrag.weg':
    'Tragen Sie sie bei der Beteiligung unter Kapitaltransaktionen ein: Monat, Zeitpunkt und Kapitalanteil danach — bei einer Erhöhung im Verhältnis der Anteile der bisherige.',
  'abbruch.kapitaltransaktion.zugang-ohne-eintrag.meldung':
    '{mutter} liefert im {monat} einen Zugang von {betrag} {waehrung} auf ihre Beteiligung an {tochter}; im Beteiligungsregister steht dazu kein Zukauf.',
  'abbruch.kapitaltransaktion.zugang-ohne-eintrag.weg':
    'Tragen Sie den Zukauf bei der Beteiligung unter Kapitaltransaktionen ein — oder berichtigen Sie die Lieferung der Mutter, wenn der Zugang in einen anderen Monat gehört.',
  'abbruch.kapitaltransaktion.abgang-ohne-eintrag.meldung':
    '{mutter} liefert im {monat} einen Abgang von {betrag} {waehrung} auf ihre Beteiligung an {tochter}; im Beteiligungsregister steht dazu kein Teilverkauf.',
  'abbruch.kapitaltransaktion.abgang-ohne-eintrag.weg':
    'Tragen Sie den Teilverkauf bei der Beteiligung unter Kapitaltransaktionen ein — oder berichtigen Sie die Lieferung der Mutter, wenn der Abgang in einen anderen Monat gehört.',
  'abbruch.kapitaltransaktion.eintrag-ohne-bewegung.meldung':
    'Im Beteiligungsregister steht für {tochter} im {monat} ein Eintrag „{art}“; die Lieferung zeigt dazu keine Bewegung.',
  'abbruch.kapitaltransaktion.eintrag-ohne-bewegung.weg':
    'Den Betrag nimmt CoKo aus der Lieferung: beim Zukauf den Zugang, beim Teilverkauf den Abgang auf der Beteiligung der Mutter, bei der Kapitalerhöhung die Erhöhung bei der Tochter. Prüfen Sie die Lieferung und den Monat des Eintrags.',
  'abbruch.kapitaltransaktion.erhoehung-quote.meldung':
    'Die Kapitalerhöhung von {tochter} im {monat} ändert den Kapitalanteil von {vorher} auf {danach} Prozent.',
  'abbruch.kapitaltransaktion.erhoehung-quote.weg':
    'Eine Kapitalerhöhung, die den Kapitalanteil ändert, rechnet CoKo noch nicht. Konsolidieren Sie die Tochter bis dahin manuell.',
  'abbruch.kapitaltransaktion.quote.meldung':
    'Der Eintrag „{art}“ für {tochter} im {monat} führt den Kapitalanteil von {vorher} auf {danach} Prozent.',
  'abbruch.kapitaltransaktion.quote.weg':
    'Ein Zukauf hebt den Kapitalanteil, ein Teilverkauf senkt ihn. Berichtigen Sie die Art oder den Kapitalanteil danach.',
  'abbruch.kapitaltransaktion.zukauf-passiv.meldung':
    'Der Kaufpreis für weitere Anteile an {tochter}, {kaufpreis} {waehrung}, liegt unter dem übernommenen Anteil am Ausgleichsposten, {anteil} {waehrung}.',
  'abbruch.kapitaltransaktion.zukauf-passiv.weg':
    'Einen passivischen Unterschiedsbetrag aus einem Zukauf rechnet CoKo noch nicht. Konsolidieren Sie die Tochter bis dahin manuell.',
  'abbruch.kapitalkonsolidierung.ohne-positionen.meldung':
    'Die Kapitalkonsolidierung hat keine Positionen für Beteiligungen oder Eigenkapital gewählt.',
  'abbruch.kapitalkonsolidierung.ohne-positionen.weg':
    'Wählen Sie unter [[Konzernabschlüsse]] am Prozess Kapitalkonsolidierung die Positionen der Beteiligungen und des Eigenkapitals. Danach rechnen Sie neu.',
  'abbruch.differenzkonto-fehlt.meldung':
    'Dem Prozess „{werkzeug}“ ist kein Differenzenkonto zugewiesen.',
  'abbruch.differenzkonto-fehlt.weg':
    'Tragen Sie es unter [[Konzernabschlüsse]] am Prozess ein, Schaltfläche „Abgrenzen“. Dorthin bucht der Prozess, was nach der Ausbuchung beider Seiten stehen bleibt — ohne dieses Konto wüsste er nicht, wohin damit.',
  'abbruch.differenzkonto-unbekannt.meldung':
    'Das Differenzenkonto {konto} des Prozesses „{werkzeug}“ gibt es nicht.',
  'abbruch.differenzkonto-waehrung-fehlt.meldung':
    'Dem Prozess „{werkzeug}“ ist kein Differenzenkonto für Währungsdifferenzen zugewiesen.',
  'abbruch.differenzkonto-waehrung-fehlt.weg':
    'Tragen Sie es unter [[Konzernabschlüsse]] am Prozess ein, Schaltfläche „Abgrenzen“. Es darf dasselbe Konto sein wie das Differenzenkonto.',
  'abbruch.differenzkonto-waehrung-unbekannt.meldung':
    'Das Differenzenkonto für Währungsdifferenzen {konto} des Prozesses „{werkzeug}“ gibt es nicht.',
  'abbruch.differenzkonto-unbekannt.weg':
    'Wählen Sie unter Abschluss → Konsolidierungsschritte ein vorhandenes Konto. Vermutlich wurde es umbenannt oder stillgelegt, nachdem der Prozess eingerichtet war.',
  'abbruch.keine-regelwerksausgabe.meldung':
    'Für die Periode {periode} ist keine Regelwerksausgabe hinterlegt.',
  'abbruch.keine-regelwerksausgabe.weg':
    'Ohne Ausgabe lässt sich kein Abschluss rechnen, weil sie Teil des Nachweises ist. Legen Sie unter Regelwerk eine Ausgabe an.',
  'abbruch.kein-regelwerkskapitel.meldung':
    'Die Regelwerksausgabe {ausgabe} hat kein Kapitel für {rechnungslegung}.',
  'abbruch.kein-regelwerkskapitel.weg':
    'Jede Rechnungslegung, nach der gerechnet werden soll, braucht in der geltenden Ausgabe ihre eigenen Stellschrauben — Rundung, Toleranzen, Verrechnungskonten.',
  'abbruch.lauf-unbekannt.meldung': 'Diese Rechnung gibt es nicht mehr.',
  'abbruch.lauf-unbekannt.weg': 'Wählen Sie einen Lauf aus der Liste der gerechneten Abschlüsse.',
  'abbruch.berichtsart.meldung': 'Die Berichtsart „{art}" gibt es nicht.',
  'abbruch.berichtsart.weg': 'Erlaubt sind Bilanz und Gewinn- und Verlustrechnung.',
  'abbruch.achse.meldung': 'Die Aufrissachse „{achse}" gibt es nicht.',

  /* --- Befunde der Freigabeprüfung (2.13) --- */
  'befund.bereits-eingefroren.meldung': 'Der Abschluss ist bereits „{status}" und damit endgültig.',
  'befund.bereits-eingefroren.weg':
    'Eine spätere Korrektur ist eine rückwirkende Anpassung als zweiter Lauf auf derselben Periode, nicht die Rücknahme der Freigabe.',
  'befund.veraltet-regelwerk.meldung':
    'Für „{abschluss}“ gilt inzwischen eine neue Ausgabe des Konzernhandbuchs.',
  'befund.veraltet-regelwerk.weg':
    'Wechseln Sie in den Bereich [[Konzernabschlüsse]] und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.veraltet-daten.meldung':
    'Von mindestens einer einbezogenen Gesellschaft sind Einzelabschlusswerte aktueller als die Daten von „{abschluss}“.',
  'befund.veraltet-daten.weg':
    'Wechseln Sie in den Bereich [[Konzernabschlüsse]] und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.veraltet-vorperiode.meldung':
    'Der Vormonat von „{abschluss}“ wurde neu gerechnet; die Anfangsbestände dieses Monats sind damit nicht mehr aktuell.',
  'befund.veraltet-frueherer-monat.meldung':
    'Der {periode} hat seit seinem Lauf neue Daten; die Vorträge dieses Monats sind damit nicht mehr aktuell.',
  'befund.veraltet-frueherer-monat.weg':
    'Wechseln Sie in den Bereich [[Konzernabschlüsse]] und rechnen Sie ab diesem Monat Monat für Monat neu. Dadurch verschwindet diese Meldung.',
  'befund.veraltet-vorperiode.weg':
    'Wechseln Sie in den Bereich [[Konzernabschlüsse]] und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.bilanz-geht-nicht-auf.meldung':
    'Die Konzernbilanz von „{abschluss}“ geht nicht auf: Aktiva und Passiva stimmen nicht überein.',
  'befund.bilanz-geht-nicht-auf.weg':
    'Öffnen Sie die [[Konzernbilanz]] und suchen Sie die Herkunft der Differenz. Geht sie auf, verschwindet diese Meldung.',
  'befund.anhang-unvollstaendig.meldung':
    '{anzahl} Pflichtangaben des Anhangs zum Geschäftsjahresende fehlen, sind veraltet oder stammen aus einer anderen Rechnung.',
  'befund.anhang-unvollstaendig.meldung.eins':
    'Eine Pflichtangabe des Anhangs zum Geschäftsjahresende fehlt, ist veraltet oder stammt aus einer anderen Rechnung.',
  'befund.anhang-unvollstaendig.weg':
    'Legen Sie unter [[Vollständigkeit]] jede Angabe an oder setzen Sie sie ausdrücklich auf „Fehlanzeige“. Mit der letzten Angabe verschwindet diese Meldung.',
  'befund.anhang-wahlrechte.meldung':
    '{anzahl} Wahlrechte des Anhangs sind unbeantwortet oder noch nicht übersetzt.',
  'befund.anhang-wahlrechte.meldung.eins':
    'Ein Wahlrecht des Anhangs ist unbeantwortet oder noch nicht übersetzt.',
  'befund.anhang-wahlrechte.weg':
    'Beantworten Sie sie im [[Anhangsbuch]] oder übergehen Sie sie mit Begründung. Die Freigabe sperren sie nicht.',
  'befund.ic-ohne-partner.meldung':
    '{anzahl} Beträge auf konzerninternen Konten haben keinen Partner; die Schuldenkonsolidierung rechnet sie nicht.',
  'befund.ic-ohne-partner.meldung.eins':
    'Ein Betrag auf einem konzerninternen Konto hat keinen Partner; die Schuldenkonsolidierung rechnet ihn nicht.',
  'befund.ic-ohne-partner.weg':
    'Tragen Sie den Partner mit dem Reporting Package oder einer Handerfassung nach. Jeden Betrag zeigt die IC-Abstimmung unter [[Konzernabschlüsse]] in der Liste „Ohne Partner“. Mit dem letzten Partner verschwindet diese Meldung.',
  'befund.beteiligung-ohne-partner.meldung':
    '{anzahl} Beteiligungen haben keine gehaltene Gesellschaft; für sie unterbleibt die Kapitalkonsolidierung.',
  'befund.beteiligung-ohne-partner.meldung.eins':
    'Eine Beteiligung hat keine gehaltene Gesellschaft; für sie unterbleibt die Kapitalkonsolidierung.',
  'befund.beteiligung-ohne-partner.ohnebuchwert':
    '{anzahl} Beteiligungen ohne Buchwert: {mutter} führt keinen Beteiligungsbuchwert für {tochter}. Die Kapitalkonsolidierung unterbleibt für sie.',
  'befund.beteiligung-ohne-partner.ohnebuchwert.eins':
    'Eine Beteiligung ohne Buchwert: {mutter} führt keinen Beteiligungsbuchwert für {tochter}. Die Kapitalkonsolidierung unterbleibt für sie.',
  'befund.beteiligung-ohne-partner.weg':
    'Tragen Sie die gehaltene Gesellschaft als Partner nach — mit dem Reporting Package oder einer Handerfassung. Die Beträge zeigt die IC-Abstimmung unter [[Konzernabschlüsse]] in der Liste „Ohne Partner“.',
  'befund.ohne-lieferung.meldung':
    '{anzahl} Gesellschaften haben für diesen Monat nicht geliefert, obwohl die Lieferung fällig war: {gesellschaften}.',
  'befund.ohne-lieferung.meldung.eins':
    '{gesellschaften} hat für diesen Monat nicht geliefert, obwohl die Lieferung fällig war.',
  'befund.ohne-lieferung.luecke.meldung':
    '{anzahl} fällige Lieferungen fehlen seit Beginn des Geschäftsjahres, die erste von {gesellschaft} für {monat}: {gesellschaften}.',
  'befund.ohne-lieferung.luecke.meldung.eins': 'Lieferung fehlt: {gesellschaft}, {monat}.',
  'befund.ohne-lieferung.luecke.eine.meldung':
    '{gesellschaft}: {anzahl} fällige Lieferungen fehlen seit Beginn des Geschäftsjahres, die erste für {monat}.',
  'befund.ohne-lieferung.begruendet.meldung':
    '{anzahl} fehlende Lieferungen sind bei einer früheren Freigabe begründet, die erste von {gesellschaft} für {monat}: „{begruendung}“ ({anwender}, {am}).',
  'befund.ohne-lieferung.begruendet.meldung.eins':
    'Lieferung fehlt: {gesellschaft}, {monat} — begründet von {anwender} am {am}: „{begruendung}“',
  'befund.ohne-lieferung.weg':
    'Lesen Sie ihre Lieferung unter [[Datenimporte]] ein und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.kfr-umgliederung-nicht-null.meldung': '{anzahl} Umgliederungen im Eigenkapital ergeben nicht null.',
  'befund.kfr-umgliederung-nicht-null.meldung.eins': 'Eine Umgliederung im Eigenkapital ergibt nicht null.',
  'befund.kfr-umgliederung-nicht-null.weg':
    'Klappen Sie in der [[Kapitalflussrechnung]] die Abstimmung gegen die Bewegungsbilanz auf: Die Spalte „Umgliederung im Eigenkapital“ zeigt beide Seiten. Meist trägt die fehlende Seite eine andere Bewegungsart; ist sie berichtigt, verschwindet diese Meldung.',
  'befund.eintrittskurs-nachtragen.meldung':
    'Für {anzahl} Gesellschaften fehlt der Eintrittskurs: {gesellschaften}. Bis dahin rechnet „{abschluss}“ mit dem Stichtagskurs des Eintrittsmonats.',
  'befund.eintrittskurs-nachtragen.meldung.eins':
    'Für {gesellschaften} fehlt der Eintrittskurs. Bis dahin rechnet „{abschluss}“ mit dem Stichtagskurs des Eintrittsmonats.',
  // R1477: Der Abgleich des Ergebnisjahres.
  'befund.ergebnisjahr-im-eigenkapital.meldung':
    'Das Ergebnis {jahr} von {gesellschaft} steht im Eigenkapital zu einem anderen Kurs, als es aus dem Jahresergebnis ausgebucht wurde.',
  'befund.ergebnisjahr-im-eigenkapital.weg':
    'Prüfen Sie unter [[Historische Kurse]] die Zeilen des Ergebnisjahres: Ihr Kurs ist der des Jahresergebnisses. Stimmt er, verschwindet diese Meldung.',
  // R1400, R1401: Die konzerninterne Differenz über der Toleranz.
  'befund.zwischenergebnis-ohne-schritt.meldung':
    '{anzahl} Zwischenergebnisse sind keinem Schritt zugeordnet und deshalb nicht eliminiert, darunter bei {einheit}.',
  'befund.zwischenergebnis-ohne-schritt.meldung.eins':
    'Ein Zwischenergebnis von {einheit} ist keinem Schritt zugeordnet und deshalb nicht eliminiert.',
  'befund.zwischenergebnis-ohne-schritt.weg':
    'Nehmen Sie unter [[Konzernabschlüsse]] die Position des Kontos in den Prozess „Zwischenergebniseliminierung Umlaufvermögen“ oder „Anlagevermögen“ auf und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.einzahlung-passt-nicht-zur-quote.meldung':
    'Die Einzahlung in {tochter} passt nicht zum Kapitalanteil: {rest} {waehrung} bleiben als Beteiligung stehen.',
  'befund.einzahlung-passt-nicht-zur-quote.weg':
    'Meist hat sich der Kapitalanteil geändert: Tragen Sie ihn im [[Beteiligungsregister]] ein und rechnen Sie den Konzernabschluss neu. Stimmt er, stimmen Sie die Lieferungen von Mutter und Tochter ab.',
  'befund.zukauf-von-anteilen.meldung':
    'Auf der Beteiligung an {tochter} steht ein Zugang von {betrag} {waehrung} ohne Kapitalerhöhung; er ist als Erwerb mit Firmenwert gerechnet.',
  'befund.zukauf-von-anteilen.weg':
    'War es eine Kapitalerhöhung, liefert die Tochter sie mit der Bewegungsart „Kapitalerhöhung“. War es ein Zukauf, tragen Sie den neuen Kapitalanteil im [[Beteiligungsregister]] ein und begründen ihn bei der Freigabe.',
  'befund.ic-differenz-nicht-geklaert.meldung':
    '{anzahl} konzerninterne Differenzen sind nicht geklärt, darunter {gesellschaft} gegen {partner}.',
  'befund.ic-differenz-nicht-geklaert.meldung.eins':
    'Die konzerninterne Differenz {gesellschaft} gegen {partner} ist nicht geklärt.',
  'befund.ic-differenz-nicht-geklaert.weg':
    'Stimmen Sie die Lieferungen beider Gesellschaften ab; jede Differenz zeigt die IC-Abstimmung unter [[Konzernabschlüsse]]. Ist eine Differenz begründet, stellen Sie die Prüfung unter „Prüfregeln festlegen“ auf „Warnung“ und begründen sie bei der Freigabe.',
  'befund.eintrittskurs-nachtragen.weg':
    'Tragen Sie unter [[Konsolidierungskreise]] bei den Mitgliedern den Eintrittskurs ein oder übernehmen Sie den Stichtagskurs, und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'befund.kurse-aktualisieren.meldung':
    '{anzahl} Kurse dieses Monats sind nicht für ihn festgestellt; „{abschluss}“ rechnet mit ihnen.',
  'befund.kurse-aktualisieren.meldung.eins':
    'Ein Kurs dieses Monats ist nicht für ihn festgestellt; „{abschluss}“ rechnet mit ihm.',
  'befund.kurse-aktualisieren.weg':
    'Tragen Sie unter [[Währungskurse]] den Kurs nach dem Monatsende neu ein oder rufen Sie ihn bei der Zentralbank ab, und rechnen Sie den Konzernabschluss neu. Dadurch verschwindet diese Meldung.',
  'kurs.befund.fortgeschrieben':
    'Der Kurs {waehrung} ({kursart}) für {periode} ist aus einem anderen Monat übernommen.',
  'kurs.befund.gerechnet':
    'Der Kurs {waehrung} ({kursart}) für {periode} ist aus anderen Kursen gerechnet und nicht für diesen Monat festgestellt.',
  'kurs.befund.frueh-manuell':
    'Der Kurs {waehrung} ({kursart}) für {periode} ist am {datum} von Hand eingetragen, vor dem Monatsende.',
  'kurs.befund.frueh-datei':
    'Der Kurs {waehrung} ({kursart}) für {periode} stammt aus einer Kursdatei, die am {datum} vor dem Monatsende eingelesen wurde.',
  'kurs.befund.nicht-letzter-handelstag':
    'Der Kurs {waehrung} ({kursart}) für {periode} stammt vom {datum}, nicht vom letzten Handelstag des Monats.',
  'kurs.befund.anderer-monat': 'Der Kurs {waehrung} ({kursart}) für {periode} stammt aus {quelle}.',
  'befund.kfr-geht-nicht-auf.meldung':
    'Die Kapitalflussrechnung von „{abschluss}“ geht nicht auf: Ein Betrag lässt sich nicht aus Bewegungen herleiten.',
  'befund.kfr-geht-nicht-auf.weg':
    'Klappen Sie in der [[Kapitalflussrechnung]] die Abstimmung gegen die Bewegungsbilanz auf: Die Spalte „Abweichung“ nennt das Konto. Meist fehlt der Bewegungsart eine Zuordnungsregel; ist sie ergänzt, verschwindet diese Meldung.',
  'befund.spiegel.nichtauf': '{spiegel} geht bei {anzahl} Konten nicht auf, darunter bei {einheit}.',
  'befund.spiegel.nichtauf.eins': '{spiegel} geht bei einem Konto von {einheit} nicht auf.',
  'befund.spiegel.offen':
    '{spiegel} ist bei {anzahl} Konten unvollständig — Beträge stehen auf „nicht zugeordnet“, darunter bei {einheit}.',
  'befund.spiegel.offen.eins':
    '{spiegel} ist bei einem Konto von {einheit} unvollständig — ein Betrag steht auf „nicht zugeordnet“.',
  'befund.spiegel.beides':
    '{spiegel} geht bei {nichtauf} Konten nicht auf und ist bei {offen} Konten unvollständig, darunter bei {einheit}.',
  'befund.spiegel.weg':
    'Liefern Sie die fehlenden Bewegungsarten nach oder ordnen Sie sie zu; die betroffenen Konten zeigt [[{seite}]]. Dann verschwindet diese Meldung. Wer den Spiegel nicht braucht, stellt ihn unter „Prüfregeln festlegen“ auf „Nicht prüfen“.',

  /* --- Prüfregeln (2.13 Abschnitt 13, R910–R914) --- */
  'pruefregeln.titel': 'Prüfregeln festlegen',
  'pruefregeln.ab': 'Gültig ab',
  'pruefregeln.ohnerecht':
    'Sie sehen, wie streng die Freigabe prüft. Ändern darf es, wer das Recht „Prüfregeln festlegen" hat.',
  'pruefregeln.spalte.pruefung': 'Prüfung',
  'pruefregeln.spalte.stufe': 'Strenge',
  'pruefregeln.spalte.festgelegt': 'Festgelegt',
  'pruefregeln.stufe.aus': 'Nicht prüfen',
  'pruefregeln.stufe.hinweis': 'Hinweis',
  'pruefregeln.stufe.warnung': 'Warnung',
  'pruefregeln.stufe.sperrt': 'Blockiert die Freigabe',
  'pruefregeln.ausgeliefert': 'Ausgeliefert',
  'pruefregeln.seit': 'seit {periode} · {wer}, {am}',
  'pruefregeln.ohnebewegungsarten': 'Noch keine Bewegungsarten angelegt — es gibt nichts zu prüfen.',
  'pruefregeln.ungeprueft': 'Ungeprüft seit {periode} — festgelegt von {wer}.',
  'pruefung.bilanz-geht-nicht-auf': 'Die Bilanz geht nicht auf',
  'pruefung.veraltet-regelwerk': 'Der Abschluss ist mit einer alten Fassung des Regelwerks gerechnet',
  'pruefung.veraltet-daten': 'Seit der Rechnung sind Daten eingegangen',
  'pruefung.veraltet-vorperiode': 'Die Vorperiode wurde neu gerechnet',
  'pruefung.veraltet-frueherer-monat': 'Ein früherer Monat des Geschäftsjahres hat seit seinem Lauf neue Daten',
  'pruefung.anhang-unvollstaendig': 'Zum Geschäftsjahresende fehlt eine Pflichtangabe des Anhangs',
  'pruefung.anhang-wahlrechte': 'Zum Geschäftsjahresende ist ein Wahlrecht des Anhangs unbeantwortet',
  'pruefung.ic-ohne-partner': 'Auf einem konzerninternen Konto steht ein Betrag ohne Partner',
  'pruefung.beteiligung-ohne-partner': 'Eine Beteiligung steht ohne gehaltene Gesellschaft da',
  'pruefung.ohne-lieferung': 'Eine Gesellschaft des Kreises hat für den Monat nichts geliefert',
  'pruefung.kfr-geht-nicht-auf': 'Die Kapitalflussrechnung geht nicht auf',
  'pruefung.kfr-umgliederung-nicht-null': 'Die Umgliederung im Eigenkapital ergibt nicht null',
  'pruefung.kurse-aktualisieren': 'Kurse aktualisieren: Ein Kurs des Monats ist angenommen oder vor dem Monatsende festgestellt',
  'pruefung.eintrittskurs-nachtragen': 'Eintrittskurs nachtragen: Historische Kurse lagen beim Eintritt nicht vor',
  'pruefung.ic-differenz-nicht-geklaert': 'Konzerninterne Differenz nicht geklärt: Zwei Gesellschaften weichen über die Toleranz voneinander ab',
  'pruefung.zwischenergebnis-ohne-schritt': 'Zwischenergebnis keinem Schritt zugeordnet: Das Konto einer Meldung steht unter keiner Position der beiden Schritte',
  'pruefung.einzahlung-passt-nicht-zur-quote': 'Einzahlung passt nicht zur Quote: Die Mutter zahlt auf eine Kapitalerhöhung mehr oder weniger ein, als ihrem Anteil entspricht',
  'pruefung.zukauf-von-anteilen': 'Zukauf von Anteilen: Zugang auf der Beteiligung ohne Kapitalerhöhung der Tochter',
  'pruefung.ergebnisjahr-im-eigenkapital': 'Das Ergebnis des Vorjahres steht im Eigenkapital anders, als es aus dem Jahresergebnis ausgebucht wurde',
  'pruefung.spiegel-anlagen': 'Der Anlagenspiegel ist unvollständig oder geht nicht auf',
  'pruefung.spiegel-eigenkapital': 'Der Eigenkapitalspiegel ist unvollständig oder geht nicht auf',
  'pruefung.spiegel-sonderposten': 'Der Sonderpostenspiegel ist unvollständig oder geht nicht auf',
  'pruefung.spiegel-rueckstellungen': 'Der Rückstellungsspiegel ist unvollständig oder geht nicht auf',
  'pruefung.spiegel-verbindlichkeiten': 'Der Kreditspiegel ist unvollständig oder geht nicht auf',
  'pruefung.inaktiv-im-kreis': 'Die Gesellschaft ist inaktiv, steht aber noch im Kreis',
  /* R1644 — Ein umgehängtes Konto erbt die Kontengruppe der Konten dort, wenn sie eindeutig ist. */
  'pruefung.kontengruppe-uneins': 'Unter einer Position hängen Konten verschiedener Kontengruppen',
  'pruefung.historischer-kurs-fehlt': 'Zu Bewegungen fehlt eine Zeile der historischen Kurse',
  'pruefung.monat-nicht-konsolidiert': 'Ein Monat des Zeitraums ist nicht konsolidiert',
  'pruefung.konsolidierungshinweis': 'Ein Konsolidierungsschritt hat einen Hinweis gegeben',
  'pruefung.ic-differenz-nicht-geklaert/schuldenkonsolidierung':
    'Konzerninterne Differenz bei Forderungen und Verbindlichkeiten nicht geklärt',
  'pruefung.ic-differenz-nicht-geklaert/aufwandertragkonsolidierung':
    'Konzerninterne Differenz bei Aufwand und Ertrag nicht geklärt',
  'pruefung.ic-differenz-nicht-geklaert/dividendenkonsolidierung':
    'Konzerninterne Differenz bei der Ausschüttung nicht geklärt',
  'pruefung.spiegel.anlagen': 'Der Anlagenspiegel',
  'pruefung.spiegel.eigenkapital': 'Der Eigenkapitalspiegel',
  'pruefung.spiegel.sonderposten': 'Der Sonderpostenspiegel',
  'pruefung.spiegel.rueckstellungen': 'Der Rückstellungsspiegel',
  'pruefung.spiegel.verbindlichkeiten': 'Der Kreditspiegel',
  'freigabe.begruendung': 'Warum trotzdem freigeben? Pflicht — der Satz steht danach beim Prüfer.',
  'freigabe.ungeprueft': 'Nicht geprüft: {liste}.',
  'uebergangene.leer': 'Bei keiner Freigabe wurde bisher eine Prüfung übergangen, und jede Prüfung lief.',
  'uebergangene.ohnekommentar': 'Ohne Kommentar übergangen',
  'uebergangene.spalte.abschluss': 'Abschluss',
  'uebergangene.spalte.befund': 'Befund',
  'uebergangene.spalte.begruendung': 'Begründung',
  'uebergangene.wer': '{wer}, {am}',
  'uebergangene.nichtgeprueft': 'Nicht geprüft',
  'abbruch.pruefregel-unbekannt.meldung': 'Die Prüfung „{pruefung}" gibt es nicht.',
  'abbruch.pruefregel-unbekannt.weg': 'Wählen Sie eine Prüfung aus der Liste „Prüfregeln festlegen".',
  'abbruch.pruefregel-untergrenze.meldung': '„{pruefung}" stellt einen Widerspruch fest und lässt sich nicht abschalten.',
  'abbruch.pruefregel-untergrenze.weg':
    'Stellen Sie die Prüfung auf „Warnung“: Sie blockiert dann nicht mehr, wird bei der Freigabe aber mit Begründung übergangen.',
  'abbruch.pruefregel-periode.meldung': '„{periode}" ist keine Periode.',
  'abbruch.pruefregel-periode.weg': 'Wählen Sie die Periode, ab der die Stufe gelten soll.',

  /* --- Änderungsnachweis --- */
  'nachweis.pruefung.leer': 'Es liegen noch keine Einträge vor.',
  'nachweis.pruefung.unversehrt':
    'Alle {anzahl} Einträge sind unversehrt. Die Kette ist von Anfang bis Ende nachgerechnet.',
  'nachweis.pruefung.kette-bricht':
    'Die Kette bricht bei Eintrag {eintrag}. Ab dieser Stelle stimmt der Nachweis nicht mehr mit den Daten überein.',

  /* ==========================================================================
     Stammdatenpflege (7.4, R235–R246)

     Die Feld- und Wertnamen stehen hier und nicht in der Datenbank: Ein
     `CHECK` trägt eine Bedeutung, kein Wort. Wer sie in der Datenbank
     beschriftete, hätte die Beschriftung an einem Ort, an dem sie niemand
     übersetzen kann.
     ========================================================================== */

  /* --- Die Gliederung umbauen (7.4 Abschnitt 12, R319–R322) --- */
  'gliederung.keinplan':
    'Für diese Bewertungsebene ist kein Positionsplan hinterlegt — ohne Plan gibt es keine Gliederung, die sich umbauen ließe.',
  'gliederung.plan': 'Positionsplan',
  'gliederung.abperiode': 'Gültig ab',
  'gliederung.korrektur': 'Das war schon immer so',
  'gliederung.umgliederung.erlaeuterung':
    'Was Sie hier ziehen, gilt ab {periode}. Die bisherige Gliederung bleibt für die Perioden davor stehen — ein Bericht über den Januar zeigt den Januar.',
  'gliederung.korrektur.erlaeuterung':
    'Die Zeile wird berichtigt, nicht umgegliedert: Die Gliederung gilt dann für alle Perioden. Freigegebene Abschlüsse bleiben unberührt, sie lesen auf ihrem Stand.',
  'gliederung.gegriffen': '{gegenstand} ist gegriffen — jetzt das Ziel anklicken.',
  'gliederung.loslassen': 'loslassen',
  'gliederung.greifen': 'Greifen — ziehen oder anklicken',
  'gliederung.hierunter': 'hierunter',
  'gliederung.vor': 'vor {position}',
  'gliederung.einfuegen': 'hier einfügen',
  'gliederung.einfuegen.erlaeuterung':
    'Die neue Position entsteht unter {position} — die Art kommt von dort und wird nicht gefragt — unter „Aktiva" hängt eine Aktivposition.',

  /* Anlegen am Bericht (7.4 Abschnitt 14, R494–R501) */
  // Vorn steht, was entsteht (7.4 Abschnitt 33, R1360).
  /* Die vier Elementtypen eines Berichts (2.1 Abschnitt 3.6, R1704, R1705) */
  'anlage.menue.konto': 'Neues Konto …',
  'anlage.menue.position': 'Neue Position …',
  'anlage.menue.referenz': 'Neue Referenz …',
  'anlage.menue.summenzeile': 'Neue Summenzeile …',
  'anlage.menue.loeschen.position': 'Position löschen …',
  'anlage.menue.loeschen.konto': 'Konto löschen …',
  'anlage.menue.loeschen.referenz': 'Referenz löschen …',
  'anlage.menue.loeschen.summenzeile': 'Summenzeile löschen …',
  'anlage.titel.konto': 'Neues Konto unter {position}',
  'anlage.titel.position': 'Neue Position unter {position}',
  'anlage.nummer': 'Nummer',
  'anlage.code': 'Code',
  'anlage.bezeichnung': 'Bezeichnung',
  'anlage.abperiode': 'Gilt ab Periode',
  'anlage.gruppe': 'Kontengruppe',
  'anlage.gruppe.uneins':
    'Die Konten unter {position} gehören zu mehreren Kontengruppen ({gruppen}). Welche gilt, muss deshalb gesagt werden — geraten wird nicht.',
  'anlage.gruppe.ohne':
    'Unter {position} hängt noch kein Konto. Die Kontengruppe muss deshalb gesagt werden — geraten wird nicht.',
  'anlage.machtzursumme':
    '{position} zählt danach auch die Positionen unter ihr.',
  'anlage.code.vorschlag': 'So zählt die Gliederung unter {position} weiter. Überschreiben ist erlaubt.',
  'anlage.nummer.vorschlag': 'So zählt der Kontenrahmen an dieser Stelle weiter. Überschreiben ist erlaubt.',
  'anlage.hindernis.rechenposition':
    'Unter dieser Zeile entsteht nichts: Ihr Wert ist eine Formel über andere Positionen, und was darunter hinge, ginge in keine Summe ein.',
  'anlage.position.rechenposition.meldung':
    'Unter {position} lässt sich keine Position anlegen: Die Position nennt ihre Glieder selbst, ihr Wert ist eine Formel.',
  'anlage.position.rechenposition.weg':
    'Hängen Sie die Position unter eine der Positionen, die in der Formel stehen — von dort zählt sie mit.',
  'anlage.konto.rechenposition.meldung':
    'An {position} lässt sich kein Konto anlegen: Die Position nennt ihre Glieder selbst, ihr Wert ist eine Formel.',
  'anlage.konto.rechenposition.weg':
    'Hängen Sie das Konto an eine der Positionen, die in der Formel stehen — von dort zählt es mit.',
  'anlage.anlegen': 'Anlegen',
  // 'anlage.nochnichtgerechnet' ist am 2026-09-16 entfallen (R569): Der Bericht
  // lädt nach dem Anlegen neu, die Laufvorsorge (R294) holt den Lauf dabei nach.
  'anlage.offeneplaene':
    'In {anzahl} weiteren Positionsplänen hat das Konto noch keine Stelle. Solange es dort nicht zugeordnet ist, fehlt es in deren Abschlüssen.',
  'anlage.konto.ohnenummer.meldung': 'Ohne Nummer kein Konto: Aus ihr entsteht der Schlüssel.',

  /* Die Absagen des Dienstes — Befund und Weg, nie eine Fehlernummer (R145). */
  'gliederung.zyklus.meldung':
    '{gegenstand} lässt sich nicht unter {position} hängen: {position} steht bereits darunter.',
  'gliederung.zyklus.weg':
    'Hängen Sie zuerst {position} woandershin — ein Zweig, der unter sich selbst steht, hat keine Wurzel mehr und verschwindet aus der Bilanz.',
  'gliederung.andereart.meldung': '{gegenstand} ist {art}, {zielart} ist etwas anderes.',
  'gliederung.andereart.weg':
    'Zwischen Aktiva, Passiva, Aufwand und Ertrag lässt sich eine Position umhängen. Eine Zeile der Kapitalflussrechnung oder des Eigenkapitalspiegels bleibt in ihrer eigenen Gliederung.',
  'gliederung.saldowechsel.meldung':
    '{gegenstand} weist bei umgekehrtem Saldo unter {ziel} aus. Nach dem Umhängen läge das eine in der Bilanz und das andere in der GuV.',
  'gliederung.saldowechsel.weg':
    'Der Saldowechsel bleibt im eigenen Bericht. Tragen Sie das Ziel im Eigenschaftsfenster aus oder hängen Sie beide gemeinsam um.',
  'gliederung.fremderplan.meldung': '{position} gehört zu einem anderen Positionsplan.',
  'gliederung.fremderplan.weg':
    'Jeder Positionsplan ist eine Gliederung für sich. Wechseln Sie oben den Plan, oder legen Sie die Position in diesem Plan neu an.',
  'gliederung.position.unbekannt.meldung': '{position} gibt es nicht.',
  'gliederung.konto.unbekannt.meldung': '{konto} gibt es nicht.',
  'gliederung.schondort.meldung': '{gegenstand} hängt bereits unter {position}.',
  'gliederung.ohnezeile.meldung': '{position} hat für diese Periode keine Gliederungszeile.',
  'gliederung.ohnezeile.weg':
    'Legen Sie sie im Blatt darunter an — Stammdatenart „Gliederung", mit Eltern und Gültigkeit.',
  'gliederung.ohnecode.meldung': 'Ohne Code keine Position: Aus ihm entsteht der Schlüssel.',
  'gliederung.wurzel.meldung': 'Eine neue Wurzel entsteht hier nicht.',
  'gliederung.wurzel.weg':
    'Aktiva, Passiva, Erträge und Aufwendungen sind die Abschnitte der Bilanz und keine Zeilen darin. Eine neue Position gehört unter eine bestehende.',
  'gliederung.code.vergeben.meldung': 'Den Code {code} gibt es schon.',
  // Seit R552 entsteht der Schlüssel NICHT mehr aus dem Code, und der Code ist
  // änderbar — die alte Handreichung sagte das Gegenteil und stand damit
  // gegen die Umstellung eines Kontenrahmens (R513).
  'gliederung.code.vergeben.weg':
    'Wählen Sie einen anderen. Ändern lässt er sich später jederzeit — die Objekt-ID bleibt davon unberührt.',

  // ---- Der Stammdatenaustausch (5.1 Abschnitt 10, R560–R565) ----

  'bereich.stammdaten.exportimport': 'Export und Import',
  'austausch.exportieren': 'Stammdaten exportieren',
  'austausch.importieren': 'Stammdaten importieren',
  'austausch.datei': 'Gelesen: {datei}',
  'austausch.zaehler.neu': 'neu',
  'austausch.zaehler.geaendert': 'geändert',
  'austausch.zaehler.befunde': 'Befunde',
  'austausch.nichtszutun': 'Nichts zu tun — die Mappe sagt dasselbe wie der Bestand.',
  'austausch.uebergangen': 'Übergangen, weil keine Stammdatenart: {blaetter}',
  'austausch.gestrichen': 'Übersprungen, weil es diese Angaben nicht mehr gibt: {angaben}',
  'austausch.liste.befunde': 'Diese Zeilen halten die Mappe an',
  'austausch.liste.rechnend': 'Diese Zeilen ändern eine Eigenschaft, die in eine Rechnung eingeht',
  'austausch.liste.uebrige': 'Diese Zeilen kommen neu dazu oder ändern eine Beschriftung',
  'austausch.bestaetigen': 'Ich habe die {anzahl} rechnenden Änderungen gesehen.',
  'austausch.bestaetigen.eine': 'Ich habe die eine rechnende Änderung gesehen.',
  'austausch.uebernehmen': 'Übernehmen',
  'austausch.grund.befunde': 'Solange Befunde offen sind, wird nichts übernommen.',
  'austausch.grund.bestaetigung': 'Bitte bestätigen Sie zuerst die rechnenden Änderungen.',
  'austausch.spalte.stelle': 'Stelle in der Mappe',
  'austausch.spalte.code': 'Code',
  'austausch.spalte.was': 'Was geschieht',
  'austausch.zeile': 'Zeile {nummer}',
  'austausch.zeile.neu': 'wird angelegt und bekommt eine Objekt-ID',
  'austausch.zeile.geaendert': 'bekommt eine neue Fassung',

  'austausch.keinemappe.meldung': 'Diese Datei ist keine Excel-Mappe.',
  'austausch.keinemappe.weg':
    'Der Weg herein ist die Mappe mit einem Blatt je Stammdatenart — eine einzelne Tabelle kann halb ankommen.',
  'austausch.befunde.meldung': '{anzahl} Zeilen lassen sich nicht übernehmen. Die erste: {erste}',
  'austausch.befunde.weg':
    'Es wurde nichts übernommen. Eine halb eingespielte Mappe wäre schlimmer als gar keine — bereinigen Sie die Datei und laden Sie sie erneut.',
  'austausch.rechnend.meldung':
    '{anzahl} Zeilen ändern eine Eigenschaft, die in eine Rechnung eingeht.',
  'austausch.rechnend.weg':
    'Sehen Sie die Liste in der Vorschau durch und bestätigen Sie sie ausdrücklich. Freigegebene Abschlüsse bleiben unverändert; der nächste Lauf rechnet mit den neuen Werten.',

  // ---- Der Feldkatalog des Hauses (7.4 Abschnitt 18.5, R554) ----

  'feld.wert.zahl.meldung': '„{wert}“ ist keine Zahl — {feld} erwartet eine.',
  'feld.wert.janein.meldung': '„{wert}“ ist weder Ja noch Nein — {feld} erwartet eines von beiden.',
  'feld.wert.periode.meldung': '„{wert}“ ist keine Periode — {feld} erwartet eine.',
  'feld.wert.vorrat.meldung': '„{wert}“ steht nicht im Vorrat von {feld}.',
  'feld.wert.weg': 'Erwartet wird: {erwartet}',
  'feld.werksfeld.meldung': '{objektart} hat bereits ein Feld {feld} ab Werk.',
  'feld.werksfeld.weg':
    'Wählen Sie einen anderen Namen. Zwei gleichnamige Felder stünden zweimal auf dem Blatt und zweimal im Export — und nur eines von beiden rechnet.',
  'feld.fest.meldung': '{was} steht fest, seit {feld} Werte trägt.',
  'feld.fest.weg':
    'Legen Sie ein neues Feld an und legen Sie dieses still. Die Werte bleiben lesbar; umgestellt würden sie stillschweigend unlesbar.',

  // ---- Der Aufbau der Bewertungsebenen (2.4 Abschnitt 9, R396–R400) ----

  // Der Teilkonzern in einer Spalte (7.3 Abschnitt 9, R407–R409)
  'spalte.tk.marke': 'TK {code}',
  'spalte.tk.band': 'Teilkonzern {name}',
  'spalte.tk.summe': 'TK-Summe',
  'spalte.tk.gesamt': 'TK gesamt',
  'spalte.tk.konsolidierung': 'TK-Konsolidierung',
  'spalte.tk.aufklappen':
    '{name} aufreißen — seine Gesellschaften treten hervor, die Sicht auf den Konzern bleibt.',
  'spalte.tk.zuklappen': '{name} wieder zu einer Spalte zusammenfassen.',

  'spalte.hb2.aufklappen':
    'Zeigen, woraus die Handelsbilanz II besteht — je Schicht eine Spalte.',
  'spalte.hb2.zuklappen': 'Die Schichten wieder zusammenfassen.',

  'ledgeraufbau.titel': 'Aufbau der Bewertungsebenen',
  'ledgeraufbau.art.rohdaten': 'Rohdaten',
  'ledgeraufbau.art.delta': 'Delta',
  'ledgeraufbau.art.gruppe': 'Gruppe',
  'ledgeraufbau.art.rohdaten.erlaeuterung': 'Was die Gesellschaft geliefert hat, unberührt. Nur der Import schreibt hierher — eine Handbuchung weist CoKo ab.',
  'ledgeraufbau.art.delta.erlaeuterung':
    'Eine Anpassungsschicht mit eigenem Namen. Überleitung, Umgliederung und Handerfassung liegen hier.',
  'ledgeraufbau.art.gruppe.erlaeuterung':
    'Ein benannter Behälter über Schichten. In eine Gruppe wird nie gebucht.',
  'ledgeraufbau.belege.keine': 'noch nichts gebucht',
  'ledgeraufbau.neu.gruppe': 'Gruppe anlegen',
  'ledgeraufbau.neu.schicht': 'Schicht anlegen',
  'ledgeraufbau.neu.titel': 'Neu in {gruppe}',
  'ledgeraufbau.neu.titel.wurzel': 'Neue Gruppe',
  'ledgeraufbau.neu.rechnungslegung': 'Rechnungslegung',
  'ledgeraufbau.neu.rechnungslegung.erlaeuterung':
    'Nach welchem Regelwerk diese Gruppe aufgestellt wird: HGB, IFRS, US-GAAP. Daran hängen die Stellschrauben, die der Abschluss anwendet — schreiben Sie es genau so, wie es daneben schon steht. Die Schichten darunter erben es. Den Schlüssel vergibt CoKo selbst.',
  'ledgeraufbau.neu.name': 'Bezeichnung',
  'ledgeraufbau.neu.name.erlaeuterung':
    'Der Name, unter dem die Schicht überall erscheint. Er lässt sich jederzeit ändern.',
  'ledgeraufbau.neu.art': 'Art',
  'ledgeraufbau.aufnehmen': 'Vorhandene Schicht aufnehmen',
  'ledgeraufbau.aushaengen': 'Aus dieser Gruppe nehmen',
  'ledgeraufbau.aushaengen.frage':
    '{mitglied} aus {gruppe} nehmen? Die Schicht bleibt mit allem, was in ihr gebucht ist — nur die Zugehörigkeit endet.',
  'ledgeraufbau.hoch': 'Eine Stelle nach oben',
  'ledgeraufbau.runter': 'Eine Stelle nach unten',
  'ledgeraufbau.umbenennen': 'Umbenennen',
  'ledgeraufbau.freie': 'In keiner Gruppe',
  'ledgeraufbau.freie.erlaeuterung':
    'Angelegt, aber nirgends eingehängt: Diese Schichten rechnen in keinem Abschluss mit.',
  'ledgeraufbau.leer': 'Diese Gruppe ist leer. Legen Sie ihre erste Schicht an.',
  'ledgeraufbau.offen.keine':
    'Diese Gruppe erreicht keine Rohdatenschicht — ein Abschluss ohne Lieferung.',
  'ledgeraufbau.offen.mehrere':
    'Diese Gruppe erreicht mehrere Rohdatenschichten. Welche die Lieferung der Gesellschaft ist, hält dann nichts mehr auseinander.',
  'ledgeraufbau.ohnelegung.meldung':
    'Eine Gruppe auf oberster Ebene braucht ihre Rechnungslegung: Es gibt darüber nichts, wovon sie sie erben könnte.',
  'ledgeraufbau.ohnelegung.weg':
    'HGB, IFRS, US-GAAP — daran hängen die Stellschrauben, die der Abschluss anwendet.',
  'ledgeraufbau.ohnename.meldung': 'Ohne Bezeichnung geht es nicht: Sie steht überall dort, wo die Kennung zu technisch wäre.',
  'ledgeraufbau.art.unbekannt.meldung': '{art} ist keine Art einer Bewertungsebene.',
  'ledgeraufbau.unbekannt.meldung': '{ledger} gibt es nicht.',
  'ledgeraufbau.keinegruppe.meldung': '{ledger} ist keine Gruppe.',
  'ledgeraufbau.keinegruppe.weg':
    'Mitglieder hat nur eine Gruppe. Eine Schicht trägt Buchungen, keine weiteren Schichten.',
  'ledgeraufbau.ohnegruppe.meldung': 'Eine Schicht entsteht nicht ohne Gruppe.',
  'ledgeraufbau.ohnegruppe.weg':
    'Sie rechnete sonst in keinem Abschluss mit. Legen Sie sie unter der Gruppe an, in die sie gehört — oder legen Sie zuerst die Gruppe an.',
  'ledgeraufbau.schondrin.meldung': '{mitglied} liegt bereits in {gruppe}.',
  'ledgeraufbau.nichtdrin.meldung': '{mitglied} liegt nicht in {gruppe}.',
  'ledgeraufbau.zyklus.meldung': '{gruppe} liegt bereits in {mitglied}.',
  'ledgeraufbau.zyklus.weg':
    'Eine Gruppe in sich selbst zu nehmen, ergäbe eine Summe, die sich selbst enthält — auf „wie viel ist das?" gäbe es dann keine Antwort.',

  'stammdaten.art.dimensionswert': 'Dimensionswerte',
  'stammdaten.art.positionsgliederung': 'Gliederung',
  'stammdaten.art.kontozuordnung': 'Konto in der Gliederung',
  /* R252, R855 — Zeilenkatalog und Zuordnung der Kapitalflussrechnung (2.8 Abschnitt 13.22). */
  'stammdaten.art.kfr_zeile': 'Zeilen der Kapitalflussrechnung',
  'stammdaten.art.kfr_zuordnung': 'Zuordnung zur Kapitalflussrechnung',
  'stammdaten.art.beteiligung': 'Beteiligungen',
  'stammdaten.art.erstkonsolidierung': 'Erstkonsolidierung',
  'stammdaten.art.kapitaltransaktion': 'Kapitaltransaktionen',
  'stammdaten.art.kreis_einheit': 'Kreiszugehörigkeit',
  'stammdaten.art.lieferrhythmus': 'Erwarteter Rhythmus der Datenlieferung',
  'stammdaten.art.datenquelle': 'Datenquellen',
  'stammdaten.neu': 'Neu anlegen',
  'stammdaten.neu.code.waehrung.erlaeuterung':
    'Der Code nach ISO 4217, drei Großbuchstaben: EUR, USD, KES. Er ist eine Weltnorm — CoKo vergibt ihn nicht selbst, und er steht so in jeder Lieferung.',
  'stammdaten.neu.titel': 'Neu: {objektart}',
  'stammdaten.neu.code': 'Code',
  'stammdaten.neu.code.erlaeuterung':
    'Aus ihm entsteht der Schlüssel, und der lässt sich später nicht mehr ändern: An ihm hängt die ganze Fassungsreihe.',
  'stammdaten.neu.codeverweis.erlaeuterung':
    'Der Schlüssel entsteht daraus und lässt sich später nicht mehr ändern. Was hier nicht in der Liste steht, gibt es noch nicht — legen Sie es zuerst an.',
  'stammdaten.neu.name': 'Bezeichnung',
  'stammdaten.neu.nummer.schonda':
    '{nummer} steht schon bei einem anderen Eintrag vorn. Das ist erlaubt — prüfen Sie nur, ob Sie es so wollen.',
  'stammdaten.neu.speichern': 'Anlegen',
  // Die Rückfallebene, wenn die Absage keinen Schlüssel trägt (R145).

  // ---- Die geschlossene Tafel über einem Bereich (7.4 Abschnitt 20) ----
  //
  // **Kein stehender Erklärtext** (R608). Was hier steht, ist immer das
  // Ergebnis des letzten Griffs — vorher steht nichts.
  'tafel.zeile': 'Eintrag',
  'tafel.spaltewaehlen': 'Ganze Spalte auswählen — dann Strg+V für alle Zeilen auf einmal',
  'tafel.einfuegen.laeuft': 'Wird geschrieben … {fertig} von {gesamt}',
  'tafel.einfuegen.teilweise': '{fertig} Zeilen geschrieben. {unbekannt} Werte kennt die Auswahlliste nicht — die stehen unverändert.',
  'tafel.einfuegen.unbekannt': 'Nichts geschrieben: {anzahl} Werte kennt die Auswahlliste nicht.',
  'tafel.einfuegen.keineperiode': '{anzahl} Werte lesen sich nicht als Periode — die stehen unverändert.',
  'tafel.einfuegen.keinezahl': '{anzahl} Werte lesen sich nicht als Zahl — die stehen unverändert.',
  'tafel.zelle.keinezahl': 'Das liest sich nicht als Zahl — die Zelle steht unverändert.',
  'stammdaten.aussondern': 'Löschen …',
  'stammdaten.benutzt.titel': 'Dieses Element wird benutzt.',
  'stammdaten.benutzt.text':
    'Es kann nicht entfernt werden, ohne alte Abschlüsse zu verändern — eine Zahl ohne ihr Konto ist keine Herleitung mehr. Es wird deshalb ab einer Periode stillgelegt: Was vorher gerechnet wurde, bleibt unverändert; neue Lieferungen laufen nicht mehr darauf.',
  'stammdaten.unbenutzt.titel': 'Dieses Element wird nirgends benutzt.',
  'stammdaten.unbenutzt.text':
    'Es kann entfernt werden. Auch das ist eine Anfügung und kein Überschreiben: Die Fassungsreihe bleibt lesbar, und im Änderungsnachweis steht, wer wann entfernt hat.',
  /* Wo ein Stammdatum vorkommt — als Wort und nicht als Tabellenname (R316).
     Der Anwender entscheidet danach, ob er stilllegt; er soll nicht erst
     raten müssen, was „core.ergebniszeile" ist. */
  'stammdaten.stelle.core.journalzeile': 'Buchungszeilen',
  'stammdaten.stelle.core.buchung': 'Buchungen',
  'stammdaten.stelle.core.ergebniszeile': 'Ergebniszeilen der Läufe',
  'stammdaten.stelle.core.zwischenergebnis': 'Zwischenergebnisse',
  'stammdaten.stelle.core.kontenmapping': 'Kontenzuordnungen',
  'stammdaten.stelle.core.konto_zuordnung': 'Konten in der Gliederung',
  'stammdaten.stelle.core.position_zuordnung': 'Positionsgliederung',
  'stammdaten.stelle.core.position': 'Positionen',
  'stammdaten.stelle.core.konto': 'Konten',
  'stammdaten.stelle.core.kontengruppe': 'Kontengruppen',
  'stammdaten.stelle.core.bewegungsart': 'Bewegungsarten',
  'stammdaten.stelle.core.ledger': 'Bewertungsebenen',
  'stammdaten.stelle.core.einheit': 'Gesellschaften',
  'stammdaten.stelle.core.beteiligung': 'Beteiligungen',
  'stammdaten.stelle.core.kreis_einheit': 'Kreiszugehörigkeiten',
  'stammdaten.stelle.core.lieferrhythmus': 'Erwarteter Rhythmus der Datenlieferung',
  'stammdaten.stelle.core.konsolidierungskreis': 'Konsolidierungskreise',
  'stammdaten.stelle.core.abschlusslauf': 'Abschlussläufe',
  'stammdaten.stelle.core.datenquelle': 'Datenquellen',
  'stammdaten.stelle.core.importlauf': 'Importläufe',
  'stammdaten.stelle.core.dimensionswert': 'Dimensionswerte',
  'stammdaten.stelle.core.ledgerelement': 'Gruppen der Bewertungsebenen',
  'stammdaten.stelle.core.partnercode': 'Partnercodes',
  'position.einordnung.titel': 'Einordnung',
  'position.einordnung.unter': 'unter {eltern}',
  'position.einordnung.oberste': 'oberste Ebene',
  'position.einordnung.ab': 'ab {periode}',
  'position.einordnung.bis': 'bis {periode}',
  'position.einordnung.leer': 'Die Position hängt in keiner Gliederung.',
  'stammdaten.abperiode': 'Stillgelegt ab Periode',
  // **Was zwei gleich aussehende Zeilen unterscheidet** (R705): Eine
  // Kontozuordnung zeigt den Namen ihres Kontos und hat sonst nichts Eigenes.
  'stammdaten.zeile.gueltigab': 'gültig ab {periode}',
  'stammdaten.stilllegen': 'Ab {periode} stilllegen',
  'stammdaten.entfernen': 'Entfernen',
  'stammdaten.arten': 'Arten',
  // Der Griff nimmt den Fokus und hoert auf die Pfeiltasten (R143) -- die
  // Beschriftung sagt, was er tut.
  'stammdaten.breite': 'Breite der Liste — mit den Pfeiltasten verstellen',
  'stammdaten.art.einheit': 'Gesellschaften',
  'stammdaten.art.kontengruppe': 'Kontengruppen',
  'stammdaten.art.konto': 'Konten',
  'stammdaten.art.bewegungsart': 'Bewegungsarten',
  'stammdaten.art.ledger': 'Bewertungsebenen',
  'stammdaten.art.konsolidierungskreis': 'Konsolidierungskreise',
  'stammdaten.art.positionsplan': 'Positionspläne',
  'stammdaten.art.position': 'Positionen',
  'stammdaten.art.analysedimension': 'Dimensionen',
  'stammdaten.art.segment': 'Segmente',
  'stammdaten.art.waehrung': 'Währungen',
  'stammdaten.art.land': 'Länder',


  /* --- Wofür ein Feld gepflegt wird (7.4 Abschnitt 23.4, R697) ---

     Der Satz des Werks. Er steht je Objektart, wo dasselbe Feld dort etwas
     anderes meint, und sonst einmal für alle. Wo das Haus einen eigenen Satz
     gepflegt hat, gewinnt dieser (R238). Wo sich der Zweck nicht belegen
     ließ, steht kein Satz — einen zu erfinden wäre schlechter als keiner. */
  'stammdaten.zweck.stillgelegt_ab': 'Ab dieser Periode steht der Eintrag nicht mehr zur Auswahl; was gebucht ist, bleibt lesbar',
  'stammdaten.zweck.sortierung': 'Die Reihenfolge in Listen und Berichten',
  'stammdaten.zweck.gueltig_von': 'Ab welcher Periode die Zuordnung gilt',
  'stammdaten.zweck.gueltig_bis': 'Bis zu welcher Periode sie gilt — leer heißt: fortgeltend',
  'stammdaten.zweck.ist_aktiv': 'Ob der Eintrag heute benutzt wird',
  'stammdaten.zweck.zahlungswirksam': 'Ob der Posten in die Kapitalflussrechnung eingeht',
  'stammdaten.zweck.umgliederung_eigenkapital':
    'Läuft in keine Zeile der Kapitalflussrechnung und muss je Gesellschaft und Monat null ergeben',
  'stammdaten.zweck.bewegungsartpflicht': 'Verlangt zu jedem Saldo eine Bewegungsart — die Grundlage jedes Spiegels',
  'stammdaten.zweck.ist_fonds': 'Gehört zum Finanzmittelfonds der Kapitalflussrechnung',
  'stammdaten.zweck.ist_kontokorrent': 'Kontokorrent: Der Saldo wechselt zwischen Forderung und Verbindlichkeit',
  'stammdaten.zweck.umrechnungsmethode': 'Mit welchem Kurs umgerechnet wird: Stichtag, Durchschnitt oder historisch',
  'stammdaten.zweck.kontoart': 'Entscheidet, ob der Saldo in die Bilanz oder die Erfolgsrechnung geht',
  'stammdaten.zweck.waehrung': 'Trägt die Umrechnung in die Konzernwährung',
  'stammdaten.zweck.einheit.waehrung': 'Die Währung, in der die Gesellschaft liefert — Ausgangspunkt der Umrechnung',
  'stammdaten.zweck.einheit.einheitsart': 'Ordnet die Gesellschaften nach ihrer Aufgabe im Konzern',
  'stammdaten.zweck.einheit.sitz_ort': 'Der Sitz in der Aufstellung des Anteilsbesitzes, § 313 Abs. 2 HGB',
  'stammdaten.zweck.einheit.land': 'Ergänzt den Sitz in der Aufstellung des Anteilsbesitzes, § 313 Abs. 2 HGB',
  /* R1448 — an der Gesellschaft heißt die Stilllegung „Inaktiv seit“. */
  'stammdaten.zweck.einheit.stillgelegt_ab':
    'Ab diesem Monat liefert die Gesellschaft nicht mehr und steht in keiner Auswahl; das Ausscheiden aus dem Konzern steht an Beteiligung und Kreismitgliedschaft',
  'stammdaten.feld.einheit.stillgelegt_ab': 'Inaktiv seit',
  'stammdaten.zweck.kontengruppe.eltern': 'Hängt die Gruppe in die Kette — von dort erbt sie, was sie nicht selbst sagt',
  'stammdaten.zweck.konto.gruppe': 'Von dieser Gruppe erbt das Konto, was es nicht selbst sagt',
  'stammdaten.zweck.konto.ist_ic': 'Kennzeichnet das Konto für die Abstimmung mit verbundenen Unternehmen',
  'stammdaten.zweck.konto.ist_beteiligung': 'Der Beteiligungsbuchwert, der in der Kapitalkonsolidierung aufgerechnet wird',
  'stammdaten.zweck.konto.ist_beteiligungsertrag': 'Erträge aus Beteiligungen — die Dividende einer einbezogenen Tochter wird hier erkannt und eliminiert',
  'stammdaten.zweck.konto.ist_eigenkapital': 'Eigenkapital: geht zum historischen Kurs um und wird erstkonsolidiert',
  'stammdaten.zweck.bewegungsart.ist_kern': 'Eine Kernbewegungsart, von der andere ihre Eigenschaften erben',
  'stammdaten.zweck.bewegungsart.kern': 'Von dieser Kernbewegungsart erbt die Bewegungsart, was sie nicht selbst sagt',
  'stammdaten.zweck.bewegungsart.spiegel': 'In welchen Spiegel die Bewegung läuft — Anlagen, Eigenkapital, Verbindlichkeiten',
  'stammdaten.zweck.bewegungsart.spiegel_spalte': 'Welche Spalte des Spiegels sie füllt',
  'stammdaten.zweck.bewegungsart.spiegel_block': 'Welcher der beiden Bestände des Anlagenspiegels — Anschaffungskosten oder Abschreibung',
  'stammdaten.zweck.bewegungsart.kfr_zeile': 'Die Zeile der Kapitalflussrechnung, in die sie eingeht',
  'stammdaten.zweck.bewegungsart.kurstyp': 'Mit welchem Kurs die Bewegung umgerechnet wird',
  'stammdaten.zweck.bewegungsart.ist_eroeffnung': 'Die Bewegung trägt den Anfangsbestand der Periode',
  'stammdaten.zweck.ledger.rechnungslegung': 'Nach welchem Regelwerk diese Ebene bewertet — Handelsrecht, IFRS, steuerlich',
  'stammdaten.zweck.ledger.art': 'Rohdaten, Delta oder Gruppe: wie die Ebene entsteht',
  'stammdaten.zweck.beteiligung.tochter': 'Die gehaltene Gesellschaft',
  'stammdaten.zweck.beteiligung.mutter': 'Die haltende Gesellschaft',
  'stammdaten.zweck.beteiligung.quote': 'Der Kapitalanteil — er bestimmt Konzernanteil und Minderheiten',
  'stammdaten.zweck.beteiligung.stimmrechtsanteil': 'Entscheidet über die Beherrschung, wo er vom Kapitalanteil abweicht. Leer heißt: wie der Kapitalanteil',
  'stammdaten.zweck.beteiligung.beteiligungsart': 'Tochter, Gemeinschaftsunternehmen, assoziiert oder sonstige Beteiligung',
  'stammdaten.zweck.beteiligung.einbeziehungsart': 'Wie die Gesellschaft eingeht: voll, quotal, at equity oder gar nicht',
  'stammdaten.zweck.beteiligung.erwerbsperiode': 'Der Zeitpunkt des Erwerbs — Grundlage der Erstkonsolidierung',
  'stammdaten.zweck.beteiligung.eintritt_zeitpunkt': 'Ob zum Beginn oder zum Ende der Periode eingetreten wird',
  'stammdaten.zweck.beteiligung.austritt_zeitpunkt': 'Ob zum Beginn oder zum Ende der Periode ausgeschieden wird',
  'stammdaten.zweck.beteiligung.anschaffungskosten': 'Der Buchwert, gegen den das Eigenkapital der Tochter aufgerechnet wird',
  'stammdaten.zweck.beteiligung.anschaffungswaehrung': 'Die Währung der Anschaffungskosten',
  'stammdaten.zweck.beteiligung.aufsatzart': 'Nur für eine Tochter, die vor ihrem ersten Monat in CoKo erworben wurde: wie sie in den Konzern kommt',
  'stammdaten.zweck.beteiligung.aufsatzmonat': 'Der erste Monat der Tochter in CoKo — in ihm wird der Stand gebucht, danach vorgetragen',
  'stammdaten.zweck.beteiligung.firmenwert_nutzungsdauer': 'Bei den Werten zum Erwerb: über wie viele Monate ab dem Erwerb der Firmenwert abgeschrieben wird',
  'stammdaten.zweck.beteiligung.firmenwert_rest': 'Bei den Ständen zum Aufsatz: der Firmenwert, wie er im letzten Konzernabschluss vor CoKo steht',
  'stammdaten.zweck.beteiligung.firmenwert_restnutzungsdauer': 'Bei den Ständen zum Aufsatz: über wie viele Monate ab dem Aufsatz der Rest abgeschrieben wird',
  'stammdaten.zweck.beteiligung.ausgleichsposten': 'Bei den Ständen zum Aufsatz: der Anteil der anderen Gesellschafter, wie er im letzten Konzernabschluss vor CoKo steht',
  'stammdaten.zweck.beteiligung.gueltig_von': 'Ab welcher Periode das Beteiligungsverhältnis gilt',
  'stammdaten.zweck.beteiligung.gueltig_bis': 'Bis wann es gilt — leer heißt: fortgeltend',
  'stammdaten.zweck.erstkonsolidierung.beteiligung': 'Auf welches Beteiligungsverhältnis sich der eingefrorene Wert bezieht',
  'stammdaten.zweck.erstkonsolidierung.konto': 'Auf welches Konto sich der eingefrorene Wert bezieht',
  'stammdaten.zweck.erstkonsolidierung.betrag': 'Der zum Erwerb eingefrorene Wert — er ändert sich nie wieder',
  'stammdaten.zweck.erstkonsolidierung.waehrung': 'Die Währung des eingefrorenen Wertes',
  'stammdaten.zweck.erstkonsolidierung.kurs': 'Der historische Kurs zum Erwerb',
  'stammdaten.zweck.erstkonsolidierung.art': 'Eigenkapital zum Erwerb oder stille Reserve',
  'stammdaten.zweck.erstkonsolidierung.nutzungsdauer_monate': 'Über wie viele Monate eine stille Reserve oder ein Geschäftswert abgeschrieben wird',
  'stammdaten.zweck.kapitaltransaktion.beteiligung': 'Zu welchem Beteiligungsverhältnis die Transaktion gehört',
  'stammdaten.zweck.kapitaltransaktion.periode': 'Der Monat der Transaktion — in ihm wird sie gebucht',
  'stammdaten.zweck.kapitaltransaktion.zeitpunkt': 'Periodenbeginn: Das Ergebnis des Monats wird schon mit dem neuen Kapitalanteil geteilt. Periodenende: noch mit dem alten',
  'stammdaten.zweck.kapitaltransaktion.art': 'Zukauf, Teilverkauf oder Kapitalerhöhung — den Betrag nimmt CoKo aus der Lieferung',
  'stammdaten.zweck.kapitaltransaktion.quote_danach': 'Der Kapitalanteil der Mutter nach der Transaktion; bei einer Kapitalerhöhung im Verhältnis der Anteile der bisherige',
  'stammdaten.zweck.kreis_einheit.einheit': 'Welche Gesellschaft zum Kreis gehört',
  'stammdaten.zweck.kreis_einheit.kreis': 'Zu welchem Konsolidierungskreis sie gehört',
  'stammdaten.zweck.kreis_einheit.gueltig_von': 'Ab welcher Periode sie dazugehört — ohne diese Zeile erscheint sie in keinem Bericht',
  'stammdaten.zweck.kreis_einheit.eintrittskurs': 'Zu welchem Kurs ihre Eröffnungsbilanz in die Konzernwährung eingeht — fixiert, ein Kurs für alles',
  'stammdaten.zweck.kreis_einheit.eintrittskurs_weg': 'Wie der Eintrittskurs zustande kam: eingetragen, nachzutragen oder als Stichtagskurs nach Rücksprache mit dem Wirtschaftsprüfer übernommen',
  'stammdaten.zweck.kreis_einheit.eintrittskurs_quelle': 'Aus welchem Monat der übernommene Stichtagskurs stammt',
  'stammdaten.zweck.lieferrhythmus.einheit': 'Für welche Gesellschaft der Rhythmus gilt',
  'stammdaten.zweck.lieferrhythmus.gueltig_von': 'Ab welchem Monat er gilt — bis zur nächsten Zeile',
  'stammdaten.zweck.lieferrhythmus.rhythmus':
    'Wie oft die Gesellschaft liefern muss. Fehlt eine fällige Lieferung, sperrt das die Freigabe; ein Monat, in dem nichts fällig ist, bleibt ohne Befund. Eine Gesellschaft, die nur zum Jahresende liefert, steht auf „jährlich“.',
  'stammdaten.feld.eintrittskurs': 'Eintrittskurs',
  'stammdaten.feld.eintrittskurs_weg': 'Weg zum Eintrittskurs',
  'stammdaten.feld.eintrittskurs_quelle': 'Stichtagskurs übernommen aus',
  'stammdaten.wert.eintrittskurs_weg.eingetragen': 'eingetragen',
  'stammdaten.wert.eintrittskurs_weg.nachtragen': 'liegt nicht vor',
  'stammdaten.wert.eintrittskurs_weg.stichtag': 'Stichtagskurs nach Rücksprache mit dem Wirtschaftsprüfer',
  'stammdaten.zweck.konsolidierungskreis.oberste_einheit': 'Die Spitze: Auf ihr landet der Konzernwert, und ihre Währung ist die Konzernwährung',
  'stammdaten.zweck.konsolidierungskreis.zweck': 'Gesetzlicher Abschluss, Teilkonzern oder Managementsicht',
  'stammdaten.zweck.konsolidierungskreis.uebergeordneter_kreis': 'Der Kreis, in dem dieser als Teilkonzern steht — leer heißt: dieser ist der oberste. Ändert keinen Betrag',
  'stammdaten.zweck.konsolidierungskreis.konzernwaehrung': 'Die Währung, in der dieser Kreis abschließt',
  'stammdaten.zweck.konsolidierungskreis.guv_umrechnung': 'Monatsdurchschnitt oder kumuliert: wie die Erfolgsrechnung umgerechnet wird',
  'stammdaten.zweck.konsolidierungskreis.fonds_mit_kontokorrent': 'Ob Kontokorrentkredite zum Finanzmittelfonds zählen',
  'stammdaten.zweck.konsolidierungskreis.geschaeftsjahr_beginn': 'Grenzt ab, welche Perioden ein Geschäftsjahr des Kreises bilden',
  'stammdaten.zweck.positionsplan.ist_vorgabe': 'Der Plan, der ohne Auswahl gilt',
  'stammdaten.zweck.position.positionsplan': 'Zu welchem Positionsplan die Zeile gehört',
  'stammdaten.zweck.position.art': 'Aktiva, Passiva, Ertrag, Aufwand, Kapitalfluss oder Eigenkapitalspiegel',
  'stammdaten.zweck.position.vorzeichen': 'Ob der Wert im Ausweis gedreht erscheint',
  'stammdaten.zweck.position.ist_summe': 'Die Zeile rechnet ihre Untergliederung zusammen',
  'stammdaten.zweck.position.spiegel': 'In welchen Spiegel die Position eingeht',
  /* R1429–R1432 — Der Saldowechsel an Position, Konto und Kontengruppe (2.7 Abschnitt 3.7a). */
  'stammdaten.zweck.position.saldowechsel_ziel':
    'Wo der verrechnete Wert der Position steht, wenn er das andere Vorzeichen trägt — etwa das Währungsergebnis im Ertrag statt im Aufwand',
  'stammdaten.zweck.konto.saldowechsel_ziel':
    'Wo das Konto je Gesellschaft steht, wenn sein Saldo das andere Vorzeichen trägt — etwa ein überzogenes Bankkonto unter den Verbindlichkeiten',
  'stammdaten.zweck.kontengruppe.saldowechsel_ziel':
    'Wo jedes Konto der Gruppe je Gesellschaft steht, wenn sein Saldo das andere Vorzeichen trägt',
  'stammdaten.sperre.saldowechsel_ziel.unterpositionen':
    'Eine Position mit Positionen darunter wechselt nicht als Ganzes; das Ziel steht an den Positionen unter ihr.',
  'stammdaten.sperre.saldowechsel_ziel.konto':
    'Kein Saldowechsel an der Position möglich: Das Konto {konto} wechselt für sich. Die Position wechselt erst als Ganzes, wenn keines ihrer Konten mehr ein Ziel trägt.',
  'stammdaten.sperre.saldowechsel_ziel.position':
    'Kein eigener Saldowechsel möglich: Die Position {position} wechselt als Ganzes nach {ziel}. Wer jedes Konto für sich wechseln will, entfernt dort zuerst das Ziel.',
  'stammdaten.zweck.analysedimension.nummer': 'Die Achse, unter der die Dimension im Aufriss erscheint',
  'stammdaten.zweck.dimensionswert.dimension': 'Zu welcher Achse der Wert gehört',
  'stammdaten.zweck.dimensionswert.eltern': 'Der übergeordnete Wert — Werte lassen sich staffeln',
  'stammdaten.zweck.partnercode.einheit': 'Welche Gesellschaft hinter dem Code des Vorsystems steht',
  'stammdaten.zweck.partnercode.eltern': 'Der übergeordnete Code — Codes lassen sich staffeln',
  'stammdaten.zweck.partnercode.ist_gruppe': 'Die Zeile fasst Codes zusammen, statt selbst einer zu sein',
  'stammdaten.zweck.positionsgliederung.position': 'Welche Position eingehängt wird',
  'stammdaten.zweck.positionsgliederung.eltern': 'Unter welche Position sie gehängt wird',
  'stammdaten.zweck.kontozuordnung.konto': 'Welches Konto zugeordnet wird',
  'stammdaten.zweck.kontozuordnung.position': 'In welche Position des Abschlusses es geht',
  'stammdaten.zweck.kontozuordnung.bemerkung': 'Warum die Zuordnung so lautet — für den Prüfer',
  'stammdaten.zweck.kontozuordnung.gruppe':
    'Welche Kontengruppe das Konto in diesen Monaten hat — sie bestimmt Kurs, Bewegungsartpflicht und Finanzmittelfonds',
  'stammdaten.zweck.kfr_zeile.standard': 'Zu welchem Standard die Zeile gehört — DRS 21 oder IAS 7',
  'stammdaten.zweck.kfr_zeile.bereich': 'In welchem Teil der Rechnung sie steht: laufende Tätigkeit, Investition, Finanzierung oder Fonds',
  'stammdaten.zweck.kfr_zeile.art': 'Ob sie Beträge aufnimmt oder über andere Zeilen summiert',
  'stammdaten.zweck.kfr_zeile.rolle': 'Welche Aufgabe die Rechnung ihr gibt, unabhängig von ihrer Nummer',
  'stammdaten.zweck.kfr_zeile.zaehlweise': 'Was eine Summe zusammenzählt — nur gewöhnliche Zeilen, nie andere Summen',
  'stammdaten.zweck.kfr_zeile.schrift': 'Ob eine Summenzeile fett oder normal steht — leer heißt die Form ihrer Art',
  'stammdaten.zweck.kfr_zeile.hintergrund': 'Welchen Hintergrund eine Summenzeile trägt — leer heißt die Form ihrer Art',
  'stammdaten.zweck.kfr_zeile.schriftfarbe': 'In welcher Farbe eine Summenzeile geschrieben steht — leer heißt die gewöhnliche Schrift',
  'stammdaten.zweck.kfr_zuordnung.position': 'An welcher Bilanz- oder GuV-Position die Referenz hängt — sie gilt auch für die Positionen darunter, bis dort eine eigene steht',
  'stammdaten.zweck.kfr_zuordnung.konto': 'An welchem einzelnen Konto die Referenz hängt — genauer als jede Position',
  'stammdaten.zweck.kfr_zuordnung.kontengruppe': 'An welcher Kontengruppe die Regel hängt — nur für Rechnungen ohne Gliederung',
  'stammdaten.zweck.kfr_zuordnung.bewegungsart': 'Welche Bewegungsart die Referenz zog — nur an Referenzen von vor dem 2026-10-06',
  'stammdaten.zweck.kfr_zuordnung.bewegungsarten': 'Welche Bewegungsarten die Referenz zieht — jede ausdrücklich angekreuzt',
  'stammdaten.zweck.kfr_zuordnung.alle_bewegungsarten': 'Ob die Referenz jede Bewegungsart zieht, auch die, die eine andere Referenz schon zieht',
  'stammdaten.zweck.kfr_zuordnung.vorzeichen_umkehren': 'Ob die Referenz das Vorzeichen gegenüber der Quelle umkehrt — etwa um den Zinsaufwand aus der laufenden Tätigkeit herauszurechnen',
  'stammdaten.zweck.kfr_zuordnung.zeile': 'In welche Zeile der Kapitalflussrechnung die Referenz zieht',
  'stammdaten.zweck.datenquelle.art': 'Woher geliefert wird: Tabellenblatt, Textdatei, Datenbank',
  'stammdaten.zweck.datenquelle.herkunft': 'Welches Vorsystem liefert',
  'stammdaten.zweck.datenquelle.zustaendig': 'Wer die Lieferung verantwortet',
  'stammdaten.zweck.organisationsordner.eltern': 'Der übergeordnete Ordner — ein Ordner ohne Eltern ist eine eigene Gliederung',
  'stammdaten.zweck.organisationszuordnung.einheit': 'Welche Gesellschaft eingeordnet wird',
  'stammdaten.zweck.organisationszuordnung.ordner': 'In welchen Ordner sie kommt',
  'stammdaten.zweck.rolle.ist_lesend': 'Ob die Rolle nur liest und nichts ändern darf',
  'stammdaten.zweck.sprache.ist_stammdatensprache': 'Ob in dieser Sprache Bezeichnungen gepflegt werden',
  'stammdaten.zweck.sprache.sortierung': 'Die Reihenfolge, in der die Rückfallkette sucht, wo eine Bezeichnung fehlt',
  'stammdaten.zweck.sprache.eigenname': 'Wie die Sprache sich selbst nennt',
  'stammdaten.zweck.waehrung.ezb_referenzkurs': 'Ob die EZB für diese Währung einen Kurs veröffentlicht — nur dann holt der Knopf etwas',
  'stammdaten.zweck.feld.objektart': 'Für welche Objektart das Hausfeld gilt',
  'stammdaten.zweck.feld.feldart': 'Was für ein Wert hineingehört: Text, Zahl, Ja/Nein, Periode, Auswahl',
  'stammdaten.zweck.feld.klasse': 'Ob das Feld ordnet, nachweist oder beschriftet',
  'stammdaten.zweck.feld.pflicht': 'Ob das Feld ausgefüllt sein muss',
  'stammdaten.zweck.feld.vorrat': 'Die wählbaren Werte, durch „|\' getrennt',

  'stammdaten.zweck': 'Zweck der Angabe',
  'stammdaten.zweck.stift': 'ändern',
  'stammdaten.zweck.aendern': 'Wofür {feld} gepflegt wird',
  'stammdaten.zweck.giltfueralle': 'Dieser Satz gilt für alle Objekte dieser Art, nicht nur für dieses.',

  'stammdaten.eintraege': 'Einträge',
  'stammdaten.gruppieren': 'Gruppieren nach',
  'stammdaten.gruppieren.keine': '— nicht gruppiert —',
  'stammdaten.sortieren': 'Sortieren nach',
  'stammdaten.sortieren.code': 'Code',
  'stammdaten.sortieren.name': 'Bezeichnung',
  'stammdaten.sortieren.reihenfolge': 'Reihenfolge im Spiegel',
  'stammdaten.sortieren.reihenfolge.allgemein': 'Reihenfolge',
  'stammdaten.einordnen.greifen': 'Greifen und an eine andere Stelle ziehen',
  'stammdaten.einordnen.vor': 'vor {name}',
  'stammdaten.einordnen.ende': 'ans Ende',
  'stammdaten.gruppe.ohne': 'Ohne Angabe',
  'stammdaten.suche': 'Bezeichnung, Kurzbezeichnung oder Schlüssel',
  'stammdaten.nichts': 'Kein Eintrag passt zu dieser Suche.',
  'stammdaten.leer.titel': 'Nichts ausgewählt',
  'stammdaten.leer.text':
    'Wählen Sie links einen Eintrag. Das Blatt zeigt dann seine Bezeichnungen in allen gepflegten Sprachen, seine Eigenschaften samt Herkunft und die Fassungen, in denen es geändert wurde.',
  'stammdaten.leer.dimensionen.titel': 'Dimensionen, die Sie selbst bestimmen',
  'stammdaten.leer.dimensionen.text':
    'Hier richten Sie zusätzliche Dimensionen ein — Kostenstelle, Profit Center, Kostenträger oder was Ihr Haus sonst auswertet. Acht sind vorgesehen. Sie geben jeder ihren Namen und pflegen darunter ihre Werte. Wählen Sie links eine aus oder legen Sie eine neue an.',
  'stammdaten.objektid': 'Objekt-ID',
  'stammdaten.objektid.kopieren': 'kopieren',
  'stammdaten.objektid.kopiert': 'kopiert',
  'stammdaten.objektid.ging.nicht': 'ging nicht — bitte von Hand markieren',

  'stammdaten.beschriftungen': 'Bezeichnungen und Kürzel',
  'stammdaten.sprache': 'Sprache',
  'stammdaten.name': 'Bezeichnung',
  'stammdaten.kuerzel': 'Kürzel',
  // **Zwei Knöpfe, zwei Wörter** (R588). Im Register unter *Betrieb → Sprachen*
  // entsteht eine Sprache — dort heißt es *Anlegen*. Am Element entsteht keine,
  // sondern eine Zeile zu einer, die das Haus längst erklärt hat.
  'stammdaten.sprache.anlegen': 'Anlegen',

  // ---- Die Sprachen des Hauses (7.4 Abschnitt 18.6, R556) ----

  'sprache.unbekannt.meldung': '„{sprache}" ist keine Sprache, die dieses Haus erklärt hat.',
  'sprache.unbekannt.weg':
    'Erklären Sie sie unter Betrieb → Sprachen und Begriffe des Hauses. Eine Sprache entsteht dort und nicht an einem einzelnen Stammdatum — sonst führt das Haus irgendwann zwei Schreibweisen derselben Sprache, und niemand bemerkt es.',
  'sprache.stillgelegt.meldung': '{sprache} ist stillgelegt und nimmt keine neuen Texte mehr an.',
  'sprache.stillgelegt.weg':
    'Die vorhandenen Texte bleiben lesbar. Wer wieder in dieser Sprache pflegen will, nimmt die Stilllegung unter Betrieb → Sprachen zurück.',
  'sprache.keine.meldung':
    'Dieses Haus führt keine einzige Sprache für Stammdaten — es lässt sich deshalb nichts benennen.',
  'sprache.keine.weg':
    'Erklären Sie unter Betrieb → Sprachen und Begriffe des Hauses mindestens eine Sprache als Stammdatensprache. Solange keine steht, kann kein Name geschrieben werden.',
  'sprachen.haus': 'Die Sprachen dieses Hauses',
  'sprachen.spalte.sprache': 'Sprache',
  'sprachen.spalte.stammdaten': 'Stammdaten',
  'sprachen.spalte.bedienung': 'Bedienung',
  'sprachen.spalte.reihenfolge': 'Reihenfolge',
  'sprachen.ja': 'ja',
  'sprachen.nein': 'nein',
  'sprachen.bedienung.mitdatei': 'liegt vor',
  'sprachen.bedienung.ohnedatei': 'mit der nächsten Auslieferung',
  'sprachen.stillgelegt': 'stillgelegt',
  'sprachen.leer': 'Dieses Haus hat noch keine Sprache erklärt.',
  'sprachen.fuehren': 'auch hierin führen',
  'sprachen.nichtmehrfuehren': 'nicht mehr führen',
  'sprachen.stilllegen': 'stilllegen',
  'sprachen.aufnehmen': 'wieder aufnehmen',

  'stammdaten.eigenschaften': 'Eigenschaften',
  'stammdaten.feld': 'Eigenschaft',
  'stammdaten.wert': 'Wert',
  'stammdaten.herkunft': 'Herkunft',
  'stammdaten.rechnend': 'rechnend',
  'stammdaten.rechnend.warnung':
    'Es sind {anzahl} Abschlüsse freigegeben. Sie bleiben unverändert — sie lesen auf ihrem Datenstand. Eine rechnende Eigenschaft wirkt erst im nächsten Lauf.',
  'stammdaten.geerbt': 'geerbt von {von}',
  'fassung.veraltet': 'Es gibt eine neuere Fassung von CoKo. Diese Seite ist älter — bitte neu laden. Bis dahin wird nichts gespeichert.',
  'fassung.neuladen': 'Neu laden',
  'anfrage.unbekannt.meldung': 'Die Angabe „{angabe}“ kennt CoKo nicht — die Seite ist vermutlich veraltet. Es wurde nichts gespeichert.',
  'anfrage.unbekannt.weg': 'Bitte die Seite neu laden und die Eingabe wiederholen.',
  'fassung.veraltet.speichern': 'Diese Seite ist älter als CoKo und speichert deshalb nicht. Bitte neu laden.',
  'stammdaten.abwerk.hinweis':
    'Konto ab Werk: Name, Nummer, Art und Eigenschaften lassen sich nicht ändern, und es lässt sich weder stilllegen noch löschen. Die Einhängung in die Gliederung können Sie ändern.',
  'stammdaten.abwerk.meldung': '{konto} ist ein Konto ab Werk und lässt sich nicht ändern, stilllegen oder löschen.',
  'stammdaten.abwerk.weg': 'Ändern lässt sich nur die Einhängung in die Gliederung — unter Positionspläne.',
  // Das eigene Konto einer Stichtagskursanpassung (2.6 Abschnitt 2.14, R1656, R1659).
  'stammdaten.eigeneskonto.meldung': '{konto} ist das eigene Konto einer Stichtagskursanpassung: CoKo führt es, und sein Name kommt vom Quellkonto.',
  'stammdaten.eigeneskonto.weg': 'Ändern Sie das Quellkonto, oder nehmen Sie dort den Haken ab.',
  'stichtagskursanpassung.titel': 'Stichtagskursanpassung',
  'stichtagskursanpassung.haken': 'Ausweis der Stichtagskursanpassung auf separatem Konto',
  'stichtagskursanpassung.ab': 'ab',
  'stichtagskursanpassung.gilt': 'Seit {ab} auf {konto}.',
  'stichtagskursanpassung.giltnicht': 'Seit {ab} wieder auf {konto}.',
  'stichtagskursanpassung.ohnemonat': 'Bitte zuerst den Monat wählen, ab dem der Haken gilt.',
  'stichtagskursanpassung.periode.meldung': '{periode} ist kein Monat.',
  'stichtagskursanpassung.konto.meldung': 'Das Konto {konto} gibt es nicht.',
  'stichtagskursanpassung.stichtag.meldung': '{konto} wird zum Stichtagskurs umgerechnet; eine Stichtagskursanpassung entsteht dort nicht.',
  'stichtagskursanpassung.ruecklage.meldung': 'Ohne {konto} unter einer Position lässt sich kein eigenes Konto anlegen.',
  'abbruch.jahresergebnis-fehlt.meldung':
    'Das Konto {konto} fehlt im Kontenplan. Es wird mit CoKo ausgeliefert und lässt sich nicht löschen — die Installation ist unvollständig.',
  'abbruch.jahresergebnis-fehlt.weg': 'Melden Sie das dem Betrieb.',
  'stammdaten.gesetzt': 'hier gesetzt',
  'stammdaten.ungepflegt': 'nirgends gepflegt',
  'stammdaten.ueberschreiben': 'überschreiben',
  'stammdaten.erben': 'erben',
  'stammdaten.keinefelder': 'Diese Stammdatenart trägt außer ihrer Bezeichnung keine Eigenschaften.',

  'stammdaten.fassungen': 'Fassungen',
  'stammdaten.fassungen.einklappen': 'Fassungen einklappen',
  'stammdaten.fassungen.ausklappen': 'Fassungen ausklappen',
  'stammdaten.geloescht': 'gelöscht',

  /* --- Die Eigenschaften, alphabetisch --- */
  'stammdaten.feld.art': 'Art',
  'stammdaten.feld.basis_ledger': 'Basis-Bewertungsebene',
  'stammdaten.feld.bewegungsartpflicht': 'Bewegungsartpflicht',
  'stammdaten.feld.code': 'Code',
  'stammdaten.feld.eltern': 'Übergeordnete Gruppe',
  'stammdaten.feld.fonds_mit_kontokorrent': 'Fonds mit Kontokorrent',
  'stammdaten.feld.geschaeftsjahr_beginn': 'Anfangsmonat des Geschäftsjahres',
  'stammdaten.feld.gruppe': 'Kontengruppe',
  'stammdaten.feld.guv_umrechnung': 'GuV-Umrechnung',
  'stammdaten.feld.ist_aktiv': 'Eingerichtet',
  'stammdaten.feld.ist_beteiligung': 'Beteiligungskonto',
  'stammdaten.feld.ist_beteiligungsertrag': 'Beteiligungsertrag',
  'stammdaten.feld.ist_delta': 'Delta-Bewertungsebene',
  'stammdaten.feld.ist_eigenkapital': 'Eigenkapitalkonto',
  'stammdaten.feld.ist_eroeffnung': 'Eröffnungswert',
  'stammdaten.feld.ist_fonds': 'Gehört zum Finanzmittelfonds',
  'stammdaten.feld.ist_kontokorrent': 'Jederzeit fällige Kreditinanspruchnahme',
  'stammdaten.feld.ist_ic': 'Konzerninternes Konto',
  'stammdaten.feld.ist_kern': 'Ist selbst ein Kern',
  'stammdaten.feld.ist_summe': 'Position mit Positionen darunter',
  'stammdaten.feld.ist_vorgabe': 'Vorgabe',
  'stammdaten.feld.kern': 'Kern-Bewegungsart',
  'stammdaten.feld.kfr_zeile': 'Zeile der Kapitalflussrechnung',
  'stammdaten.feld.konzernwaehrung': 'Konzernwährung',
  'stammdaten.feld.kontoart': 'Kontoart',
  'stammdaten.feld.kurstyp': 'Kurstyp',
  'stammdaten.wert.wirtschaftsjahr_beginn.1': 'Januar',
  'stammdaten.wert.wirtschaftsjahr_beginn.2': 'Februar',
  'stammdaten.wert.wirtschaftsjahr_beginn.3': 'März',
  'stammdaten.wert.wirtschaftsjahr_beginn.4': 'April',
  'stammdaten.wert.wirtschaftsjahr_beginn.5': 'Mai',
  'stammdaten.wert.wirtschaftsjahr_beginn.6': 'Juni',
  'stammdaten.wert.wirtschaftsjahr_beginn.7': 'Juli',
  'stammdaten.wert.wirtschaftsjahr_beginn.8': 'August',
  'stammdaten.wert.wirtschaftsjahr_beginn.9': 'September',
  'stammdaten.wert.wirtschaftsjahr_beginn.10': 'Oktober',
  'stammdaten.wert.wirtschaftsjahr_beginn.11': 'November',
  'stammdaten.wert.wirtschaftsjahr_beginn.12': 'Dezember',
  'stammdaten.wert.geschaeftsjahr_beginn.1': 'Januar',
  'stammdaten.wert.geschaeftsjahr_beginn.2': 'Februar',
  'stammdaten.wert.geschaeftsjahr_beginn.3': 'März',
  'stammdaten.wert.geschaeftsjahr_beginn.4': 'April',
  'stammdaten.wert.geschaeftsjahr_beginn.5': 'Mai',
  'stammdaten.wert.geschaeftsjahr_beginn.6': 'Juni',
  'stammdaten.wert.geschaeftsjahr_beginn.7': 'Juli',
  'stammdaten.wert.geschaeftsjahr_beginn.8': 'August',
  'stammdaten.wert.geschaeftsjahr_beginn.9': 'September',
  'stammdaten.wert.geschaeftsjahr_beginn.10': 'Oktober',
  'stammdaten.wert.geschaeftsjahr_beginn.11': 'November',
  'stammdaten.wert.geschaeftsjahr_beginn.12': 'Dezember',
  'stammdaten.feld.einheitsart': 'Art der Gesellschaft',
  'stammdaten.feld.rhythmus': 'Erwarteter Rhythmus der Datenlieferung',
  'stammdaten.feld.sitz_ort': 'Sitz (Ort)',
  'ordner.eltern.fehlt.meldung': 'Den Ordner {ordner} gibt es nicht.',
  'ordner.eltern.fehlt.weg': 'Wählen Sie einen vorhandenen Ordner aus der Liste.',
  'ordner.zirkel.meldung': 'Ein Ordner kann nicht unter sich selbst hängen.',
  'ordner.zirkel.weg': 'Wählen Sie einen Ordner, der nicht schon darunter liegt.',
  'ordner.doppelt.meldung': 'Diese Gesellschaft hängt in derselben Gliederung schon in {ordner}.',
  'ordner.doppelt.weg': 'Je Gliederung hängt eine Gesellschaft an genau einer Stelle. Beenden Sie die bisherige Zuordnung oder wählen Sie eine andere Gliederung.',
  'stammdaten.art.organisationsordner': 'Ordner',
  'stammdaten.art.organisationszuordnung': 'Zuordnung zum Ordner',
  'stammdaten.feld.ordner': 'Ordner',
  'stammdaten.feld.wurzel': 'Gliederung',
  'bereich.stammdaten.ordner': 'Ordner der Gesellschaften',

  // ---- Woran ein Bericht hängt (2.3 Abschnitt 10, R712-R716) ----

  'bereich.stammdaten.organisationselemente': 'Organisationselemente',
  'stammdaten.art.organisationselement': 'Organisationselement',
  'stammdaten.feld.objekt': 'Gemeintes Objekt',
  'stammdaten.zweck.organisationselement.art':
    'Worauf sich der Bericht bezieht — davon hängt ab, was in der Auswahl daneben steht',
  'stammdaten.zweck.organisationselement.objekt':
    'Welches Objekt gemeint ist. Bezeichnung und Zeitachse kommen von dort und werden hier nicht wiederholt',
  'stammdaten.zweck.organisationselement.sortierung':
    'Wo es in der Auswahl über dem Bericht steht — leer heißt: hinten, nach Bezeichnung',
  'stammdaten.wert.art.kreis': 'Konsolidierungskreis',
  'stammdaten.wert.art.gesellschaft': 'Gesellschaft',
  'stammdaten.wert.art.ordner': 'Ordner',
  'stammdaten.wert.art.dimensionswert': 'Wert einer eigenen Dimension',
  'stammdaten.aufnehmen': '{art} aufnehmen',
  'stammdaten.aufnehmen.speichern': 'Aufnehmen',
  'stammdaten.aufgenommen': 'Aufgenommen wird — angelegt wird die {art} dort, wo sie hingehört',
  'stammdaten.leer.organisationselemente.titel': 'Was oben im Bericht zur Wahl steht',
  'stammdaten.leer.organisationselemente.text':
    'Ein Bericht zeigt immer einen Ausschnitt — den ganzen Konsolidierungskreis, eine einzelne Gesellschaft, einen Ordner oder einen Wert einer eigenen Dimension. Was hier aufgenommen ist, steht über der Bilanz zur Auswahl. Angelegt werden die Objekte selbst weiterhin dort, wo sie hingehören; hier wird nur bestimmt, welche davon auswählbar sind.',

  // ---- Der Feldkatalog des Hauses (7.4 Abschnitt 18.5, R554) ----

  'bereich.stammdaten.eigenschaften': 'Eigenschaften erklären',
  'stammdaten.art.feld': 'Eigenschaften',
  'stammdaten.feld.objektart': 'Gilt für',
  'stammdaten.feld.feldart': 'Art der Eingabe',
  'stammdaten.feld.klasse': 'Wozu sie da ist',
  'stammdaten.feld.vorrat': 'Werte zur Auswahl, durch | getrennt',
  'stammdaten.feld.pflicht': 'Pflichtangabe',
  // **Ein Vorschlag wirkt erst, wenn ihn jemand nimmt** (R5). Der Wert steht
  // im Knopf, damit der Anwender nicht erst die Klappliste aufziehen muss, um
  // zu sehen, worauf er klickt.
  'stammdaten.vorschlag.uebernehmen': 'Vorschlag übernehmen: {wert}',
  // Warum das System etwas anderes vorschlägt (R145, R616). Einfache Sätze:
  // Hier liest ein Finanzer und kein Entwickler.
  'stammdaten.vorschlagsgrund.beherrscht_direkt':
    'Der Halter hat mit {stimmen} % die Stimmenmehrheit.',
  'stammdaten.vorschlagsgrund.beherrscht_mittelbar':
    'Der Halter beherrscht die Gesellschaft über {ueber} — Stimmen direkt: {stimmen} %.',
  'stammdaten.vorschlagsgrund.einfluss':
    'Mit {stimmen} % der Stimmen besteht maßgeblicher Einfluss, aber keine Mehrheit.',
  'stammdaten.vorschlagsgrund.keiner':
    '{stimmen} % der Stimmen — weder Mehrheit noch maßgeblicher Einfluss.',
  'stammdaten.vorschlagsgrund.aus_beteiligungsart': 'Folgt der Beteiligungsart darüber.',  'stammdaten.hausfeld': 'vom Haus erklärt',
  'stammdaten.feld.land': 'Land',
  'stammdaten.feld.nummer': 'Nummer',
  'stammdaten.feld.oberste_einheit': 'Oberste Gesellschaft',
  'stammdaten.feld.positionsplan': 'Positionsplan',
  'stammdaten.feld.rechnungslegung': 'Rechnungslegung',
  'stammdaten.feld.saldowechsel_ziel': 'Ausweis negativer Salden bei aktiviertem Saldowechsel',
  'stammdaten.feld.dimension': 'Analysedimension',
  'stammdaten.feld.position': 'Position',
  'stammdaten.feld.konto': 'Konto',
  'stammdaten.feld.gueltig_von': 'Gültig ab Periode',
  'stammdaten.feld.mutter': 'Halter (Mutter)',
  'stammdaten.feld.tochter': 'Gehaltene Gesellschaft (Tochter)',
  /* Der Kopf trägt den Namen, der Bedienhinweis steht im Zwecksatz (R840,
     Entscheidung 4 vom 2026-09-30) */
  'stammdaten.feld.quote': 'Kapitalanteil',
  'stammdaten.feld.stimmrechtsanteil': 'Stimmrechtsanteil',
  'stammdaten.zahl.nurganz': 'Hier steht nur eine ganze Zahl, ohne Nachkommastellen.',
  'stammdaten.feld.beteiligungsart': 'Beteiligungsart',
  'stammdaten.feld.einbeziehungsart': 'Einbeziehungsart',
  'stammdaten.feld.erwerbsperiode': 'Erwerbsperiode',
  'stammdaten.feld.eintritt_zeitpunkt': 'Einbeziehung ab',
  'stammdaten.feld.austritt_zeitpunkt': 'Austritt zum',
  'stammdaten.feld.anschaffungskosten': 'Anschaffungskosten',
  'stammdaten.feld.anschaffungswaehrung': 'Währung der Anschaffung',
  'stammdaten.feld.aufsatzart': 'Aufsatzart',
  'stammdaten.feld.aufsatzmonat': 'Aufsatzmonat',
  'stammdaten.feld.firmenwert_nutzungsdauer': 'Nutzungsdauer des Firmenwerts (Monate)',
  'stammdaten.feld.firmenwert_rest': 'Restbuchwert des Firmenwerts',
  'stammdaten.feld.firmenwert_restnutzungsdauer': 'Restnutzungsdauer des Firmenwerts (Monate)',
  'stammdaten.feld.ausgleichsposten': 'Ausgleichsposten der anderen Gesellschafter',
  'stammdaten.feld.periode': 'Monat',
  'stammdaten.feld.zeitpunkt': 'Zeitpunkt',
  'stammdaten.feld.quote_danach': 'Kapitalanteil danach',
  'stammdaten.feld.beteiligung': 'Beteiligung',
  'stammdaten.feld.betrag': 'Betrag',
  'stammdaten.feld.kurs': 'Kurs',
  'stammdaten.feld.nutzungsdauer_monate': 'Nutzungsdauer in Monaten',
  'stammdaten.feld.kreis': 'Konsolidierungskreis',
  'stammdaten.feld.gueltig_bis': 'Gültig bis Periode',
  'stammdaten.feld.bemerkung': 'Bemerkung',
  'stammdaten.feld.herkunft': 'Herkunft',
  'stammdaten.feld.vorlage': 'Importvorlage',
  'stammdaten.feld.einheit': 'Gesellschaft',
  'stammdaten.feld.ledger': 'Bewertungsebene',
  'stammdaten.feld.zustaendig': 'Zuständig',
  'stammdaten.feld.sortierung': 'Sortierung',
  'stammdaten.feld.spiegel': 'Spiegel',
  'stammdaten.feld.spiegel_block': 'Spiegelblock',
  'stammdaten.feld.spiegel_spalte': 'Spiegelspalte',
  'stammdaten.feld.ezb_referenzkurs': 'Referenzkurs der EZB verfügbar',
  'stammdaten.feld.stillgelegt_ab': 'Stillgelegt ab Periode',
  'stammdaten.feld.umrechnungsmethode': 'Umrechnungsmethode',
  'stammdaten.feld.vorzeichen': 'Darstellungsvorzeichen',
  'stammdaten.feld.waehrung': 'Währung',
  'stammdaten.feld.zahlungswirksam': 'Zahlungswirksam',
  'stammdaten.feld.umgliederung_eigenkapital': 'Umgliederung im Eigenkapital',
  'stammdaten.feld.zweck': 'Zweck',

  /* --- Die Wertevorräte --- */
  'stammdaten.wert.rohdaten': 'Rohdaten — nur Geliefertes',
  'stammdaten.wert.delta': 'Delta — Anpassungsschicht',
  'stammdaten.wert.gruppe': 'Gruppe — Summe ihrer Schichten',
  'stammdaten.wert.einheitsart.operativ': 'Operativ tätig',
  'stammdaten.wert.einheitsart.holding': 'Holding',
  'stammdaten.wert.einheitsart.zweckgesellschaft': 'Zweckgesellschaft',
  'stammdaten.wert.einheitsart.ruhend': 'Ruhend',
  'stammdaten.wert.rhythmus.monatlich': 'monatlich',
  'stammdaten.wert.rhythmus.quartalsweise': 'quartalsweise',
  'stammdaten.wert.rhythmus.halbjaehrlich': 'halbjährlich',
  'stammdaten.wert.rhythmus.jaehrlich': 'jährlich',
  // Der feste Vorrat der Beteiligungsarten (R615). Er steht unter dem
  // Feldnamen und nicht frei: „Tochter" ist daneben auch ein Feld, und zwei
  // Bedeutungen unter einem Schlüssel fallen erst in der Übersetzung auf.
  'stammdaten.wert.beteiligungsart.tochter': 'Tochterunternehmen (§ 290 HGB)',
  'stammdaten.wert.beteiligungsart.gemeinschaft': 'Gemeinschaftsunternehmen (§ 310 HGB)',
  'stammdaten.wert.beteiligungsart.assoziiert': 'Assoziiertes Unternehmen (§ 311 HGB)',
  'stammdaten.wert.beteiligungsart.sonstige': 'Sonstige Beteiligung',
  'stammdaten.wert.voll': 'Vollkonsolidierung',
  'stammdaten.wert.quotal': 'Quotenkonsolidierung',
  // Ein Ja/Nein-Feld des Hauses in der Tafel (R554, R840) — gespeichert als true und false
  'stammdaten.wert.true': 'Ja',
  'stammdaten.wert.false': 'Nein',
  'stammdaten.wert.equity': 'Equity-Methode',
  'stammdaten.wert.nicht': 'nicht einbezogen',
  'stammdaten.wert.beginn': 'Periodenbeginn',
  'stammdaten.wert.ende': 'Periodenende',
  'stammdaten.wert.art.eigenkapital': 'Eigenkapital zum Erwerb',
  'stammdaten.wert.art.stille_reserve': 'Stille Reserve',
  'stammdaten.wert.art.zukauf': 'Zukauf',
  'stammdaten.wert.art.teilverkauf': 'Teilverkauf',
  'stammdaten.wert.art.kapitalerhoehung': 'Kapitalerhöhung',
  'stammdaten.wert.aufsatzart.werte_zum_erwerb': 'Werte zum Erwerb',
  'stammdaten.wert.aufsatzart.staende_zum_aufsatz': 'Stände zum Aufsatz',
  'stammdaten.wert.aufsatzart.manuell': 'manuell buchen',
  'stammdaten.wert.aktiv': 'Aktivkonto',
  'stammdaten.wert.passiv': 'Passivkonto',
  'stammdaten.wert.ertrag': 'Ertragskonto',
  'stammdaten.wert.aufwand': 'Aufwandskonto',
  'stammdaten.wert.stichtag': 'Stichtagskurs',
  'stammdaten.wert.stichtag_vorperiode': 'Stichtagskurs der Vorperiode',
  'stammdaten.wert.durchschnitt': 'Durchschnittskurs',
  'stammdaten.wert.historisch': 'Historischer Kurs',
  'stammdaten.wert.einbeziehung': 'Kurs zum Einbeziehungszeitpunkt',
  'stammdaten.wert.uebertrag': 'Übertrag ohne Umrechnung',
  'stammdaten.wert.keiner': 'Kein Kurs',
  'stammdaten.wert.operativ': 'Laufende Geschäftstätigkeit',
  'stammdaten.wert.investition': 'Investitionstätigkeit',
  'stammdaten.wert.finanzierung': 'Finanzierungstätigkeit',
  'stammdaten.wert.keine': 'Keine Wirkung',
  'stammdaten.wert.spiegel.keiner': 'Kein Spiegel',
  'stammdaten.wert.alle': 'Alle Spiegel',
  'stammdaten.wert.anlagen': 'Anlagenspiegel',
  'stammdaten.wert.eigenkapital': 'Eigenkapitalspiegel',
  'stammdaten.wert.verbindlichkeiten': 'Kreditspiegel',
  'stammdaten.wert.abschreibung': 'Kumulierte Abschreibungen',
  'stammdaten.wert.aktiva': 'Aktiva',
  'stammdaten.wert.passiva': 'Passiva',
  'stammdaten.wert.kfr': 'Kapitalflussrechnung',
  'stammdaten.wert.ek_spiegel': 'Eigenkapitalspiegel',
  'stammdaten.wert.handelsrecht': 'Handelsrecht',
  'stammdaten.wert.ifrs': 'IFRS',
  'stammdaten.wert.steuerlich': 'Steuerlich',
  'stammdaten.wert.management': 'Management',
  'stammdaten.wert.umsatzkosten': 'Umsatzkostenverfahren',
  'stammdaten.wert.intern': 'Intern',
  'stammdaten.wert.gesetzlich': 'Gesetzlicher Kreis',
  'stammdaten.wert.teilkonzern': 'Teilkonzern',
  'stammdaten.wert.monatsdurchschnitt': 'Monatsdurchschnitt',
  'stammdaten.wert.ytd': 'Kumuliert seit Jahresbeginn',
  'stammdaten.wert.vorzeichen.1': 'Wie gebucht',
  'stammdaten.wert.vorzeichen.-1': 'Gedreht',


  /* ---- Kapitalflussrechnung, DRS 21 (2.8, G3) ---------------------- */

  'kfr.kontrolle': 'Kontrolle: Fonds am Ende gegen Bilanz',
  'kfr.kontrolle.stimmt': 'Geht auf: Der Finanzmittelfonds am Ende trifft den Bestand der Fondskonten auf den Cent.',
  'kfr.kontrolle.differenz': 'Geht nicht auf: Um diesen Betrag weicht der Finanzmittelfonds am Ende vom Bestand der Fondskonten ab.',
  'kfr.spalte.nummer': 'Nr.',
  'kfr.spalte.zeile': 'Zeile',
  'kfr.spalte.betrag': 'Betrag',

  // ---- Die Fondsabgrenzung (R63, R271, R272) ------------------------------
  //
  // Seit dem 2026-10-05 steht dazu weder am Bereichsband noch unter der
  // Rechnung etwas (R1120). Der Befund kommt weiter von der Rechnung; die
  // Oberfläche zeigt ihn nicht.
  'kfr.befund.kontokorrent-ungewaehlt':
    '{anzahl} Konten sind als Kontokorrent gekennzeichnet, der Konsolidierungskreis hat sich zur Fondsabgrenzung aber nicht geäußert. Sie bleiben deshalb außerhalb des Finanzmittelfonds — zulässig nach DRS 21, aber vielleicht nicht gewollt. Die Wahl steht in den Stammdaten des Kreises.',
  // Die zweite Rechnung der Kontrollzeile (2.8 Abschnitt 13.43, R1350).
  'kfr.befund.zeile-ohne-summe':
    'Zeile {zeile} steht in keiner Summe — {betrag} fehlen im Cashflow. Die Kontrollzeile zeigt die Differenz.',
  'kfr.befund.zeile-doppelt':
    'Zeile {zeile} zählt in mehr als einer Summe — {betrag} stehen doppelt im Cashflow. Die Kontrollzeile zeigt die Differenz.',
  'kfr.befund.rolle-fehlt':
    'Der Zeilensatz {standard} besetzt diese Rollen nicht: {rollen}. Beträge, die dorthin gehören, fallen aus der Rechnung — sichtbar an der Probe unter dem Fondsnachweis. Geraten wird nichts.',

  // ---- Der Wertenachweis aus einer KFR-Zeile (2.8 Abschnitt 13.14) --------
  //
  // Er hat NICHT die Form des Bilanznachweises: Eine KFR-Zeile hat keinen
  // Anfangsbestand, sie ist selbst schon eine Bewegungsrechnung. Deshalb
  // eigene Wörter — eine geliehene Beschriftung wäre hier eine falsche.
  'kfrnachweis.aufbau':
    'Was diese Zeile im Zeitraum {von} bis {bis} aufgenommen hat — je Gesellschaft, Konto und Bewegungsart, mit ihrer Herkunft.',
  'kfrnachweis.art.gebucht': 'gebucht',
  'kfrnachweis.art.gerechnet': 'gerechnet',
  'kfrnachweis.art.anfangsbestand': 'Anfangsbestand',
  'kfrnachweis.zeitraum': 'Zeitraum',
  'kfrnachweis.keinbeleg': 'keine Buchung',
  /* R856 — Der Nachweis einer KFR-Zeile zeigt ihre Regeln als Filtertabelle mit Beträgen. */
  'kfrnachweis.regel.position': 'Position',
  'kfrnachweis.regel.konten': 'Konten',
  'kfrnachweis.regel.ueberall': 'überall',
  'kfrnachweis.regel.alleuebrigen': 'alle übrigen',
  'kfrnachweis.regel.ausser': 'außer {arten}',
  'kfrnachweis.regel.alle': 'alle',
  'kfrnachweis.regel.umgekehrt': 'Vorzeichen umgekehrt',
  'kfrnachweis.regel.beteiligung': 'Beteiligungskonto der Mutter — Kaufpreis oder Verkaufserlös',
  'kfrnachweis.regel.kreis': 'Kasse aus Zugang oder Abgang im Konsolidierungskreis',
  'kfrnachweis.regel.kurs': 'Stichtagskursanpassung auf dem Finanzmittelfonds',
  'kfrnachweis.regel.rueckfall': 'Zeile an der Bewegungsart',
  'kfrnachweis.regel.fonds': 'Finanzmittelfonds am Anfang',
  // Rechtsklick → Eigenschaften (R857)
  'menue.eigenschaften': 'Eigenschaften',
  'kfreigenschaften.titel': 'Eigenschaften · {zeile}',
  'kfreigenschaften.regeln': 'Referenzierte Elemente aus Bilanz/GuV',
  // Das Fenster einer Summe (2.8 Abschnitt 13.43, R1351): Sie zieht nichts selbst.
  'kfreigenschaften.zaehlt': 'Was sie zählt',
  'kfreigenschaften.zaehlt.abschnitt': 'Zählt alle Zeilen des Abschnitts {abschnitt}.',
  'kfreigenschaften.zaehlt.darueber': 'Zählt alle Zeilen darüber.',
  'kfreigenschaften.zaehlt.seit_voriger': 'Zählt die Zeilen ihres Abschnitts seit der vorigen Summe.',
  'kfreigenschaften.zaehlt.unterzeilen': 'Zählt ihre Referenzen und die Positionen unter ihr.',
  // Seit dem 2026-10-06 überall derselbe Name (2.8 Abschnitt 13.46, R1356).
  'kfreigenschaften.quelle': 'Referenz auf Position/Konto',
  'kfreigenschaften.fonds': 'Was zum Finanzmittelfonds gehört',
  'kfreigenschaften.fonds.zweck': 'Eine Bilanzposition mit den Positionen unter ihr oder ein Konto — es gehört ganz zum Fonds',
  'kfreigenschaften.fonds.hinweis': 'Gilt für Anfang und Ende der Periode und für beide Zeilensätze.',
  'kfreigenschaften.fonds.leer': 'Noch gehört nichts zum Finanzmittelfonds.',
  'kfreigenschaften.quelle.zweck': 'Aus welcher Bilanz- oder GuV-Position — mit den Positionen darunter — oder aus welchem Konto die Zeile zieht',
  // Die Ankreuzliste der Bewegungsarten (2.8 Abschnitt 13.49, R1369).
  'kfreigenschaften.alleankreuzen': 'Alle ankreuzen',
  'kfreigenschaften.arten.fertig': 'Fertig',
  'kfreigenschaften.format': 'Format',
  'kfreigenschaften.format.standard': 'Standard',
  'kfreigenschaften.gruppe.bilanz': 'Bilanz',
  'kfreigenschaften.gruppe.guv': 'GuV',
  'kfreigenschaften.gruppe.konten': 'Konten',
  'kfreigenschaften.kontengruppe': 'Kontengruppe {name}',
  'kfreigenschaften.leer': 'Diese Zeile zieht noch nichts.',
  /* --- Die Richtung einer Zeile (2.8 Abschnitt 13.31, R1090–R1095) --- */
  'kfreigenschaften.neu': 'Referenz hinzufügen',
  'kfreigenschaften.entfernen': 'Entfernen',
  // Welche Konten unter einer Position stehen (R859, R860)
  'positionskonten.titel': 'Welche Konten hier stehen',
  'positionskonten.wirkung': 'Umhängen und Entfernen gelten',
  'positionskonten.ab': 'ab',
  'positionskonten.immer': 'immer',
  'positionskonten.freigegeben': '„Immer" reicht in freigegebene Abschlüsse zurück ({abschluesse}). Sie behalten ihren eingefrorenen Stand; jeder neu gerechnete Bericht zeigt das Konto an der neuen Stelle.',
  'positionskonten.ohneperiode': 'Ab welcher Periode? Ohne Periode gilt nur „immer".',
  'positionskonten.leer': 'Unter dieser Position steht kein Konto.',
  'positionskonten.vonanfang': 'von Anfang an',
  'positionskonten.umhaengen': 'Umhängen nach',
  'kachel.bericht.bilanz': 'Bilanz',
  'kachel.bericht.guv': 'GuV',
  'positionskonten.entfernen': 'Entfernen',
  'kfr.nachweis.summenzeile':
    'Zeile {zeile} ist eine Summenzeile — ihr Wert steht in ihren Summanden. Der Nachweis führt von dort ins Journal, nicht von hier: Eine Summe brächte tausend Belegzeilen, die niemand liest.',
  'kfr.bereich.operativ': '1. Laufende Geschäftstätigkeit',
  'kfr.bereich.investition': '2. Investitionstätigkeit',
  'kfr.bereich.finanzierung': '3. Finanzierungstätigkeit',
  'kfr.bereich.fonds': '4. Finanzmittelfonds',
  'kfr.probe.differenz':
    'Der Fondsnachweis weicht um {betrag} {waehrung} vom Bilanzbestand ab: Ein Betrag ließ sich nicht aus Bewegungen herleiten. Die Abstimmung gegen die Bewegungsbilanz nennt das Konto in der Spalte „Abweichung".',
  'kfr.kursanpassung.an': 'Stichtagskursanpassung ausgewiesen',
  'kfr.kursanpassung.aus': 'Stichtagskursanpassung nicht ausgewiesen',
  'kfr.kursanpassung.an.hilfe':
    'Die Bewegungsart „Delta Währung" steht in einer eigenen Zeile. Ausschalten heißt: Sie bleibt in der Zeile ihres Kontos.',
  'kfr.kursanpassung.aus.hilfe':
    'Die Bewegungsart „Delta Währung" steht in der Zeile ihres Kontos. Einschalten heißt: Sie bekommt eine eigene Zeile.',
  'kfr.abstimmung.titel': 'Abstimmung gegen die Bewegungsbilanz ({konten} Konten, {abweichend} mit Abweichung)',
  'kfr.abstimmung.nurabweichungen': 'Nur Abweichungen',
  'kfr.abstimmung.block.bilanz': 'Bilanz',
  'kfr.abstimmung.block.guv': 'GuV',
  'kfr.abstimmung.zeile': 'Referenz',
  // Der Grund einer Abweichung (2.1 Abschnitt 3.6, R1714).
  'kfr.abstimmung.grund.fehlt': 'fehlt',
  'kfr.abstimmung.grund.doppelt': 'doppelt',
  'kfr.abstimmung.grund.fehlt.erklaerung': 'Keine Position zieht diese Bewegung.',
  'kfr.abstimmung.grund.doppelt.erklaerung': 'Zwei Referenzen ziehen diese Bewegung mit demselben Vorzeichen.',
  'kfr.abstimmung.keine': 'keine Abweichung',
  'kfr.abstimmung.keinezeile': 'keine Referenz',
  'kfr.abstimmung.ohneart': 'Rest, keiner Buchung zugeordnet',
  'kfr.abstimmung.konto': 'Konto',
  'kfr.abstimmung.anfang': 'Anfangsbestand',
  'kfr.abstimmung.ende': 'Endbestand',
  'kfr.abstimmung.bewegungsbilanz': 'Bewegungsbilanz',
  'kfr.abstimmung.stichtagskurs': 'davon Stichtagskurs',
  'kfr.abstimmung.kreis': 'Effekt aus Zugang/Abgang Konsolidierungskreis',
  'kfr.abstimmung.umgliederung': 'Umgliederung im Eigenkapital',
  'kfr.abstimmung.inrechnung': 'In der Rechnung',
  'kfr.abstimmung.abweichung': 'Abweichung',
  /* 2.8 Abschnitt 13.51, R1675–R1677 — was keine Zeile nennen darf. */
  'kfr.abstimmung.ausgenommen': 'Ausgenommen',
  'kfr.abstimmung.ausgenommen.zweck':
    'Von der KFR ausgenommene Bewegung — was eine Ausnahme unten in der Tabelle nennt. Steht sie zusätzlich in einer Zeile, zählt sie doppelt und steht als Abweichung da.',
  'kfr.abstimmung.ausgenommen.marke': 'ausgenommen',
  'kfr.ausnahmen.titel': 'Von der KFR ausgenommene Bewegungen',
  'kfr.ausnahmen.zweck': 'Was keine Zeile der Kapitalflussrechnung nennen darf — für alle Zeilensätze gemeinsam',
  'kfr.ausnahmen.zeile': '{quelle} × {arten}',
  'kfr.ausnahmen.leer': 'Keine Ausnahme angelegt.',
  'kfr.ausnahmen.neu': 'Ausnahme hinzufügen',
  'kfr.ausnahmen.gegenprobe.auf':
    'Gegenprobe {bewegungsart}: ausgenommen {ausgenommen}, auf dem Finanzmittelfonds {fonds} — zusammen null, geht auf.',
  'kfr.ausnahmen.gegenprobe.nicht':
    'Gegenprobe {bewegungsart}: ausgenommen {ausgenommen}, auf dem Finanzmittelfonds {fonds} — geht um {rest} nicht auf. Der Hinweis unter der Rechnung nennt Monat und Gesellschaften.',
  'kfr.leer.titel': 'Keine Kapitalflussrechnung zu dieser Auswahl',
  /* R1118: nur, wenn die Rechnung in ihrer Ausgangswährung bleibt. */
  'kfr.titel.inwaehrung': '{titel} · in {waehrung}',
  'kfr.leer.grund': 'Für diesen Zeitraum liegt kein gerechneter Abschluss vor.',
  'kfr.befund.kein-lauf':
    'Zwischen {von} und {bis} liegen keine Daten vor. Eine Kapitalflussrechnung braucht je Periode einen Lauf, und ein Lauf braucht gelieferte Salden.',
  'kfr.befund.kein-anfangsbestand':
    'Für {periode} liegen keine Daten vor — ohne die Vorperiode gibt es keinen Anfangsbestand, und jede Bestandsveränderung wäre der ganze Bestand. Ein späterer Beginn des Zeitraums hilft weiter.',
  'kfr.befund.kein-katalog': 'Für die Bewertungsebene {ledger} gibt es für diese Rechnung keinen Zeilenkatalog. Die Rechnung ist älter als der Katalog und liest die Gliederung von damals; ein offener Abschluss wird deshalb nachgerechnet, ein freigegebener bleibt, wie er freigegeben wurde.',
  /* R63 — Finanzmittelfonds ist eine Kontoeigenschaft. */
  'kfr.befund.kein-fondskonto':
    'Kein Konto ist dem Finanzmittelfonds zugeordnet. Ohne Fondszugehörigkeit am Konto hat die Rechnung nichts, was sie erklären könnte.',
  /* R845 — Ein fehlender Kreiszugang wird beim Namen genannt (seit R1096 ohne Deltaposten). */
  'kfr.befund.umgliederung-nicht-null':
    '{gesellschaft}, {periode}, Spalte {spalte}: Die Umgliederung im Eigenkapital ergibt nicht null, sondern {betrag}. Eine Seite trägt vermutlich eine andere Bewegungsart — die Abstimmung gegen die Bewegungsbilanz zeigt beide Konten in der Spalte „Umgliederung im Eigenkapital".',
  /* R1677 — die Gegenprobe zu einer Ausnahme geht nicht auf. */
  'kfr.befund.gegenprobe':
    '{periode}, {bewegungsart}: Ausgenommen sind {ausgenommen}, auf dem Finanzmittelfonds stehen {fonds} — zusammen müssten sie null ergeben. Die Bewegungsart ist nicht vollständig gebucht ({gesellschaften}); die Abstimmung gegen die Bewegungsbilanz zeigt die Konten.',
  'kfr.befund.kreiszugang-fehlt':
    '{gesellschaft} ist seit {periode} im Konsolidierungskreis. Ihre Eröffnungswerte tragen nicht die Bewegungsart „Zugang Konsolidierungskreis“ und lassen sich deshalb mit {betrag} nicht herleiten — die Kapitalflussrechnung geht um diesen Betrag nicht auf. Abhilfe: die Saldenliste des Vormonats einlesen und neu rechnen — den Zugang bildet CoKo aus ihrem Stand.',

  /* --- Das Kachelbrett (7.5, R337–R349) ---

     Der Vorrat heißt, wie er heißt: „Kachel" und nicht „Widget". Das Glossar
     verbietet englische Wörter dort, wo die Fachsprache deutsche hat (R148) —
     und der Anwender, der ein Quartal lang nichts angefasst hat, liest
     „Kachel" ohne Umweg. */
  'kachel.brett.gestalten': 'Brett gestalten',
  'kachel.brett.fertig': 'Fertig',
  'kachel.brett.gestalten.hinweis':
    'Ziehen ordnet die Kacheln; die Pfeile tun dasselbe ohne Maus.',
  'kachel.brett.eigen': 'eigene Anordnung',
  'kachel.hinzufuegen': 'Kachel hinzufügen',
  'kachel.zuruecksetzen': 'Auf Auslieferung zurücksetzen',
  'kachel.zuruecksetzen.frage': 'Das Brett auf den Auslieferungszustand zurücksetzen?',
  'kachel.zuruecksetzen.hinweis': 'Ihre Anordnung geht dabei verloren.',
  'kachel.leer': 'Auf dem Brett steht nur die Arbeitsliste.',
  'kachel.leer.weg': 'Über „Brett gestalten" und „Kachel hinzufügen" kommen Kacheln dazu.',

  'kachel.katalog.titel': 'Was soll die Kachel zeigen?',
  'kachel.art.zahl': 'Zahl mit Weg',
  'kachel.art.zahl.zweck':
    'Eine Zahl, ihr Zustand und der Satz, was zu tun ist — anklickbar dorthin, wo man sie erledigt.',
  'kachel.art.verlauf': 'Verlauf über die Zeitachse',
  'kachel.art.verlauf.zweck':
    'Dieselbe Zahl über die Zeitachse — je nach Raster Monate, Quartale oder Jahre, als Balken oder Linie.',
  'kachel.art.stand': 'Stand und Fortschritt',
  'kachel.art.stand.zweck': 'Benannte Schritte mit ihrem Zustand — jeder Schritt ist anklickbar.',
  'kachel.gegenstand.schritte': 'Konsolidierungsschritte',
  'kachel.gegenstand.lieferstand': 'Lieferstand je Gesellschaft',
  'kachel.gegenstand.systemzustand': 'Systemzustand',

  'kachel.blatt.titel': 'Kachel einrichten',
  'kachel.feld.titel': 'Überschrift',
  'kachel.feld.titel.vorgabe': 'Ohne Eintrag steht die Bezeichnung aus dem Vorrat.',
  'kachel.feld.quelle': 'Die Zahl kommt',
  'kachel.quelle.gestellt': 'aus dem Bestand',
  'kachel.quelle.formel': 'aus einer Formel',
  'kachel.vorrat.formel': 'Eigene Kennzahl',
  'kachel.feld.groesse': 'Größe',
  'kachel.feld.formel': 'Formel',
  'kachel.formel.hinweis':
    'Bezüge in eckigen Klammern, Argumente mit Semikolon — zum Beispiel max([bilanz.A]; 0).',
  'kachel.formel.bezuege': 'Verfügbare Bezüge',
  'kachel.formel.einsetzen': 'In die Formel einsetzen',
  'kachel.formel.rechnung': 'Die Rechnung mit eingesetzten Zahlen',
  'kachel.formel.anzeige': 'Formel — Anzeige, kein Abschlusswert',
  'kachel.formel.anzeige.erklaerung':
    'Sie rechnet auf festgeschriebenen Werten und gelangt in keinen Bericht, keinen Abschluss und keinen Anhang.',
  'kachel.formel.steht': 'Die Formel ist gültig.',
  'kachel.feld.einheit': 'Zahlenart',
  'kachel.einheit.betrag': 'Betrag',
  'kachel.einheit.anzahl': 'Anzahl',
  'kachel.einheit.prozent': 'Prozent',
  'kachel.einheit.mswahl': 'Millisekunden',
  'kachel.einheit.ms': '{ms} ms',
  'kachel.feld.herkunft': 'Herkunft',
  'kachel.herkunft.folgt': 'Folgt der Auswahl oben',
  'kachel.herkunft.fest': 'Festgezurrt',
  'kachel.herkunft.folgt.kurz': 'folgt der Auswahl',
  'kachel.herkunft.fest.kurz': 'fest',
  'kachel.feld.darstellung': 'Darstellung',
  'kachel.darstellung.balken': 'Balken',
  'kachel.darstellung.linie': 'Linie',
  /* Auch das Zahlenfeld nennt das Raster: Es zählt in Monaten, Quartalen oder
     Jahren, und genau das steht daran. Anders als der Feldname in R430 wandert
     es mit — hier ist das richtig, weil die Zahl daneben wirklich Monate zählt. */
  'kachel.feld.einheiten.monat': 'Monate',
  'kachel.feld.einheiten.quartal': 'Quartale',
  'kachel.feld.einheiten.jahr': 'Jahre',
  'kachel.feld.schwellen': 'Ampel',
  'kachel.schwelle.gruen': 'grün ab',
  'kachel.schwelle.gelb': 'gelb ab',
  'kachel.schwelle.richtung': 'Gut ist',
  'kachel.schwelle.hoch': 'ein hoher Wert',
  'kachel.schwelle.niedrig': 'ein niedriger Wert',
  'kachel.schwelle.ohne': 'Ohne Eintrag zeigt die Kachel den Zustand, den die Zahl mitbringt.',
  'kachel.feld.breite': 'Breite',
  'kachel.breite.3': 'Viertel',
  'kachel.breite.4': 'Drittel',
  'kachel.breite.6': 'Hälfte',
  'kachel.breite.12': 'volle Breite',
  'kachel.feld.hoehe': 'Höhe',
  'kachel.hoehe.1': 'einfach',
  'kachel.hoehe.2': 'doppelt',

  'kachel.vor': 'Nach vorn',
  'kachel.zurueck': 'Nach hinten',
  'kachel.einrichten': 'Einrichten',
  'kachel.verdoppeln': 'Verdoppeln',
  'kachel.entfernen': 'Entfernen',
  'kachel.einklappen': 'Einklappen',
  'kachel.ausklappen': 'Ausklappen',
  'kachel.uebernehmen': 'Übernehmen',
  'kachel.abbrechen': 'Abbrechen',

  'kachel.laedt': 'wird geholt …',
  'kachel.keinlauf': 'Für diese Herkunft liegt kein Lauf vor.',
  'kachel.keinlauf.weg': 'Eine andere Periode wählen oder die Herkunft der Kachel ändern.',
  'kachel.verlauf.ohne': 'Die Position steht in diesem Bericht nicht.',
  'kachel.stand.ohne': 'Für diesen Stand liegen keine Angaben vor.',
  'kachel.stand.geliefert': '{geliefert} von {gesamt} geliefert',
  'kachel.stand.buchung': '1 Buchung',
  'kachel.stand.buchungen': '{anzahl} Buchungen',
  'kachel.stand.zusage': 'Über der Zusage von {budget} ms',
  'kachel.stand.ueberbudget': '{anzahl} von {gesamt} Aufrufen',
  'kachel.nachweis': 'Wertenachweis öffnen',

  /* --- Die Anmeldung (8.1, R355–R357, R362) ---

     Die Absage nennt nie, was falsch war: Wer unterscheidet, verrät, welche
     Namen es gibt — und ein Anmeldename ist in einem Haus der Nachname. */
  'anmeldung.name': 'Anmeldename',
  'anmeldung.kennwort': 'Kennwort',
  'anmeldung.anmelden': 'Anmelden',
  'anmeldung.abgeschaltet': 'Anmeldung abgeschaltet',
  'anmeldung.abgeschaltet.hinweis': 'Diese Anlage läuft ohne Anmeldung — für die Entwicklung. Jeder Aufruf gilt als „admin". In einer Auslieferung greift der Schalter nicht.',
  'anmeldung.abmelden': 'Abmelden',
  /* Mein Profil und die spielerischen Elemente (7.7, R920–R922). */
  'profil.titel': 'Mein Profil',
  'profil.zahlen': 'Zahlendarstellung',
  'profil.zahlen.hinweis': 'Gilt an jedem Rechner, an dem Sie sich anmelden.',
  'profil.spiel': 'Spielerische Elemente',
  'profil.spiel.hinweis': 'Nur für Sie sichtbar. Ohne Ihr Zutun ist alles ausgeschaltet.',
  'profil.spiel.erfolgsmomente': 'Erfolgsmomente',
  'profil.spiel.erfolgsmomente.hinweis':
    'Eine kurze Zeile über der Arbeitsliste, wenn ein Abschluss freigegeben ist. Sie erscheint einmal und verschwindet mit dem nächsten Klick.',
  'spiel.moment.freigabe': 'Abschluss {periode} steht — {kreis} ist freigegeben.',
  'profil.spiel.serie': 'Serie und Bestzeit',
  'profil.spiel.serie.hinweis':
    'Eine Zeile über der Startseite: wie viele Perioden der Kreis in Folge ohne begründete Warnung freigegeben hat, und die wenigsten Tage vom ersten Datenimport bis zur Freigabe.',
  'spiel.serie.keine': 'Serie: noch keine Periode ohne begründete Warnung',
  'spiel.serie.eins': 'Serie: 1 Periode ohne begründete Warnung',
  'spiel.serie.mehrere': 'Serie: {anzahl} Perioden in Folge ohne begründete Warnung',
  'spiel.bestzeit.eins': 'Bestzeit: 1 Tag vom ersten Datenimport bis zur Freigabe ({periode})',
  'spiel.bestzeit.mehrere': 'Bestzeit: {tage} Tage vom ersten Datenimport bis zur Freigabe ({periode})',
  'spiel.zuletzt.eins': 'zuletzt 1 Tag ({periode})',
  'spiel.zuletzt.mehrere': 'zuletzt {tage} Tage ({periode})',
  'anmeldung.laeuft': 'einen Augenblick …',
  'anmeldung.falsch': 'Anmeldename oder Kennwort stimmt nicht.',
  'anmeldung.gesperrt':
    'Zu viele Fehlversuche. Der Zugang ist für {minuten} Minuten gesperrt.',
  'anmeldung.vergessen':
    'Kennwort vergessen? Ihr Administrator setzt ein neues — dieses System verschickt keine E-Mail.',
  'anmeldung.angemeldetals': 'Angemeldet als {name}',

  'kennwort.titel': 'Kennwort wechseln',
  'kennwort.warum':
    '{name}, dieses Kennwort stammt aus der Auslieferung und muss gewechselt werden, bevor es weitergeht.',
  'kennwort.altes': 'Bisheriges Kennwort',
  'kennwort.neues': 'Neues Kennwort',
  'kennwort.nochmal': 'Neues Kennwort wiederholen',
  'kennwort.wechseln': 'Kennwort wechseln',
  'kennwort.richtlinie':
    'Mindestens zwölf Zeichen. Länge zählt, nicht Sonderzeichen — und Ihr Anmeldename darf nicht darin vorkommen.',
  'kennwort.zukurz': 'Das Kennwort ist zu kurz — es braucht mindestens zwölf Zeichen.',
  'kennwort.enthaeltnamen': 'Das Kennwort enthält Ihren Anmeldenamen.',
  'kennwort.altfalsch': 'Das bisherige Kennwort stimmt nicht.',
  'kennwort.ungleich': 'Die beiden neuen Kennwörter stimmen nicht überein.',
  'kennwort.wechselnoetig': 'Das Kennwort muss gewechselt werden.',

  /* --- Die Anwenderverwaltung (8.1, R358–R362) --- */
  'anwender.anlegen': 'Anwender anlegen',
  'anwender.spalte.name': 'Name',
  'anwender.spalte.rolle': 'Rolle',
  'anwender.spalte.rechte': 'Rechte',
  'anwender.spalte.geltung': 'Geltungsbereich',
  'anwender.spalte.zuletzt': 'Zuletzt angemeldet',
  'anwender.nie': 'nie',
  'anwender.gesperrt': 'gesperrt',
  'anwender.musswechseln': 'Kennwortwechsel steht aus',
  'anwender.aufallem': 'auf allem',
  'anwender.aktiv': 'Zugang aktiv',
  'anwender.erstkennwort': 'Erstkennwort',
  'anwender.neueskennwort': 'Neues Kennwort setzen',
  'anwender.kennwortbleibt': 'leer lassen — das bisherige bleibt',
  'anwender.rollevorschlag':
    'Die Rolle bestimmt das Kachelbrett und schlägt die Rechte vor. Was gilt, steht darunter — die Rechte hängen am Anwender.',
  'anwender.geltung.leer':
    'Leer heißt: auf allem. Ein Konzernkreis ist nur sichtbar, wenn alle seine Gesellschaften im Geltungsbereich liegen — eine konsolidierte Bilanz ohne eine Gesellschaft wäre keine Bilanz, sondern eine falsche Zahl.',
  'anwender.geltung.einheiten': 'Gesellschaften',
  'anwender.geltung.ledger': 'Bewertungsebenen',
  'anwender.geltung.von': 'Perioden ab',
  'anwender.geltung.bis': 'Perioden bis',
  'anwender.blatt.anlegen': 'Anwender anlegen',
  'anwender.blatt.aendern': 'Anwender ändern',
  'anwender.namevergeben': 'Diesen Anmeldenamen gibt es bereits.',
  'anwender.letzterverwalter':
    'Das ist der letzte Anwender mit dem Recht zu verwalten. Ohne ihn könnte niemand mehr Rechte vergeben.',
  /* Maschinenkennungen heißen beim Namen ihrer Herkunft (7.2 Abschnitt 20, R838) */
  'anwender.einrichtung': 'Einrichtung',
  'anwender.datenimport': 'Datenimport',
  'anwender.unbekannt': 'unbekannt',

  'verb.lesen': 'Lesen — Berichte, Journal und Strukturen ansehen',
  'verb.lesen.kurz': 'lesen',
  'verb.pflegen': 'Pflegen — Stammdaten und Strukturen ändern',
  'verb.pflegen.kurz': 'pflegen',
  'verb.importieren': 'Importieren — liefern, zuordnen, übernehmen',
  'verb.importieren.kurz': 'import',
  'verb.rechnen': 'Rechnen — einen Abschluss ausdrücklich rechnen',
  'verb.rechnen.kurz': 'rechnen',
  'verb.freigeben': 'Freigeben — einen Abschluss freigeben',
  'verb.freigeben.kurz': 'freigeben',
  'verb.freigabe_zuruecknehmen': 'Freigabe zurücknehmen',
  'verb.freigabe_zuruecknehmen.kurz': 'zurücknehmen',
  'verb.periode_wiedereroeffnen': 'Periode wiedereröffnen',
  'verb.periode_wiedereroeffnen.kurz': 'wiedereröffnen',
  'verb.verwalten': 'Verwalten — Anwender, Rechte, Regelwerk, Betrieb',
  'verb.verwalten.kurz': 'verwalten',
  'verb.pruefregeln_festlegen': 'Prüfregeln festlegen — wie streng die Freigabe prüft',
  'verb.pruefregeln_festlegen.kurz': 'Prüfregeln',

  /* --- Die erste Kachel des Prüfers (R353) --- */
  'abschlusstafel.titel': 'Abschlüsse und ihre Prüfcodes',
  'abschlusstafel.leer': 'Es ist noch kein Abschluss angelegt.',
  'abschlusstafel.ohnecode': 'noch nicht gerechnet',

  'kachel.groesse.befunde': 'Harte Befunde',
  'kachel.groesse.bilanzdifferenz': 'Bilanzdifferenz',
  'kachel.groesse.nachweis': 'Einträge im Änderungsnachweis',
  'kachel.groesse.antwortzeit': 'Antwortzeit, 95 % der Aufrufe',
  'kachel.groesse.buchungen': 'Buchungen im Lauf',
  'kachel.groesse.vorgaenge': 'Vorgänge im Lauf',

  /* --- Die Befunde der Formelsprache (R342) ---

     Kein Code, keine Zeichenstelle, kein `NaN`: Jeder Satz nennt die Ursache
     und sagt, was zu tun ist (R145). Wer „Unexpected token" liest, weiß
     nicht, dass hinter dem Malzeichen eine Zahl fehlt. */
  'formel.leer': 'Die Formel ist leer.',
  'formel.bezug.offen': 'Der Bezug „{text}" ist nicht geschlossen — es fehlt die Klammer „]".',
  'formel.bezug.leer': 'Zwischen den eckigen Klammern steht kein Bezug.',
  'formel.zahl.unlesbar': '„{text}" ist keine Zahl.',
  'formel.zeichen.unbekannt': 'Das Zeichen „{zeichen}" gehört nicht in eine Formel.',
  'formel.operand.fehlt': 'Nach „{zeichen}" fehlt eine Zahl oder ein Bezug.',
  'formel.klammer.zu': 'Es fehlt eine schließende Klammer.',
  'formel.klammer.auf': 'Eine schließende Klammer steht ohne öffnende.',
  'formel.ueberhang': 'Nach der Rechnung steht „{text}" — das gehört nicht mehr dazu.',
  'formel.funktion.unbekannt': 'Die Funktion „{name}" gibt es nicht. Es gibt: {vorrat}.',
  'formel.funktion.klammer': 'Nach „{name}" fehlt die öffnende Klammer.',
  'formel.funktion.stelligkeit': '„{name}" erwartet {erwartet} Werte, hier stehen {tatsaechlich}.',
  'formel.funktion.mindestens':
    '„{name}" erwartet mindestens {erwartet} Werte, hier stehen {tatsaechlich}.',
  'formel.bezug.unbekannt': 'Den Bezug „{bezug}" gibt es in diesem Bericht nicht.',
  'formel.nenner.null': 'Nicht berechenbar: Der Nenner „{nenner}" ist null.',
  'formel.nenner.null.ohne': 'Nicht berechenbar: Es wird durch null geteilt.',
  'formel.verlauf.gestellt':
    'Über die Zeitachse lassen sich nur Positionen zeichnen — „{bezug}“ ist eine Größe des laufenden Abschlusses.',
  'formel.waehrungen':
    'Die Bezüge stehen in {eine} und {andere}. Eine Kennzahl aus zwei Währungen ist keine Zahl.',

  /* --- Filtern am Spaltenkopf (7.6 Abschnitt 4, R650) ------------------ */

  'filter.hilfe': '{spalte} filtern',
  'filter.aktiv.hilfe': '{spalte} — gefiltert. Zum Ändern anklicken',
  'filter.enthaelt': 'enthält',
  'filter.von': 'von',
  'filter.bis': 'bis',
  'filter.loesen': 'Filter lösen',
  'filter.fertig': 'Fertig',
  'filter.alleloesen': 'Alle Filter lösen',
  'filter.wirkt': '{spalten} Filter wirken: {gezeigt} von {gesamt} Zeilen.',

  /* --- Der Arbeitsbereich Wechselkurse (7.6) --------------------------- */

  'kurse.paare': 'Wechselkurstabellen',
  'kurse.paare.suche': 'Währung oder Gesellschaft',
  'kurse.paare.keine': 'Keine Tabelle trifft auf diese Suche zu.',
  'kurse.paar.bestand': '{von} bis {bis}',
  'kurse.paar.luecken': '{anzahl} Monate ohne Stichtagskurs',
  'kurse.paar.vorgeschlagen': 'Noch keine Tabelle — berichtet wird darin von: {einheiten}',
  'kurse.paar.angelegt': 'Angelegt — noch ohne Kurse',
  'kurse.paar.ohneezb': 'Die EZB führt für {waehrung} keinen Referenzkurs. Diese Tabelle wird über „Datei einlesen" gefüllt.',
  'kurse.paar.ohneeuro': 'Die EZB notiert nur gegen den Euro. Diese Tabelle wird über „Datei einlesen" gefüllt.',

  'kurse.tabelle.neu': 'Neue Tabelle',
  'kurse.tabelle.neu.titel': 'Neue Wechselkurstabelle',
  'kurse.tabelle.neu.von': 'Von Währung',
  'kurse.tabelle.neu.nach': 'Nach Währung',
  'kurse.tabelle.neu.erlaeuterung':
    'Beide Währungen kommen aus dem Vorrat unter Stammdaten → Währungen. Was dort fehlt, legen Sie zuerst dort an.',
  'kurse.tabelle.anlegen': 'Anlegen',
  'kurse.tabelle.unvollstaendig.meldung': 'Eine Tabelle braucht zwei Währungen.',
  // R1478: Der historische Kurs steht in der Tabelle der Gesellschaft.
  'kurse.historisch.abgewiesen.meldung': 'Einen historischen Kurs nimmt die Monatstabelle nicht mehr an.',
  'kurse.historisch.abgewiesen.weg': 'Historische Kurse stehen je Gesellschaft unter Stammdaten › Historische Kurse.',
  'kurse.tabelle.gleich.meldung': '{waehrung} in {waehrung} umzurechnen ergibt immer eins — das ist keine Tabelle.',
  'kurse.tabelle.schonda.meldung': 'Die Tabelle {von} → {nach} gibt es schon.',

  'kurse.leer.titel': 'Wählen Sie eine Wechselkurstabelle',
  'kurse.leer.text':
    'Links stehen die gepflegten Tabellen und die, die es nach dem Bestand an Gesellschaften und Kreisen geben müsste.',

  'kurse.zeitraum.von': 'Von Periode',
  'kurse.zeitraum.bis': 'Bis Periode',

  'kurse.spalte.periode': 'Periode',
  'kurse.spalte.stichtag': 'Stichtagskurs',
  'kurse.spalte.durchschnitt': 'Durchschnittskurs',
  'kurse.spalte.ytd': 'Kumuliert seit Jahresbeginn',
  'kurse.spalte.herkunft': 'Herkunft',
  'kurse.spalte.paar': 'Währungen',
  'kurse.spalte.art': 'Kursart',
  'kurse.spalte.bisher': 'Bisher',
  'kurse.spalte.neu': 'Neu',
  'kurse.spalte.befund': 'Befund',

  'kurse.art.stichtag': 'Stichtag',
  'kurse.art.durchschnitt': 'Durchschnitt',
  'kurse.art.ytd': 'Kumuliert',
  'kurse.art.historisch': 'historisch',

  'kurse.tafel.erlaeuterung':
    'Ein Betrag in {von} mal Kurs ergibt den Betrag in {nach}. Das Geschäftsjahr beginnt im Monat {beginn} — darüber mittelt der kumulierte Kurs.',
  'kurse.tafel.leer': 'In diesem Ausschnitt steht keine Zeile.',
  'kurse.zelle.hilfe': 'Anklicken und überschreiben',
  'kurse.ytd.vorschlag.hilfe':
    'Was CoKo aus den Monatsdurchschnitten rechnen würde. Übernehmen Sie ihn über „Kumulierten Kurs rechnen".',

  'kurse.herkunft.importiert': 'importiert',
  'kurse.herkunft.manuell': 'manuell',
  'kurse.herkunft.fortgeschrieben': 'fortgeschrieben',
  'kurse.herkunft.gerechnet': 'gerechnet',
  'kurse.herkunft.unbekannt': 'nicht vermerkt',

  'kurse.quelle.datei': 'Datei einlesen',
  'kurse.sammel': 'Alle Tabellen von der EZB holen',
  'kurse.sammel.hinweis':
    'Die EZB liefert Referenzkurse für alle Tabellen gegen den Euro — auch für die, die noch leer sind. Ein Paar ohne Euro steht danach als abgewiesene Zeile in der Vorschau.',
  'kurse.sammel.hilfe':
    'Alle Wechselkurstabellen auf einmal von {adresse} holen — es werden keine Daten übermittelt. Geschrieben wird erst nach der Vorschau.',
  'kurse.quelle.ezb': 'Von der EZB holen',
  'kurse.quelle.ezb.hilfe': 'Referenzkurse von {adresse} holen — es werden keine Daten übermittelt.',
  'kurse.quelle.ezb.zu':
    'In dieser Anlage ist kein Weg nach außen eingerichtet. Laden Sie die Datei von der EZB-Seite und lesen Sie sie ein.',
  'kurse.ezb.warten': 'Die Kurse werden bei der EZB abgerufen — das dauert höchstens 30 Sekunden.',
  'kurse.ezb.warten.erneut':
    'Die EZB antwortet noch nicht. Es wird erneut versucht; nach 30 Sekunden wird abgebrochen ({sekunden} von 30 Sekunden).',
  'kurse.quelle.ytd': 'Kumulierten Kurs rechnen',
  'kurse.quelle.ytd.hilfe':
    'Aus den Monatsdurchschnitten seit Geschäftsjahresbeginn — als Vorschlag, den Sie übernehmen.',

  'kurse.nachweis': 'Herkunft der Zelle',
  'kurse.nachweis.leer': 'Klicken Sie einen Kurs an, um zu sehen, woher er kommt.',
  'kurse.nachweis.keinkurs': 'Für diesen Monat ist hier kein Kurs gepflegt.',
  'kurse.nachweis.ytdvorschlag': 'CoKo würde {wert} rechnen.',
  'kurse.nachweis.wert': 'Kurs',
  'kurse.nachweis.woher': 'Woher genau',
  'kurse.nachweis.erfasst': 'Erfasst',
  'kurse.zuruecknehmen': 'Kurs zurücknehmen',
  'kurse.zuruecknehmen.hinweis':
    'Zurücknehmen löscht nichts: Die Zeile bleibt in der Fassungsreihe lesbar, der Monat steht danach wieder als Lücke da.',


  'kurse.vorschau.datei': 'Aus der Datei',
  'kurse.vorschau.ezb': 'Von der EZB',
  'kurse.vorschau.ytd': 'Gerechneter kumulierter Kurs',
  'kurse.vorschau.leer':
    'Daraus ergibt sich keine Zeile. Prüfen Sie die Überschriften: Periode · Von · Nach · Stichtag · Durchschnitt · Kumuliert.',
  'kurse.vorschau.uebernehmen': 'Übernehmen',
  'kurse.vorschau.abbrechen': 'Verwerfen',

  'kurse.befund.neu': 'neu',
  'kurse.befund.geaendert': 'geändert',
  'kurse.befund.unveraendert': 'unverändert',
  'kurse.befund.abgewiesen': 'abgewiesen',



  /* --- Der Bereich Perioden (2.19, R670–R675) --- */
  'perioden.zweck': 'Dieses Haus führt Daten nur in diesem Zeitraum. Eine Lieferung davor oder danach weist CoKo ab, gleich auf welchem Weg sie kommt.',
  'perioden.anfang': 'Beginnt mit',
  'perioden.ende': 'Endet mit',
  'perioden.setzen': 'Zeitraum setzen',
  'perioden.vorrat': '{monate} Monate lang, davon {vorrat} noch vor Ihnen.',
  'perioden.vorrat.knapp':
    'Nur noch {vorrat} Monate. Verlängern Sie den Zeitraum, bevor die erste Lieferung abgewiesen wird.',
  'perioden.vorrat.abgelaufen':
    'Der Zeitraum ist mit {ende} abgelaufen: Jede Lieferung für einen späteren Monat wird abgewiesen.',
  'perioden.bestand': 'Gebucht ist von {aelteste} bis {juengste} — so weit muss der Zeitraum mindestens reichen.',
  'perioden.bestand.leer': 'Es ist noch nichts gebucht — der Zeitraum lässt sich frei setzen.',
  'perioden.fassung': 'Zuletzt gesetzt von {wer} am {wann}.',
  'perioden.ohne.titel': 'Dieses Haus hat keinen Zeitraum',
  'perioden.ohne.text': 'Ohne Zeitraum prüft CoKo nichts: Eine Lieferung für jeden beliebigen Monat ginge durch. Setzen Sie einen Anfang und ein Ende — sie gelten ab der nächsten Aktualisierung von CoKo.',

  'abbruch.zeitachse.anfang':
    'Dieses Datenmodell beginnt mit {grenze}. Was für {periode} geliefert wurde, ist nicht übernommen worden.',
  'abbruch.zeitachse.anfang.weg':
    'Soll dieses Haus auch ältere Monate führen, setzen Sie den Anfang unter Perioden früher — und liefern Sie danach erneut.',
  'abbruch.zeitachse.ende':
    'Die Zeitachse dieses Hauses endet mit {grenze}. Für {periode} lässt sich nichts buchen.',
  'abbruch.zeitachse.ende.weg':
    'Verlängern Sie den Zeitraum unter Perioden. Prüfen Sie dabei den Monat der Lieferung: Ein Vertipper im Jahr sieht genauso aus.',
  'abbruch.zeitachse.bestand.anfang':
    'In {periode} stehen {zeilen} Buchungszeilen. Der Anfang kann nicht dahinter liegen.',
  'abbruch.zeitachse.bestand.ende':
    'In {periode} stehen {zeilen} Buchungszeilen. Das Ende kann nicht davor liegen.',
  'abbruch.zeitachse.bestand.weg':
    'Erweitern geht immer. Wer den Zeitraum verengen will, löscht zuerst, was danach außerhalb läge.',
  'abbruch.zeitachse.unstimmig': '{anfang} bis {ende} ergibt keinen Zeitraum.',
  'abbruch.zeitachse.unstimmig.weg': 'Der Anfang muss vor dem Ende liegen — oder derselbe Monat sein.',
  /* R1050, R1051 — Die Abweisungen der Datenbank, an ihrer Kennung übersetzt (7.2 Abschnitt 30.4). */
  'abbruch.datenbank.beleg.gruppe':
    'Beleg {nummer} soll in eine Gruppe gebucht werden. Eine Gruppe ist die Summe ihrer Schichten und trägt selbst keine Buchung.',
  'abbruch.datenbank.beleg.gruppe.weg': 'Wählen Sie als Bewertungsebene eine Delta-Schicht, etwa Delta HGB.',
  'abbruch.datenbank.beleg.rohdaten':
    'Beleg {nummer} soll in die Rohdaten gebucht werden. Dort steht nur, was die Gesellschaft geliefert hat.',
  'abbruch.datenbank.beleg.rohdaten.weg': 'Buchen Sie Anpassungen in eine Delta-Schicht, etwa Delta HGB.',
  'abbruch.datenbank.beleg.nichtauf': 'Beleg {nummer} geht in {waehrung} nicht auf: Es fehlen {betrag}.',
  'abbruch.datenbank.beleg.nichtauf.weg':
    'Gleichen Sie Soll und Haben aus. Eine Ausgleichszeile entsteht nicht von selbst.',
  'abbruch.datenbank.berichtigen.unveraenderlich':
    'Beleg {nummer} wurde nicht berichtigt: Nummer, Gesellschaft, Bewertungsebene und Periode eines Belegs bleiben, wie sie sind.',
  'abbruch.datenbank.berichtigen.unveraenderlich.weg':
    'Das lässt sich nicht am Bildschirm beheben. Bitte den Betrieb verständigen.',
  'abbruch.datenbank.berichtigen.belegart':
    'Beleg {nummer} wurde nicht von Hand erfasst und lässt sich deshalb nicht berichtigen.',
  'abbruch.datenbank.berichtigen.belegart.weg': 'Stornieren Sie ihn, oder korrigieren Sie mit einer eigenen Buchung.',
  'abbruch.datenbank.berichtigen.storniert':
    'Beleg {nummer} ist durch {storno} storniert und lässt sich nicht mehr berichtigen.',
  'abbruch.datenbank.berichtigen.storniert.weg': 'Erfassen Sie die Korrektur als neuen Beleg.',
  'abbruch.datenbank.berichtigen.protokoll':
    'Beleg {nummer} wurde nicht berichtigt, weil der vorherige Stand nicht festgehalten werden konnte.',
  'abbruch.datenbank.berichtigen.protokoll.weg':
    'Das lässt sich nicht am Bildschirm beheben. Bitte den Betrieb verständigen.',
  'abbruch.datenbank.loeschen.belegart':
    'Beleg {nummer} lässt sich nicht löschen. Gelöscht werden nur von Hand erfasste Belege und Datenimporte.',
  'abbruch.datenbank.loeschen.belegart.weg': 'Stornieren Sie ihn.',
  'abbruch.datenbank.loeschen.geschlossen':
    'Der Datenimport {nummer} gehört zu {periode}, und dieser Abschluss ist geschlossen.',
  'abbruch.datenbank.loeschen.geschlossen.weg':
    'Eine neue Datei wird trotzdem angenommen und als Nachbuchung geführt. Löschen und ersetzen geht erst, wenn die Periode wieder geöffnet ist.',
  'abbruch.datenbank.loeschen.storniert':
    'Beleg {nummer} ist durch {storno} storniert und lässt sich nicht mehr löschen.',
  'abbruch.datenbank.loeschen.storniert.weg':
    'Lassen Sie beide stehen: Ein Storno ohne den aufgehobenen Beleg wäre eine Buchung ohne Bezug.',
  'abbruch.datenbank.loeschen.protokoll':
    'Beleg {nummer} wurde nicht gelöscht, weil sein Stand nicht festgehalten werden konnte.',
  'abbruch.datenbank.loeschen.protokoll.weg':
    'Das lässt sich nicht am Bildschirm beheben. Bitte den Betrieb verständigen.',
  'abbruch.datenbank.sprache.unbekannt': 'Die Sprache {sprache} ist nicht eingerichtet oder stillgelegt.',
  'abbruch.datenbank.sprache.unbekannt.weg': 'Wählen Sie eine eingerichtete Sprache.',
  'abbruch.datenbank.saldowechsel.summe':
    'Diese Position ist eine Summe und kann keinen Saldowechsel tragen.',
  'abbruch.datenbank.saldowechsel.summe.weg': 'Setzen Sie den Saldowechsel an einer Position darunter.',
  'abbruch.datenbank.saldowechsel.zielfehlt': 'Die gewählte Zielposition gibt es in dieser Gliederung nicht.',
  'abbruch.datenbank.saldowechsel.zielfehlt.weg': 'Wählen Sie eine Position derselben Gliederung.',
  'abbruch.datenbank.saldowechsel.zielsumme':
    'Die gewählte Zielposition ist eine Summe und trägt keinen eigenen Wert.',
  'abbruch.datenbank.saldowechsel.zielsumme.weg': 'Wählen Sie eine Position ohne Positionen darunter.',
  'abbruch.datenbank.saldowechsel.bericht':
    'Die gewählte Zielposition steht in einem anderen Bericht als diese Position.',
  'abbruch.datenbank.saldowechsel.bericht.weg':
    'Ein Saldowechsel bleibt in Bilanz oder GuV, wo er beginnt. Wählen Sie ein Ziel im selben Bericht.',
  'abbruch.datenbank.saldowechsel.kreis':
    'Der Saldowechsel führt über seine Ziele im Kreis auf diese Position zurück.',
  'abbruch.datenbank.saldowechsel.kreis.weg': 'Wählen Sie ein Ziel, das nicht wieder hierher weiterleitet.',
  'abbruch.datenbank.saldowechsel.kontobericht':
    'Ein Konto mit Saldowechsel lässt sich nur unter eine Position desselben Berichts hängen wie sein Ziel.',
  'abbruch.datenbank.saldowechsel.kontobericht.weg':
    'Passen Sie zuerst das Ziel des Saldowechsels am Konto oder an seiner Kontengruppe an.',
  'abbruch.datenbank.saldowechsel.kontoposition':
    'Konto und Position können nicht beide einen Saldowechsel tragen: Die Position wechselt schon als Ganzes.',
  'abbruch.datenbank.saldowechsel.kontoposition.weg':
    'Entfernen Sie zuerst das Ziel an der Position, wenn jedes Konto für sich wechseln soll.',
  'abbruch.datenbank.saldowechsel.positionkonto':
    'Konto und Position können nicht beide einen Saldowechsel tragen: Ein Konto dieser Position wechselt schon für sich.',
  'abbruch.datenbank.saldowechsel.positionkonto.weg':
    'Entfernen Sie zuerst das Ziel an den Konten oder an ihrer Kontengruppe, wenn die Position als Ganzes wechseln soll.',
  /* R1070 — Was der Server schickt, steht hier; bis zum 2026-10-05 fehlten diese 35 (7.2 Abschnitt 30.5). */
  'abbruch.erfassung.name': 'Der Beleg hat keinen Namen.',
  'abbruch.erfassung.name.weg': 'Geben Sie ihm einen kurzen Namen, unter dem Sie ihn im Ordner wiederfinden.',
  'abbruch.erfassung.begruendung': 'Die Begründung fehlt.',
  'abbruch.erfassung.begruendung.weg': 'Schreiben Sie dazu, warum eingegriffen wird — der Prüfer liest sie.',
  'abbruch.erfassung.zeilen': 'Der Beleg hat weniger als zwei Zeilen mit Betrag.',
  'abbruch.erfassung.zeilen.weg': 'Ein Beleg bucht Soll an Haben: Tragen Sie mindestens zwei Zeilen mit Betrag ein.',
  'abbruch.erfassung.konto': 'Das Konto {konto} gibt es nicht mehr.',
  'abbruch.erfassung.nurcoko': '{konto} ist ein technisches Konto ab Werk — darauf bucht nur CoKo.',
  'abbruch.erfassung.konto.weg': 'Wählen Sie in dieser Zeile ein anderes Konto.',
  'abbruch.erfassung.bewegungsart.unbekannt': 'Die Bewegungsart {art} gibt es nicht mehr.',
  'abbruch.erfassung.bewegungsart.unbekannt.weg': 'Wählen Sie in dieser Zeile eine andere Bewegungsart.',
  'abbruch.erfassung.bewegungsart': 'Auf {konto} ist die Bewegungsart {art} nicht zulässig.',
  'abbruch.erfassung.bewegungsart.weg':
    'Das Konto steht in einem Spiegel. Wählen Sie eine Bewegung dieses Spiegels, etwa einen Zugang oder eine Umbuchung.',
  'abbruch.erfassung.bewegungsart.guv.weg':
    'Auf einem Erfolgskonto wird ohne Bewegungsart gebucht. Lassen Sie das Feld leer.',
  'abbruch.erfassung.doppelt': 'Auf {konto} stehen zwei Zeilen, die sich in nichts unterscheiden.',
  'abbruch.erfassung.doppelt.weg':
    'Fassen Sie sie zu einer Zeile zusammen, oder unterscheiden Sie sie in einem Merkmal, etwa der Bewegungsart.',
  'abbruch.aendern.belegart':
    'Beleg {nummer} wurde nicht von Hand erfasst und lässt sich deshalb nicht berichtigen.',
  'abbruch.aendern.belegart.weg': 'Stornieren Sie ihn, oder korrigieren Sie mit einer eigenen Buchung.',
  'abbruch.aendern.storniert': 'Beleg {nummer} ist durch {storno} storniert und lässt sich nicht mehr berichtigen.',
  'abbruch.aendern.storniert.weg': 'Erfassen Sie die Korrektur als neuen Beleg.',
  'abbruch.umkehrung.belegart':
    'Beleg {nummer} wurde nicht von Hand erfasst und lässt sich deshalb nicht in die Folgeperiode auflösen.',
  'abbruch.umkehrung.belegart.weg': 'Aufgelöst werden nur von Hand erfasste Belege.',
  'abbruch.umkehrung.schon': 'Beleg {nummer} ist schon durch {umkehrung} in der Folgeperiode aufgelöst.',
  'abbruch.umkehrung.schon.weg': 'Ein Beleg wird einmal aufgelöst. Sehen Sie sich {umkehrung} an.',
  'abbruch.umkehrung.kette': 'Beleg {nummer} ist selbst eine Auflösung und wird nicht noch einmal aufgelöst.',
  'abbruch.umkehrung.kette.weg':
    'Stimmt der Sachverhalt nicht, stornieren Sie die Auflösung und erfassen Sie neu.',
  'abbruch.umkehrung.zeitpunkt': 'Eine Auflösung „{zeitpunkt}“ gibt es nicht.',
  // R1835: Der Ergebnisvortrag in Fremdwährung trägt den Wert des Vormonats.
  'abbruch.ergebnisvortrag-ohne-vormonat.meldung':
    '{einheit} trägt einen Ergebnisvortrag in Fremdwährung. Er steht mit dem Wert aus {vormonat} — dafür fehlt der Lauf.',
  'abbruch.ergebnisvortrag-ohne-vormonat.weg': 'Rechnen Sie zuerst {vormonat}.',
  'abbruch.umkehrung.zeitpunkt.weg':
    'Wählen Sie im Belegordner, ob der Beleg in der Folgeperiode, zum Geschäftsjahresende oder zu Beginn des neuen Geschäftsjahres endet.',
  'abbruch.beleg.unbekannt': 'Diesen Beleg gibt es nicht mehr.',
  'abbruch.beleg.unbekannt.weg': 'Laden Sie den Ordner neu — vermutlich hat ihn inzwischen jemand gelöscht.',
  'abbruch.spiegelart.meldung': 'Einen Spiegel „{art}" gibt es nicht.',
  'abbruch.spiegelart.weg': 'Es gibt den Anlagen-, den Eigenkapital- und den Verbindlichkeitenspiegel.',
  'abbruch.raster.meldung': 'Ein Raster „{raster}" gibt es nicht.',
  'abbruch.raster.weg': 'Gerechnet wird je Monat, Quartal oder Jahr.',
  'import.mapping.widerspruch.meldung':
    'Die lokale Nummer {lokal} mit diesem Namen ist schon {konto} zugeordnet.',
  'import.mapping.widerspruch.weg':
    'Eine Nummer mit demselben Namen gehört zu einem Konto. Ändern Sie die bestehende Zuordnung, statt eine zweite anzulegen.',
  'stammdaten.pflicht.meldung': 'Das Feld „{feld}" ist leer. Ohne es lässt sich nichts speichern.',
  /* R1386, R1388 — Einbeziehung ab ist Pflicht an jeder einbezogenen Beteiligung, Austritt sobald sie endet. */
  'beteiligung.eintritt.pflicht.weg':
    'Wählen Sie, ob die Gesellschaft zu Beginn oder zum Ende der Periode eintritt. Davon hängt ab, ob das Ergebnis des Erwerbsmonats in der Konzern-GuV steht.',
  'beteiligung.austritt.pflicht.weg':
    'Die Beteiligung endet. Wählen Sie, ob die Gesellschaft zu Beginn oder zum Ende dieser Periode austritt.',
  'beteiligung.aufsatz.pflicht.weg':
    'Jede Aufsatzart verlangt ihre Angaben: die Werte zum Erwerb die Nutzungsdauer des Firmenwerts, die Stände zum Aufsatz Restbuchwert, Restnutzungsdauer und Ausgleichsposten. Tragen Sie die Angabe ein oder wählen Sie eine andere Aufsatzart.',
  'beteiligung.aufsatz.ohne-art.meldung': 'Das Feld „{feld}“ gehört zum Aufsatz, aber es ist keine Aufsatzart gewählt.',
  'beteiligung.aufsatz.ohne-art.weg': 'Wählen Sie die Aufsatzart oder leeren Sie das Feld. Eine Tochter, die in CoKo erworben wird, braucht keinen Aufsatz.',
  'beteiligung.aufsatz.nach-erwerb.meldung': 'Ein Aufsatz gilt nur für eine Tochter, die vor ihrem ersten Monat in CoKo erworben wurde.',
  'beteiligung.aufsatz.nach-erwerb.weg': 'Tragen Sie die Erwerbsperiode ein; sie muss vor dem Aufsatzmonat liegen. Liegt der Erwerb im Aufsatzmonat oder danach, gilt die Lieferung, und die Aufsatzart bleibt leer.',
  'erstkonsolidierung.nur-aufsatz.meldung': 'Eigenkapital zum Einbezug wird nur für den Aufsatz mit den Werten zum Erwerb erfasst.',
  'erstkonsolidierung.nur-aufsatz.weg': 'Wählen Sie bei der Beteiligung die Aufsatzart „Werte zum Erwerb“. Wurde die Tochter in CoKo erworben, gilt ihre Lieferung; eine falsche Lieferung wird in der HB II berichtigt.',
  'abbruch.beteiligung.ohne-eintritt.meldung':
    'An der Beteiligung an {tochter} ist nicht gewählt, ab wann sie einbezogen wird.',
  'abbruch.beteiligung.ohne-eintritt.weg':
    'Wählen Sie im Beteiligungsregister „Einbeziehung ab“. Bis dahin rechnet die Kapitalkonsolidierung nicht.',
  'meldung.kein-positionsplan':
    'Für diese Bewertungsebene ist keine Gliederung freigegeben. Der Lauf hat deshalb keine Zeilen; geben Sie unter Stammdaten eine Gliederung frei.',
  'abbruch.ezb.paar':
    'Die EZB notiert nur gegen den Euro; {von} nach {nach} gibt es dort nicht.',
  'abbruch.ezb.paar.weg':
    'Holen Sie beide Kurse gegen den Euro und tragen Sie den Kreuzkurs von Hand ein — ein gerechneter Kreuzkurs wird vorgeschlagen, nie automatisch verwendet.',
  'abbruch.ezb.nichterreichbar': 'Die EZB hat in 30 Sekunden nicht geantwortet — {versuche} Versuche.',
  'abbruch.ezb.nichterreichbar.weg':
    'Rufen Sie die Kurse in einigen Minuten erneut ab. Lässt Ihr Rechenzentrum den Aufruf nach außen nicht zu, laden Sie die Kursdatei von der EZB-Seite herunter und lesen Sie sie über „Datei einlesen" ein — Vorschau und Nachweis sind dieselben.',
  'abbruch.ezb.leer':
    'Die EZB hat für {waehrung} → {gegenwaehrung} von {von} bis {bis} keinen Kurs veröffentlicht.',
  'abbruch.ezb.leer.alle': 'Die EZB hat von {von} bis {bis} für keine Kurstabelle einen Kurs veröffentlicht.',
  'abbruch.ezb.leer.weg':
    'Die EZB veröffentlicht Kurse erst für Tage, die vorbei sind. Wählen Sie einen Zeitraum, der schon begonnen hat.',
  /* --- Eingabefehler: fachlich, am Ort (7.2 Abschnitt 30, R1004) --- */
  'eingabe.keinedatei': 'Es ist keine Datei gewählt.',
  'eingabe.keinedatei.weg': 'Wählen Sie eine Datei aus und starten Sie erneut.',
  'eingabe.package.einemappe': 'Ein Reporting Package ist genau eine Mappe.',
  'eingabe.package.einemappe.weg':
    'Für mehrere Gesellschaften oder Perioden laden Sie jede Mappe für sich hoch (ein Package ist ein Gegenstand).',
  'eingabe.package.gesellschaft': 'Ein Package gilt für eine Gesellschaft, und keine ist gewählt.',
  'eingabe.package.gesellschaft.weg': 'Wählen Sie die Gesellschaft; sie wird mit dem Deckblatt der Mappe verglichen.',
  'eingabe.quelle.unbekannt': 'Die Datenquelle {quelle} gibt es nicht.',
  'eingabe.quelle.unbekannt.weg': 'Wählen Sie eine Datenquelle aus der Liste.',
  'eingabe.quelle.nichtlesbar': 'Aus der Quelle {quelle} kann heute nicht gelesen werden ({art}).',
  'eingabe.quelle.nichtlesbar.weg':
    'Dateiquellen laufen. Für Datenbanken und LucaNet-Ausleitungen liefert heute ein Mensch die Datei — wählen Sie dafür eine Dateiquelle.',
  'eingabe.lieferung.einheitperiode': 'Für die Lieferung fehlen Gesellschaft oder Periode.',
  'eingabe.lieferung.einheitperiode.weg':
    'Wählen Sie beides beim Starten des Imports — eine Quelle gilt für jede Gesellschaft, die so liefert.',
  'eingabe.stapel.gesellschaft': 'Für den Stapel ist keine Gesellschaft gewählt.',
  'eingabe.stapel.gesellschaft.weg': 'Wählen Sie die Gesellschaft — die Dateien bringen nur die Periode mit.',
  'eingabe.stapel.quelle': 'Für den Stapel ist keine Datenquelle gewählt.',
  'eingabe.stapel.quelle.weg':
    'Wählen Sie die Datenquelle — an ihr steht, ob die Periode aus dem Dateinamen oder aus einer festen Zelle kommt.',
  'abbruch.kurs-fehlt.ytd':
    'Für {waehrung} nach {konzernwaehrung} fehlt in der Periode {periode} der kumulierte Kurs.',
  /* Die Belegmaske bucht nicht in eine geschlossene Periode (2.16). Der Satz
     fehlte bis zum 2026-09-30 ganz; die Periode kommt als Periode und steht
     als Monat und Jahr da (R839) */
  'abbruch.erfassung.geschlossen':
    '{periode} ist im Kreis {kreis} geschlossen — in diesem Monat wird nicht mehr gebucht.',
  // Der geschlossene Monat (2.13 Abschnitt 20.7, R1690–R1693).
  'abbruch.periode.geschlossen.meldung': '{periode} ist im Kreis {kreis} geschlossen und wird nicht neu gerechnet.',
  'abbruch.periode.geschlossen.weg':
    'Eine Änderung gehört in {folge}, mit den Salden oder einer Buchung dort. Wer {periode} selbst ändern will, öffnet ihn im [[Konzernabschluss-Monitor]] wieder.',
  'abbruch.periode.geschlossen.weg.jahresende':
    '{periode} beendet das Geschäftsjahr; eine Änderung kann nicht im neuen Jahr nachkommen. Öffnen Sie {periode} im [[Konzernabschluss-Monitor]] wieder.',
  'datenimport.geschlossen.meldung': '{periode} ist im Kreis {kreis} geschlossen — diese Lieferung wird nicht übernommen.',
  'konzernabschluss.geschlossen.meldung': '{periode} ist geschlossen und wurde nicht neu gerechnet; es gilt sein gespeicherter Lauf.',
  'monitor.schluss.schon.meldung': '{periode} ist schon geschlossen.',
  'monitor.schluss.schon.weg': 'Laden Sie die Seite neu — der Monat wurde inzwischen geschlossen.',
  'monitor.schluss.offen.meldung': '{periode} ist nicht geschlossen.',
  'monitor.schluss.offen.weg': 'Laden Sie die Seite neu — der Monat wurde inzwischen wiedereröffnet.',
  'monitor.zuruecknahme.nichtfrei.meldung': '{periode} ist nicht freigegeben.',
  'monitor.zuruecknahme.nichtfrei.weg': 'Zurücknehmen lässt sich nur eine Freigabe. Laden Sie die Seite neu.',
  'monitor.zuruecknahme.testiert.meldung': '{periode} ist testiert.',
  'monitor.zuruecknahme.testiert.weg': 'Ein testierter Abschluss wird nicht wiedereröffnet; eine spätere Korrektur ist ein zweiter Lauf.',
  'monitor.begruendung.fehlt.meldung': 'Die Begründung fehlt.',
  'monitor.begruendung.fehlt.weg': 'Schreiben Sie in einem Satz, warum — er steht danach im Nachweis.',
  'schloss.titel': 'Schließen und Freigabe',
  'schloss.zustand.geschlossen': '{periode} ist geschlossen.',
  'schloss.zustand.geschlossen.folge': 'Keine Lieferung, keine Buchung und kein Neurechnen für diesen Monat.',
  'schloss.zustand.offen': '{periode} ist offen.',
  'schloss.zustand.offen.folge': 'Lieferungen und Buchungen wirken auf diesen Monat; er lässt sich neu rechnen.',
  'schloss.schliessen': 'Periode schließen',
  'schloss.schliessen.frage':
    '{periode} schließen? Danach nimmt der Monat keine Lieferung und keine Buchung mehr an und wird nicht neu gerechnet. Eine Korrektur kommt mit den Salden des Folgemonats.',
  'schloss.schliessen.frage.jahresende':
    '{periode} schließen? Danach nimmt der Monat keine Lieferung und keine Buchung mehr an und wird nicht neu gerechnet. Weil er das Geschäftsjahr beendet, kommt eine Korrektur nicht mit den Salden des Januar nach — dafür öffnen Sie ihn wieder.',
  'schloss.schliessen.bestaetigen': 'Monat schließen',
  'schloss.wiedereroeffnen': 'Periode wiedereröffnen',
  'schloss.wiedereroeffnen.frage':
    '{periode} wiedereröffnen? Danach nimmt der Monat wieder Lieferungen und Buchungen an und lässt sich neu rechnen.',
  'schloss.wiedereroeffnen.bestaetigen': 'Wiedereröffnen',
  'schloss.zuruecknehmen': 'Freigabe zurücknehmen',
  'schloss.zuruecknehmen.frage': 'Die Freigabe von {periode} zurücknehmen? Die freigegebenen Stände bleiben mit Prüfcode erhalten.',
  'schloss.zuruecknehmen.betroffen': 'Diese Monate verlieren ihre Freigabe:',
  'schloss.zuruecknehmen.bestaetigen': 'Freigabe zurücknehmen',
  'schloss.begruendung': 'Begründung',
  'schloss.erfolg.geschlossen': '{periode} ist geschlossen.',
  'schloss.erfolg.wiedereroeffnet': '{periode} ist wiedereröffnet.',
  'schloss.erfolg.zurueckgenommen': 'Freigabe zurückgenommen: {liste}.',
  'abbruch.erfassung.geschlossen.weg':
    'Buchen Sie in einer offenen Periode. Eine geschlossene Periode wieder zu öffnen ist ein eigenes Recht und geschieht am Abschluss, nicht in der Belegmaske.',

  ja: 'Ja',
  nein: 'Nein',

  /* Die Schreibweise einer Periode im Eingabefeld (R502) — seit dem 2026-09-30
     nimmt das Feld mehrere Schreibweisen und nennt sie in seinem Hinweis (R839) */
  'periode.muster': 'MM.JJJJ',
  'periode.muster.hinweis': 'Angenommen werden 12.2026, 12/2026, 12-2026, 2026-12 und 202612.',
  'periode.ungueltig':
    'Das liest sich nicht als Periode. Angenommen werden 12.2026, 12/2026, 12-2026, 2026-12 und 202612 — Monat und Jahr, etwa 09.2026 für den September 2026.',
  /* Die Schreibweise einer Zahl im Zahlenfeld der Stammdaten (7.2 Abschnitt 22,
     R840) — die Beispiele stehen im Zahlenformat des Anwenders */
  'zahl.muster.hinweis': 'Angenommen werden {mit} und {ohne}.',
  'zahl.ungueltig': 'Das liest sich nicht als Zahl. Angenommen werden {mit} und {ohne}.',
  'anteil.muster.hinweis': 'In Prozent: 80, 80 % oder {beispiel}.',
  'anteil.ungueltig':
    'Das liest sich nicht als Anteil. Getippt wird in Prozent zwischen 0 und 100 — etwa 80 für achtzig Prozent oder {beispiel}.',
  'anlage.nummer.ungueltig':
    'Eine Kontonummer besteht nur aus Ziffern. Was das Konto ist, steht in der Bezeichnung — nicht in der Nummer.',
  'anlage.konto.nichtnumerisch.meldung':
    'Die Nummer {nummer} ist keine Kontonummer: Eine Kontonummer besteht nur aus Ziffern.',
  'anlage.konto.nichtnumerisch.weg':
    'Tragen Sie die Ziffern als Nummer ein und den Klartext als Bezeichnung. Die Nummer wird zum Schlüssel und lässt sich danach nicht mehr ändern.',

  /* Die Kapitalflussrechnung: angelegt wird nur darunter (2.1 Abschnitt 3.6, R1706) */
  'anlage.titel.kfrzeile': 'Neue Position unter {position}',
  'anlage.titel.kfrsummenzeile': 'Neue Summenzeile unter {position}',
  'reihenfolge.menue': 'Reihenfolge ändern …',
  'reihenfolge.titel': 'Reihenfolge ändern · {element}',
  'reihenfolge.liste': 'Die Elemente dieser Ebene',
  'reihenfolge.anfang': 'An den Anfang',
  'reihenfolge.hoch': 'Nach oben',
  'reihenfolge.runter': 'Nach unten',
  'reihenfolge.ende': 'Ans Ende',
  'reihenfolge.sortieren': 'Alphanumerisch sortieren',
  'reihenfolge.immer': 'Immer alphanumerisch sortiert halten',
  'reihenfolge.uebernehmen': 'Übernehmen',
  'reihenfolge.fehler': 'Die Reihenfolge ließ sich nicht ändern.',
  'reihenfolge.veraltet.meldung': 'Die Ebene hat sich geändert, während das Fenster offen war. Öffnen Sie es neu.',
  'reihenfolge.element.meldung': '{element} steht in keiner Ebene, deren Reihenfolge sich ändern lässt.',
  'reihenfolge.art.meldung': 'Für {art} gibt es keine Reihenfolge zu ändern.',
  'kfr.loeschen.titel': 'Position löschen',
  'kfr.loeschen.titel.summenzeile': 'Summenzeile löschen',
  'kfr.loeschen.frage': '{zeile} löschen?',
  'kfr.loeschen.ohnereferenzen': 'Unter ihr hängt nichts. Sie zählt danach nirgends mehr mit.',
  'kfr.loeschen.wirdzeile': 'Unter {summe} hängt danach keine Position mehr.',
  'kfr.loeschen.knopf': 'Löschen',
  'kfr.loeschen.fehler': '{zeile} ließ sich nicht löschen.',
  'kfr.loeschen.hindernis.rolle': '{zeile} hat eine feste Aufgabe in der Rechnung und lässt sich nicht löschen.',
  'kfr.loeschen.hindernis.referenzen':
    'Unter {zeile} hängen {anzahl} Referenzen. Löschen Sie zuerst die Referenzen; eine Position, unter der etwas hängt, wird nicht gelöscht.',
  'kfr.loeschen.hindernis.summe': 'Unter {zeile} hängen Positionen. Löschen Sie zuerst die Positionen darunter.',
  'kfr.loeschen.hindernis.ohnesumme': '{zeile} zählt in keiner Summenzeile mit und lässt sich deshalb nicht löschen.',
  'kfr.loeschen.hindernis.letzte': '{zeile} ist die letzte Position unter {summe} in einer älteren Fassung und lässt sich nicht löschen.',
  'stammdaten.nummer': 'Nummer',
  'stammdaten.art.kfr_abschnitt': 'Abschnitte der Kapitalflussrechnung',
  'stammdaten.art.kfr_fonds': 'Finanzmittelfonds',
  'stammdaten.zweck.kfr_fonds.position': 'Welche Bilanzposition zum Finanzmittelfonds gehört — mit allen Positionen darunter',
  'stammdaten.zweck.kfr_fonds.konto': 'Welches einzelne Konto zum Finanzmittelfonds gehört',
  'stammdaten.art.kfr_ausnahme': 'Von der KFR ausgenommene Bewegung',
  'stammdaten.zweck.kfr_ausnahme.position': 'Welche Bilanz- oder GuV-Position die Ausnahme nennt — mit allen Positionen darunter',
  'stammdaten.zweck.kfr_ausnahme.konto': 'Welches einzelne Konto die Ausnahme nennt',
  'stammdaten.zweck.kfr_ausnahme.bewegungsarten': 'Welche Bewegungsarten keine Zeile der Kapitalflussrechnung nennen darf — jede ausdrücklich angekreuzt',
  'stammdaten.zweck.kfr_abschnitt.standard': 'Zu welchem Standard der Abschnitt gehört — DRS 21 oder IAS 7',
  'stammdaten.zweck.kfr_abschnitt.bereich': 'Welcher der vier Abschnitte: laufende Tätigkeit, Investition, Finanzierung oder Fonds',
  // Was die Position zog, bleibt an ihr (2.1 Abschnitt 3.6, R1709). Bis zum
  // 2026-10-14 ging es auf die neue Zeile über.
  'anlage.kfr.machtzursumme': '{position} zählt danach ihre Referenzen und die Positionen unter ihr zusammen.',
  'anlage.hindernis.summenzeile': '{position} ist eine Summenzeile. Unter einer Summenzeile hängt nichts.',
  'anlage.hindernis.fondsabschnitt':
    'Unter dem Finanzmittelfonds entsteht nichts: Der Fonds am Ende zählte es nicht mit.',
  // Die Referenzen beim Anlegen und im Eigenschaftsfenster (2.8 Abschnitt 13.46, R1355–R1358).
  // Die Referenzen als eigene Zeilen (2.1 Abschnitt 3.6, R1705, R1710, R1712).
  'kfr.referenz.umgekehrt': 'Vorzeichen umgekehrt',
  // Woher ein Betrag kommt, vor dem Namen (2.1 Abschnitt 3.6, R1730).
  'element.kurz.referenz': 'Ref.',
  'element.kurz.statistik': 'Stat.',
  'element.art.referenz': 'Referenz — der Betrag kommt aus der Bilanz oder GuV',
  'element.art.statistik': 'Statistikkonto — der Betrag ist von Hand gebucht',
  /*
    **Das Statistikkonto** (2.1 Abschnitt 3.6, R1715, R1726–R1732): von Hand
    gebucht, ohne Soll und Haben, nur in der Kapitalflussrechnung.
  */
  'kfr.statistik.anlegen.titel': 'Neues Konto unter {position}',
  'kfr.statistik.anlegen.hinweis':
    'Ein Konto in der Kapitalflussrechnung ist ein Statistikkonto: Es wird von Hand gebucht, ' +
    'ohne Soll und Haben, und erscheint nicht in Bilanz und GuV.',
  'erfolg.statistikkonto.angelegt': 'Statistikkonto erfolgreich angelegt',
  'kfr.befund.statistik-kurs':
    'Für {periode} fehlt der Durchschnittskurs {waehrung} → {konzernwaehrung}. Statistikkonten in {waehrung} ' +
    'zählen in diesem Monat nicht mit.',
  'beleg.spalte.betrag': 'Betrag (+/−)',
  'beleg.statistik.hinweis':
    'Statistikkonten unterliegen nicht den Regeln der Buchhaltung: kein Soll und Haben, und der Beleg muss ' +
    'nicht aufgehen. Tragen Sie den Betrag mit + oder − so ein, wie er in der Kapitalflussrechnung ' +
    'erscheinen soll.',
  'beleg.statistik.bewegungsart': 'Statistikkonto — ohne Bewegungsart',
  'beleg.statistik.gemischt':
    'Ein Statistikkonto steht nicht mit anderen Konten in einem Beleg. Buchen Sie es in einem eigenen Beleg.',
  'abbruch.erfassung.statistikgemischt': 'Der Beleg trägt Statistikkonten und andere Konten zugleich.',
  'abbruch.erfassung.statistikgemischt.weg':
    'Buchen Sie die Statistikkonten in einem eigenen Beleg — ein Statistikbeleg trägt nur Statistikkonten.',
  'abbruch.datenbank.beleg.statistikgemischt':
    'Beleg {nummer} trägt Statistikkonten und andere Konten zugleich.',
  'abbruch.datenbank.beleg.statistikgemischt.weg':
    'Buchen Sie die Statistikkonten in einem eigenen Beleg — ein Statistikbeleg trägt nur Statistikkonten.',
  'abbruch.datenbank.konto.statistikwechsel':
    'Ein Konto wurde nicht geändert: Es wird nie zum Statistikkonto und hört nie auf, eines zu sein.',
  'abbruch.datenbank.konto.statistikwechsel.weg':
    'Das lässt sich nicht am Bildschirm beheben. Bitte den Betrieb verständigen.',
  'journal.art.statistik': 'Statistikbeleg',
  'belegtyp.statistik': 'Statistikbuchung',
  'stammdaten.wert.statistik': 'Statistikkonto',
  'kfr.referenz.anlegen.titel': 'Neue Referenz unter {position}',
  'kfr.referenz.bezeichnung.hinweis': 'Vorbelegt mit der Quelle. Die Bezeichnung steht in der Zeile; keine Prüfung liest sie.',
  'kfr.referenz.loeschen.titel': 'Referenz löschen',
  'kfr.referenz.loeschen.frage': '{referenz} unter {position} löschen?',
  'kfr.referenz.loeschen.hinweis': 'Die Position zieht danach nicht mehr, was diese Referenz zog.',
  'kfr.referenz.loeschen.fonds': 'Was über diese Referenz zum Finanzmittelfonds gehörte, gehört danach nicht mehr dazu.',
  'kfr.referenz.loeschen.fehler': 'Die Referenz ließ sich nicht löschen.',
  'erfolg.referenz.angelegt': 'Referenz erfolgreich angelegt',
  'erfolg.referenz.geloescht': 'Referenz erfolgreich gelöscht',
  'kfr.referenz.unvollstaendig.meldung':
    'Eine Referenz ist noch nicht vollständig. Wählen Sie Referenz auf Position/Konto und kreuzen Sie mindestens eine Bewegungsart an.',
  'kfr.summenzeile.zaehlweise.meldung':
    'Wählen Sie, was die Summenzeile zählt: alles darüber oder seit der vorigen Summenzeile. Eine Summenzeile entsteht unter dem Band eines Abschnitts.',
  'kfr.summenzeile.referenzen.meldung': 'Eine Summenzeile zieht nichts selbst und hat deshalb keine Referenzen.',
  'kfr.zeile.summenzeile.meldung': 'Unter {zeile} lässt sich nichts anlegen: {zeile} ist eine Summenzeile.',
  'kfr.abschnitt.fonds.meldung': 'Unter dem Finanzmittelfonds entsteht nichts: Der Fonds am Ende zählte es nicht mit.',
  'kfr.zeile.keinplatz.meldung':
    'Neben {zeile} ist in der Reihenfolge kein Platz mehr für eine weitere Zeile. Legen Sie sie an einer Nachbarzeile an.',
  'anlage.hindernis.kfrkonto':
    'An einer Zeile der Kapitalflussrechnung hängt kein Konto: Beträge erreichen sie über ihre Zuordnung von Bewegungsart und Kontengruppe, nicht über ein Konto darunter.',
  'anlage.hindernis.keinesumme':
    'Unter einer aufnehmenden Zeile gibt es keine Ebene. Eine neue Zeile entsteht unter einer Summe — dort, wo sie mitgerechnet wird.',
  'kfr.zeile.unbekannt.meldung': 'Die Zeile {zeile} gibt es im Zeilenkatalog nicht.',
  'kfr.zeile.ohnecode.meldung': 'Ohne Nummer keine Zeile: Aus ihr entsteht der Schlüssel.',
  'kfr.zeile.ohnename.meldung': 'Ohne Bezeichnung keine Zeile. Tragen Sie ein, wie sie in der Rechnung heißen soll.',
  'kfr.zeile.keinesumme.meldung': 'Unter {zeile} lässt sich keine Zeile anlegen: {zeile} nimmt selbst Beträge auf.',
  'kfr.zeile.keinesumme.weg': 'Legen Sie die Zeile unter der Summe an, in die sie eingehen soll — dann rechnet diese Summe sie mit.',

  /* Die Felder einer KFR-Zeile (R507) */
  'stammdaten.feld.standard': 'Gliederungsstandard',
  'stammdaten.feld.bereich': 'Bereich der Rechnung',
  'stammdaten.feld.rolle': 'Rolle in der Rechnung',
  'stammdaten.feld.kontengruppe': 'Kontengruppe',
  'stammdaten.feld.bewegungsart': 'Bewegungsart',
  'stammdaten.feld.bewegungsarten': 'Bewegungsarten',
  'stammdaten.feld.zeile': 'Zeile',
  'stammdaten.feld.alle_bewegungsarten': 'Alle Bewegungsarten',
  'stammdaten.feld.vorzeichen_umkehren': 'Vorzeichen umkehren',
  'stammdaten.wert.bereich.operativ': 'Laufende Geschäftstätigkeit',
  'stammdaten.wert.bereich.investition': 'Investitionstätigkeit',
  'stammdaten.wert.bereich.finanzierung': 'Finanzierungstätigkeit',
  'stammdaten.wert.bereich.fonds': 'Finanzmittelfonds',
  'stammdaten.wert.rolle.zahlungsunwirksam': 'Zahlungsunwirksame Posten',
  'stammdaten.wert.rolle.erwerb': 'Erwerb konsolidierter Unternehmen',
  'stammdaten.wert.rolle.veraeusserung': 'Verkauf konsolidierter Unternehmen',
  'stammdaten.wert.rolle.wechselkurs': 'Wechselkursbedingte Änderung des Fonds',
  'stammdaten.wert.rolle.kreisaenderung': 'Konsolidierungskreisbedingte Änderung des Fonds',
  'stammdaten.wert.rolle.fonds_anfang': 'Fonds am Anfang',
  'stammdaten.wert.rolle.fonds_ende': 'Fonds am Ende',
  'stammdaten.wert.rolle.periodenergebnis': 'Periodenergebnis',
  'stammdaten.feld.zaehlweise': 'Was sie zählt',
  'stammdaten.wert.zaehlweise.abschnitt': 'Alle Zeilen ihres Abschnitts',
  'stammdaten.wert.zaehlweise.darueber': 'Alles darüber',
  'stammdaten.wert.zaehlweise.seit_voriger': 'Seit der vorigen Summe',
  'stammdaten.wert.zaehlweise.unterzeilen': 'Ihre Referenzen und die Positionen unter ihr',
  // Das Format einer Summenzeile (R1359).
  'stammdaten.feld.schrift': 'Schrift',
  'stammdaten.feld.hintergrund': 'Hintergrund',
  'stammdaten.feld.schriftfarbe': 'Schriftfarbe',
  'stammdaten.wert.schrift.fett': 'fett',
  'stammdaten.wert.schrift.normal': 'normal',
  'stammdaten.wert.hintergrund.keiner': 'keiner',
  'stammdaten.wert.hintergrund.grau': 'grau',
  'stammdaten.wert.hintergrund.blau': 'blau',
  'stammdaten.wert.hintergrund.gruen': 'grün',
  'stammdaten.wert.hintergrund.gelb': 'gelb',
  'stammdaten.wert.hintergrund.rot': 'rot',
  'stammdaten.wert.hintergrund.violett': 'violett',
  'stammdaten.wert.schriftfarbe.grau': 'grau',
  'stammdaten.wert.schriftfarbe.blau': 'blau',
  'stammdaten.wert.schriftfarbe.gruen': 'grün',
  'stammdaten.wert.schriftfarbe.rot': 'rot',
  'stammdaten.wert.schriftfarbe.violett': 'violett',
  'stammdaten.wert.art.zeile': 'Aufnehmende Zeile',
  'stammdaten.wert.art.summe': 'Summe über andere Zeilen',
  'stammdaten.wert.art.nachweis': 'Nachweis unter der Rechnung',

  /* Löschen: zwei Wege, und der Anwender wählt (R509–R512) */
  'stammdaten.stelle.buchungsbelege': 'Buchungsbelege',
  'stammdaten.stelle.datenimporte': 'Datenimporte',
  'loeschen.stammdatum.titel': '{code} löschen',
  'loeschen.element.darunter.eins':
    '{name} lässt sich nicht löschen: Unter ihr hängt noch ein Element. Eine Position, unter der etwas hängt, wird nicht gelöscht — schneiden Sie das Element erst aus und fügen Sie es anderswo ein, oder löschen Sie es.',
  'loeschen.element.darunter':
    '{name} lässt sich nicht löschen: Unter ihr hängen noch {anzahl} Elemente. Eine Position, unter der etwas hängt, wird nicht gelöscht — schneiden Sie die Elemente erst aus und fügen Sie sie anderswo ein, oder löschen Sie sie.',
  'loeschen.nichtsgefunden':
    'Auf diesem Element steht nichts. Es wird entfernt — die Fassungsreihe bleibt lesbar, im Änderungsnachweis steht, wer wann entfernt hat.',
  'loeschen.gefunden': 'Wo dieses Element vorkommt',
  'loeschen.eigen.titel': 'Geht mit',
  'loeschen.eigen.text':
    'Diese Zuordnungen gibt es nur für dieses Element. Sie hindern nichts und verschwinden mit ihm.',
  'loeschen.kinder.titel': 'Hängt daran',
  'loeschen.kinder.text':
    'Diese Elemente gibt es weiter, wenn dieses fällt. Sagen Sie, wohin sie gehören — ohne Ziel verlören sie still ihre Stelle.',
  'loeschen.ziel': 'Die Kinder gehören künftig zu',
  'loeschen.weg': 'Wie soll gelöscht werden?',
  'loeschen.weg.stilllegen': 'Ab Periode stilllegen — die Vergangenheit bleibt',
  'loeschen.weg.stilllegen.text':
    'Das Element gilt ab der genannten Periode nicht mehr, die Kinder ziehen ab derselben Periode um. Was vorher gerechnet wurde, bleibt unverändert.',
  'loeschen.weg.entfernen': 'Restlos entfernen — ohne periodengerechten Verlauf',
  'loeschen.weg.entfernen.text':
    'Die Kinder ziehen als Korrektur um, so als hätte es nie anders gestanden, und das Element verschwindet. Gedacht für den Fall, dass beim ersten Abschluss ein struktureller Fehler auffällt und nicht als Karteileiche in den Stammdaten stehen bleiben soll.',
  'loeschen.verzicht':
    'Ich hänge die Elemente darunter bewusst um und verzichte ausdrücklich auf das periodengerechte Stilllegen unter Wahrung der Vergangenheit.',
  'loeschen.gesperrt':
    'Restlos entfernen geht nicht mehr: {anzahl} freigegebene Abschlüsse kennen dieses Element. Ihr Prüfcode deckt genau diese Zeilen — was er festhält, steht still.',
  'loeschen.tun.stilllegen': 'Ab {periode} stilllegen',
  'loeschen.tun.entfernen': 'Restlos entfernen',
  'loeschen.beleg.oeffnen': 'Beleg {nummer} ansehen',

  /* Die Absagen des Löschwegs */
  'loeschen.objektart.meldung': '{objektart} ist keine gepflegte Stammdatenart.',
  'loeschen.unbekannt.meldung': 'Dieses Element gibt es nicht mehr — vermutlich hat es jemand gelöscht. Laden Sie die Seite neu.',
  'loeschen.freigegeben.meldung':
    'Der Abschluss {abschluss} für {periode} ist freigegeben und kennt dieses Element.',
  'loeschen.freigegeben.weg':
    'Legen Sie es ab einer Periode still. Ein freigegebener Abschluss ist ein eingefrorener Stand: Sein Prüfcode deckt die Zeilen, in denen das Element steht — entfernte man es, stimmte der Prüfcode nicht mehr mit dem überein, was er absichert.',
  'loeschen.ohneperiode.meldung': 'Zum Stilllegen gehört die Periode, ab der es gilt.',
  'loeschen.ohneziel.meldung': 'An diesem Element hängen {anzahl} weitere — ohne Ziel bleiben sie ohne Stelle.',
  'loeschen.ohneziel.weg': 'Sagen Sie, wohin sie künftig gehören. Sie ziehen dann im selben Handgriff um.',
  'loeschen.ohnestilllegung.meldung': '{objektart} kennt keine Stilllegung — hier gibt es nur das Entfernen.',
  'loeschen.zielistselbst.meldung': 'Das Ziel ist das Element selbst. Die Kinder brauchen ein anderes Zuhause.',

  'stammdaten.ohnecode.meldung': 'Das Feld „Code“ ist leer.',
  'stammdaten.ohnecode.weg':
    'Tragen Sie den Code ein, zum Beispiel „EUR“ bei einer Währung oder „DE“ bei einem Land. Danach lässt sich der Eintrag anlegen.',
  'strukturanlage.konto.ohnebezeichnung.meldung': 'Das neue Konto hat keine Bezeichnung.',
  'strukturanlage.konto.ohnebezeichnung.weg':
    'Tragen Sie die Bezeichnung mit der Kontonummer vorn ein, zum Beispiel „0200 Technische Anlagen“. Danach lässt sich das Konto anlegen.',

  /* Die Abschnitte des Blattes klappen auf (R515) */
  //
  // **Einzahl und Mehrzahl sind zwei Sätze** — dasselbe Paar wie im Kopf der
  // Arbeitsliste. Bis zum 2026-09-18 stand hier nur die Mehrzahl, und das fiel
  // nicht auf, weil ein neu angelegtes Objekt immer zwei Sprachen trug; seit
  // R588 trägt es eine, und dann stand dort „1 Sprachen".
  'loeschen.gefunden.erlaeuterung':
    'Bevor {code} gelöscht wird: Hier kommt es vor. Was zählt, sehen Sie an der Zahl; die Einzelheiten klappen auf.',
  'loeschen.bewegung.text':
    'Gebuchte und gerechnete Zeilen stehen auf diesem Element. Gebuchte lassen sich im Journal löschen, gerechnete entstehen beim nächsten Lauf neu.',
  'loeschen.ziel.erlaeuterung':
    'Ohne Ziel wird nicht gelöscht — ein Element ohne Zuhause verlöre still seine Stelle.',
  'loeschen.zurueck': 'Abbrechen',
  'loeschen.wirdgeprueft': 'Es wird nachgesehen, wo dieses Element vorkommt …',

  /* Der Zeitsprung in der Fassungsreihe (R517) */
  'stammdaten.fassung.heute': 'Heute',
  'stammdaten.fassung.heute.zusatz': 'der geltende Stand',
  'stammdaten.fassung.danach': 'Spätere Stände',
  'stammdaten.fassung.ansehen': 'Stand vom {zeit} ansehen',
  'stammdaten.blick.marke': 'Alt-Stand.',
  'stammdaten.blick.satz': 'Dieses Blatt steht so da, wie es am {zeit} war.',
  'stammdaten.blick.satz.ohne': 'Dieses Blatt steht so da, wie es vor seiner ersten hier verzeichneten Änderung war.',
  'stammdaten.blick.erlaeuterung':
    'Gepflegt wird immer am heutigen Stand, deshalb ist hier nichts änderbar. Die Reihe rechts bleibt vollständig — was nach dieser Fassung kam, steht dort abgesetzt und ist weiter anklickbar.',
  'stammdaten.blick.heute': 'Zurück zum heutigen Stand',

  /* Was heute anders steht (R518) */
  'stammdaten.heute.wert': 'Heute: {wert}',
  'stammdaten.blick.anders': 'Heute anders: {stellen}',
  'stammdaten.blick.gleich': 'Inhaltlich unverändert bis heute.',

  /* Die jüngste Fassung ist der heutige Stand (R522) */
  'stammdaten.fassung.heute.name': 'Heutiger Stand — zuletzt geändert am {zeit}',
}

export type Uebersetzung = typeof de
export type Sprachschluessel = keyof Uebersetzung
